import { weaponStats } from "@hyperlight/shared";
import {ShotRecoil} from './shotRecoil';
/**
 * Third-person character — the skinned GLB body (bodyModels.ts) on the UE5
 * "Manny" skeleton (`mannyRig.ts`), so Manny clips (ground locomotion + jetpack
 * flight) bind and play NATIVELY, no retargeting. The avatar code refers to
 * bones by Mixamo-style LOGICAL names (Spine2, LeftArm, …) via BONE_ALIAS,
 * which map to the Manny bones. An inner `rigRoot` applies a 180° yaw so the
 * avatar faces our -Z forward; the bob/lean live on the `body` group above it.
 * There is no procedural body: a zero-geometry bind-anchor SkinnedMesh carries
 * the mixer/bindMatrix contract and the GLB pieces bind onto it.
 *
 * Locomotion is driven by clips through an AnimationMixer when on the ground;
 * airborne / no-clips falls back to procedural poses. Aim, reload and the jetpack
 * poses use bind-agnostic LIMB TARGETING (point a bone's child-axis at a world
 * direction).
 *
 * Root (`group`) sits at the FEET. Floating-origin placement + facing are set
 * on `group` by the caller each frame.
 */
import * as THREE from "three";
import { BoundedSkinnedMesh } from './boundedSkinnedMesh';
import { applyBodyDeformers, resetBodyDeformers } from "./bodyDeformers";
import { FootfallTracker } from './footfalls';
import {DeathRagdoll,DeathPhysicsSpace,RAGDOLL_BONES,RAGDOLL_FIT,type RagdollOptions} from './deathPhysics';
import {RagdollSurface} from './ragdollSurface';
import { CrystalTravelPose } from './crystalTravelPose';
import { GlideAttitude } from './glideAttitude';
import {FlightVerticalPose,flightVerticalAlignment,FLIGHT_VERTICAL_HOLD,FLIGHT_VERTICAL_FULL} from './flightVerticalPose';
import {FlightAimTransition} from './flightAimTransition';
import {PoseInertializer} from './poseInertializer';
import { BODY_BUILDS, validateBodyBuild, bodyShapeKey, validateBodyShape, validateFaceId, hairAsset, type FaceId, type BodyShape } from "@hyperlight/shared";
import { resolveCharacterPose, itemPoseType, ITEM_POSE_PROFILES } from "@hyperlight/shared";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { DEFAULT_STYLE, ITEMS, RIFLE_BUTT_SEAT_Z, STYLE_PARTS, weaponHold, type CharacterStyle, type StylePart, type VehicleRiderPoseDef, type WeaponConstruct, type WeaponDef, type WeaponHoldDef, type WeaponStance } from "@hyperlight/shared";
import { COMBAT_ACTIONS, SWORD_COMBO_CHAIN, SWORD_AIR_CHAIN, GS_COMBO_CHAIN, type CombatActionId } from "@hyperlight/shared";
import { COSMETIC_COVERS, COSMETIC_OCCLUSION_MASKS, COSMETICS, type CosmeticSkin } from "./cosmetics";
import { appearanceVerts, applyCovers, clothingCoverage, BODY_MODELS, disposeAvatarGeometry, instanceBodyGeometry, loadPiece, type AppearanceVerts, type BodyRegion, type PieceGroup, type PiecePrim } from "./bodyModels";
import { applyGarmentPose } from "./garmentPose";
import { fitBodyNeck, fitEyePivot, type HeadFit } from "./headFit";
import { scalpHairFacets, type HairScalpTint } from "./hairScalpTint";
import { HairMotion } from "./hairPhysics";
import type { HairRigDef } from "./hairRig";
import { fitHairToHead } from "./hairFit";
import { clipForBody, rebindBodyActions } from "./bodyClips";
import { rigProfile } from "./rigProfile";
import { BasePose, legPoseOwner } from "./posePipeline";
import { WallRunTransition } from "./wallRunTransition";
import {ledgeDuration} from './ledgeClimb';
import {LedgePose} from './ledgePose';
import {DanceContact} from './danceContact';
import {bladeRunCadence} from '../combat/bladeLocomotion';
import {BladeStride} from './bladeStride';
import {FLIGHT_LAUNCH_TOTAL,FLIGHT_LAUNCH_TIME,FlightLaunchPoseClock,FlightDiveStartClock,FlightLaunchAlignment,flightLaunchClipWeight} from './flightLaunch';
import { applyRelaxedPosture } from "./posture";
import { AnimationComposition, type CompositionState } from "./animationComposition";
import { compositionForItem } from "./compositionProfiles";
import type { AnimationCompositionV1 } from "@hyperlight/shared";
import { PALETTE, PALETTE_HEX } from "./palette";
import { DIRS8, getClipsAndReports, isOnce, loadPreviewClip, RIG_YAW, type AvatarClips, type ClipKey, type ClipReport } from "./anim";
import { MANNY_RIG, MANNY_BONE_NAMES } from "./mannyRig";
import { MALE_RIG, MALE_CLOTH, type MaleClothDef } from "./maleRig";
import { FEMALE_RIG, FEMALE_CLOTH } from "./femaleRig";
import { ClothSim, CAPSULES } from "./cloth";
import { attachPbdCape, type PbdCape } from "./clothPbdCape";
import { SkinnedBvhCollider } from "./clothBvh";

/** Scratch for the per-vertex cloth step (module-level: one avatar steps at a time). */
const _pbdDown = new THREE.Vector3();
const _pbdQuat = new THREE.Quaternion();
import { AIM_OFFSET_BONES, AIM_OFFSET_ARM_START } from "./aimOffsetData";
import { AimOffsetSampler } from "./aimOffset";
import { addStylizedShading } from "../world/stylizedShading";
import { MOVES, type MoveId, canCancelInto, CANCEL_WINDOW } from "./moves";
import { FaceRig } from "./faceRig";
import { composeEyeLook } from "./eyeLook";
import { CrystalWings, CRYSTAL_WING_REST, type CrystalWingState } from './crystalWings';
import type {CrystalWeaponFrame,CrystalWeaponShape} from './crystalWeapon';
import { CrystalWingBodyPose } from './crystalWingBody';
import { DIVE_LANDING, diveLandingLead } from './diveLanding';
import { CrystalWingDockAnchor } from './crystalWingDynamics';
import { CrystalLimbAnchors } from './crystalLimbThrusters';

/** Whole-rig drop (m) so animated feet meet the ground (see rigRoot setup). */
const GROUND_DROP = 0.0;

/** Stride frequency: phase advances ~this many radians per meter walked. */
const STRIDE_RATE = 2.3;
// Boost-run KEEPS the normal sprint clip but SLOWS its stride (BOOST_STRIDE timeScale, eased by
// the boost weight) and layers a procedural "leap" — a vertical bound per stride + a forward lunge —
// so each slow, big stride reads as the player launching forward.
const BOOST_STRIDE = 0.55; // sprint-clip timeScale at full boost (slow, big strides)
const BOOST_LEAP = 0.2; // vertical bound (m) per stride at full boost
const CROUCH_DROP = 0.5; // how far the BODY sinks when crouching IDLE (m). The crouch clips tuck the legs
// UP toward a fixed pelvis instead of lowering the hips, so the avatar floated with legs pulled up; we
// drop the whole body so the feet return to the ground and the torso/camera lower.
const CROUCH_MOVE_LIFT = 0.5; // crouch-WALK tucks the legs less than crouch-idle, so the fixed drop sits
// too low while moving — ease the drop back up by this fraction at crouch-walk speed.
const BOOST_LEAN = 0.18; // forward lunge (rad) at full boost
const BOOST_FOOT_POINT = 0.8; // boost-run: plantarflex (point) an OFF-ground foot, scaled by its lift
// UNIVERSAL landing system. The avatar anticipates ANY real fall's touchdown and crossfades the fall
// loop into a land clip picked by along-ground SPEED (phase) + vertical IMPACT (tier).
const LAND_LEAD = 0.24; // start the land this many seconds (time-to-ground) before touchdown
// DIVE landing: the SuperHero flight-land clip is a fly→touch JOURNEY (the pelvis descends ~1.5m into a
// deep crouch — preserved via keepHipsPos), authored to IMPACT at ~1.05s in. Enter only its final
// approach near the ground; align the impact, then fit its recovery into the controller's one-second
// lock. The same clock lifts the wings before contact and settles them during recovery.
const DIVE_LAND_IMPACT_T = DIVE_LANDING.clipImpact;
/** Inertialization span for snapped strikes and combo links (s): long enough to
 *  hide the pose cut, short against the fastest windup (light 1 hits at 34 ms). */
const STRIKE_INERTIA_S = 0.1;
/** …growing to this for a large pose change (the light 2→3 link turns ~90°);
 *  still inside every combo step's startup, so hit timing is untouched. */
const STRIKE_INERTIA_MAX_S = 0.18;
/** Most the chain clock may trail the action clock (hitstop is ≤150 ms). */
const MAX_ANIM_LAG_MS = 300;
/** Inertialization span when movement cancels a swing's follow-through (s). */
const MOVE_CANCEL_INERTIA_S = 0.14;
const LAND_MIN_DESCENT = 3; // min descent speed (m/s) to play a land at all — below = a slope-walk, no clip
const LAND_MOVE_EXIT = 0.12; // once grounded + MOVING, cut the land to this so the run/walk takes over fast
const LAND_SPRINT_MIN = 6; // ground speed (m/s) at/above which the land is the SPRINT phase
const LAND_RUN_MIN = 3.1; // ... the RUN phase (matches RUN_ANIM_MIN)
const LAND_WALK_MIN = 0.75; // ... the WALK phase; below = STAND
/** PROCEDURAL AIR SQUAT (applyJumpLegs). Deliberately deep — a jump should read as a tuck, not a
 *  hang. Axes are the measured local-Z hinges (client/test/_thighaxis.mjs): thigh +Z swings the knee
 *  forward, shin −Z folds it, foot −Z points the toes. */
const JUMP_SQUAT_THIGH = 1.05;
const JUMP_SQUAT_SHIN = 1.55;
const JUMP_SQUAT_FOOT = 0.4;
/** The idle cycle that keeps the tuck ALIVE — legs run on opposite phases. */
const JUMP_SQUAT_HZ = 0.55;
const JUMP_SQUAT_SWING = 0.11;
/** Seconds to ease the squat in off the ground — quick, so the launch reads as a push-off. */
const JUMP_SQUAT_IN = 0.14;
const JUMP_START_DUR = 0.35; // a regular jump's phase START clip (js_<phase>) plays this long, then → bl_loop
/** Bone-local axis used by setLimb to point a bone toward its child. */
const LIMB_AXIS = new THREE.Vector3(0, 1, 0);
const _companionTarget = new THREE.Vector3();
const _companionParent = new THREE.Quaternion();
const _companionWorld = new THREE.Quaternion();
const _companionFinger = new THREE.Quaternion();
const _companionZ = new THREE.Vector3(0, 0, 1);
const _companionPalm = new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(
  new THREE.Vector3(0, 0, -1), new THREE.Vector3(0, 1, 0), new THREE.Vector3(1, 0, 0),
));

/** The avatar code refers to bones by Mixamo-style LOGICAL names; the live rig is
 *  the UE5 "Manny" skeleton (so clips bind natively, no retarget). This maps each
 *  logical name to its Manny bone. HeadTop_End has no Manny bone — the head sphere
 *  synthesizes a top point from `head` (see buildBodyGeometry). */
const BONE_ALIAS: Readonly<Record<string, string>> = {
  Hips: "pelvis", Spine: "spine_01", Spine1: "spine_03", Spine2: "spine_05",
  Neck: "neck_01", Head: "head", HeadTop_End: "head",
  LeftShoulder: "clavicle_l", LeftArm: "upperarm_l", LeftForeArm: "lowerarm_l", LeftHand: "hand_l",
  RightShoulder: "clavicle_r", RightArm: "upperarm_r", RightForeArm: "lowerarm_r", RightHand: "hand_r",
  LeftUpLeg: "thigh_l", LeftLeg: "calf_l", LeftFoot: "foot_l", LeftToeBase: "ball_l",
  RightUpLeg: "thigh_r", RightLeg: "calf_r", RightFoot: "foot_r", RightToeBase: "ball_r",
};

// ---------------------------------------------------------------------------
// GOD-MODE RIG TUNE (dev): live BIND-POSE tweaks, shared by every avatar. Mathematically
// identical to the offline build knobs (build-male.mts FIT_TWEAK / LANDMARKS / HEAD_SCALE):
// rot spins how geometry binds about the joint (clips carry it into every pose), pos moves
// the joint pivot (children stay put — same as editing a landmark), scale is uniform about
// the joint (children inherit, so the head's eyes/hair follow). Values use the NATURAL frame
// (character faces +Z, +X = character's left) so they copy 1:1 into build-male.mts.
// ---------------------------------------------------------------------------
export interface RigTune {
  rot?: [number, number, number]; // degrees, world X/Y/Z
  pos?: [number, number, number]; // metres, world X/Y/Z
  scale?: number;
}

/** Per-avatar silhouette controls used by Avatar Lab and, later, the player outfitter.
 * Unlike RigTune these never touch global bind data: two avatars can have different
 * proportions in the same scene. Values are normalized multipliers around 1. */
export interface AvatarProportions {
  headSize: number;
  shoulderWidth: number;
  armLength: number;
  armThickness: number;
  legLength: number;
  legThickness: number;
  handSize: number;
  footSize: number;
}

export const DEFAULT_AVATAR_PROPORTIONS: Readonly<AvatarProportions> = Object.freeze({
  headSize: 1,
  shoulderWidth: 1,
  armLength: 1,
  armThickness: 1,
  legLength: 1,
  legThickness: 1,
  handSize: 1,
  footSize: 1,
});
/** Exact rig calibration poses, plus a relaxed portrait presentation. */
export type DebugPose = "bind" | "a" | "t" | "up" | "portrait";
const RIG_TUNES = new Map<string, RigTune>();
const LIVE_AVATARS = new Set<Avatar>();
/** Set/clear one bone's live tune and re-apply on every live avatar (dev tooling). */
export function setGlobalRigTune(bone: string, tune: RigTune | null): void {
  if (tune && ((tune.rot && tune.rot.some((v) => v !== 0)) || (tune.pos && tune.pos.some((v) => v !== 0)) || (tune.scale !== undefined && tune.scale !== 1))) {
    RIG_TUNES.set(bone, tune);
  } else {
    RIG_TUNES.delete(bone);
  }
  for (const a of LIVE_AVATARS) a.applyRigTunes();
}
export function getGlobalRigTunes(): ReadonlyMap<string, RigTune> {
  return RIG_TUNES;
}
export function clearGlobalRigTunes(): void {
  RIG_TUNES.clear();
  for (const a of LIVE_AVATARS) a.applyRigTunes();
}

/** Bone index by name — every Manny bone plus the logical aliases above. */
const B: Record<string, number> = {};
MANNY_RIG.forEach((d, i) => { B[d[0]] = i; });
for (const [logical, manny] of Object.entries(BONE_ALIAS)) {
  const i = MANNY_RIG.findIndex((d) => d[0] === manny);
  if (i >= 0) B[logical] = i;
}

/** Per-body-model rig tables. All rigs share the Manny hierarchy/names/order for their first
 *  91 rows (89 Manny bones + eye_l/eye_r); the male additionally appends simulated cloth
 *  bones. The avatar skeleton is always BUILT from MALE_RIG (the superset topology) and
 *  RETUNED in place to another rig's rest via applyBodyRig — the shared prefix rows map 1:1
 *  onto the skeleton bones, and the trailing male cloth bones simply idle on other bodies. */
export interface BodyRigDef { rig: typeof MALE_RIG; cloth: readonly MaleClothDef[] }
export const BODY_RIGS: Record<string, BodyRigDef> = {
  male: { rig: MALE_RIG, cloth: MALE_CLOTH },
  block: { rig: MALE_RIG, cloth: MALE_CLOTH }, // hologram = the male mesh, same rig
  female: { rig: FEMALE_RIG, cloth: FEMALE_CLOTH },
};

/** Build the avatar bone hierarchy (rest position + rotation). Same 89-bone Manny
 *  hierarchy/names/order, but the given rig's rest (default MALE_RIG) — joints sit at the
 *  mesh's anatomy, so in-game proportions are the asset's. Clips are rotation-only + bound
 *  by name, so the whole Manny clip library plays unchanged on it. The root is returned
 *  unparented; the caller mounts it. */
export function buildRigBones(rig: typeof MALE_RIG = MALE_RIG): THREE.Bone[] {
  const bones = rig.map(() => new THREE.Bone());
  rig.forEach((d, i) => {
    const [name, parent, px, py, pz, qx, qy, qz, qw] = d;
    const bone = bones[i];
    bone.name = name;
    bone.position.set(px, py, pz);
    bone.quaternion.set(qx, qy, qz, qw);
    if (parent >= 0) bones[parent].add(bone);
  });
  return bones;
}

export const RIG_BONE_NAMES: readonly string[] = MANNY_BONE_NAMES;
/** Logical-name -> Manny bone-name alias (exported for the anim pipeline + tests). */
export { BONE_ALIAS };

const _q = new THREE.Quaternion();
// arm-twist driver scratch + bind locals (from the generated rig tables, per body rig)
const _twq = new THREE.Quaternion();
const _twq2 = new THREE.Quaternion();
const TWIST_BIND_CACHE = new WeakMap<object, Map<string, THREE.Quaternion>>();
function twistBindFor(rig: typeof MALE_RIG): Map<string, THREE.Quaternion> {
  let map = TWIST_BIND_CACHE.get(rig);
  if (map) return map;
  map = new Map<string, THREE.Quaternion>();
  for (const s of ["_l", "_r"]) {
    for (const n of ["hand", "upperarm", "upperarm_twist_01", "upperarm_twist_02", "lowerarm_twist_01", "lowerarm_twist_02",
      "thigh", "calf", "foot"]) {
      const row = rig.find((r) => r[0] === n + s);
      if (row) map.set(n + s, new THREE.Quaternion(row[5], row[6], row[7], row[8]));
    }
  }
  TWIST_BIND_CACHE.set(rig, map);
  return map;
}
// eye-look + rig-tune scratch
const _eyeQA = new THREE.Quaternion();
const _eyeQB = new THREE.Quaternion();
const _fhQ = new THREE.Quaternion();
const _fhQ2 = new THREE.Quaternion();
const _rtP = new THREE.Vector3();
const _rtD = new THREE.Vector3();
const _rtV = new THREE.Vector3();
const _rtE = new THREE.Euler();
const _rtQ = new THREE.Quaternion();
const _rtM = new THREE.Matrix4();
const _rtM2 = new THREE.Matrix4();
const _rtYawM = new THREE.Matrix4().makeRotationY(RIG_YAW); // rigRoot bind frame (root bone's parent)
const _aimDir = new THREE.Vector3();
const _aimParent = new THREE.Quaternion();
const _aimWorld = new THREE.Quaternion();
/** Identity = the rest local pose of the forearm/hand (Mixamo straight arm). */
const _rest = new THREE.Quaternion();
// Scratch for aimChestWorld (pure fn; reused to avoid per-frame allocation).
const _acUp = new THREE.Vector3();
const _acUp2 = new THREE.Vector3();
const _acBH = new THREE.Vector3();
const _acAH = new THREE.Vector3();
const _acTmp = new THREE.Vector3();
const _acAxis = new THREE.Vector3();
const _acYaw = new THREE.Quaternion();
const _acPitch = new THREE.Quaternion();
// Body-forward scratch (turn-rate heading + combat solves).
const _fwd = new THREE.Vector3();
// Boost-run leap: foot-height scratch (the body rises with the FEET during flight).
const _bUp = new THREE.Vector3();
const _bRoot = new THREE.Vector3();
const _bFL = new THREE.Vector3();
const _bFR = new THREE.Vector3();
// Scratch for the DISABLED shoulder booster pods (commented out per design) — restore alongside the
// booster build + placement blocks in the constructor / update().
/*
const _bDeployed = new THREE.Vector3(); // booster deployed pos (world)
const _bRetracted = new THREE.Vector3(); // booster retracted pos (world)
const _bRigQ = new THREE.Quaternion(); // rigRoot world orientation (→ rigRoot-local conversions)
const _bWorld = new THREE.Vector3(); // booster world position
const _bBoneQ = new THREE.Quaternion(); // shoulder bone WORLD orientation (carries the flight clip's roll/pitch)
const _bBodyQ = new THREE.Quaternion(); // body frame carried by the bone (bone world × corrective)
const _bQL = new THREE.Quaternion(); // pod orientation in rigRoot-local
const _bFanQ = new THREE.Quaternion();
const _bEuler = new THREE.Euler();
// Pod's orientation in the BODY frame: point-up (+Y) stays up; Ry(π) turns the flat face (+Z) to the BACK.
const _PICK_IN_BODY = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI);
*/
// Scratch for the authored rifle aim-offset (Avatar.applyAimOffset).
const _aoTarget = new THREE.Quaternion(); // per-bone local target
const _aoRight = new THREE.Vector3(); // body-right (gun-track pitch axis)
const _aoFwd = new THREE.Vector3();
const _aoUp = new THREE.Vector3();
const _aoPitch = new THREE.Quaternion(); // world pitch delta
const _aoWorld = new THREE.Quaternion(); // desired chest world
const _aoParInv = new THREE.Quaternion(); // chest parent world⁻¹
// Butt-seat scratch (seatRifleButt): weapon-frame slide rotated into the hand frame.
const _seatV = new THREE.Vector3();
const _torsoV = new THREE.Vector3(); // torsoCenterInto scratch
// Gun-aim solve scratch (pure-pitch barrel-elevation align about body-right).
const _aoMuzzle = new THREE.Vector3(); // muzzle world pos
const _aoGrip = new THREE.Vector3(); // hand world pos (barrel base)
const _aoBarrel = new THREE.Vector3(); // barrel forward (world) = muzzle − hand
const _aoAimDir = new THREE.Vector3(); // camera-forward / reticle direction (world)
const _aoBarrelP = new THREE.Vector3(); // barrel projected into the sagittal plane (⊥ body-right)
const _aoAimP = new THREE.Vector3(); // aim projected into the sagittal plane
const _aoCross = new THREE.Vector3(); // barrelP × aimP (for the signed pitch angle)
const _aoRollQ = new THREE.Quaternion(); // small aim-lean roll about body-forward
const _aoChestW = new THREE.Quaternion(); // chest world quat (measured)
const _aoRaiseW = new THREE.Quaternion(); // clavicle world during the aim raise
const _aoRaisePar = new THREE.Quaternion(); // clavicle parent world⁻¹
const RAD2DEG = 180 / Math.PI;
const clampDeg = (v: number, lim: number): number => (v < -lim ? -lim : v > lim ? lim : v);
// Glide limb-tuck scratch: a small additive bone rotation to pull the spread arms/legs in.
const COMBAT_GLOW_ORANGE = new THREE.Color(0xff6a1a); // "fighting" arm aura

/**
 * SUIT on worn clothing (2026-08-04).
 *
 * Before this, `style.suit` reached only materials[0] — an empty anchor mesh and
 * the far-LOD impostor — so the creator's SUIT swatch was invisible at every
 * distance a player actually sees themselves at. Garments live in the "colored"
 * group (materials[5]), a white material over authored COLOR_0, so tinting that
 * material is what makes the swatch mean something.
 *
 * The tint is RELATIVE TO THE DEFAULT SUIT rather than the raw colour: a plain
 * multiply by the default (a mid blue-grey) would have quietly darkened every
 * existing character's clothes to ~65%. Normalising means the default renders
 * byte-identical to before and only a *changed* swatch moves anything. Ratios
 * above 1 legitimately brighten, capped so a near-white suit cannot blow the
 * garment's shading out to a flat silhouette.
 */
const SUIT_REFERENCE = new THREE.Color(DEFAULT_STYLE.suit);
const SUIT_TINT_MAX = 1.6;
const _suitTint = new THREE.Color();

function applySuitTint(material: THREE.MeshLambertMaterial, suit: number): void {
  // Both sides convert sRGB→linear, so the ratio is a pure multiplier in the
  // same space the vertex colours already live in.
  _suitTint.setHex(suit);
  material.color.setRGB(
    Math.min(SUIT_TINT_MAX, _suitTint.r / SUIT_REFERENCE.r),
    Math.min(SUIT_TINT_MAX, _suitTint.g / SUIT_REFERENCE.g),
    Math.min(SUIT_TINT_MAX, _suitTint.b / SUIT_REFERENCE.b),
  );
}
const COMBAT_GLOW_SHIELD = new THREE.Color(0x3aa0ff); // "blocking" shield shell on the fists
const _hDesired = new THREE.Quaternion(); // combat head-aim scratch
const _hPar = new THREE.Quaternion();
const _hPitch = new THREE.Quaternion();
const _hLocal = new THREE.Quaternion();
const _hSway = new THREE.Quaternion(); // combat-blade: the clip's spine_01 deviation (breathing/lean)
const _hFwd = new THREE.Vector3();
const _hUp = new THREE.Vector3();
const _hPos = new THREE.Vector3();
// Foot grounding / per-foot 2-bone IK scratch (Avatar.applyFootGrounding / ikLeg).
const _fgDrop = new THREE.Vector3(); // flat-floor probe: worldPos → floor (Avatar.standOnFlatGround)
const _gUp = new THREE.Vector3(); // avatar up (radial), render space
const _gFwd = new THREE.Vector3(); // avatar forward, render space (knee pole)
const _gRoot = new THREE.Vector3(); // group world pos
const _gCenterPt = new THREE.Vector3(); // surface point under the body centre
const _gCenterN = new THREE.Vector3();
const _gFootPos = new THREE.Vector3(); // a foot's world pos
const _gGroundPt = new THREE.Vector3(); // surface point under a foot
const _gGroundN = new THREE.Vector3(); // surface normal under a foot
const _gTargetL = new THREE.Vector3(); // per-foot absolute ankle target (world)
const _gTargetR = new THREE.Vector3();
const _gHip = new THREE.Vector3();
const _gKnee = new THREE.Vector3();
const _gAnkle = new THREE.Vector3();
const _gGoal = new THREE.Vector3();
const _gKneeWant = new THREE.Vector3();
const _gA = new THREE.Vector3();
const _gB = new THREE.Vector3();
const _gAxis = new THREE.Vector3();
const _gPole = new THREE.Vector3();
const _gFwdPerp = new THREE.Vector3(); // body-forward ⊥ hip→goal — the anti-backward-knee reference
const _tUp = new THREE.Vector3(); // local (radial) up — for the sphere-correct turn rate
const _tCross = new THREE.Vector3();
const _gCurDir = new THREE.Vector3();
const _gWantDir = new THREE.Vector3();
const _gDelta = new THREE.Quaternion();
const _gFootQ0 = new THREE.Quaternion(); // foot CLIP world orientation, preserved across the leg solve
const _gFootPar = new THREE.Quaternion(); // foot parent world⁻¹
const _gBindMat = new THREE.Matrix4(); // foot bind world matrix (from skeleton.boneInverses)
const _gBindQ = new THREE.Quaternion();
const _gFootTarget = new THREE.Vector3(); // desired foot "up" (radial, tilted to the slope)
const _gFootUp = new THREE.Vector3(); // current top-of-foot direction (world)
const _gThighQ = new THREE.Quaternion(); // thigh CLIP world orientation, read before the leg solve
const _gAnterior = new THREE.Vector3(); // that thigh's anatomical knee-bend direction (world)
const _gGroupQ = new THREE.Quaternion(); // avatar world orientation (held knee poles are stored in it)
const _gGroupQInv = new THREE.Quaternion();
const _gPoleWorld = new THREE.Vector3(); // the held pole, rotated into world for the solve
const _mountTarget = new THREE.Vector3();
const _mountPole = new THREE.Vector3();
const _mountSwingLocal = new THREE.Vector3();
const _mountSwingStart = new THREE.Vector3();
const _mountSwingEnd = new THREE.Vector3();
const _boneEuler = new THREE.Euler();
const _boneQuat = new THREE.Quaternion();
/** Rig bones offset BEFORE the arm/leg IK (the torso chain + clavicles), so the
 *  IK re-solves the arms/legs onto the grips after the body is reshaped. Every
 *  other authored bone (wrists, ankles, toes) is offset AFTER the IK. */
const MOUNT_PRE_IK_BONES: ReadonlySet<string> = new Set([
  "pelvis", "spine_01", "spine_03", "spine_05", "neck_01", "clavicle_l", "clavicle_r",
]);
/** Extra forward torso pitch (radians) added to the authored ride pose at the
 *  vehicle's top speed, for the racing hunch. */
/** How far the rider folds down over the bars at top speed. Exported because the
 * stance editor previews against it — it used to keep a hand-synced copy, which
 * silently drifted the moment this changed and had the editor authoring stances
 * against a hunch the game no longer used. */
export const MOUNT_HUNCH_MAX = 0.26;
/** Radians the rider rolls INTO a turn at full lean, and metres the hips slide
 * the same way. A motorcyclist steers with weight: the roll alone reads as the
 * avatar being tilted by the bike, while the hip slide is what makes it read as
 * the rider CHOOSING the corner. Both are small — this is a shift of balance,
 * not a lean-out-of-the-seat stunt. */
const MOUNT_LEAN_ROLL = 0.2;
const MOUNT_LEAN_SHIFT = 0.06;
/** How far the inside hand pulls back and the outside hand pushes at full lean:
 * counter-steering pressure, and the detail that sells the weight shift when the
 * torso roll is this subtle. */
const MOUNT_LEAN_HAND = 0.05;
/** Boost tuck, on top of the speed hunch: extra forward pitch, how far the hips
 * drop toward the deck, and how much of the head lift is given up (the rider
 * stops sightseeing and gets behind the cowl). */
/** These stack ON TOP of the authored stance, which already carries a deep
 * forward hunch in its spine_01 offset plus the speed hunch — so the boost term
 * has to be small. At 0.22 rad the rider face-planted into the saddle. */
const MOUNT_BOOST_PITCH = 0.09;
const MOUNT_BOOST_CROUCH = 0.045;
const MOUNT_BOOST_HEAD = 0.45;
/** Elbows tuck in and knees grip the tank under boost. */
const MOUNT_BOOST_ELBOW = 0.09;
const MOUNT_BOOST_KNEE = 0.05;
/** Upward gaze tilt (radians) for the mounted rider, so the head reads as
 *  looking ahead down the road rather than straight along the hunched spine. */
export const MOUNT_HEAD_LIFT = -0.19;
const IK_WALK = 5.5; // speed (m/s) at/below which foot IK is full (idle + walk — this game walks fast)
const IK_RUN = 7.5; // speed (m/s) at/above which it fades to 0 (sprint/boost clips own the legs)
const IK_FOOT_TILT_MAX = 0.7; // clamp the foot-to-ground-normal tilt (rad, ~40°)
const IK_FOOT_DEADZONE = 0.14; // soft deadzone (~8°): subtract from the tilt so gentle/noisy terrain leaves feet flat (the procedural surface reads ~10° even when it looks flat); real slopes still conform
const KNEE_FWD_MIN = 0.12; // min FORWARD component of the IK bend pole vs the CALLER's pole — the fallback guard for chains with no anatomical axis (arms) (~83° lateral max)
/** …and the real knee guard: min component of the solved knee pole along the THIGH'S OWN anterior
 *  axis (see Avatar.legAnteriorFor). 0.25 ≈ 75° of lateral freedom — wide crouch stances and seated
 *  riders measure ≥0.78 on this axis, so it only ever bites on a genuinely broken (backward/sideways)
 *  knee. Anatomical, so it follows hip yaw + flexion instead of fighting them. */
const KNEE_ANAT_MIN = 0.25;
const IK_LEG_NAMES: ReadonlyArray<readonly [string, string, string]> = [
  ["thigh_l", "calf_l", "foot_l"],
  ["thigh_r", "calf_r", "foot_r"],
];
/** Boost-stream emit points: the player's HANDS and FEET (the shoulder booster pods are disabled). */
const BOOST_STREAM_BONES = ["hand_l", "hand_r", "foot_l", "foot_r"] as const;
/** HOVER AIR-SQUAT (see the hover block in the pose path). Limb targets are direction vectors in
 *  the bone's rest-parent frame — +Z is FORWARD for a thigh, so a positive value swings the knee
 *  up in front. Asymmetric lead/trail: both legs at one angle reads as a puppet. */
/** GLIDE leg sweep (applyJetLegs): thighs back, shins trailing, toes pointed — the flight
 *  silhouette the stripped f_boost clip used to carry. Same measured local-Z hinges as the hover
 *  squat, opposite sign on the thighs (back instead of forward). */
/** Bones the borrowed flight legs are allowed to drive — hip down, nothing else. */
const JET_LEG_BONES = new Set<string>(["thigh_l", "thigh_r", "calf_l", "calf_r", "foot_l", "foot_r", "ball_l", "ball_r"]);
const GLIDE_LEG_SWEEP = 0.35;
const GLIDE_LEG_TRAIL = 0.25;
const GLIDE_FOOT_POINT = 0.5;
const HOVER_KNEE_LEAD = 0.55;
const HOVER_KNEE_TRAIL = 0.35;
/** How far the shins fold back under the thighs — the fold is what makes it a SQUAT. */
const HOVER_SHIN_FOLD = 0.6;
/** Toe-point (rad) so the feet hang instead of holding the sole flat. */
const HOVER_FOOT_DANGLE = 0.45;
const _hvQ = new THREE.Quaternion();
const _hvAxis = new THREE.Vector3();
const _hvTarget = new THREE.Quaternion();
const _hRight = new THREE.Vector3();
// Weapon mount: the fixed rotation carrying our canonical mesh frame (grip at
// origin, barrel -Z, top +Y) into the right-hand `weapon_r` socket the UE clips
// pose the hands around. Solved against each weapon's neutral aim pose (body facing
// forward, pitch 0): R = handWorldQuat⁻¹, so the held weapon reads barrel-forward,
// top-up. The pistol (p_*) and rifle (r_*) clip sets hold hand_r at DIFFERENT angles,
// so each needs its own mount. hand_r isn't world-aligned — these are measured, not
// guessed. (If a clip set is re-authored, re-solve R = hand_r.worldQuaternion⁻¹ in
// the neutral aim pose — body forward, pitch 0 — via the avatar-harness.)
const WEAPON_MOUNT_PISTOL = new THREE.Quaternion(-0.387464, -0.526812, -0.618361, -0.435857);
/** Z-STOWED greatsword back-carry: Spine2-local mount (blade = weapon-local +Y, worn as a
 *  diagonal across the back, hilt at the right shoulder). Tuned by screenshot. */
const GS_BACK_POS = new THREE.Vector3(0, 0.12, 0.16);
const GS_BACK_MOUNT = new THREE.Quaternion().setFromEuler(new THREE.Euler(0.15, 0, 0.5));
/** BACK-STOW mount for holstered rifles (setStowedWeapon): SIDE flush against the back, stock
 *  up over the RIGHT shoulder, barrel diagonal DOWN toward the left hip (user call 2026-08-02).
 *  Chest-bone (Spine2) LOCAL — solved numerically in-page from the bone's world frame (the
 *  Manny spine bones' local axes are NOT body axes; hand-eulered guesses landed the gun through
 *  the chest), then hardcoded. */
/** 2026-08-10: the off-back distance (local y) depends on what the back wears —
 *  0.27 clears the drifter pack (0.2 buried the stock in the upper-right
 *  shoulder, user-reported clip), but floats off a BARE back (also user-reported),
 *  so the bare pose keeps the original 0.2. Picked by rifleBackPos(), re-applied
 *  on restyle so swapping the pack on/off re-seats a stowed gun. */
const RIFLE_BACK_POS = new THREE.Vector3(0.047, 0.2, 0.043);
const RIFLE_BACK_POS_PACK = new THREE.Vector3(0.047, 0.27, 0.043);
const RIFLE_BACK_MOUNT = new THREE.Quaternion(0.4964, 0.2598, 0.6628, 0.4968).normalize();
/** Playback speed for the Z stow/draw gesture (the authored sheathe pair reads slow raw). */
const STOW_GESTURE_SCALE = 1.6;

// Reused fitting scratch; the fixed-step solver lives in deathPhysics.ts.
const _ragImp = new THREE.Vector3(), _ragJit = new THREE.Vector3(), _ragV = new THREE.Vector3();
const _ragV2 = new THREE.Vector3(), _ragV3 = new THREE.Vector3(), _ragW = new THREE.Vector3();
const _ragQ = new THREE.Quaternion();
// BARREL POINTS FORWARD (body −Z, top +Y) — i.e. the mount is the body's own orientation carried
// into the hand. Solved with the gun-track OFF so it reads the CLIP's arms.
// It previously aligned the barrel to the WRIST-TO-WRIST line, which runs ~29° across the body;
// applyGunAim then had to yaw BOTH shoulders up to 46° to point the gun downrange — the body
// visibly spinning to aim. A real hold does not put the barrel on the wrist line: the support hand
// wraps AROUND a forward-pointing barrel, offset from its axis. Re-derive with _riflemount.mjs.
const WEAPON_MOUNT_RIFLE = new THREE.Quaternion(0.546121, 0.376081, 0.517918, 0.540441);
/** Stances choose animation/pose strategy; grip sockets choose hand placement.
 * Heavy weapons reuse the rifle locomotion clips, with dedicated upper-body
 * shoulder / hip braces layered above that moving base. */
const STANCE_AIM_PREFIX: Record<WeaponStance, "r" | "sd"> = {
  shoulder: "r",
  "over-shoulder": "r",
  underhand: "r",
  sword: "sd",
};
/** Pelvis-relative firing-hand seat for hip-carried heavy weapons, expressed in
 * body axes [right, up, forward]. Keep this target anatomically reachable: the
 * old -0.40 m vertical offset was below the pelvis, so clampToReach pulled the
 * hand, weapon, and both arms back through the abdomen. */
// Rear control stays beside the hip; the long barrel and forward top handle
// project in front. Driving the rear hand itself forward made the cross-body
// support handle anatomically unreachable and forced a gait-dependent seat.
const UNDERHAND_HAND_SEAT = [0.36, 0, -0.08] as const;
/** Stylized soft-IK give for heavy-weapon arms. This is deliberately small:
 * enough to absorb a grip/socket mismatch, never enough to read as rubber. */
const UNDERHAND_ARM_STRETCH_MAX = 1.04;
const UNDERHAND_ARM_STRETCH_HZ = 14;
/** Carry across the pelvis toward body-left, as in a braced rotary-cannon
 * stance. ADS eases this yaw out so the mechanical bore can face the reticle. */
const UNDERHAND_CARRY_YAW = THREE.MathUtils.degToRad(24);
const _primaryGripP = new THREE.Vector3();
const _primaryGripQ = new THREE.Quaternion();
const _stanceHand = new THREE.Vector3();
const _stanceElbow = new THREE.Vector3();
const _stanceShoulder = new THREE.Vector3();
const _stancePole = new THREE.Vector3();
const _stanceAxis = new THREE.Vector3();
const _stanceReachQ = new THREE.Quaternion();
const _stanceReachIdentity = new THREE.Quaternion();
const _stanceReachPivot = new THREE.Vector3();
const _stanceReachArm = new THREE.Vector3();
const _stanceReachTarget = new THREE.Vector3();
const _underhandCarryQ = new THREE.Quaternion();
const _underhandPitchQ = new THREE.Quaternion();
const _underhandLocalUp = new THREE.Vector3(0, 1, 0);
const _sightInverse = new THREE.Matrix4();
const _underhandLocalRight = new THREE.Vector3(1, 0, 0);
const _underhandWorldM = new THREE.Matrix4();
const _underhandLocalM = new THREE.Matrix4();
const _underhandWorldP = new THREE.Vector3();
const _underhandPalmP = new THREE.Vector3();
const _underhandUnitScale = new THREE.Vector3(1, 1, 1);
// The launcher rests its rear underside on the right shoulder. The grips and
// bore rotate around that seat, instead of lifting a rifle's wrist offset.
const LAUNCHER_SHOULDER_SEAT = new THREE.Vector3(0, 0.025, 0.16);
const _launcherSeat = new THREE.Vector3();
const _launcherOrigin = new THREE.Vector3();
const _launcherTarget = new THREE.Vector3();
const _launcherPole = new THREE.Vector3();
const _launcherWorldQ = new THREE.Quaternion();
const _launcherHandQ = new THREE.Quaternion();
const _launcherDeltaQ = new THREE.Quaternion();
/** Where each CLOSED FIST sits in weapon space — midpoint of the middle-finger and thumb tips,
 *  measured off the clips. The geometry is built around these, not around the wrist. */
// RE-MEASURED 2026-08-01 under the AO-led aim stack + grip IK + wrist pin (_fingergrip.mjs). The
// old values predated all three; the left fist had risen 9.6 cm, so the whole gun body hung BELOW
// the hands and the right forearm read as draped over the receiver — rolled, foreshortened, "very
// thin". ⚠ MEASURE WITH THE SHIPPING PATH (no debug overrides), and re-measure ONCE after any
// change here (the mesh's muzzle feeds applyGunAim → arms → fists; second pass settles).
const RIFLE_FIST_R: readonly [number, number, number] = [-0.105, 0.034, -0.122];
const RIFLE_FIST_L: readonly [number, number, number] = [-0.038, 0.023, -0.383];
// Left-hand support-grip IK target (the WRIST/hand bone, not the fist), in the canonical weapon
// frame (grip at origin, barrel −Z, top +Y).
// RIFLE: the clip's own wrist measured [-0.287, 0.101, -0.31] (probe _gripmeasure.mjs) — but the
// clip's support hand FLOATS 17 cm left of the weapon body, so anchoring there kept the float.
// The target instead shifts laterally by the fist→handguard delta so the CLOSED FIST (which sits
// [0.113, -0.108, -0.157] from the wrist along the hand) lands ON the handguard the mesh builds
// at x = mean-fist. y/z keep the clip's wrist so the arm's reach and elbow line stay authored.
// Re-measure after any rifle clip-set or mount change.
/* x −0.10 → −0.13: user call 2026-08-03, "move the handhold on the weapon about 3 cm to the
 * left". Weapon frame is grip-at-origin / barrel −Z / top +Y, so −X is the shooter's LEFT. */
const _wGripRifle = new THREE.Vector3(-0.13, 0.02, -0.30);
/* RETIRED 2026-08-03: the hip-carry support target used to be the r_ clips' own authored left
 * wrist, `(-0.245, 0.08, -0.28)` in the weapon frame — a point that FLOATS ~14 cm off the gun.
 * It was the least-bad option while the real foregrip sat beyond the arm's reach in the carry
 * (measured 1.07-1.17x arm length), but it is what the player actually saw: a support hand not
 * touching the rifle in every unaimed stance. seatWeaponInSupportReach closes the reach gap at
 * the source, so both stances now use the real foregrip and this fallback is gone. */
// hand_l ORIENTATION in the weapon frame at the neutral aim pose (same probe) — pinned after the
// grip IK so the palm alignment rides the GUN through the whole pitch range instead of keeping
// whatever world orientation the clip's floating hand had (which read as a heavily bent wrist).
const RIFLE_WRIST_L = new THREE.Quaternion(0.172, 0.348, -0.307, 0.869).normalize();
/** Firing-hand target bind-relative wrist ROLL (rad) while the rifle is held. The relief unrolls
 *  hand_r about the forearm axis until its measured roll lands here (the weapon counter-rotates,
 *  so the gun's orientation never moves). Adaptive on purpose: the clips author −55° at ADS but
 *  −122° at the hip carry — a fixed relief left the hip wrist collapsed into a twisted ribbon.
 *  −0.6 rad (−35°) keeps the forearm skinning healthy at every stance. */
const RIFLE_WRIST_ROLL_TARGET = -0.6;
/** PROCEDURAL FLINCH (Avatar.flinch / applyFlinch). A hit is a short directional shove of the
 *  upper body, layered over whatever the legs are doing — see flinch() for why this replaced a
 *  full-body clip. AMP is per hit in radians of total spine+head recoil; hits ACCUMULATE up to
 *  AMP_MAX so sustained fire pushes you around instead of restarting a twitch every 200 ms. */
/** Female share of the crouched ADS TORSO fold — see crouchAdsTorso. Lowered across three user
 *  reports ("still too bent forward"); her slimmer frame reads the same fold as deeper. */
const CROUCH_ADS_FEMALE = 0.15;
/** …and of the crouched HEAD tuck, which stays deep on both rigs — see crouchAdsHead. */
const CROUCH_ADS_HEAD_FEMALE = 0.8;
/** Share of the CROUCH pose's forward spine curl re-seated at the hip — see crouchHipFold. */
const CROUCH_SPINE_TO_HIP = 0.55;
/** Share of the AIRBORNE clip's torso lean removed while aiming — see adsAirTorsoHold. */
const ADS_AIR_TORSO_HOLD = 0.8;
/** …and the share held in EVERY OTHER stance (unarmed / blade). Lower than the aimed value: a free
 *  jump should still have some body in it, it just must not lie back. */
const AIR_TORSO_HOLD = 0.7;
/** Share of the airborne clip's pelvis ROLL and YAW removed — see the hip square-up in
 *  adsAirTorsoHold. Higher than the pitch hold on purpose. */
const AIR_HIP_SQUARE = 0.95;
/** Share of the airborne clip's HEAD yaw removed, so a free jump looks where it is going. */
const AIR_HEAD_SQUARE = 0.85;
/** Forward hip hinge (rad) added airborne when NOT aiming — the chest over the tucked legs. */
const AIR_JUMP_HUNCH = 0.5;
/** …scaled down hard with a gun out — see the hunch block in adsAirTorsoHold. */
const AIR_HUNCH_GUN = 0.3;
/** …and the share of THAT applied to the pelvis, which carries most of a strafe clip's lean. */
const ADS_AIR_PELVIS_SHARE = 0.75;
const FLINCH_AMP = 0.15;
const FLINCH_AMP_MAX = 0.34;
/** Seconds from impact back to neutral. Short — a flinch is a snap and a settle, not a pose. */
const FLINCH_DUR = 0.42;
/** Fraction of the impulse that lands on the HEAD (the rest is spread down the spine). The head
 *  leads a flinch: it is the part a player actually reads at third-person distance. */
const FLINCH_HEAD_SHARE = 0.45;
const _flQ = new THREE.Quaternion();
const _flFwd = new THREE.Vector3();
const _flUp = new THREE.Vector3();
const _flRight = new THREE.Vector3();
/** Support-hand GRIP CURL per phalanx rank (rad, about local Z — screenshot-calibrated).
 *  DEEPENED 2026-08-03 (user: "the hand gripping the barrel doesn't seem to be gripping at all,
 *  it's just open" — both bodies): the clips' fingers hover, and the old values left them merely
 *  bent rather than closed AROUND the handguard. */
const GRIP_CURL: readonly [number, number, number] = [0.72, 1.05, 0.8];
const GRIP_CURL_FINGERS = ["index", "middle", "ring", "pinky"] as const;
/** THUMB wrap, per rank. Without it the hand read as a loose fist BESIDE the gun however far the
 *  fingers curled — a grip is fingers under the guard AND a thumb over it. Sign is opposite the
 *  fingers' because the thumb opposes them. */
const GRIP_CURL_THUMB: readonly [number, number, number] = [-0.34, -0.5, -0.4];
/** Per-body ELBOW SWIVEL: angle (rad) about each arm's shoulder→hand axis. Signs are EMPIRICAL
 *  (verified by screenshot — the swivel direction depends on each arm's bend plane; negative on
 *  the right arm = elbow OUT, positive = elbow IN toward the ribs). Female flares both out
 *  (user call); male tucks the firing elbow IN a touch (user call 2026-08-02). */
const ELBOW_SWIVELS_FEMALE: readonly [string, string, string, number][] = [
  // −0.85 ≈ elbow 21.3cm out from chest centreline (her near-straight arm gives the swivel a
  // small orbit radius, so it takes a big angle to move centimetres).
  ["RightArm", "RightForeArm", "RightHand", -0.85],
  ["LeftArm", "LeftForeArm", "LeftHand", 0.35],
];
/** MALE: the firing elbow stays FLARED OUT (user call 2026-08-03: "it should remain flared out,
 *  not tucked"). This was +0.18 = a touch of TUCK, from a 2026-08-02 call made while the swivel
 *  was ADS-only — so it read as the elbow snapping inward the moment you aimed. The swivel is now
 *  stance-independent (the grip stack is no longer aim-gated), so a tuck here would apply at the
 *  hip carry too; flaring is what the user asked for and it holds in both stances. */
const ELBOW_SWIVELS_MALE: readonly [string, string, string, number][] = [
  ["RightArm", "RightForeArm", "RightHand", -0.20],
];
const _wGripPistol = new THREE.Vector3(0.015, -0.045, 0.02);
// Scratch for seatWeaponInSupportReach.
const _gsGrip = new THREE.Vector3();
const _gsDir = new THREE.Vector3();
const _gsTarget = new THREE.Vector3();
const _gsPole = new THREE.Vector3();
const _gsLocal = new THREE.Vector3();
const _gsSteady = new THREE.Vector3();
/**
 * ADS-MOVE GUN STEADY (2026-08-17, user: "when moving in ADS the gun swings
 * around — it should not move much at all"). The barrel DIRECTION is already
 * closed-loop corrected every frame (applyGunAim + barrel lock), but the gun's
 * POSITION rides the firing hand through the gait — a translation bob a 3×
 * scope magnifies badly. Low-pass the hand's GROUP-LOCAL position and walk the
 * hand toward the calm point, through the same capped-translation seam as the
 * gun seat (so the elbow-pole law and support-hand reseat still apply).
 * ⚠⚠ This deliberately does NOT touch the spine or hold a mean POSE — freezing
 * the torso against a moving pelvis is the documented "upper body detaches"
 * failure (2026-08-01). It calms a few centimetres of hand translation only,
 * and the cap bounds any artifact. Error-adaptive tracking lets real aim moves
 * (pitch flicks, stance changes) through without lag.
 */
const GUN_STEADY_MAX = 0.06;
const GUN_STEADY_HZ = 2.5;
// RELOAD: the support hand drops to the MAGAZINE well (canonical frame); the gun points down.
const _wMagRifle = new THREE.Vector3(0, -0.13, -0.10);
const _wMagPistol = new THREE.Vector3(0.02, -0.14, 0.0);
/** Support-hand reach ceiling as a fraction of full arm length (applyWeaponGrip). Below 1.0 so the
 *  elbow keeps a visible bend — ikLeg's own cap is 0.985 and an arm sitting there reads as a locked
 *  strut with the shoulder dragged forward to cover the distance. */
const GRIP_REACH_MAX = 0.93;
/** Slide PATH for the support hand when the bobbing gun swings the foregrip out of reach: from
 *  the grip point back toward a point UNDER the receiver by the mag well (weapon frame). A pure
 *  +Z slide walked the hand onto TOP of the tall receiver box (the grip point sits at the
 *  guard's REAR edge) and read as a floating hand — curving down keeps the palm cupping the
 *  gun's underside the whole way, like a real support hand collapsing in. */
const GRIP_SLIDE_TO = new THREE.Vector3(-0.10, -0.06, -0.15);
const GRIP_SLIDE_MAX = 0.16;
/**
 * SEAT THE GUN WITHIN THE SUPPORT ARM'S REACH (see seatWeaponInSupportReach).
 *
 * ⚠⚠ THE HIP CARRY CANNOT REACH ITS OWN FOREGRIP. Measured (client/test/_gripreach.mjs):
 * standing at the hip carry the support shoulder needs **1.07–1.17× of a fully straight arm**
 * to touch the handguard, while ADS needs only 0.86×. The difference is the gun: ADS pulls it
 * into the chest, the carry holds it out. That is why the support hand visibly floated ~14 cm
 * off the barrel in every unaimed stance — the grip IK was switched off there precisely because
 * enforcing an unreachable target locks the arm into a straight strut and the elbow-pole plane
 * then flips with the gait bob (the "spaz").
 *
 * The arm was never the thing to fix. Walk the RIGHT hand inboard so the RIFLE comes to the
 * support hand — the trigger hand keeps the pistol grip, the barrel keeps its solved direction
 * (this is a translation), and the support hand then reaches a target with a real elbow bend.
 * Same instrument the female seat already uses, generalized to "whatever the deficit is".
 */
const GUN_SEAT_MAX_CARRY = 0.20;
/** ADS is normally already in reach; this only catches the run-bob overswing (measured 1.06×
 *  at the top of a stride), and it is capped tight so the shouldered gun never visibly wanders. */
const GUN_SEAT_MAX_ADS = 0.07;
/** Low-pass (Hz) on the seat offset — it is derived from a bobbing measurement, so it has to be
 *  smoothed or it reintroduces the per-frame shake it exists to remove. */
const GUN_SEAT_HZ = 9;
/**
 * ADS GUN SEAT OFFSET (m, body axes): where the whole weapon sits while aiming, so the STOCK
 * clears the shooter's back instead of passing through it.
 *
 * ⚠ LIFTING ALONE MADE IT WORSE. The first attempt at the user's "the butt of the gun pokes
 * through the back" was a 4.5 cm straight LIFT; they came back with "clipping through the back is
 * now worse when aiming down sights" — raising a stock that is already behind the shoulder just
 * drives it further into the upper back. The direction that actually clears it is OUTBOARD and
 * slightly FORWARD (their prescription), which walks the butt around the shoulder rather than into
 * it. The lift is kept but cut right back; it was doing some good on the barrel height.
 *
 * All three are applied by walking the firing HAND in seatWeaponInSupportReach: the gun rides the
 * hand, so the trigger grip holds, and a translation leaves the barrel DIRECTION exactly where the
 * aim solve put it (reticle convergence in main.ts absorbs the few cm of muzzle offset). Each
 * rides aimK, so the hip carry is untouched.
 */
const ADS_GUN_LIFT = 0.015;
/** Outboard (toward the shooter's right) — the axis that actually walks the butt off the back. */
const ADS_GUN_OUT = 0.055;
/** …and a small push down the barrel line, so the stock rides forward off the shoulder blade. */
const ADS_GUN_FWD = 0.025;
/** Per-frame cap (rad) on the elbow-pole direction's rotation — the pole is a PREFERENCE, not a
 *  measurement, so rate-limiting it kills bob-driven plane flips without any visible lag. */
const GRIP_POLE_RATE = 0.22;
/** Cap (m) on the right-hand walk that keeps the foregrip stationary through a barrel-lock
 *  rotation (pivot-at-foregrip compensation); the remainder pivots at the mount as before. */
const BL_PIVOT_MAX = 0.12;
const _bpBefore = new THREE.Vector3(), _bpAfter = new THREE.Vector3(), _bpDelta = new THREE.Vector3();
const _bpHand = new THREE.Vector3(), _bpPole = new THREE.Vector3();
const _bpAxis = new THREE.Vector3(), _bpElb = new THREE.Vector3();
/** Low-pass rate (Hz) for the support hand's slide-along-guard — a hand that adjusts its grip
 *  smoothly instead of skating at stride frequency. */
const GRIP_SLIDE_HZ = 10;
/** Cap on the gun-track's clavicle yaw (rad, ~10°). Small ON PURPOSE: with the barrel mounted
 *  FORWARD there is no large off-axis error left to cancel, and yawing the shoulder girdle is what
 *  read as the body spinning to aim. Only the degeneracy fade below should ever bite. */
const CLAVICLE_YAW_MAX = 0.18;
// ── ADS RAISE mix (see applyAdsRaise). How the authored lift is split down the arm chain; the
//    forearm always takes the sum back so the barrel stays level. Tuned in the harness.
// FEMALE stock-seat correction (see applyAdsRaise): extra lift + rightward clavicle walk (rad)
// so the butt sits in the same shoulder crook as the male's. Screenshot-tuned.
const FEM_ADS_LIFT = -0.50;
const FEM_GUN_RIGHT = 0.08;
const FEM_GUN_DOWN = 0.10;
const _fgPos = new THREE.Vector3();
const FEM_ADS_RIGHT = -0.18;
/** Fraction of the clips' forward pelvis lean removed while aiming (gait tilt damp). */
const PELVIS_LEAN_DAMP = 0.9;
/** Fraction of the aim pitch the PELVIS carries (whole-torso up/down participation). */
const PELVIS_AIM_PITCH_SHARE = 0.75;
const _plGroupQ = new THREE.Quaternion(), _plCur = new THREE.Quaternion(), _plRest = new THREE.Quaternion();
const _plDelta = new THREE.Quaternion(), _plPitch = new THREE.Quaternion(), _plPart = new THREE.Quaternion();
/** How much of the ADS forward lean stays as SPINE CURL; the rest hinges at the hip. */
const ADS_HUNCH_SPINE_SHARE = 0.4;
/** …and while CROUCHED. LOWER on purpose: a crouched shooter folds at the HIP and keeps a
 *  straight back, so the hip takes MORE of the tilt and the upper spine less (user call
 *  2026-08-03). See the split in applyAdsStance. */
const ADS_HUNCH_SPINE_SHARE_CROUCH = 0.12;
const ADS_CLAVICLE = 0.35;  // share of the lift taken by the shoulder girdle
const ADS_UPPERARM = 0.65;  // share taken at the shoulder joint
const ADS_CHEST = 0.10;     // extra chest lift. Kept small: it rotates the chest BACKWARD, and
// the aim stack was already leaning the torso back ~9° at idle.
/** ADS shoulder-girdle settle (rad): mirrored clavicle rolls that lower both shoulder tips while
 *  aiming, upper arms countering so the barrel stands (user call 2026-08-02). */
const ADS_SHOULDER_DROP = 0.10;
const _adsQ = new THREE.Quaternion();
const RELOAD_GUN_DOWN = 1.15; // how far (rad) the gun pitches muzzle-down during the reload
// --- MESH-SPACE arm aim-offset scratch (Avatar.applyAimArmOffset) ------------------------
const _amC = new THREE.Quaternion(), _amCInv = new THREE.Quaternion();
const _amD = new THREE.Quaternion(), _amNew = new THREE.Quaternion();
const _amParent = new THREE.Quaternion(), _amIdent = new THREE.Quaternion();
const _amWorld = [new THREE.Quaternion(), new THREE.Quaternion(), new THREE.Quaternion(), new THREE.Quaternion()];
const _blMuzzle = new THREE.Vector3(), _blGrip = new THREE.Vector3();
const _blBarrel = new THREE.Vector3(), _blAim = new THREE.Vector3();
const _blDelta = new THREE.Quaternion(), _blPar = new THREE.Quaternion(), _blLocal = new THREE.Quaternion();
const _blIdentity = new THREE.Quaternion();
/** Most the barrel lock will rotate the weapon in the hands (rad, ~14°). Past this the gun would
 *  read as pivoting loose in the grip, and the gun-track's body correction is the right tool. */
const BARREL_LOCK_MAX = 0.25;
/** Bones the aim steadying low-passes. The CLAVICLES are in it deliberately: they carry the whole
 *  arm, so damping only the arm bones left the muzzle swinging with the gait regardless (measured:
 *  raising the arm damping 0.6 -> 0.93 moved 70 cm of muzzle travel to 55 cm, because the swing was
 *  never in the arms). */
const AIM_STEADY_BONES = ["LeftShoulder", "RightShoulder", "LeftArm", "RightArm",
  "LeftForeArm", "RightForeArm", "LeftHand", "RightHand",
  // The NECK/HEAD chain steadies too: the rifle head rides the clip + AO delta and only a
  // partial gaze lock, so without this the base clip's head sway reads as the head "bobbing
  // along the sightline" (measured ±5.6° at ADS idle). The AO's own head delta is added AFTER
  // this filter, so the head still turns with the aim instantly.
] as const;
/** SPINE bones get their own HALF-WEIGHT damping (see applyAimArmSteady). A full-weight slow
 *  freeze was tried first and looked BROKEN in motion — the mean of a big gait swing is a pose
 *  that never occurs, so the frozen torso detached from the moving pelvis/legs ("the whole upper
 *  torso jitters, the left hand is off the gun"). Half weight HALVES the sway (measured 45° of
 *  barrel wobble on the diagonal run clips — the user's "swings a lot forward-left/back-right")
 *  while the torso still visibly works with the gait. */
// NECK/HEAD live HERE (non-relaxed) too: while ADS-MOVING the user reported "constant bobbing
// of the upper torso, head and gun" — the head/neck were in the moveK-relaxed arm set above,
// so their damping mostly switched off exactly when the bob shows.
const AIM_STEADY_SPINE = ["spine_01", "spine_02", "spine_03", "spine_04", "neck_01", "neck_02", "Head"] as const;
const _wGrip = new THREE.Vector3(); // foregrip target (world)
const _wPole = new THREE.Vector3(); // left-elbow pole (world)
// grip-IK pole conditioning + wrist pin scratch
const _wPoleQ = new THREE.Quaternion(), _wRelQ = new THREE.Quaternion(), _wParQ = new THREE.Quaternion();
const _wShoulder = new THREE.Vector3(), _wAxis = new THREE.Vector3();
const _wPerp = new THREE.Vector3(), _wRef = new THREE.Vector3(), _wCon = new THREE.Vector3();
const _cShoulder = new THREE.Vector3();
const _cElbow = new THREE.Vector3();
const _cReach = new THREE.Vector3();
const _twA = new THREE.Vector3(), _twB = new THREE.Vector3(), _twAxis = new THREE.Vector3();
const _twNow = new THREE.Quaternion(), _twQ = new THREE.Quaternion();
const _twTNow = new THREE.Quaternion(), _twTRef = new THREE.Quaternion();
const _twPreL = new THREE.Quaternion();
/** Twist component of `q` about unit `axis` (swing–twist): project the vector part onto the axis
 *  and renormalise. `swing = q · twist⁻¹`. */
function twistAbout(q: THREE.Quaternion, axis: THREE.Vector3, out: THREE.Quaternion): THREE.Quaternion {
  const d = q.x * axis.x + q.y * axis.y + q.z * axis.z;
  out.set(axis.x * d, axis.y * d, axis.z * d, q.w);
  if (out.lengthSq() < 1e-12) out.set(0, 0, 0, 1);
  else out.normalize();
  return out;
}
const _cdA = new THREE.Vector3();
const _cdB = new THREE.Vector3();
const _cdC = new THREE.Vector3();
const _cdG = new THREE.Vector3();
const _rPitch = new THREE.Quaternion(); // reload gun-down world delta
const _rRight = new THREE.Vector3(); // body-right axis

// Shared GLB loader for held-weapon meshes (Meshy exports). Lazy so the parser only
// loads if some weapon actually declares a `model`; uncompressed GLB only (no Draco/
// meshopt setup here — the offline normalize step strips compression).
let _weaponGltf: GLTFLoader | null = null;
const weaponGltfLoader = (): GLTFLoader => (_weaponGltf ??= new GLTFLoader());

// --- Pure runtime decision functions (extracted so they are unit-testable;
//     three of the four historical bugs lived in this math with no coverage) --

/** Speed (m/s) by which locomotion has fully ramped in from idle. */
const MOVE_RAMP = 1.5;
/** Swim speed (m/s) at which the directional swim clip fully replaces the tread-water idle. */
const SWIM_MOVE_RAMP = 1.6;
/** Speed (m/s) at/above which the RUN animation plays instead of WALK. Default
 *  ground pace is above this, so movement runs by default; walk only shows when
 *  decelerating toward a stop. Set near the walk/run stride-sync crossover: the
 *  walk clip (~1.8 m/s) saturates its 1.5× cap at ~2.7 m/s and the run clip
 *  (~5.9 m/s) bottoms out its 0.6× floor at ~3.5 m/s, so ~3.1 minimizes foot-slide
 *  in the transient accel/decel band between them. */
const RUN_ANIM_MIN = 3.1;
/** Blend weight for the unarmed-combat UPPER-body clip. The combat clips are masked to the
 *  torso/arms (no leg tracks), so the legs stay 100% locomotion; this high weight makes the
 *  combat pose dominate the upper bones (~94%) over the leg clip's gun-arm pose bleed. */
const COMBAT_BLEND = 16;
/** The masked UPPER-BODY unarmed-combat overlay clips (driven at COMBAT_BLEND): the DHTH fight guard,
 *  the directional PUNCHES (legs keep their locomotion — top/bottom decoupled), + the legacy jab/idle.
 *  Their weight is shaped by combatK on entry/exit so the guard CROSSFADES instead of snapping, and
 *  they're EXCLUDED from the sum-normalize (else they'd scale the base legs down — see updateStateMachine). */
const COMBAT_OVERLAY = ["dh_guard", "dh_punch_f", "dh_punch_b", "dh_punch_l", "dh_punch_r", "u_idle", "u_jab",
  "sd_parry", "sd_block", "stow_sheathe", "stow_draw"]; // top-only overlays: parry pair + the Z stow gesture
/** A masked upper-body overlay (vs a full-body base/strike) — used for the normalize exclusion. */
const isUpperOverlay = (key: string): boolean => key.startsWith("u_") || COMBAT_OVERLAY.includes(key);
/** Emotes that play ONCE and hold their final pose (P-B2: the bed lie-down). */
const EMOTE_HOLD = new Set(["emo_lay"]);

/** SHIELD GUARD walk phase. The kickbox pack authors each octant as step-off → loop → settle, so the
 *  guard steps OFF into a direction and SETTLES out of it instead of snapping in and out of a loop. */
export type GuardPhase = "idle" | "start" | "loop" | "stop";
/** Ground speed (m/s) at which the guard is "walking" — below it, the fighter is standing. Hysteresis
 *  is unnecessary: the start/stop clips have to finish before the phase can flip back. */
const GUARD_MOVE_MIN = 0.35;

/** Jab playback speed — the Mixamo "Punching" clip is ~1s; play it this much faster so the jab
 *  snaps out. Tunable. */
const JAB_SPEED = 1.0;
/** Jab clip start offset (s) — skip the wind-up "chamber" (~first 0.38s of Punching) so the fist
 *  drives straight out instead of drawing back first. Tunable to the clip. */
const JAB_START = 0.38;
/** Combat-stance crossfade rate (1/s): how fast the guard fades in/out on RMB. Higher = snappier
 *  (a few frames); faster than the general ease2 so the stance entry isn't sluggish. Tunable. */
const COMBAT_EASE_RATE = 22;
/** How strongly the jab is steered onto the reticle at the strike (0..1). The clip extends the arm
 *  ~75° to the side; this rotates the whole arm (at the shoulder) so the shoulder→hand line points
 *  at the reticle, keeping the clip's elbow bend. 1 = dead-on the reticle; lower keeps the clip's
 *  angle. Tunable. */
const JAB_AIM = 1;
/** Extra OUTWARD angle (rad) the left jab arcs to the outside of the body, on top of aiming at the
 *  reticle — a real jab bows out past the centerline, not straight off the shoulder or across.
 *  Positive = more outward (toward the character's left, the punching side). Tunable. */
const JAB_OUTWARD = 0.2;
/** Strike playback rate — the kickbox punch/kick clips run ~1s; nudge them faster so strikes snap. */
const STRIKE_SPEED = 1.2;
/** Block-hit cover-flinch playback rate (a quick guarded recoil). */
const BLOCK_SPEED = 1.4;
/** Dodge-roll playback rate — fast, so the roll starts AND finishes with the (short) evade dash
 *  instead of trailing behind it. Pairs with character.ts DASH_WINDOW. */
const ROLL_SPEED = 2.2;
/** Sword ATTACK playback rate — a REAL animation timeScale (ladder branch below), the same
 *  convention as the dodges/rolls. The DMC retime: the SSH swings are authored long (1.68 s)
 *  and cinematic; at 2.2× a full swing runs ~765 ms, which is what makes the combo read as
 *  an action-game light chain instead of a slow-motion flourish.
 *
 *  THE CONTRACT: COMBAT_ACTIONS' attack rows are authored as clipMs / SWORD_ATTACK_ANIM —
 *  action time and clip time are the SAME clock at this one rate (the old two-clock split,
 *  where a 1.25 "swing speed" divided the gameplay timer while the clip rendered at 1.0×,
 *  is retired). Change this constant and you MUST re-derive the table and re-run
 *  `npm run bake:hitvolumes` (its ANIM_RATE/TIMER_DIVISOR rows mirror this value). */
const SWORD_ATTACK_ANIM = 2.2;
/** PARRY swipe playback rate — the SSH Block_04_Parry is authored long/cinematic like the
 *  SSH attacks (2.2333 s); at 2.2× the action spans 1015 ms (table durationMs mirrors). */
const SWORD_PARRY_ANIM = 2.2;
/** BLADE deflect-snap rate — UAL2 Sword_Block (1.23 s) as a crisp ~0.6 s reaction. */
const SWORD_BLOCK_ANIM = 2.0;
/** 2H octagonal strafe stride rates (fixed, not speed-synced — authoredSpeed is 0 after the
 *  position-track strip; authored strides are ~0.9 m/s walks / ~2.95 m/s jogs vs the
 *  greatsword.ts circling speeds). */
const GS_WALK_ANIM = 1.5;
const GS_JOG_ANIM = 1.3;
const GS_RUN_ANIM = 1.4;
const GS_SPRINT_ANIM = 0.85;
/** SSH Jump_Begin grounded-anticipation skip (s) — playback opens at the launch frame
 *  (measured: the spring is at 0.15–0.20 s; see the strafe-jump branch note). */
const SD_JUMP_SKIP_S = 0.18;
/** Sword DODGE (Space + dir) playback rate — fast, so the dodge clip lands within the short evade-dash
 *  window (mirrors ROLL_SPEED for the unarmed roll). */
const SWORD_DODGE_SPEED = 1.7;
/** ROLL playback rate. The Roll family IS the dodge (swordDodge — the sd_dodge_* hops are
 *  retired). 1.0× = the FULL 1.1 s clip: every faster rate (1.6, then 1.25) still read as
 *  the roll being cut off before it finished — the clip completes now, and the dodge's
 *  coast (character.ts DODGE_FRICTION/DODGE_WINDOW) is stretched to span it. The table's
 *  sword_roll_* durations + the bake's TIMER_DIVISOR/ANIM_RATE rows mirror this value. */
const SWORD_ROLL_ANIM = 1.0;
/** STINGER dash (sd_dash = PS Combo C4, 1.95 s) NATURAL playback rate — the DMC retime the
 *  arrival tail plays at. In FLIGHT the rate is dynamic (retimeStinger): chosen each tick so
 *  the clip's impact frame lands exactly at arrival. */
const SWORD_DASH_ANIM = 2.2;
/** Where each stinger clip's slash IMPACT sits in the raw clip (s) — the flight retime
 *  targets it. sd_dash = PS Combo C4 (lunge completes ≈1.17 s, blade lands just before the
 *  settle); sd_airdash = PS CommonAttack/NormalAttack (hand-speed burst peaks ≈1.0 s). */
const SD_DASH_IMPACTS: Record<string, number> = {
  sd_dash: 1.05, // PS Combo C4 — the LONG ground close
  sd_airdash: 1.0, // PS CommonAttack — the air close
  sd_shortdash: 0.65, // UAL2 Sword_Regular_C — the SHORT close (user pick 2026-07-23): the
  //                     spin windup is the approach; the slash bursts 0.60–0.75 → arrival
};
/** Each dash clip's NATURAL rate (the linger tail + the retime reset): the PS clips are
 *  UE-authored slow (the 2.2× DMC retime); Regular_C is UAL game-speed (1.0×). */
const SD_DASH_NATURAL: Record<string, number> = { sd_dash: 2.2, sd_airdash: 2.2, sd_shortdash: 1.0 };
/** AIR-DODGE (UAL Dodge_L/R, 1.30 s authored) playback rate — nudged up so the flip lands
 *  inside the short airborne evade window instead of trailing it. */
const AIR_DODGE_ANIM = 1.4;
/** GROUND-CONFORM rest height (m): the pose's lowest key bone is seated at this height —
 *  a body radius, so a lying torso rests ON the ground instead of its bone centerline
 *  intersecting it. See the GROUND CONFORM body pass. */
const CONFORM_REST_Y = 0.12;
/** The bones the conform pass samples for the pose's low point — the extremities + core
 *  (enough to bound any lying/tumbling pose without walking all 89 bones). */
const CONFORM_BONES = ["pelvis", "head", "hand_l", "hand_r", "foot_l", "foot_r", "spine_03"] as const;
/** FULL-BODY one-shots that END ON THE FLOOR. The lift phases (down/rise) are not the only
 *  way a body ends up lying: a poise-break knockdown does too, and the in-place policy strips
 *  its authored root drop exactly the same way, so the victim "falls" but hangs at STANDING
 *  height (user report 2026-07-24: the plunge's victim floats at the bottom of the fall).
 *  Measured with `_gpoundfeet.mts sd_hurt_heavy`: the feet finish +0.75 m over rest with the
 *  pelvis at 0.000 — a body on its back, drawn as if standing. Any future knockdown/death
 *  clip belongs in this set; the conform itself needs no per-clip tuning. */
const CONFORM_STRIKES = new Set(["sd_hurt_heavy", "kip_up"]);
/** HOLD emotes that END ON THE FLOOR — the same conform family as CONFORM_STRIKES, reached
 *  through the emote channel instead of a strike. emo_lay is a bed sleep: the in-place
 *  policy strips its authored hip drop exactly like the knockdown reels, so a sleeping
 *  resident rendered lying at STANDING pelvis height and hovered a body-height over the
 *  mattress (2026-07-31 play report: "sleeping NPCs float above the bed").
 *  Measured with `_laypose.mts emo_lay` — the held frame puts EVERY conform bone between
 *  0.940 (hand_l) and 1.101 (head) with the pelvis at 1.039: a body lying flat, drawn as
 *  if standing. The conform drops it 0.940 − CONFORM_REST_Y = 0.820 m, so the caller only
 *  has to seat the group on the mattress top (regionNpcLife.ts SLEEP_LIFT_M). */
const CONFORM_EMOTES = new Set(["emo_lay"]);
const _conformV = new THREE.Vector3();
/** Hit-flash pulse length (s) + the flash tint. */
const HIT_FLASH_S = 0.16;
const _flashWhite = new THREE.Color(0xffffff);
// (The charge-plunge air attack + its constants were REMOVED 2026-07-21 — the aerial STRING is the air kit.)
/** SUPER-JUMP takeoff: the fraction of the SuperHero "Take off 8" clip that is the WINDUP — playback holds
 *  (pauses) here while Space is charged; the remainder (the launch) fires on release. Tunable. */
const SUPERJUMP_HOLD_FRAC = 0.62;
/** Combat-stance WALK speed (m/s). The kickbox clips are authored IN-PLACE (zero root travel); the
 *  shuffle's measured planted-foot speed is ~0.9 m/s, so the body translates in LOCK-STEP with the
 *  footwork (no slide) only at this slow pace. This is the "needs to go slower" — the old 3.0 m/s
 *  overran the stride 3×. Tunable: raise for pace at the cost of some slide. */
const COMBAT_WALK_SPEED = 0.95;
/** Combat-stance ADVANCE speed (m/s) while a MOVING strike (an _f/_b variant) plays — a small lunge
 *  that steps the fighter into the punch. In-place strikes root the body (speed 0). Tunable. */
const COMBAT_ADVANCE_SPEED = 1.2;
/** Idle-breaker cadence: min seconds of standing-guard stillness before a flourish can fire, plus a
 *  random spread on top — so two fighters don't break in lockstep. */
const IDLE_BREAK_MIN = 6;
const IDLE_BREAK_VARY = 7;
// Jab aim scratch (rotate the punching arm onto the reticle without disturbing the elbow bend).
const _jSh = new THREE.Vector3();
const _jHand = new THREE.Vector3();
const _jDir = new THREE.Vector3();
const _jFwd = new THREE.Vector3();
const _jUp = new THREE.Vector3();
const _jRight = new THREE.Vector3();
const _jTarget = new THREE.Vector3();
const _jCorr = new THREE.Quaternion();
const _jCorrK = new THREE.Quaternion();
const _jArmW = new THREE.Quaternion();
const _jParent = new THREE.Quaternion();
/** The DIRS8 octant a body-local move direction falls in (0 = forward, stepping clockwise). The
 *  blendspace (dirBlend) interpolates BETWEEN octants; a per-direction one-shot has to pick one. */
function octant8(fwd: number, right: number): number {
  let ang = Math.atan2(right, fwd); // 0 = forward, + = toward the right
  if (ang < 0) ang += Math.PI * 2;
  return Math.round(ang / (Math.PI / 4)) % 8;
}

/** 8-way directional blendspace for the AIM stance: pick the two clips bracketing
 *  the move direction and weight them by the angle, so e.g. back-left blends into
 *  left continuously. `p` is the weapon prefix ("p"=pistol, "r"=rifle), `gait` is
 *  "walk"/"run". Falls back to the forward clip then idle if a direction is absent.
 *  An EMPTY prefix addresses the UNARMED sets, whose keys carry no prefix at all
 *  (crouch_f / crouch_idle, not _crouch_f) — that is the only difference. */
function dirBlend(
  target: Record<string, number>,
  has: (k: string) => boolean,
  p: string, // clip prefix: "p"/"r" (gun), "kc" (kickbox combat), "" (unarmed, bare keys)
  gait: string,
  fwd: number,
  right: number,
  weight: number,
): void {
  const key = (s: string): string => (p ? `${p}_${s}` : s);
  let ang = Math.atan2(right, fwd); // 0 = forward, + = toward the right
  if (ang < 0) ang += Math.PI * 2; // 0..2π
  const pos = ang / (Math.PI / 4); // sector position 0..8 (DIRS8 is 45° apart)
  const i0 = Math.floor(pos) % 8;
  const i1 = (i0 + 1) % 8;
  const frac = pos - Math.floor(pos);
  const put = (dir: string, w: number): void => {
    if (w <= 1e-4) return;
    for (const k of [key(`${gait}_${dir}`), key(`${gait}_f`), key("idle")]) {
      if (has(k)) { target[k] = (target[k] ?? 0) + w; return; }
    }
  };
  put(DIRS8[i0], weight * (1 - frac));
  put(DIRS8[i1], weight * frac);
}

/** UNARMED crouch movement over the bare `crouch_<dir>` set — the empty-handed twin of the gun
 *  stances' `p_/r_crouch_<dir>` clips (same GASP Crouch_Loop_* pack, no weapon).
 *
 *  ⚠ `strafe` IS THE RMB GATE (user call 2026-08-10): the OCTAGON only opens while the body is
 *  camera-locked by an aim hold, exactly as the gun stances read. Free (no RMB) the body faces
 *  wherever it is going, so crouch movement is the forward loop and nothing else — and the gate has
 *  to live here rather than fall out of the facing, because `moveDirLocal` mixes frames (fwd is
 *  measured against the BODY facing, right against the CAMERA), so a free strafe still arrives
 *  carrying a lateral component that would otherwise blend in a diagonal clip.
 *
 *  Degrades in order: the unarmed set -> `fallbackPrefix`'s crouch set (the unarmed-combat branch
 *  keeps the pistol clips as its safety net) -> the legacy forward `crouch_walk` -> `walk`.
 *  Kept as one helper so all three unarmed crouch branches can't drift apart. */
function crouchStrafe(
  target: Record<string, number>,
  has: (k: string) => boolean,
  strafe: boolean,
  fwd: number,
  right: number,
  weight: number,
  fallbackPrefix = "",
): void {
  const pre = has("crouch_f") ? "" : fallbackPrefix && has(`${fallbackPrefix}_crouch_f`) ? fallbackPrefix : null;
  if (pre !== null) {
    if (strafe) return dirBlend(target, has, pre, "crouch", fwd, right, weight);
    // Free crouch: the forward loop only — same clip family, no directional blend.
    const fk = pre ? `${pre}_crouch_f` : "crouch_f";
    target[fk] = (target[fk] ?? 0) + weight;
    return;
  }
  const chain = fallbackPrefix ? [`${fallbackPrefix}_crouch_walk`] : [];
  for (const k of [...chain, "crouch_walk", "crouch_idle", "walk"]) {
    if (has(k)) { target[k] = (target[k] ?? 0) + weight; return; }
  }
}

/** Resolve a combo strike's clip variant from the player's MOVEMENT INTENT at the press: forward
 *  advances (the `_f` step-in variant), a backpedal jab retreats (`_b`), and stationary / pure-strafe
 *  strikes stay in-place (the bare `base`). Falls back to the in-place clip when the directional twin
 *  isn't loaded (only the jab ships a `_b`). Pure; `has` gates on loaded clips. */
export function resolveStrikeVariant(
  base: string,
  intent: "fwd" | "back" | "none",
  has: (k: string) => boolean,
): string {
  if (intent === "fwd" && has(`${base}_f`)) return `${base}_f`;
  if (intent === "back" && has(`${base}_b`)) return `${base}_b`;
  return base;
}

/** Combat-stance target GROUND SPEED (m/s) for the controller this tick: a MOVING strike lunges
 *  (`advance`), an IN-PLACE strike ROOTS the fighter (0, so a stationary attack can't slide), and
 *  otherwise the body holds the lock-step `walk` shuffle. Pure (speeds injected for testability). */
export function combatTargetSpeed(
  striking: boolean,
  strikeMoving: boolean,
  walk: number = COMBAT_WALK_SPEED,
  advance: number = COMBAT_ADVANCE_SPEED,
): number {
  if (striking) return strikeMoving ? advance : 0;
  return walk;
}

/** Target base-action weights for the current movement state (NO easing). UNARMED
 *  faces its velocity, so it's a forward-only speed blend (idle/walk/run/sprint).
 *  The AIM stance faces the camera, so it's an 8-way directional BLENDSPACE
 *  (dirBlend) over the pistol (`p_*`) or rifle (`r_*`) clips, selected by `aimSet`.
 *  The two sets crossfade by `stanceK`. The per-frame weight easing in
 *  updateStateMachine handles every state-to-state transition; this function only
 *  sets the instantaneous target blend. `has(key)` gates on loaded clips. */
export function selectLocomotion(s: {
  speed: number;
  underhand?: boolean;
  grounded: boolean;
  jet: "none" | "hover" | "glide" | "dive";
  stanceK: number;
  sprinting: boolean;
  crouching: boolean;
  /** Swimming in deep water — whole-body swim clips replace ground/air locomotion. */
  swim?: boolean;
  moveFwd: number;
  moveRight: number;
  climbRate?: number;
  turnRate: number;
  jumpT: number;
  landT: number;
  /** Free entry windup + burst countdown: authored Normal_to_Boost, then flight. */
  glideLaunchT?: number;
  /** Real engine effort separates the relaxed glide from the authored boost. */
  flightPower?: number;
  /** Ctrl-dive anticipation: turn into the plunge while momentum is held. */
  divePreparation?:number;
  /** Visual-only Ctrl startup and follow-through; never an engine timer. */
  diveStartT?:number;
  diveStartWeight?:number;
  /** Measured travel pitch in radians, positive downward, in either thrust mode. */
  flightPitch?: number;
  /** Continuous powered near-vertical ascent; brief/coasting pull-ups keep f_pitch_up. */
  straightUpTime?: number;
  /** Visual handoff survives a reset of the qualifying timer/pitch cone. */
  flightVerticalBlend?:number;
  /** Signed wall phase: ±1 catch, ±2 run, ±3 jump away. */
  wallRunState?: number;
  ledgeClimbState?:number;
  /** Post-takeoff up-to-idle SETTLE one-shot countdown (bridges f_takeoff -> f_idle with
   *  the clean Up_to_idle_02 clip, settling in place only). */
  toIdleT?: number;
  landJet?: boolean;
  /** Jetted within ~0.3s — a flight-rhythm gap, so hold the leap rather than fall_loop. */
  jetRecent?: boolean;
  /** The land clip chosen at touchdown (land_<stand|walk|run>_<heavy|light>, or f_land). */
  landClip?: string;
  /** Which aim-stance set the held weapon uses: "p"=pistol/sidearm, "r"=rifle/main. */
  aimSet?: "p" | "r" | "sd";
  /** Aiming down sights: hold the aim pose through jump/hover instead of breaking
   *  into a jump/fall/launch clip (the aim layer keeps the gun on the reticle). */
  aiming?: boolean;
  stance?: "relaxed" | "readied";
  /** SWORD RMB "focus" hold (camera-locked). When set, forward running uses the SSH directional JOG
   *  (sd_run_*); when clear, the sword's forward run falls back to the UNARMED run clip (free movement). */
  swordFocus?: boolean;
  /** LAUNCHED/JUGGLED body (the lift_ set + kip_up) — a full-body override above everything:
   *  a ballistic body has no locomotion. Set for enemy/remote avatars from the streamed
   *  `lf` posture (avatar.setLift). */
  liftKey?: string;
  /** STAGGERED body (parried — st_loop/st_end): full-body override just below lift (a
   *  riposte knockup converts the stun into a juggle). From the streamed `st` posture. */
  staggerKey?: string;
  /** 2H HELD BLOCK (gs_block_loop): full-body hold below the strike channel — the raise/
   *  impact/lower one-shots play over it, the hold shows between them (avatar.gsBlockHold). */
  gsBlockKey?: string;
  /** 2H GREATSWORD MODE: its own stance idle + the octagonal walk/jog strafe sets while
   *  CIRCLING (swordFocus = locked/focused); free movement keeps body-faces-velocity. */
  gsMode?: boolean;
  /** Between AERIAL-string hits (airborne, chain open, no strike in flight): float the
   *  sd_air_idle combat stance instead of leap/fall — the air-rave hold. */
  airCombo?: boolean;
  /** SUPER-JUMP takeoff clip currently rendered ("" = none) — f_superjump. When set it OVERRIDES locomotion
   *  (the grounded charge windup AND the airborne launch) until the launch clip finishes. */
  superJumpKey?: string;
  /** UNARMED COMBAT stance (RMB, empty-handed): legs use the camera-locked directional
   *  footwork, the DHTH combat clip layers the upper body. */
  combat?: boolean;
  /** RMB-while-unarmed SLOW WALK (the current unarmed behavior): an 8-way omnidirectional walk
   *  blendspace (camera-locked). No combat. Mutually exclusive with `combat`. */
  walkMode?: boolean;
  /** Guard UP. In the DHTH fighting stance that is the full-body double-guard (dh_block, rooted); in
   *  the SHIELD stance (walkMode) it selects the kickbox fighter's whole-body clip set. */
  blocking?: boolean;
  /** SHIELD GUARD walk phase + the octant it is walking (see Avatar.updateGuardWalk): the kickbox
   *  walk family is step-off → loop → settle, so the stance is a phase machine, not just a blend. */
  guardPhase?: GuardPhase;
  guardDir?: string;
  /** Jab one-shot countdown (s) — selects u_jab while > 0 (legacy fallback path only). */
  punchT?: number;
  /** Block-hit reaction countdown (s) — drives the shield-glow flash (no clip; see updateCombatGlow). */
  blockHitT?: number;
  /** Active strike clip key ("" / absent = none). A masked PUNCH (strikeFull=false) overlays the upper
   *  body over the locomotion legs; a FULL strike (strikeFull=true: kick/roll/flinch/parry) plays at
   *  full weight and roots. Driven by Avatar.move/evadeRoll/blockHit/parry. */
  strikeKey?: string;
  strikeFull?: boolean;
  /** STINGER dash live (soft-lock gap closer): the dash clip owns the body while the sim
   *  homes. `stingerKey` picks the clip (ground C4 vs airborne CommonAttack). */
  stinger?: boolean;
  stingerKey?: string;
  /** AIR-EVADE circling (1H): the aerial float owns the body during the orbit. */
  airEvade?: boolean;
  /** Active full-body idle-breaker clip key ("" / absent = none) — replaces kc_idle while standing. */
  idleBreakKey?: string;
  /** NPC ACTIVITY EMOTE ("" / absent = none): a looping UAL activity clip
   *  (counter service, farm work, drinking, dancing …) that replaces the
   *  neutral idle while STANDING — movement fades it exactly like the idle. */
  emoteKey?: string;
  /** IN CONVERSATION (ambient NPCs): the relaxed talk idle replaces the
   *  neutral idle while standing, with occasional gesture one-shots. */
  talking?: boolean;
  /** Active talk-gesture one-shot key ("" / absent = none). */
  talkKey?: string;
  /** Airborne inside an active flight chain (jumpChain>=1: jumped/jetting, not yet landed):
   *  hold the leap pose for the jet-off descents BETWEEN hover/glide taps instead of the
   *  falling spread-eagle. Only a no-jump fall (walked off a ledge, jumpChain===0) falls. */
  leapHold?: boolean;
  /** Airborne in a boost BIG-LEAP: the sprint-start launch (bl_start, while bigLeapT>0) → the airborne
   *  fall loop (bl_loop). Overrides the generic leap/fall pose. */
  bigLeap?: boolean;
  bigLeapT?: number;
  /** Compatibility hint recording flight earlier in the current airborne stint. */
  jetLaunched?: boolean;
  /** A regular ground jump's phase START clip ("js_run"/"js_sprint"/…) + its countdown — the launch
   *  push-off, picked by along-ground speed, before the airborne loop takes over. */
  jumpStartKey?: string;
  jumpStartT?: number;
  has: (key: string) => boolean;
}): Record<string, number> {
  const target: Record<string, number> = {};
  const add = (key: string, w: number): void => {
    if (w > 0 && s.has(key)) target[key] = (target[key] ?? 0) + w;
  };
  // Add `w` to the first key in the chain that actually loaded (graceful fallback).
  const addFb = (w: number, ...keys: string[]): void => {
    if (w <= 0) return;
    for (const k of keys) if (s.has(k)) { target[k] = (target[k] ?? 0) + w; return; }
  };
  const armed = s.stanceK > 0.5;

  // SWORD AIR ATTACK — the 3-phase plunge (begin → loop → land) OWNS the whole body from launch through
  // touchdown, so it overrides everything (the airborne leap/fall AND the generic ground landing). The
  // A LAUNCHED body overrides EVERYTHING — it is ballistic, not locomoting. Above even the
  // air attack: a juggled victim mid-plunge is still a juggled victim.
  if (s.liftKey && s.has(s.liftKey)) return (add(s.liftKey, 1), target);
  // A STAGGERED (parried) body reels — full-body stun, below lift only (the riposte's
  // knockup converts the stun into a juggle and the lift set takes over).
  if (s.staggerKey && s.has(s.staggerKey)) return (add(s.staggerKey, 1), target);
  // FULL-BODY SWORD STRIKE — a full-body sd_ one-shot owns the body ANYWHERE. Airborne it
  // beats the jump/leap/fall path (the rising uppercut, the aerial string, the GroundPound
  // dive all rendered as jump/fall clips while their damage swept invisibly — the stanceK
  // bug's airborne sibling); GROUNDED it must sit ABOVE the landT block, because the slam's
  // own descent trips the landing anticipation and land_stand_heavy was cutting off the
  // GroundPound's authored impact at touchdown. One site, grounded-agnostic: a sword swing
  // rides through the grounded edge and finishes its arc. Order: below lift (a juggled
  // body still wins), above everything else.
  // VICTIM REACTIONS play in ANY stance (2026-08-02): the hit_* flinches and the hurt-heavy
  // knockdown used to display only for the sd stance (or the combat branch, which is why lab
  // brawlers flinched but rifle/idle victims froze) — a shot enemy must react regardless.
  if (s.strikeKey && s.strikeFull
    && (s.aimSet === "sd" || s.strikeKey.startsWith("hit_") || s.strikeKey === "sd_hurt_heavy")
    && s.has(s.strikeKey)) {
    return (add(s.strikeKey, 1), target);
  }
  if(s.ledgeClimbState&&!s.swim){
    addFb(1,s.ledgeClimbState===1?'ledge_step':'ledge_climb','jump');return target;
  }
  if(s.wallRunState&&!s.grounded&&s.jet==='none'&&!s.swim){
    const side=s.wallRunState<0?'l':'r';
    addFb(1,`wall_${Math.abs(s.wallRunState)===3?'jump':'run'}_${side}`,'jump','run');
    return target;
  }
  // 2H HELD BLOCK — the guard hold owns the body between one-shots. MOVING under the
  // guard walks the pack's Guard_Walk 8-way set (gs_gwalk_*, blade-up strafe — Valheim
  // blocks while walking); still = the hold loop. dirBlend's fallback covers the two
  // missing forward diagonals with gs_gwalk_f.
  if (s.gsBlockKey && s.has(s.gsBlockKey)) {
    const mv = Math.min(1, s.speed / MOVE_RAMP);
    if (mv > 0.001 && s.has("gs_gwalk_f")) {
      // Blade-up strafe: the Guard_Walk set, or Guard_Jog once Shift lifts the pace
      // past the run tier (greatsword.ts speeds straddle RUN_ANIM_MIN by design).
      dirBlend(target, s.has, "gs", s.speed >= RUN_ANIM_MIN ? "gjog" : "gwalk", s.moveFwd, s.moveRight, mv);
      if (1 - mv > 0.001) add(s.gsBlockKey, 1 - mv);
    } else {
      add(s.gsBlockKey, 1);
    }
    return target;
  }
  // STINGER — the soft-lock gap-closer dash: the dash clip owns the body for the short
  // homing travel (grounded-agnostic so terrain bumps can't flicker it into the jump).
  if (s.stinger) {
    const k = s.stingerKey ?? "sd_dash";
    if (s.has(k)) return (add(k, 1), target);
  }
  // AERIAL-STRING FLOAT: between air hits the fighter HOLDS the floating combat stance
  // rather than flailing into the fall loop (the strike override above wins while a swing
  // is actually in flight).
  if ((s.airCombo || s.airEvade) && !s.grounded && s.has("sd_air_idle")) {
    return (add("sd_air_idle", 1), target);
  }

  // SUPER-JUMP takeoff (charge windup → launch) OWNS the body from the grounded charge through the airborne
  // launch, so it overrides locomotion until the SuperHero takeoff clip finishes (then the rise pose takes over).
  if (s.superJumpKey && s.has(s.superJumpKey)) return (add(s.superJumpKey, 1), target);

  // AIR / transitions (stance-agnostic) override everything. Landing uses the
  // jetpack land (f_land) if we touched down from a jet, else the ordinary land.
  // Only the DIVE flourish survives here — see selectLand for why the generic land clip is gone.
  if (s.landT > 0 && s.landClip === "f_dive_land") {
    // The touchdown picked a land clip by impact (heavy/light) + next phase (stand/walk/run),
    // or the jetpack land. Hold it (it crossfades out into the chosen locomotion as landT decays).
    if (s.landClip && s.has(s.landClip)) return (add(s.landClip, 1), target);
    if (s.landJet && s.has("f_land")) return (add("f_land", 1), target);
    if (s.has("land")) return (add("land", 1), target);
  }
  // SWIM — whole-body, replaces ground/air locomotion in deep water (mirrors how the
  // flight clips own the pose airborne). Tread-water idle ↔ 4-way surface swim, with
  // dive/rise on a clear vertical move. The sim faces + moves the body; root is dropped.
  if (s.swim && s.has("sw_idle")) {
    const climb = s.climbRate ?? 0;
    const moveK = Math.min(1, s.speed / SWIM_MOVE_RAMP);
    const vertK = Math.min(1, Math.abs(climb) / 3);
    const idleK = Math.max(0, 1 - Math.max(moveK, vertK));
    add("sw_idle", idleK);
    const act = 1 - idleK;
    if (act > 0.001) {
      const hv = moveK + vertK || 1;
      const hW = (act * moveK) / hv;
      const vW = (act * vertK) / hv;
      if (hW > 0.001) {
        const f = s.moveFwd, rt = s.moveRight, fast = !!s.sprinting; // Shift → front crawl
        if (Math.abs(f) >= Math.abs(rt)) {
          if (f >= 0) addFb(hW, fast ? "sw_crawl" : "sw_fwd", "sw_fwd", "sw_idle");
          else addFb(hW, "sw_back", "sw_fwd", "sw_idle"); // no fast backward stroke
        } else {
          const lat = rt >= 0;
          if (fast) addFb(hW, lat ? "sw_crawl_r" : "sw_crawl_l", lat ? "sw_r" : "sw_l", "sw_fwd", "sw_idle");
          else addFb(hW, lat ? "sw_r" : "sw_l", "sw_fwd", "sw_idle");
        }
      }
      if (vW > 0.001) addFb(vW, climb >= 0 ? "sw_rise" : "sw_dive", "sw_fwd", "sw_idle");
    }
    if (!Object.keys(target).length) add("sw_idle", 1);
    return target;
  }
  // Flight clips own the whole authored body. Directional blends use measured
  // travel, while the final pose passes add mild coasting arms, trajectory
  // incidence, and reversible landing preparation. Explicit actions above win.
  // DIVE (hold-Ctrl plunge): the head-down Boost_Down clip is the MAIN pose (user call — it reads
  // better than the glide set aimed down), banking clips blended at half weight on the WASD drift.
  const vertical=THREE.MathUtils.clamp(s.flightVerticalBlend??((s.flightPower??0)>.5
    ?THREE.MathUtils.smoothstep(s.straightUpTime??0,FLIGHT_VERTICAL_HOLD,FLIGHT_VERTICAL_FULL):0),0,1);
  const diveStart=s.has('f_dive_start')?s.diveStartWeight??flightLaunchClipWeight(s.diveStartT??((s.divePreparation??0)>0?FLIGHT_LAUNCH_TOTAL-.3*(s.divePreparation??0):0)):0;
  if (s.jet === "dive") {
    add('f_dive_start',diveStart);
    addFb((1-diveStart)*vertical,"f_pitch_up","f_boost","f_idle");
    const rest=(1-diveStart)*(1-vertical);
    const lr = Math.min(1, Math.abs(s.moveRight)) * 0.5;
    if (lr > 0) addFb(rest*lr, s.moveRight > 0 ? "f_bank_right" : "f_bank_left", "f_dive", "f_pitch_down");
    addFb(rest*(1 - lr), "f_dive", "f_pitch_down", "bl_loop", "jump");
    return target;
  }
  if (s.jet === "glide") {
    const preparing=s.has('f_dive_start')?diveStart:THREE.MathUtils.smoothstep(s.divePreparation??0,0,1);
    const diveBank=Math.min(1,Math.abs(s.moveRight))*.5;
    if(s.has('f_dive_start'))add('f_dive_start',preparing);
    else {
      addFb(preparing*diveBank,s.moveRight>0?"f_bank_right":"f_bank_left","f_dive","f_pitch_down");
      addFb(preparing*(1-diveBank),"f_dive","f_pitch_down","bl_loop","jump");
    }
    // Only the free entry uses a one-shot. Directional poses are shared by
    // powered flight and coasting, so releasing thrust cannot change the bank.
    const launchW = s.has("f_launch") ? (1-preparing)*flightLaunchClipWeight(s.glideLaunchT ?? 0) : 0;
    addFb(launchW, "f_launch", "f_boost", "f_idle");
    const rest = 1 - preparing - launchW;
    const pitch = THREE.MathUtils.clamp(s.flightPitch ?? Math.atan2(-(s.climbRate ?? 0),Math.max(.01,s.speed)),-Math.PI/2,Math.PI/2);
    const down = Math.max(0,pitch);
    // Calibrated against the authored chest angles. Keep some Turn_Down at
    // vertical because Boost_Down itself leans slightly past straight down.
    const shallow = Math.min(1,(down+.244)/.524)*THREE.MathUtils.smoothstep(down,0,.105);
    const deep = THREE.MathUtils.clamp((Math.min(down,1.55)-.28)/1.465,0,1);
    const rise = THREE.MathUtils.smoothstep(-pitch,.04,.55);
    const turn = s.turnRate ?? 0,rate = Math.abs(turn),tilt = Math.abs(pitch);
    // Existing actual-turn filtering and action crossfades carry reversals
    // through neutral. Stronger authored turns replace ordinary banks gradually;
    // neither camera input nor a second procedural roll drives this layer.
    const bank = THREE.MathUtils.smoothstep(rate,.06,1.2)
      *(1-.7*THREE.MathUtils.smoothstep(tilt,.15,.75))
      *(1-THREE.MathUtils.smoothstep(tilt,.8,1.45));
    const hard = THREE.MathUtils.smoothstep(rate,1.35,2.8);
    // Keep the outgoing vertical silhouette above the instantaneous directional
    // blend: multiplying it by the new level pitch would erase the exit fade.
    const directional=rest*(1-vertical);
    const travel = directional*(1-bank),corner = directional*bank;
    addFb(travel*(1-rise)*(1-shallow),"f_boost","f_idle","idle");
    addFb(travel*(1-rise)*shallow*(1-deep),"f_pitch_down","f_boost","f_idle");
    addFb(travel*(1-rise)*shallow*deep,"f_dive","f_pitch_down","f_boost","f_idle");
    addFb(travel*rise,"f_pitch_up","f_boost","f_idle");
    addFb(rest*vertical,"f_pitch_up","f_boost","f_idle");
    const bankKey=turn>0?"f_bank_right":"f_bank_left";
    addFb(corner*(1-hard),bankKey,"f_boost","f_idle");
    addFb(corner*hard,turn>0?"f_turn_right":"f_turn_left",bankKey,"f_boost","f_idle");
    return target;
  }
  // HOVER — the clip holds the stance (armed: the weapon flight idle; unarmed: the strafe
  // blendspace). The authored clip owns the legs and the aim layer owns the weapon.
  if (s.jet === "hover") {
    const P = s.aimSet ?? "p";
    const fwd = s.moveFwd, right = s.moveRight;
    // ⚠ ONLY GUNS have a weapon flight set. The BLADE (aimSet "sd") has no f_sd_* clips, so the
    // armed path gave it the weapon idle's fallback and NOTHING for the four directions — a
    // hovering swordsman just hung there while an unarmed one strafed (user 2026-08-03: "we are
    // not applying the same hover movement animations to the blade combat stance that we do when
    // unarmed"). It takes the unarmed 4-way blendspace, exactly as it takes the unarmed jump.
    if (armed && P !== "sd") {
      const lat = Math.min(1, Math.abs(right));
      if (lat > 0.01) addFb(lat, `f_${P}_${right > 0 ? "right" : "left"}`, `f_${P}_idle`);
      addFb(1 - lat, `f_${P}_idle`, "f_idle", "idle");
      return target;
    }
    const mag = Math.min(1, Math.hypot(fwd, right));
    addFb(1 - mag, "f_idle", "idle");
    if (fwd > 0.01) addFb(fwd, "f_fwd", "f_idle", "idle");
    if (-fwd > 0.01) addFb(-fwd, "f_back", "f_idle", "idle");
    if (right > 0.01) addFb(right, "f_right", "f_idle", "idle");
    if (-right > 0.01) addFb(-right, "f_left", "f_idle", "idle");
    return target;
  }
  // AIRBORNE (no jet): the leap pose.
  if (!s.grounded) {
    // An AIMED gun keeps its own airborne aim pose through a double jump: the
    // authored flip would carry the spine, arms and weapon with it. Only its
    // legs are borrowed (Avatar.applyDoubleJumpLegs).
    const aimedGun = s.underhand || armed && s.aiming && (s.aimSet ?? "p") !== "sd";
    if (s.jumpStartKey === 'djump' && (s.jumpStartT ?? 0) > 0 && s.has('djump') && !aimedGun) {
      add('djump', 1);
      return target;
    }
    // BOOST BIG-LEAP: the sprinting-Rfoot launch (bl_start) for its short window, then the airborne
    // fall loop (bl_loop) — a forward-bounding leap, overriding the generic jump/fall and the gun idle.
    if (s.bigLeap) {
      if ((s.bigLeapT ?? 0) > 0) return (addFb(1, "bl_start", "jump"), target);
      return (addFb(1, "bl_loop", "jump"), target);
    }
    // ⚠⚠ THE JET-LAUNCHED FREE-FALL FLIGHT POSES ARE REMOVED (user call 2026-08-03, third report of
    // "some weird upper body falling animation"). A stint that had touched hover kept `jetLaunched`
    // for the rest of its airborne life, so the instant Space was RELEASED and the jet dropped, the
    // full-body SuperHero dive `f_down` came in at WEIGHT 1.00 (measured) and contorted the whole
    // upper body head-down for the entire descent. That is what "as soon as the spacebar is let go,
    // the body deforms as it falls" was — a clip, not a solver.
    //
    // A jet-launched descent now falls like any other: the stance's own jump pose, held through the
    // arc, exactly as the branch below already does. `jetLaunched` stays on the state object (it is
    // a real signal) — it simply no longer selects a clip.
    void s.jetLaunched;
    // The WHOLE airborne arc holds the JUMP clip (UAL Jump_Start, clamped into its airborne
    // pose) — the old rise→fall-loop crossfade is retired (user call 2026-07-21: never show
    // the generic fall flail). Gun stances keep their own airborne aim pose; the SWORD holds
    // the same jump as unarmed (its float belongs to the AIR RAVE only — the airCombo
    // override above). Jet-launched stints keep their flight poses (branch above).
    // THE STANCE'S OWN air clip, not the pistol's. This hard-coded "p_jump" for every armed stance,
    // so a drawn RIFLE jumped with the pistol's pose (and a CROUCHED jump did too). Falls back
    // pistol -> unarmed so a stance that ships no jump still animates.
    // CROUCHED keeps its own crouch-jump when the set has one.
    if (armed && (s.aimSet ?? "p") !== "sd") {
      const P = s.aimSet ?? "p";
      // DESCENDING gets the stance's FALL loop when it ships one; the jump pose is the RISE.
      // ⚠⚠ NO DESCENT-SPECIFIC FALL POSE. `${P}_fall` (and the generic `fall_loop`) used to take
      // over below a climb rate of −1.5, and its upper body is the thing the user kept calling the
      // "weird upper body falling animation" (removed entirely 2026-08-03). The whole airborne arc
      // holds the stance's JUMP pose now — which is what the 2026-07-21 call already asked for
      // ("never show the generic fall flail") before the fall clip crept back in for armed stances.
      if (s.crouching) addFb(1, `${P}_crouch_jump`, `${P}_jump`, "p_jump", "jump");
      else addFb(1, `${P}_jump`, "p_jump", "jump");
    } else {
      addFb(1, "jump");
    }
    return target;
  }

  const move = Math.min(1, s.speed / MOVE_RAMP); // idle <-> moving
  const idle = 1 - move;
  const run = s.speed >= RUN_ANIM_MIN || s.sprinting; // run tier vs walk tier
  const un = 1 - s.stanceK;
  const st = s.stanceK;

  // SHIELD GUARD / RMB-WHILE-UNARMED SLOW WALK. The body is camera-locked (controller faces the aim),
  // so movement is an 8-way blendspace rather than a forward-only gait.
  //
  // GUARD UP (shield mode, RMB) = THE KICKBOX FIGHTER, whole body and one pack: anim_idle standing,
  // and the anim_walk_<DIR> family moving — entered through that direction's step-off (_start) and
  // left through its settle (_stop), which is how the pack authors them. Dropping straight into a
  // loop from a standstill slides the first stride; the transitions are what make it read as a
  // fighter stepping off, not a mannequin sliding. The phase machine is Avatar.updateGuardWalk.
  //
  // GUARD DOWN = the plain slow strafe: the neutral om_walk over the neutral idle.
  if (s.walkMode) {
    if (s.crouching) {
      // walkMode IS the RMB hold (main.ts sets it from `!held && character.aiming`), so the
      // crouch octagon is open here.
      add("crouch_idle", idle);
      if (move > 0.001) crouchStrafe(target, s.has, true, s.moveFwd, s.moveRight, move);
      return target;
    }
    if (s.blocking) {
      // A cover flinch / parry is a FULL-BODY reaction: it plays alone, recoils in place, and drops
      // back into the stance — the same one-shot path the fighting stance uses.
      if (s.strikeKey && s.strikeFull && s.has(s.strikeKey)) {
        add(s.strikeKey, 1);
        return target;
      }
      // Mid-transition, the step-off / settle clip OWNS the body — it is a whole-body move, and
      // cross-blending the loop into it would smear the very footfall it exists to plant.
      const phase = s.guardPhase ?? "idle";
      if (phase === "start" || phase === "stop") {
        const key = `kc_walk_${s.guardDir ?? "f"}_${phase}`;
        if (s.has(key)) {
          add(key, 1);
          return target;
        }
      }
      addFb(idle, "kc_idle", "idle"); // standing: the fighter's idle
      if (move > 0.001) dirBlend(target, s.has, "kc", "walk", s.moveFwd, s.moveRight, move);
      return target;
    }
    addFb(idle, "idle");
    if (move > 0.001) {
      dirBlend(target, s.has, "om", run && s.has("om_run_f") ? "run" : "walk", s.moveFwd, s.moveRight, move);
    }
    return target;
  }

  // UNARMED COMBAT stance (RMB, empty-handed) — DHTH MASKED-OVERLAY model. The body is camera-locked,
  // so the LEGS use the 8-way directional footwork (base layer); the DHTH fight-guard masks the UPPER
  // body at COMBAT_BLEND (so it dominates the torso/arms). PUNCHES are masked upper one-shots over the
  // moving legs (top/bottom decoupled); KICKS / dodge-rolls / block-flinches / parry are FULL-body
  // one-shots that root; a held BLOCK shows the full-body double-guard. Priority: full strike > block >
  // (legs + masked punch/guard).
  if (s.combat) {
    if (s.crouching) {
      // Empty-handed: the UNARMED octagonal crouch, not the pistol set (the fists belong to the
      // masked upper body, and a crouched fighter still strafes). Falls back to p_crouch_* for a
      // build that hasn't deployed the unarmed strafe clips.
      addFb(idle, "crouch_idle", "p_crouch_idle");
      if (move > 0.001) crouchStrafe(target, s.has, true, s.moveFwd, s.moveRight, move, "p");
      return target;
    }
    // FULL-body override: an active kick / dodge-roll / block-flinch / parry plays alone (it roots).
    if (s.strikeKey && s.strikeFull && s.has(s.strikeKey)) {
      add(s.strikeKey, 1);
      return target;
    }
    // Held BLOCK: the full-body double-guard (rooted).
    if (s.blocking && s.has("dh_block")) {
      add("dh_block", 1);
      return target;
    }
    // LEGS (base layer): 8-way strafe footwork while moving (kickbox walk, retained), stance legs while still.
    addFb(idle, "dh_stance", "fist_stance", "p_idle");
    if (move > 0.001) {
      if (s.has("kc_walk_f")) dirBlend(target, s.has, "kc", "walk", s.moveFwd, s.moveRight, move);
      else dirBlend(target, s.has, "p", run ? "run" : "walk", s.moveFwd, s.moveRight, move);
    }
    // UPPER body (masked overlay at COMBAT_BLEND): a directional PUNCH one-shot, else the fight guard.
    if (s.strikeKey && !s.strikeFull && s.has(s.strikeKey)) addFb(COMBAT_BLEND, s.strikeKey, "dh_guard", "u_idle");
    else addFb(COMBAT_BLEND, "dh_guard", "u_idle");
    return target;
  }

  // UNARMED (faded by 1-stanceK): the body faces its velocity, so this is a
  // forward-only speed blend (idle -> walk -> run -> sprint), incl. crouch.
  if (un > 0.001) {
    if (s.crouching) {
      // CROUCH (unarmed): the octagonal crouch strafe, but ONLY while RMB camera-locks the body —
      // free crouch movement is the forward loop, the same read the gun stances give (user call
      // 2026-08-10). The RMB stance itself normally runs through the walkMode branch above; this
      // `aiming` gate keeps the two consistent for any path that reaches here holding the aim.
      add("crouch_idle", idle * un);
      if (move > 0.001) crouchStrafe(target, s.has, !!s.aiming, s.moveFwd, s.moveRight, move * un);
    } else {
      // Conversation stance (ambient NPCs): the relaxed talk idle carries
      // the stand, with an occasional gesture one-shot riding on top. The
      // gesture also fires for plain AMBIENT idlers (talkKey set, not
      // talking) so bystanders shift and glance instead of freezing.
      // An ACTIVITY EMOTE (UAL loop — counter service, farm work, drink…)
      // outranks the talk idle: a serving shopkeeper keeps serving.
      if (s.emoteKey && s.has(s.emoteKey) && idle > 0.001) {
        add(s.emoteKey, idle * un);
      } else if (s.talkKey && s.has(s.talkKey) && idle > 0.001) {
        add(s.talkKey, idle * un);
      } else if (s.talking && idle > 0.001) {
        addFb(idle * un, "npc_talk", "idle");
      } else {
        add("idle", idle * un);
      }
      if (move > 0.001) addFb(move * un, s.sprinting ? "sprint" : run ? "run" : "walk", "run", "walk");
    }
  }

  // ARMED stance (faded by stanceK): a gun is drawn. While AIMING the body
  // strafe-locks, so movement is an 8-way directional BLENDSPACE over the
  // pistol/rifle clips (dirBlend); SPRINTING (forward-only, body faces velocity)
  // uses the dedicated gun sprint clip — it must not vanish just because a gun is
  // out (that dropped sprint for the default rifle loadout).
  if (st > 0.001) {
    const P = s.aimSet ?? "p";
    // (Sword full-body strikes are handled by the grounded-agnostic override at the top of
    // the selector — they must outrank landT, so they can't live down here.)
    if (s.crouching) {
      // CROUCH strafe: same 8-way directional blendspace as walk/run, over the p/r_crouch_<dir>
      // (Crouch_Loop_*) clips. Falls back to the forward crouch_walk if a dir clip is absent.
      add(`${P}_crouch_idle`, idle * st);
      if (move > 0.001) dirBlend(target, s.has, P, "crouch", s.stance === "relaxed" ? 1 : s.moveFwd, s.stance === "relaxed" ? 0 : s.moveRight, move * st);
    } else if (P === "sd" && s.gsMode) {
      // THE 2H GREATSWORD STANCE (user call 2026-07-22): its own combat idle, and CIRCLING
      // (swordFocus = a live lock/focus — face-this-enemy mode) strafes the pack's
      // OCTAGONAL sets — walk, or jog once Shift lifts the pace past the run tier.
      // Free (unlocked) movement keeps body-faces-velocity on the shared run/sprint clips.
      addFb(idle * st, "gs_idle", "sd_idle");
      if (move > 0.001) {
        if (s.swordFocus) dirBlend(target, s.has, "gs", run ? "jog" : "walk", s.moveFwd, s.moveRight, move * st);
        // FREE movement carries the sword on the pack's own cycles (user call 2026-07-22):
        // Run_01 for the run tier, Run_02 for sprint, the forward strafe clip for a walk.
        else addFb(move * st, s.sprinting ? "gs_sprint" : run ? "gs_run" : "gs_walk_f", "run", "walk");
      }
    } else {
      add(`${P}_idle`, idle * st);
      if (move > 0.001) {
        // FOCUS/LOCK strafe outranks the sprint clip: Shift while locked IS the jog tier
        // (the speed straddles RUN_ANIM_MIN), not a sprint (user call 2026-07-22).
        if (s.sprinting && !s.aiming && !(P === "sd" && s.swordFocus)) addFb(move * st, `${P}_sprint`, `${P}_run_f`);
        // SWORD free movement (no RMB focus): the body faces velocity, so the forward RUN uses the UNARMED
        // run clip (natural cadence). RMB focus uses the full Powerful Sword Run octagon.
        else if (P === "sd" && s.swordFocus) dirBlend(target,s.has,P,"run",s.moveFwd,s.moveRight,move*st);
        else if (P === "sd" && !s.swordFocus && run) addFb(move * st, "run", "walk");
        else dirBlend(target, s.has, P, run ? "run" : "walk", s.stance === "relaxed" ? 1 : s.moveFwd, s.stance === "relaxed" ? 0 : s.moveRight, move * st);
      }
    }
  }
  // ARMED UPPER-BODY OVERLAY (the top-only parry deflection, user call 2026-07-22): a
  // MASKED sd_ one-shot rides OVER the armed locomotion at COMBAT_BLEND — the clip is
  // upperOnly (legs stripped at load), so the legs stay 100% locomotion and movement
  // continues. The weight-sum normalization excludes it (COMBAT_OVERLAY).
  // The rifle Z-stow gesture (stow_* = legs-stripped sheathe pair) masks over ANY stance —
  // the legs keep their locomotion while the arm reaches over the shoulder.
  if (s.strikeKey && !s.strikeFull && (s.aimSet === "sd" || s.strikeKey.startsWith("stow_")) && s.has(s.strikeKey)) add(s.strikeKey, COMBAT_BLEND);
  return target;
}

/**
 * Forward body lean (rotation.x; NEGATIVE leans the top toward -Z, our forward).
 *
 * ⚠⚠ THIS IS A ROOT ROTATION — it pitches the ENTIRE avatar, so no spine/aim layer above it can
 * correct it. The glide term reaches `glideK * 1.15` ≈ 66°, which is the superman flight attitude
 * and correct for a glide. But `glideK` covers glide AND dive and eases out SLOWLY, so it outlives
 * the state that earned it: drop out of a glide into a hover and the whole body stays pitched over
 * for the decay. `aimK` cancels it — an aiming body holds its sight line, whatever the jets were
 * doing a moment ago. (User report 2026-08-03: triple-jump into flight, then immediately switch to
 * hover-fire — "the whole torso flips around". It is this, not the aim stack, and it is why every
 * probe of a SETTLED hover came back clean.)
 */
export function bodyLean(speedK: number, sprinting: boolean, airK: number, glideK: number, hasMixer: boolean, aimK = 0): number {
  const glideLean = glideK * (hasMixer ? 0.32 : 1.15) * (1 - aimK);
  return -speedK * (sprinting ? 0.16 : 0.07) * (1 - airK) - glideLean;
}

/**
 * Solve the WORLD orientation for the chest (the bone the gun-arms hang off) so
 * that the gun points at the reticle AND the torso stays upright — no roll.
 *
 * The gun's direction relative to the chest is fixed by the clip (`gunLocal` =
 * the gun direction expressed in chest-local space). Pointing the gun at `aim`
 * is a 2-DOF constraint, leaving 1 free DOF: the roll about the aim axis. We
 * spend it keeping the chest's up in the vertical plane (so the torso never
 * tilts sideways — the "leans right while aiming" bug). Construction:
 *   q1 = minimal rotation gunLocal → aim          (gun lands on target)
 *   q2 = roll about `aim` so the chest up → the vertical-plane up (no roll)
 *   R  = q2 ∘ q1
 * Pure + deterministic so on-target + no-roll are unit-testable.
 */
export function aimChestWorld(gunLocal: THREE.Vector3, aim: THREE.Vector3, worldUp: THREE.Vector3, out: THREE.Quaternion): THREE.Quaternion {
  const g = _acBH.copy(gunLocal).normalize();
  const a = _acAH.copy(aim).normalize();
  const u = _acUp.copy(worldUp).normalize();
  const q1 = _acYaw.setFromUnitVectors(g, a); // gunLocal -> aim (minimal)
  // Where the chest-local up (+Y) lands after q1, vs where we want it: the part
  // of world-up perpendicular to the aim axis (the upright direction in the
  // vertical plane through `aim`). Roll about `aim` to close that gap.
  const upA = _acTmp.set(0, 1, 0).applyQuaternion(q1);
  const upT = _acAxis.copy(u).addScaledVector(a, -u.dot(a));
  if (upT.lengthSq() < 1e-8) return out.copy(q1); // aiming along world-up: leave as-is
  upT.normalize();
  const cross = _acUp2.crossVectors(upA, upT).dot(a);
  const roll = Math.atan2(cross, Math.max(-1, Math.min(1, upA.dot(upT))));
  const q2 = _acPitch.setFromAxisAngle(a, roll);
  return out.copy(q2).multiply(q1);
}

/** Shared per-frame scale (0..1) on the avatar body + clothing lighting fill, poked by main each
 *  frame from the day/night factor: 1 in daylight, ramping to 0 across dusk. Without this the
 *  self-lit fill below stays constant and the avatar reads as GLOWING against a dark night world;
 *  fading it to 0 lets the body go as dark as the surrounding terrain after sundown. */
export const BODY_FILL = { value: 1 };

/** Warm studio-style diffuse fill for the low-poly male: enough ambient response to keep
 * downward planes readable, while retaining the reference model's cheek/torso facet range.
 * The baked `amount`/`faceAmount` is the FULL-DAYLIGHT floor; BODY_FILL scales it toward night. */
function addBodyLightingFloor(mat: THREE.Material, amount = 0.18, faceAmount = amount): void {
  const prev = mat.onBeforeCompile;
  mat.onBeforeCompile = (shader, renderer) => {
    if (typeof prev === "function") prev.call(mat, shader, renderer);
    shader.uniforms.uBodyFill = BODY_FILL;
    const spatialFaceFill = faceAmount !== amount;
    if (spatialFaceFill) {
      shader.vertexShader = `varying float vAvatarFaceFill;\n${shader.vertexShader}`.replace(
        "#include <project_vertex>",
        `#include <project_vertex>
         vAvatarFaceFill = smoothstep(1.43, 1.52, transformed.y) *
           (1.0 - smoothstep(0.085, 0.12, abs(transformed.x)));`,
      );
      shader.fragmentShader = `varying float vAvatarFaceFill;\n${shader.fragmentShader}`;
    }
    const fillExpr = spatialFaceFill
      ? `mix(${amount.toFixed(3)}, ${faceAmount.toFixed(3)}, vAvatarFaceFill)`
      : amount.toFixed(3);
    shader.fragmentShader = `uniform float uBodyFill;\n${shader.fragmentShader}`.replace(
      "#include <opaque_fragment>",
      `outgoingLight = mix(outgoingLight, diffuseColor.rgb, (${fillExpr}) * uBodyFill);\n#include <opaque_fragment>`,
    );
  };
  const prevKey = mat.customProgramCacheKey;
  mat.customProgramCacheKey = function () {
    return (typeof prevKey === "function" ? prevKey.call(this) : "") + `|body-fill:${amount}:${faceAmount}`;
  };
  mat.needsUpdate = true;
}

export class Avatar {
  readonly group = new THREE.Group();
  private crystalWings: CrystalWings | null = null;
  private readonly crystalTravelPose = new CrystalTravelPose();
  private readonly glideAttitude = new GlideAttitude();
  private readonly flightVerticalPose=new FlightVerticalPose();
  private flightHorizontalSpeed=0;
  private readonly wingPosition = new THREE.Vector3();
  private readonly wingRotation = new THREE.Quaternion();
  private readonly wingInverse = new THREE.Quaternion();
  private readonly wingState: CrystalWingState = { ...CRYSTAL_WING_REST };
  private readonly wingDock = new CrystalWingDockAnchor();
  private readonly wingLimbs = new CrystalLimbAnchors();
  private readonly wingBody = new CrystalWingBodyPose();
  private wingDockBody: Avatar['glbBody'] = null;
  private wingDockPieces = -1;

  private body: THREE.Group; // bob + lean
  private rigRoot: THREE.Group; // 180° yaw: Mixamo +Z forward -> our -Z forward
  private boneByName = new Map<string, THREE.Bone>();
  /** Inverse of each controlled bone's PARENT rest world rotation (Mixamo /
   *  natural space), for limb targeting. */
  private restParentInv = new Map<string, THREE.Quaternion>();
  /** Rest local quaternion of the chest (spine_05). The aim layer pitches the chest
   *  every frame; flight clips don't animate spine_05, so the mixer never resets it
   *  and the pitch would COMPOUND into a spin. We reset the chest to this base before
   *  each mixer.update so the aim solve always starts from a clean pose (idempotent).
   *  Grounded clips that DO animate spine_05 just overwrite this reset. */
  private chestRest = new THREE.Quaternion();
  private skeleton: THREE.Skeleton;
  private skinned: THREE.SkinnedMesh[] = [];
  private impostor!: THREE.Mesh;
  private farLOD = false;
  private materials: THREE.Material[] = [];
  /** True = this avatar renders with the module-shared ambient material set (citizens):
   *  never tint, never flash, never dispose those materials. */
  private readonly sharedMats: boolean = false;
  /** The "colored" material worn GARMENTS draw with (see the constructor). */
  private readonly garmentMat: THREE.MeshLambertMaterial;
  private readonly baseShirtMat = buildColoredMaterial();
  private readonly basePantsMat = buildColoredMaterial();
  private styledParts: THREE.Object3D[] = []; // cosmetic meshes + GLB groups (torn down on restyle)
  /** Async piece loads requested by the LATEST setStyle (body, hair, garments) —
   *  styleSettled() awaits them so boot can hold the reveal until the body is
   *  actually dressed instead of streaming armor in over a bare character. */
  private stylePromises: Promise<unknown>[] = [];
  // --- GLB body model (style.body, e.g. "male"): a skinned base body that replaces the
  // procedural block body once its template streams in (the block body doubles as the
  // loading placeholder). Clothing "covers" hide body regions via per-avatar index rebuilds.
  private glbBody: { meshes: THREE.SkinnedMesh[]; geos: THREE.BufferGeometry[]; prims: PiecePrim[] } | null = null;
  /** Runtime face driver (emotes / talk visemes / auto-blink / webcam capture). */
  readonly faceRig = new FaceRig();
  private glbHair: THREE.SkinnedMesh[] = [];
  private glbHairGeos: THREE.BufferGeometry[] = [];
  private hairAssetKey: string | null = null;
  private hairHeadFitKey = "";
  private hairRevision = 0;
  private pendingHair: Promise<unknown> | null = null;
  private hairMotion: HairMotion[] = [];
  hairPhysicsEnabled = true;
  private glbBodyActive = false;
  /** GOD-MODE SOLO PIECE: when set, every normal mesh is hidden and ONLY this one skinned
   *  piece renders (still bound to the live skeleton + cloth sim) — for inspecting a single
   *  armor/cloth piece off the body. Null = normal rendering. */
  private soloMeshes: THREE.SkinnedMesh[] = [];
  private soloActive = false;
  private soloUrl = "";
  private bodyModelId = "";
  private bodyAssetKey = "";
  private bodyRevision = 0;
  private pendingBody: Promise<unknown> | null = null;
  /** HOLOGRAPHIC body: same mesh as EXPLORER, drawn with the holo material (BODY_MODELS.holo). */
  private holoBody = false;
  private hiddenRegions = new Set<BodyRegion>();
  private garmentOcclusionMasks = new Set<string>();
  private styledSkinned: THREE.SkinnedMesh[] = []; // skinned GLB cosmetics currently worn
  /** PER-VERTEX cloth (clothPbd.ts), the opt-in alternative to the bone-chain cage. Both paths
   *  ship: this holds the split-out simulated meshes while it is the active one, and is empty
   *  otherwise. Switching re-applies the style, since the split happens at mount time. */
  private pbdCapes: PbdCape[] = [];
  private pbdCloth = false;
  /** BVH mesh colliders (clothBvh.ts) — the PBD path's collider source when pbdBvh is on;
   *  capsules remain for the limbs and as the fallback when meshes are unavailable. */
  private pbdBvh = true;
  private pbdMeshCols: SkinnedBvhCollider[] = [];
  /** Cheap change-detector: body prims / worn pieces / flag. Colliders rebuild when it moves,
   *  which also covers the async body + garment loads without hooking their promises. */
  private pbdColliderSig = "";
  /** Refit cadence for the BVH colliders (1 = every frame). Exposed for cost measurement. */
  pbdRefitEvery = 1;
  private pbdFrameNo = 0;
  /** Worn-gear collider gates, mirrored for the per-vertex path (which resolves its own set). */
  private shoulderGearWorn = false;
  /** Last applied style — the PBD toggle re-applies it, since the mesh split happens at mount. */
  private lastStyle: CharacterStyle | null = null;
  // Rest world matrices/quaternions (natural space) of the attachment bones.
  private attach = new Map<string, { invMat: THREE.Matrix4; invQuat: THREE.Quaternion; world: THREE.Vector3 }>();
  private proportions: AvatarProportions = { ...DEFAULT_AVATAR_PROPORTIONS };

  private phase = 0;
  // Boost-run leap: the body rises WITH THE FEET — peaks during flight (both feet off the ground),
  // dips at footplant — so it's locked to the gait. boostLiftLo/Hi auto-calibrate the stance↔flight
  // envelope to whatever clip/rig scale is playing.
  private boostLiftLo = 0.15;
  private boostLiftHi = 0.45;
  /** Ground sampler installed by main.ts: given a render-space position, fills outPoint + outNormal
   *  with the planet surface point/normal beneath it (render space) and returns true; false when
   *  there's no planet underfoot. Drives the per-foot ground IK (applyFootGrounding). */
  groundProbe: ((worldPos: THREE.Vector3, outPoint: THREE.Vector3, outNormal: THREE.Vector3) => boolean) | null = null;
  private groundIkK = 0; // eased master weight for foot IK (0 off, 1 full)
  /** DEV toggle (window.__hl.devFootIK): force the foot-grounding IK off so the raw clip pose can be
   *  A/B'd against the IK-solved one — e.g. to see whether idle bow-legs come from the IK or the clip. */
  footIkEnabled = true;
  private ikPelvis = 0; // eased pelvis drop (m) — lowers the hips so legs don't over-extend on slopes/steps
  private ikSlope: [number, number] = [0, 0]; // eased per-foot ground-height delta vs centre (m, along up)
  private ikFootW: [number, number] = [0, 0]; // eased per-foot planted weight (1 planted, 0 mid-swing)
  // Per-foot SMOOTHED knee-bend pole, in AVATAR-LOCAL space. The raw clip-pole goes degenerate near a
  // straight leg (its ⊥ component → 0) and on slopes (the goal tilts), which flicked the knee outward;
  // easing the pole through that (holding the last good direction) kills the jitter.
  // ⚠⚠ LOCAL, NOT WORLD (2026-08-10): held in world space this went stale the moment the BODY TURNED —
  // the knee kept pointing where the body used to face, and the ~18%/frame re-ease could not keep up
  // with a turn. Measured mid-turn (test/_kneeturn.mjs): the knee sat ~75° off its anatomical axis,
  // pinned there by the anti-hyperextension clamp — i.e. the clamp was the only thing keeping it from
  // going fully backward. Stored local, a turn CARRIES the pole and the smoothing only tracks real
  // pose changes. ikLeg still takes/returns world space; applyFootGrounding converts per frame.
  private ikPole: [THREE.Vector3, THREE.Vector3] = [new THREE.Vector3(), new THREE.Vector3()];
  private ikCenterNormal = new THREE.Vector3(0, 1, 0); // eased BROAD ground normal under the body centre — BOTH feet conform to this one shared normal, so they tilt symmetrically (no per-foot terrain-noise asymmetry)
  private ikSoleLocal: [THREE.Vector3, THREE.Vector3] | null = null; // per-foot "top of foot" axis in bone-local space (calibrated once from the bind pose) — lets us flatten the sole onto the ground regardless of the clip's foot pitch
  /** Per-THIGH anatomical anterior axis in that bone's own local space, calibrated from the bind pose
   *  (see legAnteriorFor). Keyed by bone so only the LEG chains get the knee guard — an elbow bends
   *  the other way. Rebuilt with the rig (a new body model is a new bind). */
  private legAnterior: Map<THREE.Bone, THREE.Vector3> | null = null;
  private idleT = 0;
  private airK = 0;
  private speedK = 0;
  private weapon: THREE.Group | null = null;
  private weaponId: string | null = null;
  private weaponTargetId: string | null = null; // desired held weapon (setWeapon)
  private readonly shotRecoil=new ShotRecoil();
  private crystalWeaponShape:CrystalWeaponShape|null=null;
  private readonly crystalWeaponFrame:CrystalWeaponFrame={active:false,aim:new THREE.Matrix4(),body:new THREE.Matrix4()};
  private readonly crystalWeaponPosition=new THREE.Vector3();
  private readonly crystalWeaponOffset=new THREE.Vector3();
  private readonly crystalWeaponRotation=new THREE.Quaternion();
  private readonly crystalWeaponPitch=new THREE.Quaternion();
  private readonly crystalWeaponScale=new THREE.Vector3();
  /** Mount rotation of the CURRENT weapon (per clip-set); reload recomposes from it. */
  private weaponMount = WEAPON_MOUNT_PISTOL.clone();
  /** Resolved presentation data for the current weapon. Kept separate from combat class. */
  private holdDef: WeaponHoldDef = weaponHold({ class: "sidearm" });
  private weaponStance: WeaponStance = "shoulder";
  /** Hand-bone-local position after aligning the primary grip socket. Seat corrections add to it. */
  private weaponBasePosition = new THREE.Vector3(0, 0.04, 0);
  /** Smoothed and currently-applied local-X length multipliers. The applied
   * value is removed before the mixer/IK stack is evaluated again each frame. */
  private underhandStretchL = 1;
  private underhandStretchR = 1;
  private underhandStretchAppliedL = 1;
  private underhandStretchAppliedR = 1;
  private muzzle: THREE.Object3D | null = null;
  /** Rear point on the barrel axis (rifle only) — muzzle − bore = the gun's TRUE mechanical
   *  axis, which the aim solves align to the reticle. Null falls back to the hand_r proxy. */
  private bore: THREE.Object3D | null = null;
  /** Weapon holster "pixelate" dissolve: 0 = solid, 1 = fully gone. Shared by the
   *  current weapon's cloned materials (only one weapon exists per avatar). */
  private weaponDis = { value: 0 };
  private dissolve = 1;
  private aimK = 0;
  private aimPitch = 0;
  private aiming = false;
  /** UNARMED DHTH combat (RMB, empty-handed): clip-driven fighting stance on the upper body. */
  private combatActive = false;
  private walkActive = false; // RMB-while-unarmed slow omnidirectional-walk stance (not combat)
  private combatK = 0; // eased stance weight — drives the orange arm glow
  private walkK = 0; // eased walk-mode weight — keeps the head looking at the reticle while RMB-walking
  private blocking = false;
  private blockK = 0; // eased block weight — drives the shield-fist glow
  /** SHIELD GUARD walk phase + the octant it stepped off into (see updateGuardWalk). */
  private guardPhase: GuardPhase = "idle";
  private guardDir: string = "f";
  private punchT = 0; // jab one-shot countdown (s)
  private blockHitT = 0; // block-hit reaction one-shot countdown (s)
  // KICKBOX strikes (full-body one-shots over the kickbox stance). The active strike clip plays at
  // full weight while strikeT counts down; the recovery/cancel state (activeMoveId/cancelT) is driven
  // by Avatar.move() (P1 per-move dispatch — see moves.ts).
  private strikeKey = ""; // currently-playing strike clip key ("" = none)
  /** Active strike's body layer: "upper" = masked punch overlay (legs keep moving, no root);
   *  "full" = full-body one-shot (kick / dodge-roll / block-flinch / parry — roots the base). */
  private strikeLayer: "upper" | "full" = "full";
  private strikeT = 0; // strike one-shot countdown (s) — RENDER only: how long the one-shot holds the body
  /** The COMBAT_ACTIONS id currently executing (null = none), and how far into it we are in ACTION
   *  MILLISECONDS. Action time — not clip time — is authoritative for gameplay; the clip merely
   *  visualizes it. This is the clock the damage windows, the combo cancel and the swept blade arc all
   *  read, and it is the clock the SERVER reads too (COMBAT_ACTIONS is shared), so prediction and
   *  authority cannot drift. It is REAL milliseconds — never scale it by a playback rate of any kind
   *  (see the note where the old swing-speed divisor was deleted, ~:365). */
  private actionId: CombatActionId | null = null;
  private actionElapsedMs = 0;
  /** Sword combo: index of the NEXT swing in SWORD_COMBO_CHAIN, and the countdown during which a press
   *  continues the chain (else it restarts at hit 1). */
  private comboStep = 0;
  private comboWindowT = 0;
  /** SUPER-JUMP charge — the SuperHero "Take off 8" clip (f_superjump): "charge" plays the windup then HOLDS
   *  (paused) at SUPERJUMP_HOLD_FRAC while Space is charged; "launch" resumes it (the jump bit) on release.
   *  Rendered via the superJumpKey override; driven by setSuperJumpCharge + updateSuperJump. */
  private superJump: "charge" | "launch" | "" = "";
  private superJumpKey = "";
  private activeMoveId: MoveId | null = null; // move currently in its recovery window ("" strikeKey ⇒ none)
  private moveHitConfirmed = false; // the active move has landed (set via onMoveHit) — arms the cancel
  private cancelT = 0; // remaining cancel window (s) after a confirmed light
  // Idle breakers: occasional full-body flourishes when standing guard (kc_break1/2).
  private idleBreakKey = ""; // active breaker clip key ("" = none)
  /** NPC activity emote (looping UAL clip key; "" = none) — see setEmote. */
  private emoteKey = "";
  private idleBreakT = 0; // breaker one-shot countdown (s)
  private idleStillT = 0; // time spent standing still in the stance (s) — gates the next breaker
  private idleBreakNextT = IDLE_BREAK_MIN; // stillness needed before the next breaker (randomized)
  // CONVERSATION (ambient NPCs): npc_talk idle + occasional npc_gesture one-shots.
  private talking = false;
  private conversationPerformance = false;
  private conversationIdle: "npc_talk" | "emo_foldarms" = "npc_talk";
  private ambient = false; // settlement persona: idle gestures always on
  private talkGestureKey = ""; // active gesture one-shot ("" = none)
  private talkGestureT = 0; // gesture countdown (s)
  private talkNextT = 2.5; // stillness until the next gesture (randomized)
  // SOCIAL HEAD LOOK: ease the head toward a world point (NPC glancing at
  // the player) without turning the body. Cleared each frame by default.
  private headLookTgt = new THREE.Vector3();
  private headLookHas = false;
  private headLookFromHead = false;
  private headLookK = 0;
  private headLookYaw = 0;
  private headLookPitch = 0;
  /** Eye-look state: face axes in head-bone-local (captured at bind), the head-look lead
   *  the eyes track ahead of the head, and the idle saccade offset/timer. */
  private eyeUpLocal = new THREE.Vector3(0, 1, 0);
  private eyeRightLocal = new THREE.Vector3(-1, 0, 0);
  private headEyeOffset = 0;
  private eyePivotOffset = new THREE.Vector3();
  private eyePivotRest = [new THREE.Vector3(), new THREE.Vector3()];
  private eyeLead = new THREE.Vector2();
  private eyeSac = new THREE.Vector2();
  private eyeSacT = 0;
  /** God-mode rig tuner: per-bone bind base (local pos + bind inverse + bind world). */
  private rigTuneBase: { pos: THREE.Vector3; inv: THREE.Matrix4; world: THREE.Matrix4 }[] = [];
  /** The active body rig table (see BODY_RIGS) + its arm-twist bind locals. The skeleton is
   *  BUILT from MALE_RIG and retuned in place by applyBodyRig when the body model changes. */
  private bodyRig: typeof MALE_RIG = MALE_RIG;
  private twistBind = twistBindFor(MALE_RIG);
  /** The fixed mount frame boneInverses were captured in at construction (rigRoot under the
   *  identity-placed group): world_i = mountFrame * naturalWorld_i. Lets applyBodyRig rebuild
   *  bind inverses later without baking the avatar's live world transform into them. */
  private bindMountFrame = new THREE.Matrix4();
  /** Simulated cloth chains (cloak etc — bones appended to the rig by build-male.mts). */
  private clothSim: ClothSim | null = null;
  /** Diagnostic/runtime budget gate. Ambient crowds can suspend cloth without
   *  disturbing the rest of the avatar state machine. */
  private clothSimulationEnabled = true;
  /** Tracks the current shadow override so asynchronously mounted GLB pieces
   *  inherit the same diagnostic setting as already-mounted meshes. */
  private shadowsEnabled = true;
  /** Eased 0..1 submersion factor fed to the cloth sim (buoyancy + drag while swimming). */
  private clothWaterK = 0;
  /** CO-ROTATING cloth reference (the foot planet's group). Planets spin + the floating
   *  origin rebases, so a world-space cloth sim reads the carry motion as phantom wind —
   *  main.ts sets this for the player; avatars parented under a planet group (NPCs)
   *  derive it automatically from group.parent. */
  clothFrame: THREE.Object3D | null = null;
  /** Ambient air velocity for the cloth (the outfitter's motion preview; weather later). */
  readonly clothWind = new THREE.Vector3();
  /** God-mode CALIBRATION POSE: when set, the skeleton is stomped to a static reference
   *  pose (bind / A / T / arms-up) at the very end of the pose pipeline — for inspecting
   *  skinning + dialing rig tunes against a known stance instead of a moving preview. */
  private debugPose: DebugPose | null = null;
  private debugPoseCache = new Map<DebugPose, Map<number, THREE.Quaternion>>();
  // Combat glow meshes (additive, parented to forearm/fist bones). Arms = orange "fighting"
  // aura (combatK); fists = orange→blue "shield" shell (blockK) + a jab flash (punchT).
  private glowArmMat?: THREE.SpriteMaterial;
  private glowFistMat?: THREE.SpriteMaterial;
  private glowArms: THREE.Sprite[] = [];
  private glowFists: THREE.Sprite[] = [];
  /** Head rest orientation in GROUP space (bind = facing forward); the combat head-aim
   *  re-points the head to the reticle from this base. */
  private headRestRelGroup = new THREE.Quaternion();
  /** Rest orientation of spine_01 in GROUP space (bind = upright, facing forward). The combat
   *  blade re-points the torso to this (yawed) when moving, so the walk's forward hunch + hip
   *  sway never leak into the fighting platform. */
  private spineRestRelGroup = new THREE.Quaternion();
  /** spine_01 rest LOCAL (bind) — lets the combat blade re-base the torso heading while keeping
   *  the clip's spine_01 sway (breathing/lean) instead of locking it rigid. */
  private spineRestLocal = new THREE.Quaternion();
  /** The punchT value at the START of the current jab — for the strike envelope. */
  private punchT0 = 0;
  private moveK = 0; // eased move ramp (0 idle, 1 walking) — drives the combat blade compensation
  /** Combat torso blade (rad): the ABSOLUTE yaw of the torso relative to the body heading in the
   *  combat stance (idle + moving). 0 = square to the reticle (arms dead-forward); more negative =
   *  more bladed. Kept small so the arms read forward instead of riding the ~55° hip blade. Tunable. */
  combatBladeYaw = -1;
  private hoverK = 0;
  private glideK = 0;
  private jet: "none" | "hover" | "glide" | "dive" = "none";
  /** Shoulder BOOSTER jets — DISABLED (commented out per design). Two triangular thrusters that used to
   *  MATERIALIZE above/behind the shoulders on a fuel-using boost and emit the boost stream from their
   *  nozzles. The pods are gone; the boost streams now come out of the hands and feet (boosterStreamSources).
   *  Restore this field alongside the build + placement blocks (constructor / update) to bring the pods back. */
  // private boosters: { grp: THREE.Group; nozzle: THREE.Object3D; bone: THREE.Bone; deployOff: THREE.Vector3; retractOff: THREE.Vector3; corrective: THREE.Quaternion }[] = [];
  private boosterDis = { value: 1 }; // 1 = fully gone (retracted), 0 = solid (deployed)
  private boostVisK = 0; // eased boost intensity 0..1 (drives the nozzle pulse + stream brightness)
  private styleKey = "";
  private mixer: THREE.AnimationMixer | null = null;
  /** Dev authoring mixer used by Animation Lab. It is intentionally separate
   * from locomotion so authored clips are sampled without gameplay layers. */
  private authoringMixer: THREE.AnimationMixer | null = null;
  private authoringAction: THREE.AnimationAction | null = null;
  private authoringDuration = 0;
  /** Gameplay interaction sample queued for the final animation stack. Unlike
   * the Animation Lab's immediate sampler, this is applied inside update()
   * before mounted FK/IK so the persistent rider stance composes on top. */
  private layeredAuthoringTime: number | null = null;
  private authoringSamplers: Array<{
    bone: THREE.Bone;
    property: "quaternion" | "position";
    interpolant: THREE.Interpolant;
  }> = [];
  private authoringRest = new Map<string, { position: THREE.Vector3; quaternion: THREE.Quaternion; scale: THREE.Vector3 }>();
  private acts: Partial<Record<ClipKey, THREE.AnimationAction>> = {};
  /** Ambient-crowd optimization: only actions participating in the current
   *  crossfade stay registered with the mixer. Player/remotes retain the dense
   *  legacy path unless their caller explicitly enables this. */
  private sparseMixerActions = false;
  private sparseMixerActionKeys = new Set<ClipKey>();
  /** Current eased blend weight per base action (locomotion + air). */
  private weights = new Map<ClipKey, number>();
  /** Authored ground speed (m/s) baked into each clip — for stride-syncing. */
  private authoredSpeed = new Map<ClipKey, number>();
  private prevGrounded = true;
  private prevJet: "none" | "hover" | "glide" | "dive" = "none";
  private flightHoverAge=1;
  private readonly flightAimTransition=new FlightAimTransition();
  /** Carries the displayed pose's momentum through snapped clip changes
   *  (strike starts and combo links) instead of cutting to the new frame. */
  private readonly poseInertia=new PoseInertializer();
  private flightAimEngaged=false;
  private flightAimPoseSwitch=false;
  private seenHoverAirborne = false; // hover already engaged this airborne stint (so the liftoff
  //   one-shot fires only on the FIRST engage — the launch — not a mid-fall re-engage)
  private jumpT = 0; // takeoff one-shot countdown
  private landT = 0; // touchdown land-clip countdown (every fall touchdown sets it)
  private diveLandActive=false;
  private diveLandGrounded=false;
  private landClip = ""; // the land clip selected at touchdown (only a real fall sets it)
  private bigLeapActive = false; // airborne in a boost big-leap (drives the sprint-start/loop/land anim)
  private bigLeapT = 0; // boost big-leap sprint-START (bl_start) one-shot countdown
  private jumpStartKey = ""; // a regular jump's phase START clip (js_<phase>), picked at the launch
  private jumpStartT = 0; // that start clip's countdown (then → bl_loop)
  private glideLaunchT = 0; // free flight-entry (f_launch) animation clock
  private readonly flightLaunchPoseClock=new FlightLaunchPoseClock();
  private readonly flightDiveStartClock=new FlightDiveStartClock();
  private readonly flightLaunchAlignment=new FlightLaunchAlignment();
  private diveStartPitch=0;
  private previousDivePreparation=0;
  private toIdleT = 0; // post-takeoff up-to-idle settle (f_to_idle = Up_to_idle_02) countdown
  private jetRecentT = 0; // time-since-jet; >0 => a touchdown should use the jetpack land
  private landIsJet = false; // the active landing is a jetpack land (f_land vs land)
  /** Weapon stance crossfade: 0 = unarmed set, 1 = pistol set (eased). */
  private stanceK = 0;
  /** Smoothed body-local move direction (forward = +1, strafe-right = +1) +
   *  vertical climb rate (m/s, + = rising) for up/down flight clips. */
  private moveFwd = 0;
  private moveRight = 0;
  private climbRate = 0;
  private wingLateralSpeed = 0;
  private wingForwardSpeed = 0;
  private straightUpTime = 0;
  private wallRunState=0;
  private ledgeClimbState=0;
  private ledgeClimbTime=0;
  private ledgeExitAge=1;
  private ledgeWeaponK=0;
  private readonly ledgeAnchor=new THREE.Vector3();
  private readonly ledgeContact=new LedgePose();
  private readonly danceContact=new DanceContact();
  setLedgeClimb(state:number,time=0,anchor?:Readonly<{x:number;y:number;z:number}>):void {
    if(state!==this.ledgeClimbState&&state){
      this.jumpStartT=this.landT=this.jumpT=0;this.diveLandActive=false;
      this.fallSpeed=0;this.jetRecentT=0;this.crystalTravelPose.reset();
      this.acts[state===1?'ledge_step':'ledge_climb']?.reset();
      this.ledgeContact.bind(this.skeleton,[...(this.glbBody?.meshes??[]),...this.styledSkinned]);
    }
    if(!state&&this.ledgeClimbState)this.ledgeExitAge=0;
    this.ledgeClimbState=state;this.ledgeClimbTime=time;
    if(anchor)this.ledgeAnchor.copy(anchor);
  }
  private readonly wallCatch=new WallRunTransition();
  /** Sole reach measured on the two retargeted wall-run rigs, plus 2 cm skin clearance. */
  get wallRunClearance():number{return (this.lastStyle?.body==='female'?.78:.88)*(1+(this.lastStyle?.bodyShape?.stature??0)*.10);}
  setWallRun(state:number,time=0):void {
    if(state===this.wallRunState)return;
    const previous=this.wallRunState;this.wallRunState=state;
    if(!state){this.wallCatch.cancel();return;}
    const jump=Math.abs(state)===3,key=`wall_${jump?'jump':'run'}_${state<0?'l':'r'}`;
    if(Math.abs(previous)===1&&Math.abs(state)===2&&Math.sign(previous)===Math.sign(state))return;
    if(jump)this.wallCatch.cancel();
    else this.wallCatch.begin(this.skeleton.bones);
    const action=this.acts[key];
    if(action){action.reset();action.time=Math.min(Math.max(0,time),action.getClip().duration-.01);}
    this.jumpStartT=0;this.landT=0;this.diveLandActive=false;
    if(jump)this.doubleJumpWingT=.32;
  }
  private wallOwnsPose():boolean {
    return !this.mounted&&!this.swimming&&(this.wallRunState!==0||this.ledgeClimbState!==0||['wall_run_l','wall_run_r','wall_jump_l','wall_jump_r','ledge_step','ledge_climb'].some(k=>(this.weights.get(k)??0)>.02));
  }
  /** Swimming in deep water — selects the whole-body swim clips (set in update()). */
  private swimming = false;
  private jumpChain = 0; // character jump-chain count (>=1 => airborne in an active flight chain)
  private prevJumpChain = 0; // last frame's jumpChain — detects the double-jump edge (1->2)
  /** Crouch (kneeling) stance — toggled from the sim, with an eased weight. */
  private crouching = false;
  private crouchK = 0;
  private mounted = false;
  private mountedBlend = 0;
  private mountedPose: VehicleRiderPoseDef | null = null;
  private vehicleMountSwing = 0;
  private vehicleMountSide = -1;
  private vehicleMountSwingReverse = false;
  /** Normalized ride speed (0..1) set by the driver each frame, and its eased
   * value — drives how far the rider hunches forward over the bars at speed. */
  private mountedSpeedK = 0;
  private mountedHunchK = 0;
  /** Signed lean, -1 (hard left) .. 1 (hard right), eased. */
  private mountedLeanK = 0;
  private mountedLeanTarget = 0;
  /** Boost tuck, 0..1, eased. */
  private mountedBoostK = 0;
  private mountedBoostTarget = 0;
  /** Per-avatar overrides of the ride-pose tuning constants, so the stance
   * editor can preview hunch depth / head lift live. Default to the constants. */
  private mountHunchMax = MOUNT_HUNCH_MAX;
  private mountHeadLift = MOUNT_HEAD_LIFT;
  /** DEBUG (harness only): force a single raw clip; null = normal pipeline. */
  debugClip: string | null = null;
  /** DEBUG (harness only): skip the aim layer to measure its effect on the legs. */
  debugNoAim = false;
  // --- Authored rifle AIM OFFSET (replaces the crude single-bone torso pitch) -----------------
  /** Samples the baked UE aim-offset grid (aimOffsetData.ts) into per-bone local deltas. */
  private aimSampler = new AimOffsetSampler();
  /** The torso bones the aim offset drives (spine_01..05, neck_01/02, head), in AIM_OFFSET_BONES order. */
  private aimBones: (THREE.Bone | undefined)[] = [];
  /** Each aim bone's BIND local (for the pre-mixer reset, so clips that don't animate the
   *  spine — e.g. flight — leave it upright for the offset to build on, like chestRest). */
  private aimRest: THREE.Quaternion[] = [];
  /** Per-frame sampled local delta per aim bone (preallocated). */
  private aimDelta: THREE.Quaternion[] = [];
  /** Per-bone application weight: a gentle ramp off the pelvis so the waist never seams,
   *  then full up the spine/neck/head (the authored poses already taper the lower spine). */
  private aimMask: number[] = [];
  /** Maps the camera aim pitch (rad) onto the grid pitch axis (deg). 1.0 = the grid is sampled AT
   *  the camera pitch — required now that the ARM chains ride the grid too (mesh-space additive),
   *  so the authored poses aim the held gun rather than decorate a separate solve. The old 1.7
   *  over-drive belonged to the era when the grid was torso-only flourish. (Harness-tunable.) */
  aimOffsetPitchScale = 1.0;
  /** Torso YAW lead into turns (deg per rad/s of body turn): the body already yaw-locks to the
   *  camera, so this just adds a subtle authored upper-body lead while turning. (Harness-tunable.) */
  aimOffsetYawLead = 7;
  /** spine_05 rest orientation relative to the GROUP (upright base for the gun-track pin). */
  private chestRestRelGroup = new THREE.Quaternion();
  /** Pelvis upright rest relative to the group (for the aim-time gait-lean damp). */
  private pelvisRestRelGroup = new THREE.Quaternion();
  /** Cached spine_05 bone (the chest the gun-arms hang off; pinned to track the reticle). */
  private chestBone?: THREE.Bone;
  /** Body heading (yaw) last frame + smoothed turn rate (rad/s, + = right). */
  private prevFwd = new THREE.Vector3(); // last frame's world forward — for the radial-up turn rate
  private hasPrevFwd = false;
  private turnRate = 0;
  /** A small intentional torso ROLL (rad) about body-forward while aiming — the natural shooter's
   *  blade/lean. Kept small so it reads as style, not the over-rotated bug. Eased by aimK. (Tunable.) */
  aimRoll = 0.09;
  /** How hard the gun-arms are damped toward their low-passed pose while aiming (0 = full gait swing,
   *  1 = fully steady). Raised from 0.6: the muzzle's lateral swing is the HAND swinging with the
   *  gait, and no amount of re-pointing the weapon fixes a moving grip — measured 70 cm of muzzle
   *  travel at a run. This is the lever that actually holds the sight picture. (Tunable.) */
  aimArmSteady = 0.97;
  /** ADS RAISE amount (rad) — how far the weapon comes UP to the sightline while aiming.
   *  Barrel-preserving, so it does not get cancelled by the gun-track solve (see applyAdsRaise).
   *  0 disables it. (Harness-tunable.) */
  adsRaise = 0.30;
  /** ADS STANCE (see applyAdsStance): torso blade yaw + forward hunch (rad) while aiming a gun.
   *  Barrel-preserving posture — tune entirely by eye (live: __lab.avatar.adsBlade/adsHunch).
   *  Defaults sit at the VISIBLE end: from the game's behind-the-shoulder camera only the shrug,
   *  cheek weld and shoulder-line rotation read at all, and they need this much to land. */
  adsBlade = 0.8;
  adsHunch = 0.3;
  /** EXTRA hunch (rad) folded in while CROUCHED and aiming a gun (user report 2026-08-03: "the
   *  head sticks mostly straight up when crouched"). A crouched shooter is COMPACT — the chest
   *  comes down behind the gun and the skull tucks over the stock — but the crouch clip only
   *  folds the LEGS, and the head gaze-pin then stands the skull back up vertical over an
   *  untouched torso. This is spine-only (no extra hip hinge: the crouch clip already owns the
   *  hip and adding more folded the character double), still arm-countered, so the barrel
   *  stands. Eased by crouchK · aimK. */
  adsHunchCrouch = 0.42;
  /** Cheek-weld share: how much of the live hunch the HEAD takes as a chin-down tuck. The crouch
   *  value is larger because the tucked-down / eyes-up skull is the whole read of a crouched
   *  shooter from behind. See applyHeadLook. */
  adsHeadTuck = 0.6;
  adsHeadTuckCrouch = 0.95;
  /** Support-hand rifle grip, AUTHORED in the weapon frame (grip origin, barrel −Z, top +Y) —
   *  live-tunable. The first cut inherited the CLIP's measured wrist, which FLOATS 12 cm above
   *  the barrel: the hand draped over the top of the gun, palm down, stretching the arm along
   *  the weapon (the "strained thin" profile). A support hand cups the handguard from BELOW. */
  gripLTarget = new THREE.Vector3().copy(_wGripRifle);
  gripLWristQ = new THREE.Quaternion().copy(RIFLE_WRIST_L);
  /** Last frame's support-elbow pole (world) — rate-limited to stop bob-driven plane flips. */
  private gripPolePrev: THREE.Vector3 | null = null;
  /** Smoothed slide-along-guard distance (m) — see applyWeaponGrip. */
  private gripSlideS = 0;
  /** Cached leg-track samplers per clip name — see legSamplersFor. */
  private jumpLegSamplers = new Map<string, { bone: THREE.Bone; interp: { evaluate(t: number): unknown }; duration: number }[]>();
  /** Seconds airborne on the current no-jet stint — the clock the sampled leg poses run on. */
  private airLegT = 0;
  /** ADS-move gun steady: low-passed firing-hand position, GROUP-LOCAL metres
   *  (null = disengaged; re-seeds from the live pose on engage). */
  private gunSteadyLocal: THREE.Vector3 | null = null;
  /** Smoothed elbow bend-plane pole for the gun-seat ikLeg (zero = unseeded);
   *  held through near-straight frames so the elbow plane can never sign-flip. */
  private gunSeatPole = new THREE.Vector3();
  /** Smoothed inboard gun-seat distance (m) — see seatWeaponInSupportReach. */
  private gunSeatS = 0;
  /** PROCEDURAL FLINCH state — see flinch() / applyFlinch(). */
  private flinchT = 0;
  private flinchAmp = 0;
  private flinchDirF = 0;
  private flinchDirR = 0;
  /** Low-passed local pose per gun-arm bone, for the aim arm-steadying (lazily seeded). */
  private armSmooth = new Map<string, THREE.Quaternion>();

  constructor(scene: THREE.Scene, opts?: { sharedMaterials?: boolean; crystalWings?: boolean }) {
    // SHARED CITIZEN MATERIALS (2026-08-02 perf deep fix): ambient settlement citizens all
    // draw from ONE module-wide material set — every per-avatar colour axis (skin/hair/eye)
    // is painted into their own COLOR_0 clones, so nothing avatar-specific lives in the
    // materials. Kills hundreds of per-avatar uniform uploads + state switches in towns.
    // The player, remote players, and combat avatars keep private sets: they tint suit/
    // accent/holo, hit-flash their emissive, and dissolve weapons.
    this.sharedMats = opts?.sharedMaterials === true;
    this.materials = this.sharedMats ? sharedAmbientMaterials() : buildAvatarMaterials();
    // WORN CLOTHING gets its own instance of the "colored" material so the suit
    // tint lands on garments ONLY. The body model puts its own non-skin prims
    // (irises among them) in that same group, and tinting the shared material
    // turned a rust suit into rust EYES. Shared-material citizens keep pointing
    // at the one instance — they are never tinted anyway.
    this.garmentMat = this.sharedMats
      ? (this.materials[5] as THREE.MeshLambertMaterial)
      : buildColoredMaterial();

    this.body = new THREE.Group();
    this.group.add(this.body);
    this.rigRoot = new THREE.Group();
    this.rigRoot.rotation.y = RIG_YAW; // single source: Mixamo +Z -> our -Z forward
    // GROUND CONTACT: clips pose the legs around Manny's slightly-bent rest stance while the
    // male bind stands straight-legged with soles at exactly 0 — so animated feet ride a hair
    // above the ground. Drop the whole rig by this constant (all bodies, all poses); the
    // foot-grounding IK then resolves contact instead of hovering. (Tunable.)
    this.rigRoot.position.y = -GROUND_DROP;
    this.body.add(this.rigRoot);

    // --- skeleton + rest data (computed in NATURAL space, before the flip) ---
    const bones = buildRigBones();
    for (const b of bones) this.boneByName.set(b.name, b);
    // Alias logical Mixamo-style names (Spine2, LeftArm, …) to the Manny bones, so
    // the avatar code below can keep referencing bones by logical name.
    for (const [logical, manny] of Object.entries(BONE_ALIAS)) {
      const bo = this.boneByName.get(manny);
      if (bo) this.boneByName.set(logical, bo);
    }
    // Snapshot the chest's rest local pose for the per-frame aim-solve reset (see chestRest).
    this.chestRest.copy(this.boneByName.get("Spine2")!.quaternion);
    // Resolve the aim-offset torso bones (+ their bind rest) and build the per-bone weight ramp.
    // Bones are at BIND here (clips load async), so .quaternion is the rest local.
    const AIM_RAMP: Record<string, number> = { spine_01: 0.8, spine_02: 0.9 }; // ease off the waist; default 1
    for (const name of AIM_OFFSET_BONES) {
      const bone = this.boneByName.get(name);
      this.aimBones.push(bone);
      this.aimRest.push(bone ? bone.quaternion.clone() : new THREE.Quaternion());
      this.aimDelta.push(new THREE.Quaternion());
      this.aimMask.push(AIM_RAMP[name] ?? 1);
    }
    const natural = new THREE.Object3D();
    natural.add(bones[0]);
    natural.updateMatrixWorld(true);
    const worldPos = new Map<string, THREE.Vector3>();
    const worldQuat = new Map<string, THREE.Quaternion>();
    for (const b of bones) {
      worldPos.set(b.name, b.getWorldPosition(new THREE.Vector3()));
      worldQuat.set(b.name, b.getWorldQuaternion(new THREE.Quaternion()));
    }
    for (const [logical, manny] of Object.entries(BONE_ALIAS)) {
      if (worldPos.has(manny)) worldPos.set(logical, worldPos.get(manny)!.clone());
      if (worldQuat.has(manny)) worldQuat.set(logical, worldQuat.get(manny)!.clone());
    }
    // Controlled-bone parent inverses for limb targeting (legs, upper arms, and
    // forearms — the melee guard/jab points the forearms too).
    for (const name of ["LeftUpLeg", "RightUpLeg", "LeftArm", "RightArm", "LeftForeArm", "RightForeArm"]) {
      const parent = MANNY_RIG[B[name]][1];
      const parentName = MANNY_RIG[parent][0];
      this.restParentInv.set(name, worldQuat.get(parentName)!.clone().invert());
    }
    // Attachment-bone rest matrices for styled parts + weapon. Spine1 (=spine_03, mid-torso) is the
    // BACK-PACK anchor: it rides the body's lean/gait but NOT the gun-track aim rotation (which is on
    // spine_05), so the jetpack stays strapped to the body instead of swinging with the aim.
    // Anchor bones for the cosmetic slots: Head (head), Spine2/spine_05 (chest-position ref), Spine1/
    // spine_03 (torso-armor + back anchor), RightHand (weapon), + the per-slot bones for shoulders,
    // hands, waist, pants, shoes (each cosmetic parents to the bone it rides).
    for (const name of [
      "Head", "Spine2", "Spine1", "RightHand",
      "clavicle_l", "clavicle_r", "upperarm_l", "upperarm_r", "hand_l", "hand_r", "pelvis", "thigh_l", "thigh_r", "foot_l", "foot_r",
    ]) {
      const bone = this.boneByName.get(name)!;
      const m = bone.matrixWorld.clone();
      this.attach.set(name, {
        invMat: m.clone().invert(),
        invQuat: worldQuat.get(name)!.clone().invert(),
        world: worldPos.get(name)!.clone(),
      });
    }

    // --- bind anchor ---------------------------------------------------------
    // The base body is the GLB model (setBodyModel below). This zero-geometry
    // SkinnedMesh exists ONLY to anchor the binding contract: it is skinned[0],
    // the AnimationMixer root, and the bindMatrix source every GLB piece binds
    // with (mountSkinnedPrim). It renders nothing and never becomes visible.
    const anchor = new THREE.SkinnedMesh(new THREE.BufferGeometry(), this.materials[0]);
    anchor.visible = false;
    this.skinned = [anchor];

    // Mount bones + meshes under the flipped rigRoot, then bind.
    this.rigRoot.add(bones[0]);
    this.rigRoot.add(anchor);
    this.group.updateMatrixWorld(true);
    this.skeleton = new THREE.Skeleton(bones);
    for (const bone of bones) {
      this.authoringRest.set(bone.name, {
        position: bone.position.clone(),
        quaternion: bone.quaternion.clone(),
        scale: bone.scale.clone(),
      });
    }
    // Bound at the same point the procedural suit mesh used to bind, so the
    // captured bindMatrix (= rigRoot's world matrix here) is bit-identical and
    // every GLB piece keeps binding in the same space.
    anchor.bind(this.skeleton);
    anchor.frustumCulled = false;

    // God-mode rig tuner: snapshot the bind base (locals + inverses) so live tweaks are
    // idempotent, and register for global tune broadcasts.
    this.rigTuneBase = this.skeleton.bones.map((b, i) => ({
      pos: b.position.clone(),
      inv: this.skeleton.boneInverses[i].clone(),
      world: this.skeleton.boneInverses[i].clone().invert(),
    }));
    // mountFrame = bindWorld(root) / naturalWorld(root): the rig-mount transform every bind
    // inverse was captured under (applyBodyRig rebuilds inverses through it).
    {
      const rootLocal = new THREE.Matrix4().compose(
        new THREE.Vector3(MALE_RIG[0][2], MALE_RIG[0][3], MALE_RIG[0][4]),
        new THREE.Quaternion(MALE_RIG[0][5], MALE_RIG[0][6], MALE_RIG[0][7], MALE_RIG[0][8]),
        new THREE.Vector3(1, 1, 1),
      );
      this.bindMountFrame.copy(this.rigTuneBase[0].world).multiply(rootLocal.invert());
    }
    LIVE_AVATARS.add(this);
    this.captureEyePlacement();
    if (RIG_TUNES.size) this.applyRigTunes();
    if (MALE_CLOTH.length) this.clothSim = new ClothSim(MALE_CLOTH, this.boneByName);
    // Eye-look face axes in head-local (natural space: face = +Z, character's right = -X).
    const headQn = worldQuat.get("head");
    if (headQn) {
      const inv = headQn.clone().invert();
      this.eyeUpLocal.set(0, 1, 0).applyQuaternion(inv);
      this.eyeRightLocal.set(-1, 0, 0).applyQuaternion(inv);
    }

    // Distant-remote impostor. Town citizens explicitly keep full visual detail
    // throughout their shorter view range and never enter this path.
    const hip = worldPos.get("Hips")!.y;
    const impGeo = new THREE.CapsuleGeometry(0.24, 1.25, 3, 6);
    impGeo.translate(0, hip - 0.12, 0);
    const wc = new Float32Array(impGeo.attributes.position.count * 3);
    wc.fill(1);
    impGeo.setAttribute("color", new THREE.Float32BufferAttribute(wc, 3));
    this.impostor = new THREE.Mesh(impGeo, this.materials[0]);
    this.impostor.visible = false;
    this.rigRoot.add(this.impostor);

    // Every avatar gets the GLB base body without waiting for setStyle — the
    // animation lab, stance editor, and harnesses construct bare Avatars.
    // (After the impostor exists: setBodyModel → refreshBodyVisibility reads it.)
    this.setBodyModel("male");
    this.setHairModel(hairAsset("male", DEFAULT_STYLE.hair));

    // SHOULDER BOOSTER JETS — DISABLED (commented out per design). These two turbine pods used to
    // MATERIALIZE above/behind the shoulders on a fuel-using boost and emit the boost stream from their
    // nozzles. The pods no longer render; the boost STREAMS now come out of the HANDS and FEET instead
    // (see boosterStreamSources + the per-frame block in update(), also commented). The build is kept
    // below for easy restore — un-comment this block AND the placement block in update() to bring it back.
    /*
    const accent = (this.materials[2] as THREE.MeshLambertMaterial).emissive;
    // ANCHOR each booster to its SHOULDER (clavicle) bone — the deployed/retracted positions are OFFSETS
    // from the live (animated) shoulder bone position each frame, so the boosters ride the shoulders' bob/
    // lean/twist instead of floating rigidly off the root. (See the per-frame placement in update().)
    for (const side of [-1, 1]) {
      const bone = this.boneByName.get(side < 0 ? "clavicle_l" : "clavicle_r");
      if (!bone) continue;
      const grp = new THREE.Group();
      const bodyMat = (this.materials[3] as THREE.MeshLambertMaterial).clone();
      bodyMat.color.multiplyScalar(0.8);
      this.applyDissolve(bodyMat, this.boosterDis);
      // TURBINE BARREL pod: a rounded jet-engine barrel whose LONG AXIS runs along local +Y (point-up, so it
      // runs PARALLEL to the body/spine — same alignment as the old guitar-pick). A LatheGeometry profile
      // (revolved around Y) bulges to a ROTUND CENTER and tapers to rounded caps at both ends → a stout
      // turbine barrel. The exhaust glow sits at the bottom cap.
      const barrelProfile = [
        new THREE.Vector2(0.000, -0.135), // bottom cap tip (on-axis → closed, rounded end)
        new THREE.Vector2(0.045, -0.115),
        new THREE.Vector2(0.072, -0.085),
        new THREE.Vector2(0.087, -0.040),
        new THREE.Vector2(0.094, 0.010), // ROTUND CENTER — max radius (the barrel's belly)
        new THREE.Vector2(0.087, 0.060),
        new THREE.Vector2(0.072, 0.105),
        new THREE.Vector2(0.045, 0.140),
        new THREE.Vector2(0.000, 0.160), // top cap tip (rounded nose)
      ];
      const barrelGeo = new THREE.LatheGeometry(barrelProfile, 16); // revolve around Y → a round turbine barrel
      const body = new THREE.Mesh(barrelGeo, bodyMat);
      grp.add(body);
      // Exhaust glow at the wide base (bottom), facing DOWN — a flat additive blob (no cone).
      const nozMat = new THREE.MeshBasicMaterial({ color: accent.clone().lerp(new THREE.Color(0xffffff), 0.4), transparent: true, opacity: 0.9, blending: THREE.AdditiveBlending, depthWrite: false, fog: false });
      this.applyDissolve(nozMat, this.boosterDis);
      const nozzle = new THREE.Mesh(new THREE.SphereGeometry(0.06, 10, 8), nozMat);
      nozzle.position.set(0, -0.12, 0); // at the base, below the pick — the down-blast glow
      nozzle.scale.set(1, 0.45, 0.7); // flattened, wide exhaust mouth
      grp.add(nozzle);
      // Offsets from the shoulder bone (rigRoot frame): DEPLOYED = up + behind + a touch out; RETRACTED =
      // tucked toward the spine + lower + in front, so it slides OUT of the back as it materializes.
      const deployOff = new THREE.Vector3(side * 0.22, 0.15, -0.18); // FURTHER apart (x), a little above, CLOSER to the body (z)
      const retractOff = new THREE.Vector3(side * -0.05, 0.0, 0.02);
      this.rigRoot.add(grp);
      this.boosters.push({ grp, nozzle, bone, deployOff, retractOff, corrective: new THREE.Quaternion() });
    }
    // CORRECTIVE (computed once from the BIND pose): maps each shoulder bone's world orientation to the BODY
    // frame, so at runtime `boneWorldQuat × corrective` is the body frame AS CARRIED BY THE BONE — i.e. it
    // includes the flight clip's pitch AND ROLL. This makes the pods a rigid extension of the skeleton.
    this.group.updateMatrixWorld(true);
    {
      const rrq = new THREE.Quaternion();
      this.rigRoot.getWorldQuaternion(rrq);
      const bq = new THREE.Quaternion();
      for (const b of this.boosters) {
        b.bone.getWorldQuaternion(bq);
        b.corrective.copy(bq).invert().multiply(rrq);
      }
    }
    */
    this.group.updateMatrixWorld(true); // (kept — buildCombatGlow below reads updated bone matrices)

    this.buildCombatGlow();
    this.setStyle(DEFAULT_STYLE);
    this.group.visible = false;
    scene.add(this.group);
    if (opts?.crystalWings) this.setCrystalWings(true);

    // Rest orientation of the head relative to the GROUP (bind pose = facing forward). The
    // combat head-aim re-points the head to the reticle from this, regardless of the clip's
    // turned-aside stance head. Captured now while the skeleton is still at rest (clips load async).
    this.group.updateMatrixWorld(true);
    const headBone = this.boneByName.get("Head");
    if (headBone) {
      this.headRestRelGroup
        .copy(this.group.getWorldQuaternion(_q).invert())
        .multiply(headBone.getWorldQuaternion(_aimWorld));
    }
    const spineBone = this.boneByName.get("Spine"); // spine_01 (upright, forward at bind)
    if (spineBone) {
      this.spineRestRelGroup
        .copy(this.group.getWorldQuaternion(_q).invert())
        .multiply(spineBone.getWorldQuaternion(_aimWorld));
      this.spineRestLocal.copy(spineBone.quaternion); // bind local (clips load async, so this is rest)
    }
    // Rest orientation of the CHEST (spine_05) relative to the GROUP — the upright base the
    // aim-offset gun-track pin pitches from so the held gun tracks the reticle regardless of the
    // authored lower-spine curl applied below it (see applyAimOffset).
    this.chestBone = this.boneByName.get("Spine2");
    if (this.chestBone) {
      this.chestRestRelGroup
        .copy(this.group.getWorldQuaternion(_q).invert())
        .multiply(this.chestBone.getWorldQuaternion(_aimWorld));
    }
    // Pelvis upright rest (same pattern) — the aim-time lean damp measures forward pitch from it.
    const pelvisBone = this.boneByName.get("pelvis");
    if (pelvisBone) {
      this.pelvisRestRelGroup
        .copy(this.group.getWorldQuaternion(_q).invert())
        .multiply(pelvisBone.getWorldQuaternion(_aimWorld));
    }

    void getClipsAndReports().then(({ clips, reports }) => this.attachClips(clips, reports));
  }

  private basePose: BasePose | null = null;
  private readonly footfallTracker = new FootfallTracker();
  private footfallMask = 0;
  private readonly footfallLeft = new THREE.Vector3();
  private readonly footfallRight = new THREE.Vector3();

  /** Consume this animation frame's contacts: bit 0 left, bit 1 right. */
  takeFootfalls(): number {
    const mask = this.footfallMask;
    this.footfallMask = 0;
    return mask;
  }

  private updateFootfalls(dt: number, speed: number, grounded: boolean): void {
    if (!Number.isFinite(dt) || dt < 0 || dt > .25 || speed <= .6 || !grounded || this.swimming
      || this.mounted || this.jet !== 'none' || this.strikeT > 0 || this.debugClip) {
      this.footfallTracker.reset();
      return;
    }
    // Hit-stop freezes the pose, including a swing already in progress.
    if (dt === 0) return;
    const left = this.boneByName.get('foot_l'), right = this.boneByName.get('foot_r');
    if (!left || !right) { this.footfallTracker.reset(); return; }
    left.updateWorldMatrix(true, false); right.updateWorldMatrix(true, false);
    this.group.worldToLocal(left.getWorldPosition(this.footfallLeft));
    this.group.worldToLocal(right.getWorldPosition(this.footfallRight));
    // Relaxed locomotion faces travel and uses forward clips, including while
    // the body's turn is still catching up with a new movement direction.
    const moving = this.resolvedPose.stance === 'readied' && Math.hypot(this.moveFwd, this.moveRight) > .1;
    this.footfallMask = this.footfallTracker.update(this.footfallLeft, this.footfallRight,
      moving ? this.moveFwd : 1, moving ? this.moveRight : 0);
  }
  private readonly composition = new AnimationComposition();
  private customComposition = false;
  private compositionGrounded = true;
  private resolvedPose = resolveCharacterPose({ itemId: null, speed: 0, crouching: false, grounded: true, aimHeld: false });
  poseDiagnostics() { return { ...this.resolvedPose }; }

  /** Install a validated composition without replacing the movement mixer. */
  setAnimationComposition(profile: AnimationCompositionV1 | null): void {
    this.composition.install(profile);
    this.customComposition = profile !== null;
  }

  playCompositionAction(id: string): boolean {
    const item = itemPoseType(this.weaponTargetId ? ITEMS[this.weaponTargetId]?.weapon : undefined);
    if (!this.customComposition && this.composition.diagnostics().movement !== item) this.composition.install(compositionForItem(item));
    return this.composition.play(id, this.compositionState());
  }

  cancelCompositionAction(): void { this.composition.cancel(); }

  /** Tool-only seek; game code should issue play requests on action edges. */
  seekCompositionAction(id: string, time: number): boolean {
    return this.composition.seek(id, time, this.compositionState());
  }

  compositionDiagnostics() { return this.composition.diagnostics(); }

  private compositionState(): CompositionState {
    return {
      aiming: this.aiming || this.swordFocus || this.walkActive || this.combatActive, reloading: this.reloadP !== null,
      airborne: !this.compositionGrounded || this.jet !== "none",
      equipment: this.weaponTargetId,
      compatible: this.weaponId === this.weaponTargetId && !!this.mixer
        && this.composition.diagnostics().movement === itemPoseType(this.weaponTargetId ? ITEMS[this.weaponTargetId]?.weapon : undefined),
      action: this.strikeT > 0 || this.stowGestureT > 0 || this.airCombatOwnsBody(),
      interaction: this.mounted || this.swimming || this.layeredAuthoringTime !== null,
    };
  }
  private readonly sourceClips: AvatarClips = {};

  private attachClips(clips: AvatarClips, reports: Map<string, ClipReport>): void {
    const keys = Object.keys(clips) as ClipKey[];
    if (keys.length === 0) return;
    this.resetAuthoringPose();
    this.applyRigTunes();
    this.mixer = new THREE.AnimationMixer(this.skinned[0]);
    this.footfallTracker.reset();
    this.basePose = new BasePose(this.skeleton.bones.filter(bone => MANNY_BONE_NAMES.includes(bone.name)));
    for (const key of keys) {
      this.sourceClips[key] = clips[key];
      const clip = clipForBody(clips[key]!, this.bodyRig);
      const action = this.mixer.clipAction(clip);
      action.enabled = true;
      if (isOnce(key)) {
        action.setLoop(THREE.LoopOnce, 1);
        action.clampWhenFinished = true;
      }
      const initialWeight = key === "idle" ? 1 : 0;
      action.setEffectiveWeight(initialWeight);
      this.weights.set(key, initialWeight);
      this.acts[key] = action;
      if (!this.sparseMixerActions || initialWeight > 0) {
        action.play();
        if (this.sparseMixerActions) this.sparseMixerActionKeys.add(key);
      }
      // Authored ground speed (m/s) = baked root XZ travel / duration — used to
      // STRIDE-SYNC playback to the sim's ground speed so feet don't slide.
      const r = reports.get(key);
      // FBX root travel is centimetres (Mixamo and UE/Manny both export cm); 0.01 → metres.
      const drift = r ? Math.hypot(r.rootDriftX, r.rootDriftZ) * 0.01 : 0;
      this.authoredSpeed.set(key, clip.duration > 1e-3 ? drift / clip.duration : 0);
    }
  }

  /** Body changes replace only rig-dependent clips; playback phase and layer
   * weights survive. Source clips are immutable and shared by every avatar. */
  private refreshBodyClips(): void {
    if (!this.mixer) return;
    rebindBodyActions(this.mixer, this.acts, this.sourceClips, this.bodyRig, () => {
      this.resetAuthoringPose();
      this.applyRigTunes();
    });
    this.jumpLegSamplers.clear();
    this.footfallTracker.reset();
    this.basePose = new BasePose(this.skeleton.bones.filter(bone => MANNY_BONE_NAMES.includes(bone.name)));
  }

  /** Animation Lab: canonical bones exposed through a narrow authoring API so
   * tooling does not depend on Avatar's private rig maps. */
  humanoidBoneNames(): string[] {
    return this.skeleton.bones.map((bone) => bone.name);
  }

  humanoidBone(name: string): THREE.Bone | undefined {
    return this.skeleton.bones.find((bone) => bone.name === name);
  }

  /** Install an authored interaction clip. Tools may sample it immediately;
   * gameplay queues it into Avatar.update's layer stack. */
  setAuthoringClip(clip: THREE.AnimationClip | null, loop = false): void {
    this.authoringMixer?.stopAllAction();
    this.authoringMixer = null;
    this.authoringAction = null;
    this.authoringDuration = clip?.duration ?? 0;
    this.layeredAuthoringTime = null;
    this.authoringSamplers = [];
    this.resetAuthoringPose();
    if (!clip) return;
    for (const track of clip.tracks) {
      const match = /^\.bones\[([^\]]+)\]\.(quaternion|position)$/.exec(track.name);
      if (!match) continue;
      const bone = this.humanoidBone(match[1]);
      if (!bone) continue;
      this.authoringSamplers.push({
        bone,
        property: match[2] as "quaternion" | "position",
        interpolant: track.createInterpolant(),
      });
    }
    this.authoringMixer = new THREE.AnimationMixer(this.skinned[0]);
    this.authoringAction = this.authoringMixer.clipAction(clip);
    this.authoringAction.setLoop(loop ? THREE.LoopRepeat : THREE.LoopOnce, loop ? Infinity : 1);
    this.authoringAction.clampWhenFinished = !loop;
    this.authoringAction.play();
    this.setAuthoringTime(0);
  }

  private resetAuthoringPose(): void {
    for (const bone of this.skeleton.bones) {
      const rest = this.authoringRest.get(bone.name);
      if (!rest) continue;
      bone.position.copy(rest.position);
      bone.quaternion.copy(rest.quaternion);
      bone.scale.copy(rest.scale);
    }
  }

  /** Immediately sample an authored clip. Animation Lab requests a clean base
   * because its IK pass is reapplied on every UI resample. Gameplay should use
   * setLayeredAuthoringTime so persistent context FK/IK can compose afterward. */
  setAuthoringTime(time: number, resetBasePose = false): void {
    if (!this.authoringMixer || !this.authoringAction) return;
    this.sampleAuthoringPose(time, resetBasePose);
  }

  /** Queue a sparse interaction clip for Avatar.update's production layer
   * stack. Pass null when the interaction is inactive. */
  setLayeredAuthoringTime(time: number | null): void {
    this.layeredAuthoringTime = time === null
      ? null
      : THREE.MathUtils.clamp(time, 0, this.authoringDuration);
  }

  private sampleAuthoringPose(time: number, resetBasePose: boolean): void {
    if (!this.authoringMixer || !this.authoringAction) return;
    if (resetBasePose) this.resetAuthoringPose();
    const sampleTime = THREE.MathUtils.clamp(time, 0, this.authoringDuration);
    this.authoringMixer.setTime(sampleTime);
    // A second AnimationMixer targeting Avatar's shared skeleton can retain its
    // binding state without writing the new value after the clip is rebuilt.
    // Apply the exact compiled interpolants explicitly so editor and sparse
    // gameplay sampling are deterministic.
    for (const sampler of this.authoringSamplers) {
      const value = sampler.interpolant.evaluate(sampleTime);
      if (sampler.property === "quaternion") {
        sampler.bone.quaternion.set(value[0], value[1], value[2], value[3]).normalize();
      } else {
        sampler.bone.position.set(value[0], value[1], value[2]);
      }
    }
    this.group.updateMatrixWorld(true);
  }

  /** Probe/debug: which locomotion clips drive this avatar ([] = procedural). */
  locomotionKeys(): string[] {
    return Object.keys(this.acts);
  }

  /** Snap a set of actions' weights to 0 — used at jet/land edges so a one-shot
   *  (takeoff/launch/land) doesn't crossfade out of a competing air pose. */
  private zeroActs(keys: string[]): void {
    for (const k of keys) {
      if (this.acts[k]) {
        this.weights.set(k, 0);
        this.acts[k]!.setEffectiveWeight(0);
      }
    }
  }

  /** GOD-MODE preview (dev outfitter): load + SOLO an arbitrary anim-library clip by
   *  URL on the mannequin (bypasses the state machine, like debugClip). null returns
   *  to the live preview. The loaded action is cached in `acts` so debugClip solos it. */
  async previewClip(url: string | null): Promise<void> {
    // Retargeted preview clips carry a `.bones[pelvis].position` track (keepHipsPos — the
    // authored crouch/lie/climb journey); most clips don't. When no active action animates
    // the property, three's PropertyMixer falls back to the ORIGINAL state it captured at
    // first binding — which is whatever pose happened to be live then. Reset the pelvis to
    // its bind on every preview switch so that fallback (and the next solo) is deterministic.
    const pelvis = this.boneByName.get("pelvis");
    const bind = this.bodyRig.find((b) => b[0] === "pelvis");
    if (pelvis && bind) pelvis.position.set(bind[2], bind[3], bind[4]);
    if (!url) {
      this.debugClip = null;
      return;
    }
    if (!this.mixer) return;
    if (!this.acts[url]) {
      const clip = await loadPreviewClip(url);
      if (!clip || !this.mixer) return;
      this.sourceClips[url] = clip;
      const act = this.mixer.clipAction(clipForBody(clip, this.bodyRig));
      act.play();
      act.setEffectiveWeight(0);
      this.acts[url] = act;
    }
    this.debugClip = url;
  }

  /** Apply a character style: tint the suit palette, then rebuild the cosmetic slot parts from the
   *  COSMETICS catalogue (cosmetics.ts). A slot value of "none" (or one with no catalogue entry) adds
   *  nothing → the base skinned body shows through. Every variant is a skinned GLB piece
   *  (2026-07-31 clothing purge: the procedural primitive builders are gone). */
  /** Resolves once every async piece the CURRENT style requested (body, hair,
   *  garments) has loaded and mounted — never rejects (a missing piece resolves
   *  null and is skipped). Boot awaits this so the reveal shows a DRESSED
   *  character instead of streaming armor in over a bare one. */
  styleSettled(): Promise<void> {
    return Promise.allSettled([...this.stylePromises, this.crystalWings?.ready]).then(() => undefined);
  }

  /** Standard player apparatus. Ambient NPCs and isolated asset labs opt out. */
  setCrystalWings(enabled: boolean): void {
    if (enabled === !!this.crystalWings) return;
    this.crystalWings?.dispose();
    this.crystalWings = enabled ? new CrystalWings(this.group) : null;
    this.wingDock.reset();this.wingLimbs.reset();this.wingBody.reset();this.wingDockPieces=-1;
    this.crystalWings?.setAuraColor(this.lastStyle?.auraColor ?? DEFAULT_STYLE.auraColor ?? 0xffffff);
    this.crystalWings?.ready.catch(error => console.warn('Crystal Wings could not load', error));
  }

  crystalWingsReady(): Promise<void> { return this.crystalWings?.ready ?? Promise.resolve(); }
  /** Local player only: the camera that owns the reticle. While aiming, the
   *  crystal wings keep out of a cone of `clear` × the half view height around
   *  it. Call after the camera moves; the pose is stored in this avatar's frame. */
  setSightCamera(camera: THREE.PerspectiveCamera | null, clear = .3): void {
    const sight = this.crystalWings?.sight;
    if (!sight) return;
    sight.active = !!camera;
    if (!camera) return;
    this.group.updateWorldMatrix(true, false);
    camera.updateMatrixWorld();
    _sightInverse.copy(this.group.matrixWorld).invert();
    sight.origin.setFromMatrixPosition(camera.matrixWorld).applyMatrix4(_sightInverse);
    camera.getWorldDirection(sight.dir).transformDirection(_sightInverse);
    sight.up.set(0, 1, 0).transformDirection(camera.matrixWorld).transformDirection(_sightInverse);
    sight.tan = Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2) / camera.zoom * clear;
  }

  /** Live comparison for the wing studio; springs/formation steering still run. */
  get crystalWingBodyFollow(): boolean { return this.wingBody.enabled; }
  set crystalWingBodyFollow(enabled: boolean) { this.wingBody.enabled=enabled; }

  private updateCrystalWings(dt: number, speed: number, grounded: boolean, strafe: number, sprinting: boolean): void {
    if (!this.crystalWings) return;
    this.flightAnchorInto(this.wingPosition, this.wingRotation);
    const scale = (this.lastStyle?.body === 'female' ? .93 : 1)
      * (1 + (this.lastStyle?.bodyShape?.stature ?? 0) * .10);
    if (!this.farLOD && (this.wingDockBody !== this.glbBody || this.wingDockPieces !== this.styledSkinned.length)) {
      this.wingDockBody=this.glbBody;this.wingDockPieces=this.styledSkinned.length;
      const meshes=[...(this.glbBody?.meshes ?? []),...this.styledSkinned];
      this.wingDock.bind(meshes,this.wingPosition,this.wingRotation,scale,this.boneByName,this.skeleton);
      this.wingLimbs.bind(this.boneByName,this.skeleton,meshes,scale);
      this.wingBody.bind(this.boneByName,this.skeleton,scale);
    }
    if(!this.farLOD){this.wingDock.update(this.wingRotation,scale);this.wingLimbs.update();this.wingBody.update(this.wingPosition,this.wingRotation,scale);}
    this.group.worldToLocal(this.wingPosition);
    this.group.getWorldQuaternion(this.wingInverse).invert();
    this.wingRotation.premultiply(this.wingInverse);
    const state = this.wingState;
    const rolling = this.isRolling() || (this.strikeT > 0 && this.strikeKey.startsWith('kc_roll_'));
    state.jet = this.jet; state.speed = speed; state.power=this.flightPower; state.launchRemaining=this.flightLaunchRemaining; state.aiming = this.aimK; state.pitch = this.aimPitch;
    state.divePreparation=this.divePreparation;
    state.verticalAscent=this.flightVerticalPose.amount;
    this.doubleJumpWingT = grounded || this.jet !== 'none' ? 0 : Math.max(0, this.doubleJumpWingT - dt);
    state.jumpThrust = THREE.MathUtils.smoothstep(this.doubleJumpWingT, 0, .18);
    // Draw/stow, flinches and dodges share the one-shot animation channel.
    // Only a real attack may deploy the attacking fan; holding an item cannot.
    const action=this.actionId?COMBAT_ACTIONS[this.actionId]:null;
    state.attacking = !rolling && (this.punchT > 0 || (this.strikeT > 0
      && (this.activeMoveId !== null || (action !== null && action.clip === this.strikeKey && action.damage > 0))));
    state.guarding = this.blocking && (this.aiming || this.swordFocus || this.combatActive || this.walkActive);
    state.compact = this.mounted || this.swimming || rolling || this.ledgeClimbState!==0;
    state.wallSide = !state.compact&&Math.abs(this.wallRunState)<=2?Math.sign(this.wallRunState):0;
    state.grounded = grounded; state.sprinting = sprinting; state.approach = this.jet === 'glide' ? this.flightApproach : 0; state.strafe = strafe; state.visible = !this.farLOD;
    state.turnRate = this.turnRate; state.climb = this.climbRate; state.forward = this.moveFwd;
    state.lateralSpeed = this.wingLateralSpeed; state.forwardSpeed = this.wingForwardSpeed;
    state.landingTime=this.diveLandActive&&this.landT>0?DIVE_LANDING.lock-this.landT:-1;
    state.backOffset = this.backGearWorn ? .31 : .21;
    const parent = this.group.parent;
    const frame = this.clothFrame ?? (parent && !(parent as THREE.Scene).isScene ? parent : null);
    if(this.crystalWeaponEnabled){
      const hand=this.boneByName.get('RightHand');
      this.group.updateWorldMatrix(true,false);
      this.crystalWeaponFrame.active=this.aiming&&!!hand&&this.weaponId===this.weaponTargetId&&!state.compact;
      const timing=weaponStats(ITEMS[this.weaponTargetId??'']?.weapon??{});
      this.crystalWeaponFrame.trigger=this.weaponTriggerHeld;this.crystalWeaponFrame.charge=this.weaponCharge;
      this.crystalWeaponFrame.deployTime=timing.deployTime;this.crystalWeaponFrame.stowTime=timing.stowTime;
      this.crystalWeaponFrame.body.copy(this.group.matrixWorld);
      this.group.getWorldQuaternion(this.crystalWeaponRotation);
      this.crystalWeaponPitch.setFromAxisAngle(_underhandLocalRight,-this.aimPitch);
      this.crystalWeaponRotation.multiply(this.crystalWeaponPitch);
      if(hand)hand.getWorldPosition(this.crystalWeaponPosition);
      else this.crystalWeaponPosition.set(0,1.2,0).applyMatrix4(this.group.matrixWorld);
      this.crystalWeaponOffset.set(0,.085,-.075).multiplyScalar(scale).applyQuaternion(this.crystalWeaponRotation);
      this.crystalWeaponPosition.add(this.crystalWeaponOffset);
      this.crystalWeaponFrame.aim.compose(this.crystalWeaponPosition,this.crystalWeaponRotation,this.crystalWeaponScale.setScalar(scale));
      if((this.crystalWeaponShape==='minigun'||this.crystalWeaponShape==='launcher')&&this.weapon){
        this.weapon.updateWorldMatrix(true,false);
        this.crystalWeaponFrame.aim.copy(this.weapon.matrixWorld);
      }
      this.crystalWings.weaponMorph.setShape(this.crystalWeaponShape!,timing.mobility);
      this.crystalWings.weaponFrame=this.crystalWeaponFrame;
    }else if(this.crystalBladeEnabled){
      // The blade forms on the light-blade construct's own frame, so its hit
      // segment, trail and reach are exactly the light blade's.
      const drawn=this.swordFocus&&!!this.weapon&&this.weaponId===this.weaponTargetId&&this.dissolve<.5&&!state.compact;
      this.group.updateWorldMatrix(true,false);
      this.crystalWeaponFrame.active=drawn;
      this.crystalWeaponFrame.body.copy(this.group.matrixWorld);
      if(this.weapon){this.weapon.updateWorldMatrix(true,false);this.crystalWeaponFrame.aim.copy(this.weapon.matrixWorld);}
      const timing=weaponStats(ITEMS[this.weaponTargetId??'']?.weapon??{});
      this.crystalWeaponFrame.trigger=false;this.crystalWeaponFrame.charge=0;
      this.crystalWeaponFrame.deployTime=timing.deployTime;this.crystalWeaponFrame.stowTime=timing.stowTime;
      this.crystalWings.weaponMorph.setShape('blade',timing.mobility);
      this.crystalWings.weaponFrame=this.crystalWeaponFrame;
    }else{
      this.crystalWings.weaponFrame=null;this.crystalWings.weaponMorph.reset();
    }
    this.crystalWings.update(dt, state, this.wingPosition, this.wingRotation, scale,frame,this.wingDock,this.wingLimbs,this.wingBody,this.crystalTravelPose.wingFrame);
  }

  setStyle(style: CharacterStyle): void {
    // Aura edits update uniforms immediately, even when the garment cache key
    // is unchanged. Keep the current trait for late loading and reattachment.
    const aura=style.auraColor ?? DEFAULT_STYLE.auraColor ?? 0xffffff;
    this.crystalWings?.setAuraColor(aura);if(this.lastStyle)this.lastStyle.auraColor=aura;
    const key = `${validateFaceId(style.face, style.body)}:${validateBodyBuild(style.bodyBuild)}:${bodyShapeKey(style.bodyShape)}:${style.body ?? "male"}:${style.head}:${style.torso}:${style.arms}:${style.legs}:${style.feet}:${style.suit}:${style.accent}:${style.skin ?? 0xffffff}:${style.hair ?? "spiky"}:${style.hairColor ?? -1}:${style.eyeColor ?? -1}:${style.shirtColor ?? DEFAULT_STYLE.shirtColor}:${style.pantsColor ?? DEFAULT_STYLE.pantsColor}`;
    if (key === this.styleKey) return;
    this.styleKey = key;
    this.wingDock.reset();this.wingLimbs.reset();this.wingBody.reset();this.wingDockPieces=-1;
    // Fresh settle ledger: styleSettled() answers for THIS style's pieces only.
    this.stylePromises.length = 0;
    const shapeChanged = bodyShapeKey(this.lastStyle?.bodyShape) !== bodyShapeKey(style.bodyShape);
    this.lastStyle = { ...style, bodyBuild: validateBodyBuild(style.bodyBuild), bodyShape: validateBodyShape(style.bodyShape) };
    this.applyBodyShape(shapeChanged);
    this.hairColor = style.hairColor ?? DEFAULT_STYLE.hairColor ?? 0x2a2320;
    this.eyeColor = style.eyeColor ?? DEFAULT_STYLE.eyeColor ?? 0x3e6b50;
    // Skin tone is a VERTEX PAINT (2026-08-02): painted into the avatar's COLOR_0 clones
    // (exactly the multiply materials[4]/[7].color used to do) so the materials stay
    // avatar-agnostic and ambient citizens can share one set.
    this.skinTint = style.skin ?? 0xffffff;
    // Worn-gear cloth colliders (2026-08-10): a BACK piece (the pack) stands
    // ~6 cm proud of the torso capsule, and without its own collider the cape
    // hangs straight through it. Style-keyed, so it swaps with the outfit.
    // Pauldrons get one too (2026-08-11): a shoulder plate stands ~1.5 cm proud of the deltoid
    // capsule, so without it the cape settles INSIDE the armour and then saws out of it as the
    // shoulder turns. Same reason as the pack — the sim can only avoid what it is told about.
    this.backGearWorn = style.body !== "female" && style.torso === "drifter";
    const shoulderWorn = style.body !== "female" && style.arms === "drifter";
    this.shoulderGearWorn = shoulderWorn;
    this.clothSim?.setGearCapsules(
      [
        ...(this.backGearWorn ? [["spine_01", "spine_03", 0.215] as [string, string, number]] : []),
        ...(shoulderWorn ? [
          ["clavicle_l", "upperarm_l", 0.105] as [string, string, number],
          ["clavicle_r", "upperarm_r", 0.105] as [string, string, number],
        ] : []),
      ],
      this.boneByName,
    );
    // A stowed gun sits against whatever the back now wears — re-seat it.
    this.stowedGroup?.position.copy(this.rifleBackPos());
    this.paintAppearance();
    this.baseShirtMat.color.setHex(style.shirtColor ?? DEFAULT_STYLE.shirtColor!);
    this.basePantsMat.color.setHex(style.pantsColor ?? DEFAULT_STYLE.pantsColor!);
    if (!this.sharedMats) {
      (this.materials[0] as THREE.MeshLambertMaterial).color.setHex(style.suit);
      (this.materials[2] as THREE.MeshLambertMaterial).emissive.setHex(style.accent);
      // The hologram projects in the accent: lit colour for the form, emissive for the glow floor.
      (this.materials[6] as THREE.MeshLambertMaterial).color.setHex(style.accent);
      (this.materials[6] as THREE.MeshLambertMaterial).emissive.setHex(style.accent);
      applySuitTint(this.garmentMat, style.suit);
    }

    // Base body model (async: the body is absent until the template lands — prefetched at boot).
    this.setBodyModel(style.body ?? "male", validateFaceId(style.face, style.body));
    this.setHairModel(hairAsset(style.body ?? "male", style.hair), style.body === "female" ? "female" : "male", validateFaceId(style.face, style.body));

    // Replaceable cosmetic layers (for example hair) use coarse regions. Clothing uses precise,
    // reversible face masks generated from garment coverage — each body's build ships its own
    // masks, and ids absent from the mounted body template simply no-op in applyCovers.
    this.hiddenRegions.clear();
    this.garmentOcclusionMasks.clear();
    if (style.hair === "none") this.hiddenRegions.add("hair");
    // Apply coverage only for pieces that can actually mount on this rig.
    // Female Rookie pieces are fitted; legacy Drifter armor is male-only.
    for (const part of STYLE_PARTS) {
      const def = COSMETICS[part]?.[style[part]];
      if (style.body === "female" && !def?.femaleSkin) continue;
      const covers = COSMETIC_COVERS[part]?.[style[part]];
      if (covers) for (const r of covers) this.hiddenRegions.add(r);
      const masks = COSMETIC_OCCLUSION_MASKS[part]?.[style[part]];
      if (masks) for (const id of masks) this.garmentOcclusionMasks.add(id);
    }
    this.refreshCovers();

    // Tear down old parts (meshes AND GLB groups). Dispose GEOMETRY only — the materials are the
    // shared palette (suit/accent/dark), reused across parts, so they must outlive a restyle —
    // and skinned cosmetics share their template geometry across all avatars (never disposed).
    for (const part of this.styledParts) {
      part.removeFromParent();
      part.traverse((o) => { if (o instanceof THREE.Mesh) disposeAvatarGeometry(o.geometry); });
    }
    this.styledParts.length = 0;
    this.styledSkinned.length = 0;
    this.refreshCovers();
    for (const c of this.pbdCapes) c.dispose();
    this.pbdCapes.length = 0;

    // Dispatch each worn slot variant (all skinned GLB pieces; load async).
    for (const part of STYLE_PARTS) {
      const def = COSMETICS[part]?.[style[part]];
      if (!def) continue; // "none" or unknown → the base body shows
      this.loadCosmeticSkin(def, part);
      if (style.body !== "female") for (const skin of def.attachments ?? []) this.loadCosmeticSkin({ skin }, part);
    }
    this.refreshBodyVisibility();
  }

  // --- GLB body model -------------------------------------------------------

  /** RETUNE the skeleton's rest to another body rig (same Manny hierarchy, different joint
   *  anatomy — e.g. male ↔ female). Bone rest locals, bind inverses, and every cached
   *  rest-state derived from them are rebuilt in place, so all mounted GLB pieces (which
   *  bind by joint index against this skeleton) deform correctly for the new body. The
   *  trailing male cloth bones (absent from other rigs) keep their male locals and idle. */
  private applyBodyRig(def: BodyRigDef): void {
    if (def.rig === this.bodyRig) return;
    this.bodyRig = def.rig;
    resetBodyDeformers(this.skeleton);
    this.twistBind = twistBindFor(def.rig);
    const bones = this.skeleton.bones;
    const rows = def.rig;
    // 1. rest locals: shared-prefix rows map 1:1 onto the skeleton bones (guard by name);
    //    bones beyond the rig (male cloth chains) reset to their authored male locals.
    for (let i = 0; i < bones.length; i++) {
      if (i < rows.length && bones[i].name === rows[i][0]) {
        const [, , px, py, pz, qx, qy, qz, qw] = rows[i];
        bones[i].position.set(px, py, pz);
        bones[i].quaternion.set(qx, qy, qz, qw);
      } else {
        const rest = this.authoringRest.get(bones[i].name);
        if (rest) {
          bones[i].position.copy(rest.position);
          bones[i].quaternion.copy(rest.quaternion);
        }
      }
      bones[i].scale.set(1, 1, 1);
      const ar = this.authoringRest.get(bones[i].name);
      if (ar) {
        ar.position.copy(bones[i].position);
        ar.quaternion.copy(bones[i].quaternion);
        ar.scale.set(1, 1, 1);
      }
    }
    // 2. natural-space world transforms (feet at origin, +Z forward — the frame the GLB
    //    pieces are authored in), walked parent-first in table order.
    const naturalWorld: THREE.Matrix4[] = new Array(bones.length);
    const local = new THREE.Matrix4();
    for (let i = 0; i < bones.length; i++) {
      local.compose(bones[i].position, bones[i].quaternion, _rtV.set(1, 1, 1));
      const parent = MALE_RIG[i][1];
      naturalWorld[i] = parent >= 0
        ? new THREE.Matrix4().multiplyMatrices(naturalWorld[parent], local)
        : local.clone();
    }
    const naturalPos = (i: number) => new THREE.Vector3().setFromMatrixPosition(naturalWorld[i]);
    const naturalQuat = (i: number) => new THREE.Quaternion().setFromRotationMatrix(naturalWorld[i]);
    // 3. bind inverses + rig-tune base, through the constructor's captured mount frame.
    for (let i = 0; i < bones.length; i++) {
      const world = new THREE.Matrix4().multiplyMatrices(this.bindMountFrame, naturalWorld[i]);
      this.skeleton.boneInverses[i].copy(world).invert();
      this.rigTuneBase[i] = {
        pos: bones[i].position.clone(),
        inv: this.skeleton.boneInverses[i].clone(),
        world,
      };
    }
    // 4. rest-state caches derived from the bind.
    this.chestRest.copy(this.boneByName.get("Spine2")!.quaternion);
    AIM_OFFSET_BONES.forEach((_, i) => {
      const bone = this.aimBones[i];
      if (bone) this.aimRest[i].copy(bone.quaternion);
    });
    for (const name of ["LeftUpLeg", "RightUpLeg", "LeftArm", "RightArm", "LeftForeArm", "RightForeArm"]) {
      const parent = MANNY_RIG[B[name]][1];
      this.restParentInv.set(name, naturalQuat(parent).invert());
    }
    for (const [name, a] of this.attach) {
      const i = B[name];
      if (i === undefined) continue;
      a.invMat.copy(naturalWorld[i]).invert();
      a.invQuat.copy(naturalQuat(i)).invert();
      a.world.copy(naturalPos(i));
    }
    const headQn = naturalQuat(B.head);
    {
      const inv = headQn.clone().invert();
      this.eyeUpLocal.set(0, 1, 0).applyQuaternion(inv);
      this.eyeRightLocal.set(-1, 0, 0).applyQuaternion(inv);
    }
    // Head/spine rest-relative caches: the constructor captured the MALE rest
    // — a body retune (female) must recapture, or the head-look slerps toward
    // the OLD rest and fights the new one every frame (the wayfarer
    // head-glitch report, 2026-07-31). Bones are at the new rest here.
    this.group.updateMatrixWorld(true);
    const hb = this.boneByName.get("Head");
    if (hb) {
      this.headRestRelGroup
        .copy(this.group.getWorldQuaternion(_q).invert())
        .multiply(hb.getWorldQuaternion(_aimWorld));
    }
    const sb = this.boneByName.get("Spine");
    if (sb) {
      this.spineRestRelGroup
        .copy(this.group.getWorldQuaternion(_q).invert())
        .multiply(sb.getWorldQuaternion(_aimWorld));
    }
    // 5. cloth: only rigs that ship cloth chains simulate them.
    this.clothSim = def.cloth.length ? new ClothSim(def.cloth, this.boneByName) : null;
    // 6. re-apply global tunes/proportions on the new base; stale debug poses invalidate.
    this.debugPoseCache.clear();
    this.applyRigTunes();
    this.refreshBodyClips();
  }

  /** Swap the base body model (GLB; unknown ids fall back to "male"). Async:
   *  the avatar body is simply absent until the template lands; templates are
   *  module-cached so only the first-ever load waits. Race-guarded by body and head. */
  private setBodyModel(id: string, face: FaceId = "base"): void {
    const assetKey = `${id}:${face}`;
    if (assetKey === this.bodyAssetKey) {
      if (this.pendingBody) this.stylePromises.push(this.pendingBody);
      return;
    }
    this.bodyAssetKey = assetKey;
    const revision = ++this.bodyRevision;
    this.bodyModelId = id;
    this.headEyeOffset = 0;
    this.teardownGlbBody();
    // The foot IK's "top of foot" axis is calibrated from the BIND pose and cached. A new body is a
    // new bind, so a stale axis would be rotated to the sky instead of the sole — recalibrate.
    // Same for the knee guard's anatomical axis (male ↔ female are different rest skeletons).
    this.ikSoleLocal = null;
    this.legAnterior = null;
    // A different body model may be a different REST SKELETON (male ↔ female) — retune
    // before any of the new body's pieces mount.
    this.applyBodyRig(BODY_RIGS[id] ?? BODY_RIGS.male);
    // Unknown ids fall back to the male body — there is no procedural body.
    const def = BODY_MODELS[id] ?? BODY_MODELS.male;
    this.holoBody = def.holo === true;
    const head = id === "female" || id === "male" ? `/models/${id}/head_${face}.glb` : def.head;
    const bodyP = head
      ? Promise.all([loadPiece(def.body), loadPiece(head)])
        .then(([body, head]) => {
          if (!body || !head) return null;
          const fit = head.prims.find(p => p.geometry.userData.referenceFit)?.geometry.userData.referenceFit as HeadFit | undefined;
          return { prims: [...fitBodyNeck(body.prims, fit, id === "female" ? "female" : "male"), ...head.prims], fit };
        })
      : loadPiece(def.body);
    const mounts: Promise<unknown>[] = [];
    mounts.push(bodyP.then((tpl) => {
      if (this.bodyRevision !== revision || !tpl) return;
      this.headEyeOffset = ("fit" in tpl ? (tpl.fit as HeadFit | undefined)?.eyeOffset : 0) ?? 0;
      this.buildGlbBody(tpl);
    }));
    this.pendingBody = Promise.all(mounts);
    this.stylePromises.push(this.pendingBody);
  }

  /** Hair swaps independently of the body and head. Discard stale asynchronous loads. */
  private setHairModel(url: string | null, body: "male" | "female" = "male", face: FaceId = "base"): void {
    const fitKey = `${body}:${face}`;
    if (url === this.hairAssetKey && fitKey === this.hairHeadFitKey) {
      if (this.pendingHair) this.stylePromises.push(this.pendingHair);
      return;
    }
    this.teardownHair();
    this.hairAssetKey = url;
    this.hairHeadFitKey = fitKey;
    if (!url) return;
    const revision = this.hairRevision;
    this.pendingHair = loadPiece(url).then(tpl => {
      if (revision !== this.hairRevision || !tpl) return;
      this.hairScalpTint = tpl.prims.find(prim => prim.geometry.userData.scalpTint)?.geometry.userData.scalpTint as HairScalpTint | undefined;
      for (const prim of fitHairToHead(tpl.prims, body, face)) {
        const av = appearanceVerts(prim);
        const geo = instanceBodyGeometry(prim, true);
        geo.userData.hairHeadFit = prim.geometry.userData.hairHeadFit ?? fitKey;
        this.glbHairGeos.push(geo);
        this.registerPaintTarget(geo, av, prim.geometry, false);
        const mesh = this.mountSkinnedPrim(geo, prim.group, this.holoBody);
        this.glbHair.push(mesh);
        const hairRig = prim.geometry.userData.hairRig as HairRigDef | undefined;
        if (hairRig) this.hairMotion.push(new HairMotion(mesh, prim.geometry, hairRig));
      }
      this.refreshBodyVisibility();
      this.paintAppearance();
    });
    this.stylePromises.push(this.pendingHair);
  }

  private teardownHair(): void {
    this.hairRevision++;
    for (const motion of this.hairMotion) motion.dispose();
    this.hairMotion.length = 0;
    const attrs = new Set(this.glbHairGeos.map(g => g.getAttribute("color")));
    this.paintTargets = this.paintTargets.filter(t => !attrs.has(t.attr));
    for (const mesh of this.glbHair) mesh.removeFromParent();
    for (const geo of this.glbHairGeos) disposeAvatarGeometry(geo);
    this.glbHair.length = this.glbHairGeos.length = 0;
    this.hairAssetKey = null;
    this.hairHeadFitKey = "";
    this.pendingHair = null;
    this.hairScalpTint = undefined;
    this.paintAppearance();
  }

  /** Instantiate the loaded body template: per-avatar geometries (shared attributes, own index
   *  for the covers system, own COLOR_0 where hair/iris paint applies) bound to the live
   *  skeleton; hide the procedural placeholder. */
  private buildGlbBody(tpl: { prims: PiecePrim[] }): void {
    const meshes: THREE.SkinnedMesh[] = [];
    const geos: THREE.BufferGeometry[] = [];
    for (const prim of tpl.prims) {
      const av = appearanceVerts(prim);
      // Skin-group prims are ALWAYS paintable: the skin-tone axis is baked into their
      // COLOR_0 clone (see paintAppearance), not a material tint.
      const paintable = av.hair.length > 0 || av.eye.length > 0 || prim.group === "skin";
      const coverable = !!prim.regions || !!prim.occlusionMasks;
      const geo = coverable || paintable ? instanceBodyGeometry(prim, paintable) : prim.geometry;
      if (coverable || paintable) geos.push(geo);
      if (paintable) this.registerPaintTarget(geo, av, prim.geometry, prim.group === "skin");
      meshes.push(this.mountSkinnedPrim(geo, prim.group, this.holoBody));
    }
    this.glbBody = { meshes, geos, prims: tpl.prims };
    this.glbBodyActive = true;
    this.refreshCovers();
    this.refreshBodyVisibility();
    this.paintAppearance();
  }

  // ── appearance painting (2026-07-31 hair/eye colour axes) ────────────────────
  // Hair + iris colours are per-avatar COLOR_0 rewrites over the vert sets
  // appearanceVerts derives from each body/hair template prim. Skin-group prims
  // additionally bake the SKIN TINT per vertex (2026-08-02): every vert is rebuilt
  // from the immutable template (so repaints never compound) as
  //   base (authored skin, or the painted hair/iris) × style skin tint
  // — exactly the multiply materials[4]/[7].color used to do, moved into the paint
  // so ambient citizens can share one material set.
  private paintTargets: {
    attr: THREE.BufferAttribute; tpl: THREE.BufferAttribute;
    position: THREE.BufferAttribute;
    index: THREE.BufferAttribute;
    scalpFacets?: { definition: HairScalpTint; vertices: Uint32Array };
    hair: Set<number>; eye: Set<number>; brow: Set<number>; skinTinted: boolean; hairShade: boolean;
  }[] = [];
  private hairColor = DEFAULT_STYLE.hairColor ?? 0x2a2320;
  private eyeColor = DEFAULT_STYLE.eyeColor ?? 0x3e6b50;
  private skinTint = DEFAULT_STYLE.skin ?? 0xffffff;
  private hairScalpTint?: HairScalpTint;

  private registerPaintTarget(geo: THREE.BufferGeometry, verts: AppearanceVerts, tplGeo: THREE.BufferGeometry, skinTinted: boolean): void {
    const attr = geo.getAttribute("color") as THREE.BufferAttribute | undefined;
    const tpl = tplGeo.getAttribute("color") as THREE.BufferAttribute | undefined;
    if (attr && tpl) this.paintTargets.push({ attr, tpl, position: tplGeo.getAttribute("position") as THREE.BufferAttribute, index: tplGeo.index!, hair: new Set(verts.hair), eye: new Set(verts.eye), brow: new Set(verts.brow), skinTinted, hairShade: tplGeo.userData.appearance === "hair" });
  }

  private paintAppearance(): void {
    // THREE.Color converts the sRGB hexes into the linear working space —
    // vertex colours are linear, so raw sRGB floats washed out bright.
    // setXYZ handles normalized (Uint8) attributes itself.
    const hairC = new THREE.Color(this.hairColor);
    // Brows ride the hair colour a shade DARKER (user call 2026-08-10) — the
    // character-art trick that keeps the brow line readable on pale hair.
    // 0.55 in LINEAR space ≈ one solid value step in sRGB.
    const browC = hairC.clone().multiplyScalar(0.55);
    const eyeC = new THREE.Color(this.eyeColor);
    const tint = new THREE.Color(this.skinTint);
    const cropColor = new THREE.Color(0, 0, 0);
    let cropVertices = 0;
    if (this.hairScalpTint) {
      for (const t of this.paintTargets) {
        if (!t.skinTinted) continue;
        if (t.scalpFacets?.definition !== this.hairScalpTint) {
          t.scalpFacets = { definition: this.hairScalpTint, vertices: scalpHairFacets(this.hairScalpTint, t.position, t.index) };
        }
        for (const v of t.scalpFacets.vertices) {
          cropColor.r += t.tpl.getX(v); cropColor.g += t.tpl.getY(v); cropColor.b += t.tpl.getZ(v);
          cropVertices++;
        }
      }
      // One material color across the crop; polygon lighting supplies its facets.
      if (cropVertices) cropColor.multiplyScalar(1 / cropVertices).multiply(tint).lerp(hairC, this.hairScalpTint.density);
    }
    for (const t of this.paintTargets) {
      if (t.skinTinted) {
        // Full rebuild from the template: base × skin tint. Hair/iris verts sit on the
        // skin prims too and were multiplied by the old material tint — keep that.
        const arr = t.tpl.array as ArrayLike<number>;
        const k = t.tpl.itemSize, s = t.tpl.normalized ? 1 / 255 : 1;
        for (let v = 0; v < t.attr.count; v++) {
          const src = t.hair.has(v) ? hairC : t.brow.has(v) ? browC : t.eye.has(v) ? eyeC : null;
          const r = src ? src.r : arr[v * k] * s;
          const g = src ? src.g : arr[v * k + 1] * s;
          const b = src ? src.b : arr[v * k + 2] * s;
          t.attr.setXYZ(v, r * tint.r, g * tint.g, b * tint.b);
        }
        if (this.hairScalpTint && t.scalpFacets) for (const v of t.scalpFacets.vertices) t.attr.setXYZ(v, cropColor.r, cropColor.g, cropColor.b);
      } else {
        for (const v of t.hair) {
          // Preserve the authored lock/tie values at every hue; rebuild from the
          // immutable neutral template so recolouring never compounds.
          const shade = t.hairShade ? t.tpl.getX(v) / .30 : 1;
          t.attr.setXYZ(v, hairC.r * shade, hairC.g * shade, hairC.b * shade);
        }
        for (const v of t.brow) t.attr.setXYZ(v, browC.r, browC.g, browC.b);
        for (const v of t.eye) t.attr.setXYZ(v, eyeC.r, eyeC.g, eyeC.b);
      }
      t.attr.needsUpdate = true;
    }
  }

  private teardownGlbBody(): void {
    if (this.glbBody) {
      for (const m of this.glbBody.meshes) m.removeFromParent();
      for (const g of this.glbBody.geos) disposeAvatarGeometry(g);
      this.glbBody = null;
    }
    this.teardownHair();
    this.paintTargets.length = 0; // all targets belong to body/hair geometries
    this.glbBodyActive = false;
    this.refreshBodyVisibility();
  }

  /** Create + mount one skinned GLB primitive on the live skeleton (body, hair or cosmetic).
   *  `holo` draws it as the holographic projection instead of its material group — the BODY and
   *  HAIR opt in on the holo body; worn clothing stays solid on top of it. */
  private mountSkinnedPrim(
    geo: THREE.BufferGeometry, group: PieceGroup, holo = false, garment = false,
  ): THREE.SkinnedMesh {
    const fingerprint = geo.userData.rigFingerprint;
    if (fingerprint && fingerprint !== rigProfile(this.bodyRig).fingerprint) throw new Error("Cannot mount a character piece on an incompatible body rig");
    const hasFaceMorphs = !garment && (geo.morphAttributes.position?.some(a => !a.name.startsWith("shape_") && !a.name.startsWith("build_")) ?? false);
    const faceSkin = group === "skin" && hasFaceMorphs;
    const oralInterior = geo.userData.appearance === "fixed";
    const eyeSurface = geo.userData.appearance === "eyes";
    const faceDetail = group === "colored" && hasFaceMorphs && !oralInterior && !eyeSurface;
    const mesh = new BoundedSkinnedMesh(
      geo,
      // The brow/lash offset would pull oral walls through lips and eye globes
      // through lids. Eye meshes can have targets on their lower-lash vertices.
      holo ? this.materials[6] : faceSkin || oralInterior ? this.materials[7] : faceDetail ? this.materials[8]
        : garment && group === "colored" ? this.garmentMat : this.glbMaterialFor(group),
    );
    // face-morph prims (the body_face mesh): build the blendshape name->index dictionary
    // (no-op for morphless prims). Influences are driven per avatar via setFaceInfluence.
    mesh.updateMorphTargets();
    this.applyShapeToMesh(mesh, validateBodyShape(this.lastStyle?.bodyShape));
    mesh.userData.avatarShadowDefault = !holo;
    mesh.castShadow = !holo && this.shadowsEnabled; // light passes through a hologram
    mesh.receiveShadow = !holo && group === "accent";
    if (faceDetail) mesh.renderOrder = 1;
    this.rigRoot.add(mesh);
    // Bind exactly like the procedural body (same skeleton, same bind matrix) — the piece
    // geometry is authored in the same natural rest space, so no remapping is needed.
    mesh.bind(this.skeleton, this.skinned[0].bindMatrix);
    return mesh;
  }

  private glbMaterialFor(group: PieceGroup): THREE.Material {
    return group === "skin" ? this.materials[4] : group === "accent" ? this.materials[2] : this.materials[5];
  }

  private applyShapeToMesh(mesh: THREE.SkinnedMesh, shape: BodyShape): void {
    const build = validateBodyBuild(this.lastStyle?.bodyBuild);
    for (const { id } of BODY_BUILDS) {
      const index = mesh.morphTargetDictionary?.[`build_${id}`];
      if (index !== undefined && mesh.morphTargetInfluences) mesh.morphTargetInfluences[index] = id === build ? 1 : 0;
    }
    for (const [axis, value] of Object.entries(shape)) {
      const index = mesh.morphTargetDictionary?.[`shape_${axis}`];
      if (index !== undefined && mesh.morphTargetInfluences) mesh.morphTargetInfluences[index] = value;
    }
  }

  private applyBodyShape(updateProportions: boolean): void {
    const shape = validateBodyShape(this.lastStyle?.bodyShape);
    // Length edits travel through the rig so equipment and animation contacts follow.
    if (updateProportions) this.setProportions({ ...DEFAULT_AVATAR_PROPORTIONS,
      legLength: 1 + shape.stature * 0.12,
      armLength: 1 + shape.stature * 0.06,
      shoulderWidth: 1 + shape.muscle * 0.035 + shape.mass * 0.025,
    });
    this.rigRoot.traverse(object => {
      if (object instanceof THREE.SkinnedMesh) this.applyShapeToMesh(object, shape);
    });
    this.pbdColliderSig = "";
  }

  /** Face blendshape write-path (FaceRig + dev): set one morph influence by its ARKit name
   *  across every face mesh that carries it (face skin + brows/lashes move in lockstep). */
  setFaceInfluence(name: string, v: number): void {
    if (!this.glbBody) return;
    for (const m of this.glbBody.meshes) {
      const i = m.morphTargetDictionary?.[name];
      if (i !== undefined && m.morphTargetInfluences) m.morphTargetInfluences[i] = v;
    }
  }

  /** Current value of one face blendshape (meters/probes; 0 when absent). */
  getFaceInfluence(name: string): number {
    if (this.glbBody) for (const m of this.glbBody.meshes) {
      const i = m.morphTargetDictionary?.[name];
      if (i !== undefined && m.morphTargetInfluences) return m.morphTargetInfluences[i];
    }
    return 0;
  }

  /** Blendshape names available on the loaded body (empty until the GLB template lands). */
  faceInfluenceNames(): string[] {
    const out: string[] = [];
    if (this.glbBody) for (const m of this.glbBody.meshes) {
      for (const n of Object.keys(m.morphTargetDictionary ?? {})) if (!n.startsWith("shape_") && !n.startsWith("build_") && !out.includes(n)) out.push(n);
    }
    return out;
  }

  /** Re-apply cosmetic regions + garment face masks to the live body index. */
  private refreshCovers(): void {
    if (!this.glbBody) return;
    // The original male hair includes a scalp layer in the body asset. New
    // hairstyles carry their own fitted roots; retaining that layer obscures
    // buzz cuts and undercuts. Helmet coverage still hides the accessory too.
    const bodyHidden = new Set(this.hiddenRegions);
    if (this.bodyModelId === "male" && (this.lastStyle?.hair ?? "spiky") !== "spiky") bodyHidden.add("hair");
    const clothCoverage = clothingCoverage(this.styledSkinned);
    let g = 0;
    for (const prim of this.glbBody.prims) {
      if (!prim.regions && !prim.occlusionMasks) continue;
      applyCovers(this.glbBody.geos[g++], prim, bodyHidden, this.garmentOcclusionMasks, clothCoverage);
    }
    this.refreshBodyVisibility();
  }

  /** Single source of visibility truth for body meshes across LOD / body-model / covers /
   *  solo state. In SOLO mode everything normal is hidden and only the solo piece shows. */
  private refreshBodyVisibility(): void {
    const solo = this.soloActive;
    const glbOn = !solo && this.glbBodyActive && !this.farLOD;
    if (this.glbBody) for (const m of this.glbBody.meshes) m.visible = glbOn;
    for (const m of this.glbHair) m.visible = glbOn && !this.hiddenRegions.has("hair");
    for (const part of this.styledParts) part.visible = !solo && !this.farLOD;
    for (const m of this.soloMeshes) m.visible = solo && !this.farLOD;
    this.impostor.visible = !solo && this.farLOD;
  }

  /** GOD-MODE: show ONLY the piece at `url` (a /models/male/*.glb), hiding the body + all
   *  cosmetics — the piece still rides the live skeleton + cloth sim, so a soloed cape flows.
   *  Null restores normal rendering. Race-guarded against rapid switches. */
  setSoloPiece(url: string | null): void {
    if ((url ?? "") === this.soloUrl) return;
    this.soloUrl = url ?? "";
    for (const m of this.soloMeshes) { m.removeFromParent(); disposeAvatarGeometry(m.geometry); }
    this.soloMeshes.length = 0;
    this.soloActive = !!url;
    this.refreshBodyVisibility();
    if (!url) return;
    const want = this.soloUrl;
    loadPiece(url).then((tpl) => {
      if (this.soloUrl !== want || !tpl) return;
      for (const prim of tpl.prims) {
        // body prims carry region covers → own a fresh index; others share the template geo
        const geo = prim.regions || prim.occlusionMasks ? instanceBodyGeometry(prim) : prim.geometry;
        const mesh = this.mountSkinnedPrim(geo, prim.group);
        this.soloMeshes.push(mesh);
      }
      this.refreshBodyVisibility();
    });
  }

  /** Load a SKINNED cosmetic piece (template-cached, shared geometry) onto the skeleton. */
  private loadCosmeticSkin(def: CosmeticSkin, part: StylePart): void {
    // Each supported body loads its own verified bind-space export.
    if (this.bodyRig === FEMALE_RIG && !def.femaleSkin) return;
    const key = this.styleKey; // a restyle before this resolves → discard
    // Skinned garments ship PER BODY FAMILY: the catalogue lists the male path; other rigs
    // load their own build of the same piece. A piece that has no build for this body
    // resolves null in loadPiece and is simply skipped.
    const url = this.bodyRig === FEMALE_RIG ? def.femaleSkin! : def.skin;
    const skinP = loadPiece(url);
    this.stylePromises.push(skinP);
    skinP.then((tpl) => {
      if (this.styleKey !== key || !tpl) return;
      let attached = false;
      for (const prim of tpl.prims) {
        const mesh = this.mountSkinnedPrim(prim.geometry, prim.group, false, true);
        if (def.colorField && prim.geometry.userData.appearance !== "fixed") {
          mesh.material = def.colorField === "shirtColor" ? this.baseShirtMat : this.basePantsMat;
          // These shirts have one fabric surface. A folded collar/sleeve must
          // show its inside instead of disappearing and exposing the skin.
          const side = prim.geometry.userData.thinCloth ? THREE.DoubleSide : THREE.FrontSide;
          if (mesh.material.side !== side) { mesh.material.side = side; mesh.material.needsUpdate = true; }
          mesh.onBeforeRender = () => applyGarmentPose(mesh);
          mesh.onBeforeShadow = () => applyGarmentPose(mesh);
        }
        mesh.userData.stylePart = part; // lets the BVH collider builder find worn plates
        this.styledParts.push(mesh);
        this.styledSkinned.push(mesh);
        // PER-VERTEX path: split the simulated region out of this garment (no-op for pieces
        // that have none, which is every piece but the cloak).
        if (this.pbdCloth) {
          const cape = attachPbdCape(mesh, {
            isCageBone: (n) => n.startsWith("cl_"),
            capsules: this.pbdCapsules(true),
          });
          if (cape) { cape.setShadows(this.shadowsEnabled); this.pbdCapes.push(cape); attached = true; }
        }
      }
      // A new cape needs the full collider set; a new worn piece (the pauldron) needs to be
      // added to the capes' colliders. Either way, rebuild.
      if (attached || (this.pbdCloth && this.pbdCapes.length)) this.refreshPbdColliders();
      this.refreshCovers();
    });
  }

  /** Request the held weapon (or null to holster). The actual swap is animated in
   *  updateWeaponVisual: the old weapon "pixelates" out, the new one in. */
  setWeapon(id: string | null): void {
    if (id !== this.weaponTargetId) {
      // Reach seating belongs to one weapon's geometry. Carrying its offset
      // into the next weapon briefly displaces that weapon and its hands.
      this.gunSeatS = 0;
      this.gunSteadyLocal = null;
    }
    this.weaponTargetId = id;
    const wdef = id ? ITEMS[id]?.weapon : undefined;
    const cls = wdef?.class;
    // Crystal-enabled players and remotes derive the gun form from the same
    // catalog; NPCs without crystal mode keep their modeled weapons.
    if(this.crystalWeaponShape!==null&&wdef&&cls!=='melee')
      this.setCrystalWeapon(wdef.wingForm??(weaponHold(wdef).stance==='underhand'?'minigun':weaponHold(wdef).stance==='over-shoulder'?'launcher':'carbine'));
    this.construct = wdef?.construct ?? null;
    if (wdef) {
      this.holdDef = weaponHold(wdef);
      this.weaponStance = this.holdDef.stance;
    }
    // A GUN (main/sidearm) OR the melee SWORD being drawn puts the body in the armed locomotion stance +
    // its own clip set whenever it's out — not only while aiming. Bare hands (holstered / no weapon)
    // keep the unarmed set.
    // The ARM CANNON overlays the arm rather than being held: with it lowered you read as
    // UNARMED (the arm just swings). Bringing it up on RMB borrows the PISTOL stance + aim
    // layer — see `wantArmed`, which folds cannonOut in only while aiming.
    this.gunOut = (cls === "main" || cls === "sidearm") && this.construct !== "cannon";
    this.swordOut = cls === "melee";
    this.cannonOut = this.construct === "cannon";
    // Pick the armed-stance clip set by weapon class: main/rifle -> rifle, melee -> sword (sd_),
    // everything else (sidearm / none) -> pistol.
    // The cannon aims on the PISTOL set ("p"): one arm up, compact — the arm-cannon read.
    const prefix = this.construct === "cannon" ? "p"
      : this.weaponStance === "sword" ? STANCE_AIM_PREFIX.sword
      : cls === "main" ? STANCE_AIM_PREFIX[this.weaponStance]
      : "p";
    if (id && prefix !== this.aimPrefix) {
      this.aimPrefix = prefix;
      // Switching weapon CLASS clears any in-flight strike so a stale sword swing can't bleed onto a gun
      // stance (and vice versa). The ACTION clock clears with it — otherwise a holstered sword's arc
      // would keep sweeping for the rest of its window. The COMBO PROGRESS deliberately survives
      // (DMC weapon-switch contract): cycle to the cannon mid-chain, fire, cycle back inside the
      // grace window, and the next press throws combo step 2 — the switch is a cancel, not a reset.
      // comboWindowT keeps draining on its own, so an abandoned chain still lapses naturally.
      this.strikeT = 0;
      this.strikeKey = "";
      this.actionId = null;
    }
  }
  /** Opt into wing-built player weapons. The catalog chooses the silhouette
   * and mobility chooses how many physical pieces it borrows. NPCs without
   * wings or without this opt-in retain their modeled weapons. */
  setCrystalWeapon(shape:CrystalWeaponShape|null):void{
    if(shape===this.crystalWeaponShape)return;
    if(shape===null||this.crystalWeaponShape===null)this.crystalWings?.weaponMorph.reset();
    this.crystalWeaponShape=shape;
    if(this.weapon)this.weapon.visible=!this.isCrystalGun(this.weaponId);
    if(this.stowedGroup)this.stowedGroup.visible=shape===null;
  }
  /** All player ranged forms assemble from wings, including the arm cannon. */
  get crystalWeaponEnabled():boolean{return this.isCrystalGun(this.weaponTargetId);}
  /** Whether the actual mounted weapon should hide its modeled geometry. */
  private isCrystalGun(id:string|null):boolean{
    if(this.crystalWeaponShape===null||!this.crystalWings||!id)return false;
    const w=ITEMS[id]?.weapon;
    return !!w&&(w.class==='main'||w.class==='sidearm')&&(w.construct===undefined||w.construct==='cannon');
  }
  /** Crystal mode is on and the target weapon is the forearm light blade. */
  get crystalBladeEnabled():boolean{
    if(this.crystalWeaponShape===null||!this.crystalWings||!this.weaponTargetId||this.gsSword)return false;
    const w=ITEMS[this.weaponTargetId]?.weapon;
    return !!w&&w.class==='melee'&&w.construct==='blade';
  }
  get crystalWeaponReady():boolean{return this.crystalWeaponEnabled&&this.aiming&&this.weaponId===this.weaponTargetId&&this.crystalWings?.weaponMorph.shape===this.crystalWeaponShape&&this.crystalWings.weaponMorph.ready;}
  get crystalBladeReady():boolean{return this.crystalBladeEnabled&&this.swordFocus&&this.crystalWings?.weaponMorph.shape==='blade'&&this.crystalWings.weaponMorph.ready;}
  /** Armed-stance clip prefix: "p"=pistol, "r"=rifle, "sd"=sword (set from the held weapon class). */
  private aimPrefix: "p" | "r" | "sd" = "p";
  /** True while the ARM CANNON is manifested — unarmed locomotion + a procedural arm aim. */
  cannonOut = false;
  /** True while a GUN (not melee) is drawn — drives the armed locomotion stance. */
  private gunOut = false;
  private weaponTriggerHeld=false;
  private weaponCharge:number|undefined;
  setWeaponTrigger(held:boolean,charge?:number):void {this.weaponTriggerHeld=held;this.weaponCharge=charge;}
  /** True while the melee SWORD is drawn — also drives the armed stance, over the sd_ clip set. */
  private swordOut = false;

  /** The mode construct currently built (null for a real carried weapon / nothing). */
  private construct: WeaponConstruct | null = null;
  /** Drive the holster/draw "pixelate" dissolve toward the requested weapon. */
  private updateWeaponVisual(dt: number): void {
    const rate = dt * 6; // ~0.17s in/out
    if (this.weaponId !== this.weaponTargetId) {
      if (this.weapon) {
        // Dissolve the current weapon OUT, then drop it (next frame builds the new).
        this.dissolve = Math.min(1, this.dissolve + rate);
        if (this.dissolve >= 1) this.disposeWeapon();
      } else if (this.weaponTargetId !== null) {
        this.buildWeapon(this.weaponTargetId); // starts at dissolve = 1, fades in
      } else {
        this.weaponId = null;
      }
    } else if (this.weapon) {
      this.dissolve = Math.max(0, this.dissolve - rate); // settle to solid
    }
    this.ledgeWeaponK+=((this.ledgeClimbState?1:0)-this.ledgeWeaponK)*(1-Math.exp(-20*dt));
    if (this.weapon) this.weaponDis.value = Math.max(this.dissolve,this.ledgeWeaponK);
    // BACK-STOWED copy: pixelate in after a stow, out on a draw (its own uniform + clock,
    // a touch faster than the hand weapon so the crossfade reads as one motion).
    if (this.stowedGroup) {
      if (this.stowFading) {
        this.stowDis.value = Math.min(1, this.stowDis.value + dt * 8);
        if (this.stowDis.value >= 1) this.dropStowedGroup();
      } else {
        this.stowDis.value = Math.max(0, this.stowDis.value - dt * 8);
      }
    }
    // Judge the weapon actually built: a rifle dissolving out during a swap to
    // the blade is still a crystal gun and must not flash its model.
    if(this.weapon)this.weapon.visible=!this.isCrystalGun(this.weaponId);
    if(this.stowedGroup)this.stowedGroup.visible=this.crystalWeaponShape===null;
    // The crystal blade replaces the light blade's glow; its markers stay live.
    if(this.weapon){const crystal=this.crystalBladeEnabled;this.weapon.traverse(o=>{if(o.userData.lightBlade)o.visible=!crystal;});}
  }

  private disposeWeapon(): void {
    if (this.weapon) {
      this.weapon.removeFromParent();
      this.weapon.traverse((o) => {
        if (o instanceof THREE.Mesh) {
          o.geometry.dispose();
          (o.material as THREE.Material).dispose();
        }
      });
    }
    this.weapon = null;
    this.weaponId = null;
    this.muzzle = null;
    this.bore = null;
  }

  /** Inject the holster "pixelate" dissolve into ANY lit material (shares this.weaponDis):
   *  blocks of the mesh (quantized local position) discard past the threshold, with a cyan
   *  energy edge. Both chunks it patches (`begin_vertex`, `dithering_fragment`) exist in all
   *  of three's lit materials, so a loaded GLB weapon (MeshStandardMaterial) keeps the effect.
   *  (Pattern: flora.ts onBeforeCompile.) */
  private applyDissolve(m: THREE.Material, uniform: { value: number } = this.weaponDis): void {
    m.transparent = true;
    m.onBeforeCompile = (shader) => {
      shader.uniforms.uDis = uniform;
      shader.vertexShader =
        "varying vec3 vDisP;\n" +
        shader.vertexShader.replace("#include <begin_vertex>", "#include <begin_vertex>\n  vDisP = position;");
      shader.fragmentShader =
        "uniform float uDis;\nvarying vec3 vDisP;\nfloat dHash(vec3 p){return fract(sin(dot(p,vec3(12.9898,78.233,37.719)))*43758.5453);}\n" +
        shader.fragmentShader.replace(
          "#include <dithering_fragment>",
          "float db = dHash(floor(vDisP*48.0));\n  if (db < uDis) discard;\n  if (uDis > 0.001 && db >= uDis && db < uDis + 0.16) gl_FragColor.rgb += vec3(0.25,0.7,1.0)*1.6;\n  #include <dithering_fragment>",
        );
    };
  }

  /** A MeshLambertMaterial clone that "pixelates" away as weaponDis 0->1 (the procedural
   *  weapon primitives use this; GLB weapons get applyDissolve on their own materials). */
  private dissolveMat(base: THREE.MeshLambertMaterial): THREE.MeshLambertMaterial {
    const m = base.clone();
    this.applyDissolve(m);
    return m;
  }

  /** Load a GLB held-weapon mesh (Meshy export — canonical hand frame: grip at origin,
   *  barrel -Z, top +Y, metres) into the already-mounted group `w`. Async: if the player
   *  holsters or swaps before it resolves, the result is disposed. Each loaded material is
   *  cloned + given the holster dissolve so draw/holster reads exactly like the primitives. */
  private loadWeaponModel(id: string, url: string, w: THREE.Group): void {
    weaponGltfLoader()
      .loadAsync(url)
      .then((gltf) => {
        const disposeScene = (): void =>
          gltf.scene.traverse((o) => {
            if (o instanceof THREE.Mesh) {
              o.geometry.dispose();
              (o.material as THREE.Material).dispose();
            }
          });
        // Raced: holstered or swapped to another weapon while loading — drop it.
        if (this.weapon !== w || this.weaponId !== id) {
          disposeScene();
          return;
        }
        let muzzle: THREE.Object3D | null = null;
        let bore: THREE.Object3D | null = null;
        gltf.scene.traverse((o) => {
          if (o.name === "muzzle") muzzle = o;
          else if (o.name === "bore") bore = o;
          if (o instanceof THREE.Mesh) {
            o.frustumCulled = false;
            const mat = (o.material as THREE.Material).clone();
            this.applyDissolve(mat);
            o.material = mat;
          }
        });
        // ⚠ Measure while the scene is still DETACHED: Box3.setFromObject works in the
        // object's current world frame, and once the model hangs off the posed hand bone
        // those are avatar-world coords — stored as a hand-local position they put the
        // muzzle a torso away, and the RMB aim solve chased it (chest flipped fully over).
        const box = new THREE.Box3().setFromObject(gltf.scene);
        // Legacy model fallback only. New weapon GLBs author muzzle + bore
        // sockets, because the bounds centre is not the barrel axis on scopes,
        // launch tubes, rotary drums, or other asymmetric silhouettes.
        if (!muzzle) {
          const m = new THREE.Object3D();
          m.position.set((box.min.x + box.max.x) / 2, (box.min.y + box.max.y) / 2, box.min.z);
          w.add(m);
          muzzle = m;
          // Second point on the same line = a TRUE −Z mechanical axis for the aim
          // solves (the hand_r fallback carries a ~7° permanent false tilt).
        }
        if (!bore) {
          const rear = new THREE.Object3D();
          const p = muzzle.position;
          rear.position.set(p.x, p.y, p.z + 0.25);
          // Match the muzzle's parent frame. Authored sockets normally share
          // gltf.scene; the legacy synthetic muzzle is already under `w`.
          (muzzle.parent ?? w).add(rear);
          bore = rear;
        }
        w.add(gltf.scene);
        // A catalog fireSocket installs stable markers synchronously in
        // buildWeapon. Never replace those references when async geometry
        // arrives: doing so changes the projectile origin mid-burst. Legacy
        // external models without metadata still adopt their authored nodes.
        if (!this.muzzle) this.muzzle = muzzle;
        if (!this.bore) this.bore = bore;
        if (this.weaponStance === "shoulder" && this.hasSupportGrip()) this.seatRifleButt(box.max.z);
      })
      .catch((e) => {
        console.warn(`[avatar] weapon model "${url}" failed to load:`, e instanceof Error ? e.message : e);
      });
  }

  /**
   * BUTT-IN-THE-SHOULDER-POCKET (2026-08-11, user call): the r_* hold clips put
   * the grip hand RIFLE_BUTT_SEAT_Z in front of the armpit crook, so a stock
   * ending there rests on the shoulder. A model whose butt runs past that spec
   * slides FORWARD along its own axis until it seats (and a short one slides
   * back) — the hold point accommodates the stock, never the reverse. Authored
   * rifles are built to the spec (build-rifles.mts hard-fails otherwise), so
   * this is the safety net for future/converted models.
   */
  private seatRifleButt(buttZ: number): void {
    if (!this.weapon || !Number.isFinite(buttZ)) return;
    const dz = buttZ - RIFLE_BUTT_SEAT_Z;
    if (Math.abs(dz) < 0.005) return;
    _seatV.set(0, 0, -dz).applyQuaternion(this.weaponMount);
    this.weapon.position.copy(this.weaponBasePosition).add(_seatV);
  }

  /**
   * Re-parent a construct group onto a FOREARM bone with its local +Z along the
   * elbow→hand axis. Mounting on the forearm (not the hand) is what keeps a
   * forearm-mounted construct PARALLEL to the arm — wrist rotation can no longer
   * swing it off-axis. Returns the forearm length so callers can reach past the fist.
   */
  private mountOnForearm(w: THREE.Group, side: "Right" | "Left"): number {
    const fore = this.boneByName.get(`${side}ForeArm`)!;
    const hand = this.boneByName.get(`${side}Hand`)!;
    fore.add(w); // off the hand (set up generically by the caller) and onto the forearm
    const along = hand.position.clone().normalize(); // down-arm dir in forearm-LOCAL
    const L = hand.position.length() || 0.26;
    // ref avoids a degenerate cross when `along` ≈ ±Y.
    const ref = Math.abs(along.y) > 0.9 ? new THREE.Vector3(0, 0, 1) : new THREE.Vector3(0, 1, 0);
    const bx = new THREE.Vector3().crossVectors(ref, along).normalize();
    const by = new THREE.Vector3().crossVectors(along, bx).normalize();
    const q = new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(bx, by, along));
    // applyArmOverrides re-copies weaponMount onto the group EVERY frame, so the
    // forearm basis has to live there — setting w.quaternion alone gets clobbered.
    this.weaponMount.copy(q);
    this.weaponBasePosition.set(0, 0, 0);
    w.quaternion.copy(q);
    w.position.set(0, 0, 0); // at the elbow; geometry offsets do the placing
    return L;
  }

  /** Procedural RIFLE meshes (canonical frame: grip at origin, barrel −Z, top +Y), shared by
   *  the HELD weapon and the BACK-STOWED copy. Returns the muzzle tip + bore rear reference —
   *  the aim solves measure the gun's TRUE mechanical axis as (tip − bore); the old
   *  hand→muzzle proxy put a ~7° permanent false tilt into every barrel measurement. */
  private static buildRifleMeshes(w: THREE.Object3D, dark: THREE.Material, glow: THREE.Material): { tip: THREE.Mesh; bore: THREE.Object3D } {
    const M = (g: THREE.BufferGeometry, mat: THREE.Material): THREE.Mesh => new THREE.Mesh(g, mat);
    const axX = (RIFLE_FIST_R[0] + RIFLE_FIST_L[0]) / 2;
    const fr: readonly [number, number, number] = [axX, RIFLE_FIST_R[1], RIFLE_FIST_R[2]];
    const fl: readonly [number, number, number] = [axX, RIFLE_FIST_L[1], RIFLE_FIST_L[2]];
    const receiver = M(new THREE.BoxGeometry(0.045, 0.07, 0.40), dark); receiver.position.set(fr[0], fr[1] + 0.012, (fr[2] + fl[2]) / 2);
    const handguard = M(new THREE.BoxGeometry(0.044, 0.052, 0.17), dark); handguard.position.set(fl[0], fl[1], fl[2]);
    const barrel = M(new THREE.CylinderGeometry(0.013, 0.015, 0.20, 8), dark); barrel.rotation.x = Math.PI / 2; barrel.position.set(fl[0], fl[1] + 0.008, fl[2] - 0.18);
    const stock = M(new THREE.BoxGeometry(0.04, 0.07, 0.17), dark); stock.position.set(fr[0], fr[1] + 0.012, fr[2] + 0.22);
    const grip = M(new THREE.BoxGeometry(0.034, 0.1, 0.05), dark); grip.position.set(fr[0], fr[1] - 0.045, fr[2]); grip.rotation.x = -0.28;
    const mag = M(new THREE.BoxGeometry(0.03, 0.11, 0.05), dark); mag.position.set(fr[0], fr[1] - 0.075, fr[2] - 0.10);
    const tip = M(new THREE.SphereGeometry(0.018, 6, 5), glow); tip.name = "muzzle"; tip.position.set(fl[0], fl[1] + 0.008, fl[2] - 0.30);
    const bore = new THREE.Object3D(); bore.position.set(fl[0], fl[1] + 0.008, fr[2] + 0.2);
    w.add(receiver, handguard, barrel, stock, grip, mag, tip, bore);
    return { tip, bore };
  }

  /** BACK-STOW (2026-08-02): the equipped-but-holstered gun rides the player's back. A separate
   *  mesh group on the chest bone (Spine2), so the held-weapon machinery (mount resets, aim
   *  solves, dissolves) never sees it. Constructs conjure/dissolve instead — nothing to stow. */
  private stowedId: string | null = null;
  private stowedGroup: THREE.Group | null = null;
  /** Whether the current style wears a back piece (set in setStyle) — picks the
   *  stow anchor's off-back distance. */
  private backGearWorn = false;

  /** Where the back-stowed rifle sits: flush against skin, or proud of a worn pack. */
  private rifleBackPos(): THREE.Vector3 {
    return this.backGearWorn ? RIFLE_BACK_POS_PACK : RIFLE_BACK_POS;
  }
  /** Back-copy dissolve uniform (independent of the hand weapon's): 1 = gone, 0 = solid. */
  private stowDis = { value: 1 };
  /** Seconds left in the Z stow/draw GESTURE — while > 0 the aim stack stands down
   *  (see applyArmOverrides; aiming through the gesture made the arms flail). */
  private stowGestureT = 0;
  /** Eased 0..1 mirror of the gesture window (fades the support-hand grip). */
  private stowGestureK = 0;
  /** True while the back copy is dissolving OUT (removed once fully gone). */
  private stowFading = false;

  /** STOW/DRAW GESTURE (user call 2026-08-02): the authored back-carry sheathe pair from the
   *  2H set, as LEGS-STRIPPED duplicates (`stow_sheathe`/`stow_draw`) played on the MASKED
   *  upper layer — locomotion continues underneath. Sped up ×1.6; the caller times the actual
   *  hand↔back weapon swap to land mid-reach (main.ts delays the armed flip). */
  playStowGesture(stow: boolean): void {
    const key = stow ? "stow_sheathe" : "stow_draw";
    const a = this.acts[key];
    if (!a) return;
    // Open the gesture window: applyArmOverrides eases the AIM stack out for the
    // clip's run. Aiming through the gesture left both solvers fighting the clip
    // — the chest offset + barrel lock corrected the reach mid-swing and the
    // support-hand IK chased the moving gun (user-visible: arms flailing when
    // Z is pressed with RMB held).
    const timing=weaponStats(ITEMS[this.weaponTargetId??this.weaponId??'']?.weapon??{});
    this.stowGestureT = Math.max(.05,stow?timing.stowTime:timing.deployTime);
    a.reset();
    a.setLoop(THREE.LoopOnce, 1);
    a.clampWhenFinished = true;
    a.timeScale = a.getClip().duration/this.stowGestureT;
    this.strikeKey = key;
    this.strikeLayer = "upper";
    this.strikeT = Math.min(a.getClip().duration / STOW_GESTURE_SCALE, 0.7);
    this.activeMoveId = null;
    this.moveHitConfirmed = false;
    this.cancelT = 0;
  }

  setStowedWeapon(id: string | null): void {
    if (id === this.stowedId) return;
    this.stowedId = id;
    const wdef = id ? ITEMS[id]?.weapon : undefined;
    const wantsCopy = !!wdef && !wdef.construct && wdef.class === "main"
      && weaponHold(wdef).stance === "shoulder";
    if (!wantsCopy) {
      // DISSOLVE OUT (don't pop): mark fading; updateWeaponVisual removes it once gone.
      this.stowFading = this.stowedGroup !== null;
      return;
    }
    this.dropStowedGroup(); // weapon changed while stowed → rebuild fresh
    const back = this.boneByName.get("Spine2") ?? this.boneByName.get("Spine1");
    if (!back) return;
    const g = new THREE.Group();
    // Dissolve-clone materials on the back copy's OWN uniform, so it pixelates in/out on its
    // own clock (sharing this.weaponDis would slave it to the hand weapon's fade).
    const dark = this.materials[3] as THREE.MeshLambertMaterial;
    const glow = this.materials[2] as THREE.MeshLambertMaterial;
    const darkD = dark.clone(), glowD = glow.clone();
    this.applyDissolve(darkD, this.stowDis);
    this.applyDissolve(glowD, this.stowDis);
    // Authored GLB rifles ride the back as themselves; the procedural rifle is
    // the no-model fallback. The GLB streams in async — an empty back for a few
    // frames beats flashing the WRONG rifle and swapping it.
    if (wdef.model) {
      const url = wdef.model;
      weaponGltfLoader().loadAsync(url).then((gltf) => {
        if (this.stowedGroup !== g || this.stowedId !== id) {
          gltf.scene.traverse((o) => {
            if (o instanceof THREE.Mesh) {
              o.geometry.dispose();
              (o.material as THREE.Material).dispose();
            }
          });
          return; // raced: drawn or swapped while loading
        }
        gltf.scene.traverse((o) => {
          if (o instanceof THREE.Mesh) {
            o.frustumCulled = false;
            const mat = (o.material as THREE.Material).clone();
            this.applyDissolve(mat, this.stowDis);
            o.material = mat;
          }
        });
        g.add(gltf.scene);
      }).catch(() => { /* mesh missing: the empty group is an honest no-render */ });
    } else {
      Avatar.buildRifleMeshes(g, darkD, glowD);
    }
    g.quaternion.copy(RIFLE_BACK_MOUNT);
    g.position.copy(this.rifleBackPos());
    back.add(g);
    this.stowedGroup = g;
    this.stowFading = false;
    this.stowDis.value = 1; // materialize in (updateWeaponVisual eases toward 0)
  }

  private dropStowedGroup(): void {
    if (!this.stowedGroup) return;
    this.stowedGroup.parent?.remove(this.stowedGroup);
    this.stowedGroup.traverse((o) => {
      if (o instanceof THREE.Mesh) {
        o.geometry.dispose();
        (o.material as THREE.Material).dispose();
      }
    });
    this.stowedGroup = null;
    this.stowFading = false;
  }

  /** Resolve stance + sockets once when a weapon is built. The primary socket is
   * aligned to the existing hand mount, so authored models no longer have to put
   * every trigger grip at the same implicit origin/orientation. */
  private configureWeaponHold(wdef: Pick<WeaponDef, "class" | "hold">): void {
    this.holdDef = weaponHold(wdef);
    this.weaponStance = this.holdDef.stance;
    const twoHandedGun = !!this.holdDef.support && this.weaponStance !== "sword";
    this.weaponMount.copy(twoHandedGun ? WEAPON_MOUNT_RIFLE : WEAPON_MOUNT_PISTOL);
    const r = this.holdDef.primary.rotation;
    if (r) {
      _primaryGripQ.fromArray(r as [number, number, number, number]).normalize().invert();
      this.weaponMount.multiply(_primaryGripQ);
    }
    this.weaponBasePosition.set(0, 0.04, 0);
    _primaryGripP.fromArray(this.holdDef.primary.position as [number, number, number]);
    if (_primaryGripP.lengthSq() > 0) {
      _primaryGripP.applyQuaternion(this.weaponMount);
      this.weaponBasePosition.sub(_primaryGripP);
    }
    const support = this.holdDef.support;
    if (support) {
      this.gripLTarget.fromArray(support.position as [number, number, number]);
      const sr = support.rotation;
      if (sr) this.gripLWristQ.fromArray(sr as [number, number, number, number]).normalize();
      else this.gripLWristQ.copy(RIFLE_WRIST_L);
    }
    this.gripSlideS = 0;
    this.gunSeatS = 0;
    this.gunSteadyLocal = null;
    this.gripPolePrev = null;
  }

  private hasSupportGrip(): boolean {
    return !!this.holdDef.support && this.weaponStance !== "sword" && !this.construct;
  }

  private buildWeapon(id: string): void {
    const wdef = ITEMS[id]?.weapon;
    const cls = wdef?.class ?? "sidearm";
    // Mount on the right-hand weapon socket FIRST so the group is live + dissolving in
    // immediately; the geometry is filled below — synchronously for the procedural
    // primitives, or asynchronously once the GLB resolves. The rifle clip set holds the
    // hand at a different angle than the pistol set, so pick the matching mount. NOTE: the
    // reload twirl in applyArmOverrides MUST recompose from this.weaponMount — never set
    // this.weapon.rotation.<axis> directly, or it clobbers the (non-axis-aligned) mount.
    const w = new THREE.Group();
    this.configureWeaponHold(wdef ?? { class: cls });
    w.quaternion.copy(this.weaponMount);
    w.position.copy(this.weaponBasePosition);
    this.weapon = w;
    this.weaponId = id;
    this.muzzle = null;
    this.bore = null;
    this.dissolve = 1; // materialize in
    this.boneByName.get("RightHand")!.add(w);

    // GLB override (Meshy export) — load onto the same mount, keep the primitives as fallback.
    const model = wdef?.model;
    if (model) {
      // Install gameplay sockets BEFORE starting the async model request. This
      // removes the body/camera fallback window and keeps one muzzle Object3D
      // alive for the weapon's entire draw, including turns and first-frame fire.
      if (wdef.fireSocket) {
        const muzzle = new THREE.Object3D();
        muzzle.name = "runtime-muzzle";
        muzzle.position.fromArray(wdef.fireSocket.muzzle as [number, number, number]);
        const bore = new THREE.Object3D();
        bore.name = "runtime-bore";
        bore.position.fromArray(wdef.fireSocket.bore as [number, number, number]);
        w.add(muzzle, bore);
        this.muzzle = muzzle;
        this.bore = bore;
      }
      this.loadWeaponModel(id, model, w);
      return;
    }

    // --- Procedural low-poly weapon (canonical frame: grip at origin, barrel -Z, top +Y) ---
    const dark = this.dissolveMat(this.materials[3] as THREE.MeshLambertMaterial);
    const glow = this.dissolveMat(this.materials[2] as THREE.MeshLambertMaterial);
    const accentOf = (): THREE.Color => (this.materials[2] as THREE.MeshLambertMaterial).emissive.clone();
    const construct = ITEMS[id]?.weapon?.construct;
    if (construct === "cannon") {
      // ARM CANNON — a heavy bracer swallowing the hand, emitter throat where the fist would be;
      // bolts leave `this.muzzle` at the tip, so the shot comes out of the ARM.
      //
      // It rides the RIGHT forearm because the PISTOL clips are what aim it: those clips put the
      // weapon in the right hand and brace it with the left, and the aim rig is solved for that
      // hand. The clips are used EXACTLY as authored — there is no procedural arm posing here, so
      // the arm the animation aims and the arm the barrel sits on have to be the same one.
      const L = this.mountOnForearm(w, "Right");
      const coreMat = new THREE.MeshBasicMaterial({ color: accentOf().lerp(new THREE.Color(0xffffff), 0.5), fog: false });
      const haloMat = new THREE.MeshBasicMaterial({
        color: accentOf(), transparent: true, opacity: 0.3,
        blending: THREE.AdditiveBlending, depthWrite: false, fog: false,
      });
      this.applyDissolve(coreMat);
      this.applyDissolve(haloMat);
      const bracer = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.1, L * 0.8), dark);
      bracer.position.set(0, 0.012, L * 0.45);
      const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.036, 0.05, L * 0.55, 8), dark);
      barrel.rotation.x = Math.PI / 2; // cylinder +Y → +Z (down the arm, toward the hand)
      barrel.position.set(0, 0.01, L + 0.06);
      const ring = new THREE.Mesh(new THREE.TorusGeometry(0.042, 0.011, 6, 10), coreMat); // charged throat
      ring.position.set(0, 0.01, L + 0.2);
      const halo = new THREE.Mesh(new THREE.SphereGeometry(0.05, 8, 6), haloMat);
      halo.position.set(0, 0.01, L + 0.22);
      const tip = new THREE.Mesh(new THREE.SphereGeometry(0.02, 6, 5), glow);
      tip.name = "muzzle";
      tip.position.set(0, 0.01, L + 0.26); // clear of the fist, so bolts don't spawn inside the hand
      w.add(bracer, barrel, ring, halo, tip);
      this.muzzle = tip;
      return;
    }
    this.bladeBaseMark = null;
    this.bladeTipMark = null;
    if (cls === "melee" && this.gsSword) {
      // 2H GREATSWORD (the Valheim mode — docs/2h-combat-spec.md): a HAND-HELD sword, not
      // the forearm construct — the 2H clips two-hand a hilt at the palm, so the prop rides
      // the default hand mount like the guns. Canonical frame: grip at the origin (where
      // the palm wraps), HANDLE along −Y (the pistol grip axis — the fingers' wrap), so the
      // BLADE extends +Y, out the thumb side of the fist. Dark body + accent energy core,
      // the house construct look.
      const accent = (this.materials[2] as THREE.MeshLambertMaterial).emissive;
      const coreMat = new THREE.MeshBasicMaterial({ color: accent.clone().lerp(new THREE.Color(0xffffff), 0.55), fog: false });
      this.applyDissolve(coreMat);
      const grip = new THREE.Mesh(new THREE.CylinderGeometry(0.024, 0.027, 0.34, 8), dark);
      grip.position.set(0, 0.02, 0); // two-hand handle: the off hand grabs below the palm
      const pommel = new THREE.Mesh(new THREE.SphereGeometry(0.036, 8, 6), dark);
      pommel.position.set(0, -0.17, 0);
      const guard = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.05, 0.06), dark);
      guard.position.set(0, 0.2, 0);
      const bladeLen = 1.18;
      const bladeMid = 0.24 + bladeLen / 2;
      const blade = new THREE.Mesh(new THREE.BoxGeometry(0.06, bladeLen, 0.016), dark);
      blade.position.set(0, bladeMid, 0);
      const core = new THREE.Mesh(new THREE.BoxGeometry(0.024, bladeLen, 0.02), coreMat);
      core.position.set(0, bladeMid, 0);
      const tip = new THREE.Mesh(new THREE.ConeGeometry(0.045, 0.16, 4), coreMat);
      tip.position.set(0, 0.24 + bladeLen + 0.07, 0); // cone +Y = blade direction already
      // Trail anchors — the streak samples the WHOLE blade, guard to apex.
      const baseMark = new THREE.Object3D();
      baseMark.position.set(0, 0.24, 0);
      const tipMark = new THREE.Object3D();
      tipMark.position.set(0, 0.24 + bladeLen + 0.15, 0);
      w.add(grip, pommel, guard, blade, core, tip, baseMark, tipMark);
      this.bladeBaseMark = baseMark;
      this.bladeTipMark = tipMark;
      this.muzzle = null;
      if (this.gsStowed) this.remountGreatsword(); // rebuilt mid-stow → straight to the back
      return;
    }
    if (cls === "melee") {
      // FOREARM LIGHT-BLADE — mounted on the FOREARM bone (not the hand) so it stays PARALLEL to the
      // forearm and rides it naturally; wrist rotation no longer swings it off-axis. The blade lies along
      // the forearm→hand axis (= the hand bone's fixed LOCAL offset, pose-independent), dropped to the
      // underside, lancing PAST the fist. "Made of light" = unlit MeshBasic core (always bright) + an
      // additive sheath halo, accent-tinted, both with the holster dissolve. Offsets/roll are tunable —
      // verify in the avatar harness.
      const fore = this.boneByName.get("RightForeArm")!;
      const hand = this.boneByName.get("RightHand")!;
      fore.add(w); // re-parent off the hand (set up generically above) onto the forearm
      const along = hand.position.clone().normalize();   // down-arm dir in forearm-LOCAL (toward the hand)
      const L = hand.position.length() || 0.26;          // forearm length
      // Orthonormal basis with local +Z = `along` (the blade's long axis); a roll about the arm puts the
      // blade on the underside (tuned in-harness). ref avoids degeneracy when `along` ≈ ±Y.
      const ref = Math.abs(along.y) > 0.9 ? new THREE.Vector3(0, 0, 1) : new THREE.Vector3(0, 1, 0);
      const bx = new THREE.Vector3().crossVectors(ref, along).normalize();
      const by = new THREE.Vector3().crossVectors(along, bx).normalize();
      const q = new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(bx, by, along)); // local +Z = along (elbow→hand)
      const BLADE_ROLL = 0; // roll about the arm axis (0 = blade vertical/edge-on). Tunable.
      if (BLADE_ROLL) q.multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), BLADE_ROLL));
      // applyArmOverrides re-copies this.weaponMount onto the weapon EVERY frame (~line 2467), so the
      // forearm-basis orientation must live in weaponMount — otherwise it's clobbered back to the pistol
      // mount, which (relative to the forearm) lands nearly anti-parallel to the arm = the "flipped" blade.
      this.weaponMount.copy(q);
      w.quaternion.copy(q);
      w.position.set(0, 0, 0); // at the forearm joint (elbow); geometry offsets place the blade (tip past the hand)

      const accent = (this.materials[2] as THREE.MeshLambertMaterial).emissive;
      const coreMat = new THREE.MeshBasicMaterial({ color: accent.clone().lerp(new THREE.Color(0xffffff), 0.6), fog: false });
      const sheathMat = new THREE.MeshBasicMaterial({ color: accent.clone(), transparent: true, opacity: 0.34, blending: THREE.AdditiveBlending, depthWrite: false, fog: false });
      this.applyDissolve(coreMat);
      this.applyDissolve(sheathMat);
      const UNDER = -0.045;       // drop to the underside of the forearm (−Y in the rolled basis)
      const backZ = 0.05;        // blade body starts just past the elbow
      const tipZ = L + 0.71;     // tip lances ~0.7 m past the wrist (+50% total length, user
      //                            call 2026-07-21 — bake-hitvolumes TIP_EXTRA mirrors this)
      const len = tipZ - backZ;
      const midZ = (backZ + tipZ) / 2;
      const core = new THREE.Mesh(new THREE.BoxGeometry(0.018, 0.05, len), coreMat);
      core.position.set(0, UNDER, midZ);
      const sheath = new THREE.Mesh(new THREE.BoxGeometry(0.042, 0.078, len), sheathMat);
      sheath.position.set(0, UNDER, midZ);
      const tip = new THREE.Mesh(new THREE.ConeGeometry(0.03, 0.13, 4), coreMat); // pointed light tip
      tip.rotation.x = Math.PI / 2; // cone +Y → +Z (toward the hand / forward)
      tip.position.set(0, UNDER, tipZ + 0.05);
      const emitter = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.06, 0.09), dark); // projector nub at the wrist
      emitter.position.set(0, UNDER * 0.6, backZ);
      // Trail anchors — the swing-streak ribbon (fx/bladeTrail.ts) samples these two world
      // points every render frame while an attack is live.
      const baseMark = new THREE.Object3D();
      baseMark.position.set(0, UNDER, backZ);
      const tipMark = new THREE.Object3D();
      tipMark.position.set(0, UNDER, tipZ + 0.115); // the light-tip's apex
      for (const glowPart of [emitter, sheath, core, tip]) glowPart.userData.lightBlade = true;
      w.add(emitter, sheath, core, tip, baseMark, tipMark);
      this.bladeBaseMark = baseMark;
      this.bladeTipMark = tipMark;
      this.muzzle = null;
    } else {
      // Built in a canonical frame: GRIP at the origin (where the hand wraps),
      // barrel pointing -Z (forward), top +Y. The group transform below rotates
      // this into the Manny hand_r bone so the grip sits in the palm.
      const M = (g: THREE.BufferGeometry, mat: THREE.Material): THREE.Mesh => new THREE.Mesh(g, mat);
      if (cls === "main") {
        // RIFLE: receiver + barrel + stock + grip + magazine.
        // FULL-SIZE rifle, BUILT AROUND THE CLIPS' OWN HANDS rather than the reverse: the firing
        // grip sits at the origin (hand_r) and the HANDGUARD is centred on the support hand at
        // −RIFLE_HAND_SPAN. The old mesh was both too small and axis-misaligned, so the back hand
        // missed the handle and the front hand floated beside the barrel.
        // Residual offset of the whole weapon from the hand line, measured identically on BOTH
        // hands (client/test/_riflegrip.mjs) — the mount fixes the AXIS, this seats the body on it.
        // Compact again (the full-size version put the support grip beyond the left arm's 0.43 m
        // reach, so the hand could never anchor).
        // BUILT INSIDE THE CLOSED FISTS. Measured (client/test/_fingergrip.mjs) in weapon space:
        // the right hand's fingers close around RIFLE_FIST_R and the left's around RIFLE_FIST_L.
        // Placing the grip at the weapon ORIGIN put it 16 cm behind and 7 cm below the actual
        // closed fist, so the hands read as held NEAR the gun rather than holding it. The wrist is
        // the IK target; the FIST is ~14 cm further along the hand, and that is what must contain
        // the geometry.
        // The two fists are laterally offset from each other (the hands wrap the gun from slightly
        // different sides), so a STRAIGHT weapon cannot pass through both centres. Run the body
        // down the mean offset — each hand then sits ~6 cm off the axis, which is what wrapping
        // fingers look like, instead of 29° of body rotation to fake alignment.
        const marks = Avatar.buildRifleMeshes(w, dark, glow);
        this.muzzle = marks.tip;
        this.bore = marks.bore;
      } else {
        // PISTOL: slide + grip + trigger guard, compact.
        const slide = M(new THREE.BoxGeometry(0.034, 0.05, 0.19), dark); slide.position.set(0, 0.035, -0.05);
        const grip = M(new THREE.BoxGeometry(0.032, 0.1, 0.045), dark); grip.position.set(0, -0.025, 0.025); grip.rotation.x = -0.32;
        const guard = M(new THREE.BoxGeometry(0.02, 0.03, 0.05), dark); guard.position.set(0, 0.0, -0.02);
        const tip = M(new THREE.SphereGeometry(0.018, 6, 5), glow); tip.name = "muzzle"; tip.position.set(0, 0.035, -0.16);
        w.add(slide, grip, guard, tip);
        this.muzzle = tip;
      }
    }
  }

  private bladeBaseMark: THREE.Object3D | null = null;
  private bladeTipMark: THREE.Object3D | null = null;

  /** The light blade's segment in the avatar's SCENE space (render space) — the swing-streak
   *  sampler. False when no blade is mounted. getWorldPosition refreshes the matrices, so
   *  this is exact whenever it's called in the frame. */
  bladeWorld(outBase: THREE.Vector3, outTip: THREE.Vector3): boolean {
    if (!this.bladeBaseMark || !this.bladeTipMark) return false;
    this.bladeBaseMark.getWorldPosition(outBase);
    this.bladeTipMark.getWorldPosition(outTip);
    return true;
  }

  /** The equipped construct's accent color (the light-blade tint) — the streak matches it. */
  bladeAccent(): THREE.Color {
    return (this.materials[2] as THREE.MeshLambertMaterial).emissive;
  }

  setAim(aiming: boolean, pitch: number): void {
    this.aiming = aiming;
    this.aimPitch = pitch;
  }

  /** The barrel's aim direction (world) — the heading/elevation the gun is solved to. The fired
   *  bolt travels along THIS from the muzzle, so it leaves the gun straight (matching the barrel)
   *  instead of veering toward the over-the-shoulder camera reticle. */
  aimForward(out: THREE.Vector3): THREE.Vector3 {
    return this.aimDirWorld(out);
  }

  /** SWORD RMB "focus" hold (the controller is camera-locked). Set separately from `aiming` because the
   *  sword must NOT fire the gun-aim chest layer (setAim stays gun-gated) — this only flips the sword's
   *  forward locomotion from the unarmed run to the Powerful Sword Run octagon. */
  setSwordFocus(on: boolean): void {
    if(this.swordFocus&&!on&&this.crystalBladeEnabled)this.endBladeAttack();
    this.swordFocus = on;
  }
  private swordFocus = false;
  private bladeRunPhase = 0;
  private readonly bladeStride = new BladeStride();
  private readonly solveBladeLeg = (thigh:string,calf:string,foot:string,target:THREE.Vector3,pole:THREE.Vector3):void => {
    this.ikLeg(thigh,calf,foot,target,pole,1);
  };

  /** SWORD AIR ATTACK aim PITCH (rad, look-down positive) — during an air attack the whole body pivots in
   *  3D toward the aim: yaw already comes from the body facing the camera, this tilts it up/down so the
  /** The camera-forward / reticle direction in WORLD space, reconstructed from the body facing.
   *  The body yaw-locks to the camera heading while aiming, and the over-shoulder offset cancels
   *  in the look direction, so camera-forward == heading pitched by aimPitch (pitch > 0 = down).
   *  This is exact — no need to pass the camera in. */
  private aimDirWorld(out: THREE.Vector3): THREE.Vector3 {
    return out.set(0, -Math.sin(this.aimPitch), -Math.cos(this.aimPitch)).applyQuaternion(this.group.quaternion).normalize();
  }

  // (setMelee/meleeSwing — the legacy procedural blade guard — deleted 2026-07-23 with the
  //  dead unarmed system: every caller passed false; remotes now relay setSwordFocus.)

  /** Soft additive glow sprites parented to the forearm/fist bones (ride the skinned
   *  animation, billboard to the camera). No bloom/outline pass exists in this engine —
   *  soft additive sprites are the glow idiom (cf. the jet flames). */
  private buildCombatGlow(): void {
    const tex = glowTexture();
    const mk = (): THREE.SpriteMaterial =>
      new THREE.SpriteMaterial({
        map: tex,
        color: COMBAT_GLOW_ORANGE.clone(),
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        fog: false,
      });
    this.glowArmMat = mk();
    this.glowFistMat = mk();
    const add = (boneName: string, mat: THREE.SpriteMaterial, size: number, into: THREE.Sprite[]): void => {
      const bone = this.boneByName.get(boneName);
      if (!bone) return;
      const s = new THREE.Sprite(mat);
      s.scale.setScalar(size);
      s.visible = false;
      bone.add(s);
      into.push(s);
    };
    add("LeftForeArm", this.glowArmMat, 0.22, this.glowArms);
    add("RightForeArm", this.glowArmMat, 0.22, this.glowArms);
    add("LeftHand", this.glowFistMat, 0.26, this.glowFists);
    add("RightHand", this.glowFistMat, 0.26, this.glowFists);
  }

  /** Drive the combat glows from combatK (orange arm aura), blockK (blue shield shell on the
   *  fists) and the jab one-shot (a fist flash). Hidden when not fighting or at far LOD. */
  private updateCombatGlow(): void {
    if (!this.glowArmMat || !this.glowFistMat) return;
    const aura = this.combatK;
    const shield = this.blockK;
    if (this.farLOD || (aura < 0.01 && shield < 0.01)) {
      for (const s of this.glowArms) s.visible = false;
      for (const s of this.glowFists) s.visible = false;
      return;
    }
    const pulse = 0.82 + 0.18 * Math.sin(this.idleT * 6); // subtle breathing
    const flash = this.punchT > 0 ? Math.min(1, this.punchT / 0.2) : 0; // jab impact pop
    const blockFlash = this.blockHitT > 0 ? Math.min(1, this.blockHitT / 0.18) : 0; // blocked-hit shield pop
    // Forearms: orange "fighting" aura, faded by combatK.
    this.glowArmMat.color.copy(COMBAT_GLOW_ORANGE);
    this.glowArmMat.opacity = aura * 0.45 * pulse;
    for (const s of this.glowArms) s.visible = true;
    // Fists: orange→blue as block ramps. Block is GLOW-ONLY (no clip), so the shield IS the read: a
    // blocked hit forces full blue + a bright pop; a jab flashes too.
    this.glowFistMat.color.copy(COMBAT_GLOW_ORANGE).lerp(COMBAT_GLOW_SHIELD, Math.max(shield, blockFlash));
    this.glowFistMat.opacity = Math.max(aura * 0.5, shield * 0.85) * pulse + flash * 0.8 + blockFlash * 0.7;
    const fs = 0.26 * (1 + shield * 0.5 + flash * 0.4 + blockFlash * 0.5);
    for (const s of this.glowFists) {
      s.visible = true;
      s.scale.setScalar(fs);
    }
  }

  /** Re-base the torso to a steady, mostly-forward fighting platform in the combat stance —
   *  ALWAYS (idle + moving), not just while moving. The legs supply a strongly bladed pelvis
   *  (fist_stance ~55°, or the squared-up pistol footwork when moving); left alone the torso
   *  inherits that whole turn and the arms ride off-axis. This yaws spine_01 to `combatBladeYaw`
   *  (absolute, relative to the body heading) so the torso faces the reticle with only a slight
   *  blade — the clip's relative arm/upper-spine guard rides on top, arms forward. World-space
   *  yaw at spine_01 only (never the pelvis/root), so it can't tilt the body. */
  private applyCombatBlade(): void {
    const k = this.combatK; // applies whenever in the combat stance (idle or moving)
    if (k < 0.01) return;
    const spine = this.boneByName.get("Spine"); // spine_01 — base of the torso
    if (!spine?.parent) return;
    this.group.updateMatrixWorld(true);
    // Keep the clip's spine_01 SWAY (breathing/lean) — its deviation from the bind rest — so the
    // re-based torso still breathes instead of locking rigid. Captured BEFORE we overwrite it.
    _hSway.copy(this.spineRestLocal).invert().multiply(spine.quaternion); // restLocal⁻¹ · clipLocal
    // Re-orient the torso BASE to a steady target: its UPRIGHT rest orientation (from bind: level,
    // no hunch, no roll), yawed by `combatBladeYaw`, in world — then re-apply the sway on top. This
    // replaces whatever turn the bladed pelvis (idle) or squared footwork (moving) would impose on
    // the torso, so the arms read forward instead of dragged around the hip blade, but still sway.
    this.group.getWorldQuaternion(_hDesired); // group world quat
    _hUp.set(0, 1, 0); // yaw axis in the GROUP-local frame = the planet up (a level turn)
    _hPitch.setFromAxisAngle(_hUp, this.combatBladeYaw);
    _hDesired.multiply(_hPitch).multiply(this.spineRestRelGroup).multiply(_hSway); // (group·bladeYaw·uprightRest)·sway
    spine.parent.getWorldQuaternion(_hPar).invert();
    _hLocal.copy(_hPar).multiply(_hDesired);
    spine.quaternion.slerp(_hLocal, k);
  }

  /**
   * How much of the CROUCHED ADS fold applies right now (0..1) — shared by the torso curl
   * (applyAdsStance) and the head cheek weld (applyHeadLook) so the two always agree.
   *
   * The extra fold belongs to a LEVEL or RAISED sight line. Aiming DOWN, the base ADS lean
   * already carries the torso over the gun, and stacking the crouch curl on top of it folded the
   * character face-first into the ground (measured at 26° of depression). So it tapers out
   * across the downward half of the pitch range. `aimPitch > 0` is looking DOWN.
   */
  private crouchAdsAmount(): number {
    if (this.crouchK < 1e-3) return 0;
    // The extra fold belongs to a LEVEL or RAISED sight line — see the taper note above.
    return this.crouchK * (1 - Math.max(0, Math.min(1, (this.aimPitch - 0.08) / 0.34)));
  }

  /**
   * Per-body share of the crouched ADS fold, split so the TORSO and the HEAD can be tuned apart.
   *
   * They were one number and that made them fight: the female's torso fold had to come down (three
   * user reports), but shrinking it also shrank the cheek weld — and the deep head tuck is the
   * whole reason the crouched pose reads as aimed at all (the gaze pin re-derives the skull from
   * the GROUP's rest orientation, so without the tuck the head stands vertical on a folded body).
   * The head keeps most of its tuck on both rigs; only the torso is pulled back.
   */
  private crouchAdsTorso(): number {
    return this.crouchAdsAmount() * (this.bodyModelId === "female" ? CROUCH_ADS_FEMALE : 1);
  }

  private crouchAdsHead(): number {
    return this.crouchAdsAmount() * (this.bodyModelId === "female" ? CROUCH_ADS_HEAD_FEMALE : 1);
  }

  /** Lock the HEAD onto the reticle — used by BOTH aim stances (gun aimK and unarmed combatK).
   *  Clips turn/bob the head with the gait or the DHTH stance; this re-aims it to the camera
   *  heading + aim pitch from the FIXED group-rest orientation (not the clip's animated head), so
   *  the head never wobbles and always faces the crosshair. World-space solve, eased by `weight`,
   *  so it overrides the clip regardless of the spine/neck pose underneath. */
  private applyHeadLook(weight: number): void {
    if (weight < 0.01) return;
    const head = this.boneByName.get("Head");
    if (!head?.parent) return;
    this.group.updateMatrixWorld(true);
    this.group.getWorldQuaternion(_hPitch); // group world quat (temp — reused as the pitch quat below)
    // Head at its REST orientation relative to the group = facing forward (the heading).
    _hDesired.copy(_hPitch).multiply(this.headRestRelGroup);
    // Group horizontal axes for the reticle pitch (extract BEFORE overwriting _hPitch).
    _hFwd.set(0, 0, -1).applyQuaternion(_hPitch);
    _hUp.set(0, 1, 0).applyQuaternion(_hPitch);
    _hRight.crossVectors(_hFwd, _hUp).normalize();
    // Pitch toward the reticle about the group's horizontal right axis (aimPitch>0 = look down).
    const pitch = Math.max(-0.8, Math.min(0.8, this.aimPitch));
    _hPitch.setFromAxisAngle(_hRight, -pitch);
    _hDesired.premultiply(_hPitch);
    // Convert to the head's local frame and ease in by `weight`.
    head.parent.getWorldQuaternion(_hPar).invert();
    _hLocal.copy(_hPar).multiply(_hDesired);
    head.quaternion.slerp(_hLocal, weight);
    // CHEEK WELD (rifle ADS): tuck the chin down toward the stock with the hunch. Composed AFTER
    // the gaze pin (which would otherwise level it back out) so the head visibly drops into the
    // sights — the single most readable "aimed" cue from the game's behind-the-shoulder camera.
    // CROUCHED it has to be much deeper: the gaze pin re-derives the head from the GROUP's rest
    // orientation, so it is blind to the crouch entirely and leaves the skull standing vertical
    // on a folded body. The tuck is what puts the head back over the gun.
    const tuck = this.aimPrefix === "r" && this.gunOut
      ? (this.adsHunch * this.adsHeadTuck
        + this.adsHunchCrouch * this.adsHeadTuckCrouch * this.crouchAdsHead()) * this.aimK
      : 0;
    if (Math.abs(tuck) > 1e-3) {
      _hPitch.setFromAxisAngle(_hRight, -tuck); // − = chin DOWN (sign verified in the lab)
      this.rotateBoneWorld("Head", _hPitch);
    }
  }

  /** IN CONVERSATION (ambient NPCs): swaps the stand to the relaxed talk
   *  idle + occasional gesture one-shots (npc_talk / npc_gesture1..3). */
  /** NPC ACTIVITY EMOTE (2026-07-31): a looping UAL activity clip that carries
   *  the STAND instead of the neutral idle (counter service, farm work,
   *  drinking, dancing…). "" clears. Movement fades it like any idle. */
  setEmote(key: string): void {
    if (key === this.emoteKey) return;
    const a = key ? this.acts[key] : null;
    if (key && a) {
      a.reset();
      // HOLD emotes (lie-down) play once and clamp on their final pose; the
      // rest loop (repetition reads as ambience).
      if (EMOTE_HOLD.has(key)) { a.setLoop(THREE.LoopOnce, 1); a.clampWhenFinished = true; }
      else a.setLoop(THREE.LoopRepeat, Infinity);
    }
    this.emoteKey = key && a ? key : "";
  }

  setTalking(active: boolean): void {
    this.talking = active;
    if (!active) {
      this.talkGestureKey = "";
      this.talkGestureT = 0;
    }
  }

  private companionWeight = 0;
  private companionTime = 0;
  /** A sparse palm presentation layered over the fresh locomotion pose. */
  setCompanionPose(weight: number, time: number, response = 0): void {
    if (weight <= 0 && this.companionWeight > 0) this.faceRig.setPerformance(null);
    this.companionWeight = weight;
    this.companionTime = time;
    if (weight > 0) {
      const murmuring = Math.max(0, Math.sin(time * .85)) ** 8;
      this.faceRig.setPerformance({
        face: { mouthSmileLeft: .13 * weight, mouthSmileRight: .13 * weight,
          jawOpen: (.045 * murmuring * (.5 + .5 * Math.sin(time * 13)) + response * .025) * weight },
        pitch: (Math.sin(time * 1.8) * .012 + response * .025) * weight,
      });
    }
  }

  private applyCompanionPose(): void {
    const w = this.companionWeight;
    if (w <= 0) return;
    this.group.updateMatrixWorld(true);
    _companionTarget.set(-.38, 1.17 + Math.sin(this.companionTime * 1.8) * .008, -.43)
      .applyMatrix4(this.group.matrixWorld);
    this.applyHumanoidIk("hand_l", _companionTarget, w, [-.7, -.8, .1]);
    const hand = this.boneByName.get("hand_l");
    if (hand?.parent) {
      // Manny's fingers run along +X and curl toward +Y (the palm normal).
      // Aim the fingers forward and the open palm upward in the player's frame.
      hand.parent.getWorldQuaternion(_companionParent).invert();
      this.group.getWorldQuaternion(_companionWorld);
      _companionWorld.multiply(_companionPalm);
      hand.quaternion.slerp(_companionParent.multiply(_companionWorld), w);
    }
    for (const finger of ["index", "middle", "ring", "pinky"]) for (let joint = 1; joint <= 3; joint++) {
      const bone = this.boneByName.get(`${finger}_0${joint}_l`);
      if (!bone) continue;
      _companionFinger.setFromAxisAngle(_companionZ, joint === 1 ? .12 : .08);
      bone.quaternion.slerp(_companionFinger, w);
    }
    this.group.updateMatrixWorld(true);
  }

  /** An in-place scene owns the social idle while active. Keep the requested activity
   * underneath so release restores it; never enable random talk gestures in this mode.
   * Gameplay/locomotion still have their normal higher priority in the pose selector. */
  setConversationPerformance(active: boolean, idle: "npc_talk" | "emo_foldarms" = "npc_talk"): void {
    this.conversationPerformance = active;
    this.conversationIdle = idle;
    if (active) {
      this.talkGestureKey = "";
      this.talkGestureT = 0;
      this.talkNextT = 2.5;
    }
  }

  /** AMBIENT persona (settlement NPCs): idle gesture one-shots fire on a
   *  relaxed cadence even outside conversations — weight shifts and
   *  look-arounds instead of a mannequin stand. Never set on the player. */
  setAmbient(active: boolean): void {
    this.ambient = active;
  }

  /** Keep only the currently blended clips active in the AnimationMixer.
   *  Ambient citizens load the same large library as the player but normally
   *  use one or two locomotion/social clips at once. */
  setSparseMixerActionsEnabled(enabled: boolean): void {
    if (enabled === this.sparseMixerActions) return;
    this.sparseMixerActions = enabled;
    this.sparseMixerActionKeys.clear();
    for (const key of Object.keys(this.acts) as ClipKey[]) {
      const action = this.acts[key]!;
      const weight = this.weights.get(key) ?? 0;
      if (!enabled || weight > 1e-4) {
        action.enabled = true;
        action.play();
        action.setEffectiveWeight(weight);
        if (enabled) this.sparseMixerActionKeys.add(key);
      } else {
        action.setEffectiveWeight(0);
        action.stop();
      }
    }
  }

  /** Runtime diagnostic gate used by the live crowd profiler. Re-enabling
   *  reseeds the drape so time spent suspended is not interpreted as motion. */
  setClothSimulationEnabled(enabled: boolean): void {
    if (enabled === this.clothSimulationEnabled) return;
    this.clothSimulationEnabled = enabled;
    if (enabled) this.clothSim?.reseed();
    for (const c of this.pbdCapes) if (enabled) c.reseed();
  }

  /** The collider set the PER-VERTEX cloth avoids: the same body capsules the bone-chain sim
   *  uses, plus whatever the outfit is currently wearing. Resolved to live bones, so they carry
   *  the animated pose. */
  private pbdCapsules(includeTorsoAndGear: boolean): { a: THREE.Object3D; b: THREE.Object3D; r: number }[] {
    const out: { a: THREE.Object3D; b: THREE.Object3D; r: number }[] = [];
    const add = (an: string, bn: string, r: number) => {
      const a = this.boneByName.get(an), b = this.boneByName.get(bn);
      if (a && b) out.push({ a, b, r });
    };
    // With BVH mesh colliders active only the TORSO capsule and the gear approximations are
    // dropped — the real surfaces replace them. Everything else stays capsule: limbs genuinely
    // are capsule shaped and move fastest, and the deltoid/armpit is a CREASED junction —
    // putting the upper arm into the mesh snapshot creates a concave wedge where closest-point
    // queries read free-space hem verts as "inside" (measured 44–106 mm of false violation at
    // run/sprint), which no normal trick fixes. Mesh for smooth surfaces, capsules for creases.
    for (const [an, bn, r] of CAPSULES) {
      if (!includeTorsoAndGear && an === "pelvis") continue;
      add(an, bn, r);
    }
    if (includeTorsoAndGear && this.backGearWorn) add("spine_01", "spine_03", 0.215);
    // The pauldron ALWAYS collides as capsules, even on the BVH path: it is a THIN SHELL, and
    // closest-point sign queries on a thin plate are inherently ambiguous (a cape vert outside
    // the plate reads "inside" whenever the query lands on the plate's inner face — measured as
    // 90–155 mm phantom violations). Proxies for plates, mesh for closed smooth surfaces.
    if (this.shoulderGearWorn) { add("clavicle_l", "upperarm_l", 0.105); add("clavicle_r", "upperarm_r", 0.105); }
    return out;
  }

  /** Build BVH colliders over the real surfaces the cape can touch: the body's torso band
   *  (arms/head excluded — limbs keep capsules, and the simulated cloth never reaches the
   *  head; everything above the collar split is worn, not cloth) plus any worn shoulder
   *  plate. See clothBvh.ts for the cost model. */
  private buildPbdMeshColliders(): { col: SkinnedBvhCollider; margin: number }[] {
    const out: { col: SkinnedBvhCollider; margin: number }[] = [];
    // The body snapshot is the SMOOTH TORSO TUBE only: y band above the crotch split and below
    // the neck, arms excluded entirely. Arms in the snapshot were tried and REVERTED twice —
    // excluding them (dominant-bone test) also dropped the upperarm_twist-weighted chest and
    // left ~370 tris, while including them created the armpit wedge (see pbdCapsules). The
    // deltoid is covered by the clavicle→upperarm capsules, the crotch/thighs by leg capsules.
    const isLimbOrHead = (n: string): boolean =>
      /^(lowerarm|hand|thumb|index|middle|ring|pinky|head|eye|upperarm_l$|upperarm_r$)/.test(n);
    for (const m of this.glbBody?.meshes ?? []) {
      if (((m.geometry.attributes.position as THREE.BufferAttribute | undefined)?.count ?? 0) < 300) continue;
      const col = new SkinnedBvhCollider(m, { yMin: 0.95, yMax: 1.52, excludeBone: isLimbOrHead, minTris: 40 });
      if (col.triCount) out.push({ col, margin: 0.008 });
    }
    // (The pauldron is deliberately NOT here — thin shells are capsule territory, see
    // pbdCapsules. Add future pieces only if they are closed, smooth surfaces.)
    return out;
  }

  /** (Re)resolve the PBD capes' collider set. Called when a cape attaches, a worn piece or the
   *  body finishes loading (signature check in the step block), or the BVH flag flips. */
  private refreshPbdColliders(): void {
    this.pbdColliderSig = `${this.glbBody?.meshes.length ?? 0}:${this.styledSkinned.length}:${this.pbdBvh}`;
    if (!this.pbdCapes.length) { this.pbdMeshCols = []; return; }
    const meshes = this.pbdBvh ? this.buildPbdMeshColliders() : [];
    this.pbdMeshCols = meshes.map((m) => m.col);
    // body not loaded yet → keep the full capsule set as the fallback; the signature moves
    // when it lands and this re-runs
    const capsules = this.pbdCapsules(!this.pbdBvh || meshes.length === 0);
    for (const c of this.pbdCapes) c.setColliders(capsules, meshes);
  }

  /** Collider source for the PBD path. true = BVH over the real body/armour surfaces
   *  (three-mesh-bvh); false = the hand-placed capsule set. */
  setPbdBvh(on: boolean): void {
    this.pbdBvh = on; // signature check in the step block rebuilds on the next frame
  }

  get pbdBvhEnabled(): boolean { return this.pbdBvh; }

  /** DEBUG (probes): worst current violation of the real-surface constraints across capes. */
  pbdDebugViolationMM(): number {
    let worst = 0;
    for (const cape of this.pbdCapes) worst = Math.max(worst, cape.sim.debugWorstMeshViolationMM());
    return +worst.toFixed(2);
  }

  /** Per-frame cost of the BVH collider pipeline (ms totals since last read; divide by
   *  samples/contactN for per-frame numbers). */
  pbdClothStats(reset = true): {
    capes: number; colliders: number; tris: number; samples: number;
    skinMs: number; refitMs: number; contactMs: number; contactN: number;
  } {
    let skin = 0, refit = 0, samples = 0, tris = 0, contact = 0, cn = 0;
    for (const c of this.pbdMeshCols) {
      skin += c.skinMs; refit += c.refitMs; samples = Math.max(samples, c.samples); tris += c.triCount;
    }
    for (const cape of this.pbdCapes) { contact += cape.sim.contactMs; cn = Math.max(cn, cape.sim.contactN); }
    const out = { capes: this.pbdCapes.length, colliders: this.pbdMeshCols.length, tris, samples,
      skinMs: skin, refitMs: refit, contactMs: contact, contactN: cn };
    if (reset) {
      for (const c of this.pbdMeshCols) { c.skinMs = 0; c.refitMs = 0; c.samples = 0; }
      for (const cape of this.pbdCapes) { cape.sim.contactMs = 0; cape.sim.contactN = 0; }
    }
    return out;
  }

  /** Choose the cloth solver. false = the shipped bone-chain cage (cloth.ts); true = per-vertex
   *  PBD on the garment mesh (clothPbd.ts). The split happens when a garment is mounted, so this
   *  re-applies the current style. Both paths remain available at runtime. */
  setPbdCloth(on: boolean): void {
    if (on === this.pbdCloth) return;
    this.pbdCloth = on;
    const style = this.lastStyle;
    this.styleKey = ""; // force a rebuild — mounting is where the mesh split happens
    if (style) this.setStyle(style);
  }

  /** Which cloth solver is active. */
  get pbdClothEnabled(): boolean { return this.pbdCloth; }

  /** Runtime diagnostic gate used to isolate avatar shadow-pass cost. Preserve
   *  each mesh's authored/default caster state instead of making every cosmetic
   *  cast when the override is released. */
  setShadowsEnabled(enabled: boolean): void {
    if (enabled === this.shadowsEnabled) return;
    this.shadowsEnabled = enabled;
    this.group.traverse((o) => {
      if (!(o instanceof THREE.Mesh)) return;
      if (o.userData.avatarShadowDefault === undefined) o.userData.avatarShadowDefault = o.castShadow;
      o.castShadow = enabled && o.userData.avatarShadowDefault === true;
    });
  }

  /** SOCIAL HEAD LOOK: ease the head toward a RENDER-space point without
   *  turning the body (an NPC glancing at a passing player). Null clears;
   *  targets far behind the shoulders fade out instead of owl-necking. */
  setHeadLookTarget(pos: THREE.Vector3 | null, fromHead = false): void {
    this.headLookHas = pos !== null;
    this.headLookFromHead = fromHead;
    if (pos) this.headLookTgt.copy(pos);
  }

  /** SOCIAL head look: ease the head toward `headLookTgt` (render space),
   *  clamped so the neck never owls past the shoulders; targets behind
   *  fade the look out instead. Weight eases so glances turn smoothly. */
  private applyHeadLookAt(dt: number): void {
    const head = this.boneByName.get("Head");
    if (!head?.parent) return;
    let want = this.headLookHas ? 1 : 0;
    let targetYaw = 0;
    let targetPitch = 0;
    if (this.headLookHas) {
      this.group.updateMatrixWorld(true);
      this.group.getWorldQuaternion(_hPar);
      // Target direction in the GROUP frame (fwd = −Z).
      if (this.conversationPerformance || this.headLookFromHead) head.getWorldPosition(_hPos);
      else this.group.getWorldPosition(_hPos);
      _hFwd.copy(this.headLookTgt).sub(_hPos);
      const inv = _hLocal.copy(_hPar).invert();
      _hFwd.applyQuaternion(inv);
      const len = _hFwd.length();
      if (len < 0.5) want = 0;
      else {
        targetYaw = Math.atan2(_hFwd.x, -_hFwd.z);
        targetPitch = Math.asin(Math.max(-1, Math.min(1, _hFwd.y / len)));
        if (Math.abs(targetYaw) > 1.45) want = 0; // behind the shoulders
        targetYaw = Math.max(-1.05, Math.min(1.05, targetYaw));
        targetPitch = Math.max(-0.55, Math.min(0.55, targetPitch));
      }
    }
    if (!want) { targetYaw = 0; targetPitch = 0; }
    // The player and analytic NPC poses can update on different clocks. Low-pass
    // the ANGLES (shortest yaw arc), not just the final blend weight, so a
    // sub-frame target step cannot whip the head between absolute solutions.
    const angleEase = 1 - Math.exp(-10 * dt);
    let yawDelta = (targetYaw - this.headLookYaw) % (Math.PI * 2);
    if (yawDelta > Math.PI) yawDelta -= Math.PI * 2;
    if (yawDelta < -Math.PI) yawDelta += Math.PI * 2;
    this.headLookYaw += yawDelta * angleEase;
    this.headLookPitch += (targetPitch - this.headLookPitch) * angleEase;
    const yaw = this.headLookYaw;
    const pitch = this.headLookPitch;
    this.eyeLead.set(yaw, pitch); // eyes lead the head (applyEyeLook)
    this.headLookK += (want - this.headLookK) * Math.min(1, dt * 6);
    if (this.headLookK < 0.02) return;
    this.group.getWorldQuaternion(_hPitch); // group world quat
    _hDesired.copy(_hPitch).multiply(this.headRestRelGroup);
    _hUp.set(0, 1, 0).applyQuaternion(_hPitch);
    _hFwd.set(0, 0, -1).applyQuaternion(_hPitch);
    _hRight.crossVectors(_hFwd, _hUp).normalize();
    _hPitch.setFromAxisAngle(_hUp, -yaw); // yaw is right-positive; +up rotation turns left
    _hDesired.premultiply(_hPitch);
    _hPitch.setFromAxisAngle(_hRight, pitch);
    _hDesired.premultiply(_hPitch);
    head.parent.getWorldQuaternion(_hPar).invert();
    _hLocal.copy(_hPar).multiply(_hDesired);
    head.quaternion.slerp(_hLocal, this.headLookK * 0.85);
  }

  /** PROCEDURAL ARM TWIST: distribute wrist/shoulder roll onto the arm twist bones, which
   *  carry skin weights along the limb (the LBS candy-wrapper fix). UE-native clips animate
   *  these bones but Mixamo retargets leave them untracked — driving them EVERY frame from
   *  the live pose keeps the deformation uniform across the whole library and across clip
   *  switches (an untracked bone would otherwise stay frozen at the last UE clip's twist).
   *  Runs after all arm modifiers so aim/reload/punch poses feed it too. */
  private applyArmTwist(): void {
    for (const s of ["_l", "_r"]) {
      // forearm flesh follows the WRIST roll, ramping up toward the hand
      // Fractions LOWERED 0.66/0.33 → 0.30/0.15 (A/B'd by screenshot at the rifle hold, where the
      // wrist carries ~55° of authored roll): the auto-skinned twist weights are broad, so heavy
      // distribution thinned the WHOLE forearm ("right forearm is VERY thin holding a rifle") —
      // LBS loses volume wherever the twist spreads. Lighter fractions keep the forearm full; the
      // residual concentrates at the wrist crease, which this low-poly mesh hides completely.
      this.writeTwist("hand" + s, "lowerarm_twist_01" + s, "lowerarm_twist_02" + s, 0.30, 0.15);
      // deltoid flesh follows only part of the upper-arm roll (counter-rotation, shoulder-most least)
      this.writeTwist("upperarm" + s, "upperarm_twist_01" + s, "upperarm_twist_02" + s, -0.67, -0.33);
    }
  }

  /** Swing-twist decomposition: the source bone's bind-relative roll about its own +X (the
   *  bone axis) written into two twist bones as fractions f1/f2 of the angle. */
  private writeTwist(srcName: string, t1: string, t2: string, f1: number, f2: number): void {
    const src = this.boneByName.get(srcName);
    const bind = this.twistBind.get(srcName);
    if (!src || !bind) return;
    _twq.copy(bind).invert().multiply(src.quaternion);
    let ang = 2 * Math.atan2(_twq.x, _twq.w); // twist about +X; (−2π, 2π] -> (−π, π]
    if (ang > Math.PI) ang -= 2 * Math.PI;
    else if (ang < -Math.PI) ang += 2 * Math.PI;
    // Cap near the wraparound only. The old ±60° cap was meant to stop "candy wrapper"
    // collapse, but it CAUSED one for the rifle: the r_* clips author the wrist at ~121° of
    // local-X roll (their grip frame), and with the twist bones capped at 60° the last third
    // of the forearm absorbed an 81° skinning jump — the "right forearm is very skinny holding
    // a rifle" report. Distributing the REAL angle keeps the gradient smooth (0→80→121°); the
    // genuine collapse hazard is the ±180° sign flip, so the cap now sits just below it.
    ang = THREE.MathUtils.clamp(ang, -2.4, 2.4);
    for (const [name, f] of [[t1, f1], [t2, f2]] as const) {
      const b = this.boneByName.get(name);
      const tb = this.twistBind.get(name);
      if (!b || !tb) continue;
      const h = (ang * f) / 2;
      _twq2.set(Math.sin(h), 0, 0, Math.cos(h));
      b.quaternion.copy(tb).multiply(_twq2);
    }
  }

  /** FACE LIFE: advance the FaceRig channels (emote / visemes / auto-blink / capture) and
   *  write blendshape influences onto the face meshes, then compose the rig's head offset
   *  (talk nods, captured head motion) onto the head bone AFTER every head-look solve.
   *  A live reticle aim wins — the offset fades out with the head-lock weight. */
  private applyFace(dt: number, headLockK: number): void {
    if (this.farLOD || !this.glbBodyActive) return;
    this.faceRig.update(dt, this.faceWrite);
    const off = this.faceRig.headOffset;
    const k = 1 - headLockK;
    if (Math.abs(off.pitch) + Math.abs(off.yaw) + Math.abs(off.roll) < 0.002 || k <= 0.01) return;
    const head = this.boneByName.get("head");
    if (!head) return;
    // head-local frame = the eye axes' frame (face +Z natural); pitch +down matches nod
    _fhQ.setFromAxisAngle(this.eyeRightLocal, off.pitch * k);
    _fhQ2.setFromAxisAngle(this.eyeUpLocal, -off.yaw * k);
    _fhQ.multiply(_fhQ2);
    head.quaternion.multiply(_fhQ);
  }
  private readonly faceWrite = (name: string, v: number): void => this.setFaceInfluence(name, v);

  /** EYE LIFE: the procedural eye bones (appended after the Manny bones; the eyeball meshes
   *  are weighted 100% to them) get a subtle gaze layer — they lead the social head-look and
   *  dart in small idle saccades between refixations. No clip animates them, so their locals
   *  are ours to own. Angles are face-relative: +yaw = character's right, +pitch = up. */
  private applyEyeLook(dt: number): void {
    const el = this.boneByName.get("eye_l");
    const er = this.boneByName.get("eye_r");
    if (!el || !er || this.farLOD) return;
    this.eyeSacT -= dt;
    if (this.eyeSacT <= 0) {
      this.eyeSacT = 0.7 + Math.random() * 2.4;
      this.eyeSac.set((Math.random() - 0.5) * 0.22, (Math.random() - 0.5) * 0.1);
    }
    // captured GAZE (webcam / clip playback) overrides the idle saccade layer outright.
    // Clamped tighter than the saccade range and ducked toward centre as the lids close —
    // the iris bulge otherwise pokes through half-closed lids / the brow at gaze extremes.
    const gz = this.faceRig.capturedGaze;
    let gzYaw = 0, gzPitch = 0;
    if (gz) {
      const blink = Math.max(this.getFaceInfluence("eyeBlinkLeft"), this.getFaceInfluence("eyeBlinkRight"));
      const duck = 1 - Math.min(1, blink) * 0.75;
      gzYaw = Math.max(-0.24, Math.min(0.24, gz.yaw)) * duck;
      gzPitch = Math.max(-0.15, Math.min(0.15, gz.pitch)) * duck;
    }
    const lead = this.headLookK > 0.05 ? 0.5 : 0;
    // A full brow-down narrows the socket laterally. Contract horizontal gaze only during
    // that expression so the sclera cannot overtake either outer corner; neutral gaze keeps
    // its full range.
    const browDown = Math.max(this.getFaceInfluence("browDownLeft"), this.getFaceInfluence("browDownRight"));
    const browGazeDuck = 1 - Math.min(1, browDown);
    const yaw = Math.max(-0.26, Math.min(0.26, gz ? gzYaw : this.eyeLead.x * lead + this.eyeSac.x)) * browGazeDuck;
    const pitch = Math.max(-0.16, Math.min(0.16, gz ? gzPitch : this.eyeLead.y * lead + this.eyeSac.y));
    // face axes in head-local; eye local rest = identity (head-aligned), so this composes
    // directly. +about-up turns to the character's LEFT, so negate yaw (right-positive).
    composeEyeLook(_eyeQA, _eyeQB, this.eyeUpLocal, this.eyeRightLocal, yaw, pitch);
    const k = 1 - Math.exp(-28 * dt); // saccadic: eyes snap fast
    el.quaternion.slerp(_eyeQA, k);
    er.quaternion.slerp(_eyeQA, k);
  }

  private captureEyePlacement(): void {
    for (const [i, name] of ["eye_l", "eye_r"].entries()) {
      const bone = this.boneByName.get(name);
      if (bone) this.eyePivotRest[i].copy(bone.position);
    }
  }

  /** Runs after the final debug/animation rotations, with no accumulated offset. */
  private applyAuthoredEyePivots(): void {
    for (const [i, name] of ["eye_l", "eye_r"].entries()) {
      const bone = this.boneByName.get(name);
      if (!bone) continue;
      this.eyePivotOffset.copy(this.eyeRightLocal).multiplyScalar(this.headEyeOffset * (i === 0 ? -1 : 1));
      fitEyePivot(bone, this.eyePivotRest[i], this.eyePivotOffset);
    }
  }

  /** Re-apply all global rig tunes to this avatar's skeleton (see setGlobalRigTune). Resets
   *  to the captured bind base first, so repeated application is idempotent. */
  applyRigTunes(): void {
    // The full rig rebuild below restores authored scales outright, so any
    // transient IK multiplier has already been discarded and must not be
    // divided out on the next frame.
    this.underhandStretchAppliedL = 1;
    this.underhandStretchAppliedR = 1;
    this.underhandStretchL = 1;
    this.underhandStretchR = 1;
    const bones = this.skeleton.bones;
    for (let i = 0; i < bones.length; i++) {
      bones[i].position.copy(this.rigTuneBase[i].pos);
      bones[i].scale.set(1, 1, 1);
      this.skeleton.boneInverses[i].copy(this.rigTuneBase[i].inv);
    }
    for (const [name, tune] of RIG_TUNES) {
      const bone = this.boneByName.get(name);
      if (!bone) continue;
      const i = bones.indexOf(bone);
      if (i < 0) continue;
      const base = this.rigTuneBase[i];
      const p = _rtP.setFromMatrixPosition(base.world);
      // NATURAL frame -> bind space: the rig mounts under RIG_YAW (pi about Y), so X and Z
      // axes/offsets negate. Rotation applies about the (moved) joint position.
      const rot = tune.rot ?? [0, 0, 0];
      _rtE.set((-rot[0] * Math.PI) / 180, (rot[1] * Math.PI) / 180, (-rot[2] * Math.PI) / 180, "XYZ");
      const dp = _rtD.set(-(tune.pos?.[0] ?? 0), tune.pos?.[1] ?? 0, -(tune.pos?.[2] ?? 0));
      // new bind world = T(p+dp) . R . T(-p) . baseWorld -> boneInverses = inverse
      _rtM.makeRotationFromEuler(_rtE);
      _rtM2.makeTranslation(-p.x, -p.y, -p.z);
      _rtM.multiply(_rtM2);
      _rtM2.makeTranslation(p.x + dp.x, p.y + dp.y, p.z + dp.z);
      _rtM2.multiply(_rtM).multiply(base.world);
      this.skeleton.boneInverses[i].copy(_rtM2).invert();
      // pivot move: this bone's local position shifts; child pivots stay put (landmark
      // semantics). Rotations are NOT written to locals — clips own them; bind carries it.
      if (tune.pos) {
        const parent = bone.parent;
        const pi = parent && (parent as THREE.Bone).isBone ? bones.indexOf(parent as THREE.Bone) : -1;
        _rtQ.setFromRotationMatrix(pi >= 0 ? this.rigTuneBase[pi].world : _rtYawM);
        bone.position.copy(this.rigTuneBase[i].pos).add(_rtV.copy(dp).applyQuaternion(_rtQ.invert()));
        _rtQ.setFromRotationMatrix(base.world).invert();
        _rtV.copy(dp).applyQuaternion(_rtQ);
        for (const c of bone.children) {
          if (!(c as THREE.Bone).isBone) continue;
          const ci = bones.indexOf(c as THREE.Bone);
          if (ci >= 0) c.position.copy(this.rigTuneBase[ci].pos).sub(_rtV);
        }
      }
      if (tune.scale !== undefined && tune.scale !== 1) bone.scale.setScalar(tune.scale);
    }

    // The rig's long bones use local X as their length axis. Applying length/width once at
    // each chain root moves its descendants without multiplying the value once per segment;
    // Y/Z scale the limb radially. Skinned clothing shares this skeleton and follows too.
    const scale = (name: string, x: number, y: number, z: number): void => {
      const bone = this.boneByName.get(name);
      if (bone) bone.scale.multiply(_rtV.set(x, y, z));
    };
    const p = this.proportions;
    scale("head", p.headSize, p.headSize, p.headSize);
    for (const side of ["l", "r"] as const) {
      scale(`clavicle_${side}`, p.shoulderWidth, 1, 1);
      scale(`upperarm_${side}`, p.armLength, p.armThickness, p.armThickness);
      scale(`thigh_${side}`, p.legLength, p.legThickness, p.legThickness);
      scale(`hand_${side}`, p.handSize, p.handSize, p.handSize);
      scale(`foot_${side}`, p.footSize, p.footSize, p.footSize);
    }
    this.captureEyePlacement();
    this.basePose?.capturePlacement();
  }

  /** Set this avatar's own body proportions without affecting any other live avatar. */
  setProportions(values: Partial<AvatarProportions>): void {
    const clamp = (value: number | undefined, fallback: number): number =>
      Number.isFinite(value) ? THREE.MathUtils.clamp(value!, 0.7, 1.3) : fallback;
    const next = { ...this.proportions };
    for (const key of Object.keys(DEFAULT_AVATAR_PROPORTIONS) as (keyof AvatarProportions)[]) {
      next[key] = clamp(values[key], next[key]);
    }
    this.proportions = next;
    this.applyRigTunes();
  }

  getProportions(): Readonly<AvatarProportions> {
    return this.proportions;
  }

  /** Freeze the skeleton after animation/IK (null = live animation). Bind/A/T/UP
   * are exact calibration poses; portrait adds the relaxed neck alignment for
   * character creation while retaining a level, still face. */
  setDebugPose(pose: DebugPose | null): void {
    this.debugPose = pose;
  }

  /** Exposed-bike rider mode. The procedural pose is applied after normal
   * animation, so authored mount clips can replace it later. */
  /** Driver-side ride speed as a 0..1 fraction of the vehicle's top speed; the
   * mounted pose eases toward a deeper hunch as this rises. */
  setMountedSpeed(k: number): void {
    this.mountedSpeedK = Math.max(0, Math.min(1, k));
  }

  /** Signed cornering load, -1 (hard left) .. 1 (hard right). The rider shifts
   * weight INTO the turn: torso roll, hips sliding the same way, and asymmetric
   * pressure on the bars. Driven from lateral acceleration, not steering input,
   * so it answers what the vehicle is actually doing. */
  setMountedLean(k: number): void {
    this.mountedLeanTarget = Math.max(-1, Math.min(1, k));
  }

  /** Boost, 0..1 — the rider hunkers down behind the cowl. */
  setMountedBoost(k: number): void {
    this.mountedBoostTarget = Math.max(0, Math.min(1, k));
  }

  /** Override the ride-pose tuning (racing-hunch depth + head lift, radians).
   * Used by the stance editor to preview values before they are baked into the
   * MOUNT_HUNCH_MAX / MOUNT_HEAD_LIFT constants. */
  setMountTuning(hunchMax: number, headLift: number): void {
    this.mountHunchMax = hunchMax;
    this.mountHeadLift = headLift;
  }

  setMounted(mounted: boolean, pose?: VehicleRiderPoseDef): void {
    this.setMountedBlend(mounted ? 1 : 0, pose);
  }

  /** Blend normal locomotion into the authored rider pose during root-warped
   * mount/dismount transitions. A full 1 is identical to setMounted(true). */
  setMountedBlend(blend: number, pose?: VehicleRiderPoseDef): void {
    if(blend>0)this.setWallRun(0);
    const k = THREE.MathUtils.clamp(blend, 0, 1);
    const active = k > 0.001;
    this.mountedBlend = k;
    this.mountedPose = active ? pose ?? null : null;
    if (active === this.mounted) return;
    this.mounted = active;
    if (!active) { this.mountedSpeedK = 0; this.mountedLeanTarget = 0; this.mountedBoostTarget = 0; }
    // Foot grounding is bypassed while mounted, so its smoothed slope, plant,
    // pelvis, and knee-pole state would otherwise remain frozen for the entire
    // ride and distort the first standing frames after dismounting.
    this.resetFootGrounding();
    if (active) {
      this.setWeapon(null);
      this.setJet("none");
      this.setAim(false, 0);
      this.resetCloth();
    }
  }

  /** Halo-style side mount: progress is only the seat<->ground portion (the
   * preceding approach walk is 0). Negative side means boarding from local
   * left, which swings the right/outside leg over the saddle. */
  setVehicleMountSwing(progress: number | null, side = -1, reverse = false): void {
    this.vehicleMountSwing = progress === null ? 0 : THREE.MathUtils.clamp(progress, 0, 1);
    this.vehicleMountSide = side === 0 ? -1 : Math.sign(side);
    this.vehicleMountSwingReverse = reverse;
  }

  private resetFootGrounding(): void {
    this.groundIkK = 0;
    this.ikPelvis = 0;
    this.ikSlope[0] = this.ikSlope[1] = 0;
    this.ikFootW[0] = this.ikFootW[1] = 0;
    this.ikPole[0].set(0, 0, 0);
    this.ikPole[1].set(0, 0, 0);
    this.ikCenterNormal.set(0, 1, 0).applyQuaternion(this.group.quaternion).normalize();
  }

  /** Build a calibration pose as absolute bone LOCAL rotations. "bind" is the authored
   *  mesh exactly; A/T/UP re-aim each arm segment (upperarm->lowerarm->hand) at a world
   *  direction via minimal swings from the bind pose, chained down the arm. */
  private buildDebugPose(name: DebugPose): Map<number, THREE.Quaternion> {
    const locals = new Map<number, THREE.Quaternion>();
    this.bodyRig.forEach((d, i) => locals.set(i, new THREE.Quaternion(d[5], d[6], d[7], d[8])));
    if (name === "bind" || name === "portrait") return locals;
    const idx = (n: string): number => this.skeleton.bones.findIndex((b) => b.name === n);
    const wq = (i: number) => new THREE.Quaternion().setFromRotationMatrix(this.rigTuneBase[i].world);
    const wp = (i: number) => new THREE.Vector3().setFromMatrixPosition(this.rigTuneBase[i].world);
    for (const side of ["l", "r"] as const) {
      const chain = [`upperarm_${side}`, `lowerarm_${side}`, `hand_${side}`];
      const ends = [`lowerarm_${side}`, `hand_${side}`, `middle_metacarpal_${side}`];
      // this side's outward sign from its own bind arm direction (frame-agnostic)
      const sgn = Math.sign(wp(idx(`lowerarm_${side}`)).x - wp(idx(`upperarm_${side}`)).x) || 1;
      const target =
        name === "t" ? new THREE.Vector3(sgn, 0, 0)
          : name === "a" ? new THREE.Vector3(sgn * 0.707, -0.707, 0)
            : new THREE.Vector3(sgn * 0.17, 0.985, 0).normalize(); // "up": arms overhead
      let parentQ = wq(idx(`clavicle_${side}`)); // clavicle stays at bind
      for (let k = 0; k < chain.length; k++) {
        const bi = idx(chain[k]);
        // Transport the child's rest frame with its already-posed parent.
        // Independently swinging every segment from world bind introduces an
        // artificial forearm roll (about 83 degrees in the male overhead pose).
        const row = this.bodyRig[bi];
        const newWorld = parentQ.clone().multiply(new THREE.Quaternion(row[5], row[6], row[7], row[8]));
        const cur = wp(idx(ends[k])).sub(wp(bi)).normalize()
          .applyQuaternion(wq(bi).invert()).applyQuaternion(newWorld);
        const swing = new THREE.Quaternion().setFromUnitVectors(cur, target);
        newWorld.premultiply(swing);
        locals.set(bi, parentQ.clone().invert().multiply(newWorld));
        parentQ = newWorld;
      }
    }
    return locals;
  }

  private applyDebugPose(): void {
    if (!this.debugPose) return;
    let pose = this.debugPoseCache.get(this.debugPose);
    if (!pose) {
      pose = this.buildDebugPose(this.debugPose);
      this.debugPoseCache.set(this.debugPose, pose);
    }
    const bones = this.skeleton.bones;
    for (const [i, q] of pose) bones[i].quaternion.copy(q);
    // a solo'd keepHipsPos clip may have displaced the pelvis; hold body offsets neutral too
    bones[1].position.set(this.bodyRig[1][2], this.bodyRig[1][3], this.bodyRig[1][4]);
    this.body.position.y = 0;
    this.body.rotation.x = 0;
    if (this.debugPose === "portrait") this.alignRelaxedPosture(1, 0);
  }

  /** UNARMED DHTH fighting stance (RMB while empty-handed): clip-driven upper-body combat.
   *  Currently UNFINISHED/disabled in play — see UNARMED_COMBAT_ENABLED (main.ts) + setWalkMode. */
  setCombat(active: boolean): void {
    this.combatActive = active;
  }

  /** RMB-while-unarmed SLOW WALK stance (the current unarmed behavior): the 8-way omnidirectional walk
   *  blendspace (camera-locked, no combat clips/glow). Mutually exclusive with setCombat. */
  setWalkMode(active: boolean): void {
    this.walkActive = active;
  }

  /** Hold-to-block. In the DHTH fighting stance: the full-body DOUBLE-GUARD (dh_block) + the blue
   *  shield GLOW, movement rooted. In the SHIELD stance (walkMode): the kickbox fighter's clip set —
   *  anim_idle standing, the anim_walk_<DIR> family moving, and it does NOT root (backing away behind
   *  the guard is the whole point of the construct).
   *  A well-timed press is a PARRY (see parry()); a hit while holding is a block (low damage + blockHit). */
  setBlocking(blocking: boolean): void {
    this.blocking = blocking;
  }

  /**
   * SHIELD GUARD walk phases. The kickbox walk family is authored per octant as
   *   step-off (_start) → loop → settle (_stop),
   * and the loop only reads right when it is entered and left through those: dropped into cold, its
   * first stride slides, and cutting it dead on release leaves the fighter mid-air-step. So the guard
   * runs a phase machine — stand → step off into an octant → loop it → settle out of it → stand.
   *
   * Turning mid-walk does NOT restart the step-off: the loops cross-blend through dirBlend (the
   * pack's octants are 45° apart and blend cleanly), and re-planting a step every time the stick
   * crosses a sector boundary would stutter. A start only fires from a standstill.
   *
   * Phases end when their CLIP ends, not on a timer: stride-sync warps playback rate to the sim's
   * ground speed, so a wall-clock timer would leave the fighter frozen on the last (clamped) frame
   * of a fast-played step.
   */
  private updateGuardWalk(speed: number): void {
    if (!this.walkActive || !this.blocking) {
      this.guardPhase = "idle";
      return;
    }
    const moving = speed > GUARD_MOVE_MIN;
    const dir = DIRS8[octant8(this.moveFwd, this.moveRight)];
    const done = (phase: "start" | "stop"): boolean => {
      const a = this.acts[`kc_walk_${this.guardDir}_${phase}`];
      return a === undefined || a.time >= a.getClip().duration - 1e-3;
    };
    /** Enter a transition. A missing clip is not fatal — skip straight to the phase it leads to. */
    const begin = (phase: "start" | "stop", d: string): void => {
      const a = this.acts[`kc_walk_${d}_${phase}`];
      this.guardDir = d;
      if (a === undefined) {
        this.guardPhase = phase === "start" ? "loop" : "idle";
        return;
      }
      a.reset();
      a.setLoop(THREE.LoopOnce, 1);
      a.clampWhenFinished = true;
      this.guardPhase = phase;
    };

    switch (this.guardPhase) {
      case "idle":
        if (moving) begin("start", dir);
        break;
      case "start":
        if (!moving) begin("stop", this.guardDir);
        else if (done("start")) this.guardPhase = "loop";
        break;
      case "loop":
        if (!moving) begin("stop", this.guardDir);
        else this.guardDir = dir; // turning cross-blends the loops; no new step-off
        break;
      case "stop":
        if (moving) begin("start", dir);
        else if (done("stop")) this.guardPhase = "idle";
        break;
    }
  }

  /** Throw a jab — play the u_jab ("Punching") clip from past its wind-up (JAB_START), sped up,
   *  so the fist drives straight out. The forward torso (combat blade) keeps it on-axis. */
  punch(): void {
    const a = this.acts["u_jab"];
    const d = a?.getClip().duration ?? 1;
    if (a) {
      a.reset();
      a.time = Math.min(JAB_START, d - 0.05); // skip the chamber
    }
    // Window = remaining clip (from JAB_START) at the sped-up rate, capped — covers strike + recoil.
    this.punchT = Math.min((d - JAB_START) / JAB_SPEED, 0.4);
    this.punchT0 = this.punchT; // for the strike envelope (chest aim)
  }

  /** Throw a directional MOVE (E=punch / Q=kick × f/b/l/r — see moves.ts). A PUNCH (layer "upper") plays
   *  as a MASKED upper-body one-shot over the moving legs (top/bottom decoupled — no root); a KICK (layer
   *  "full") plays full-body and roots. Records the recovery + cancel state — a hit-confirmed punch opens a
   *  window to cancel into a kick. Self-gates: while still recovering, only a valid cancel fires. Returns
   *  whether it fired (false if mid-recovery or the clip never loaded). */
  move(id: MoveId): boolean {
    // GUARD FIRST: `id` ultimately arrives OFF THE WIRE (DroneMeleeMsg.move → drones.ts casts
    // it), so an unknown value must be a no-op, not a crash — one bad relay would take down
    // every client in the room. (canCancel also indexes MOVES, so this precedes it.)
    const def = MOVES[id];
    if (!def) return false;
    if (this.strikeT > 0 && !this.canCancel(id)) return false; // mid-recovery, not a valid cancel
    const a = this.acts[def.clip];
    if (!a) return false; // DHTH clip not loaded — no attack
    const d = a.getClip().duration;
    a.reset();
    a.setLoop(THREE.LoopOnce, 1);
    a.clampWhenFinished = true;
    this.strikeKey = def.clip;
    this.strikeLayer = def.layer; this.strikeT = d / STRIKE_SPEED;
    this.activeMoveId = id;
    this.moveHitConfirmed = false;
    this.cancelT = 0;
    this.punchT = Math.min(this.strikeT, 0.25); // keep the fist-glow flash
    this.punchT0 = this.punchT;
    if (def.layer === "full") this.snapToStrike(); // full-body kick: show it THIS frame
    return true;
  }

  /** Play the evade dodge-roll clip for a dash direction (cosmetic; the controller does the travel). */
  evadeRoll(dir: "f" | "b" | "l" | "r"): void {
    const a = this.acts[`kc_roll_${dir}`];
    if (!a) return;
    a.reset();
    a.setLoop(THREE.LoopOnce, 1);
    a.clampWhenFinished = true;
    this.strikeKey = `kc_roll_${dir}`; // ride the full-body one-shot path    this.strikeLayer = "full";
    this.strikeT = a.getClip().duration / ROLL_SPEED; // fast — matches the dash window
    // The roll is NOT a cancellable move — clear any lingering hit-confirm/cancel state so a heavy can't
    // recovery-cancel-fire DURING the roll (e.g. light-hit → evade within the 0.25s cancel window).
    this.activeMoveId = null;
    this.moveHitConfirmed = false;
    this.cancelT = 0;
    this.snapToStrike(); // show it THIS frame, synced with the dash (no fade-in lag)
  }

  /** Snap the combat base weights so a just-fired FULL-body one-shot (kick / dodge-roll / block-flinch /
   *  parry) is at full weight THIS frame — it appears the instant its movement starts instead of fading
   *  in over a few frames. ONLY for full-body strikes (masked punches must NOT zero the legs). */
  private snapToStrike(): void {
    for (const key of Object.keys(this.acts)) {
      if (key.startsWith("kc_") || key.startsWith("dh_") || key.startsWith("u_") || key.startsWith("hit_")) {
        this.weights.set(key, key === this.strikeKey ? 1 : 0);
      }
    }
  }

  /** The active move connected — arm the cancel window (only a LIGHT actually becomes cancellable). */
  onMoveHit(): void {
    if (!this.activeMoveId) return;
    this.moveHitConfirmed = true;
    if (MOVES[this.activeMoveId].weight === "light") this.cancelT = CANCEL_WINDOW;
  }

  /** Whether `next` can recovery-cancel the move currently recovering (main.ts gates a fast heavy). */
  canCancel(next: MoveId): boolean {
    return canCancelInto(
      this.activeMoveId ? { id: this.activeMoveId, hitConfirmed: this.moveHitConfirmed, cancelT: this.cancelT } : null,
      next,
    );
  }

  /** SWORD COMBO swing — each call advances the chain (sword_light_1 → _2 → _3 → wrap, SWORD_COMBO_CHAIN).
   *  A press during the current swing's COMMIT phase (before its `cancelIntoMs`) is rejected (return -1) so
   *  main.ts can BUFFER it and retry the instant the cancel window opens; a press after `cancelGraceMs`
   *  lapsed restarts at hit 1. Plays the full-body one-shot; the controller's attackDash carries the lunge;
   *  damage lands on the FIXED TICK, where main.ts sweeps the action's baked blade arc (activeAction() +
   *  HIT_ARCS). Every timing here comes from COMBAT_ACTIONS in real ms — the same table the server reads.
   *  Returns the combo step that fired (0-based), or -1 if not fired (clip missing / still committing). */
  swordSwing(): number {
    const prev = this.actionId ? COMBAT_ACTIONS[this.actionId] : null;
    // Still committing the current action? The caller buffers and retries. (Actions with no cancelIntoMs
    // — the dodges/rolls — are not chainable into a swing at all; main.ts's dashCooldownT gates those.)
    if (prev && prev.cancelIntoMs !== undefined && this.chainClockMs() < prev.cancelIntoMs) return -1;

    if (this.comboWindowT <= 0) this.comboStep = 0; // chain lapsed → restart at hit 1
    const step = this.comboStep;
    const id = SWORD_COMBO_CHAIN[step] ?? SWORD_COMBO_CHAIN[0];
    const def = COMBAT_ACTIONS[id];
    const a = this.acts[def.clip];
    if (!a) return -1; // clip not loaded
    a.reset();
    a.setLoop(THREE.LoopOnce, 1);
    a.clampWhenFinished = true;
    this.actionId = id;
    this.actionElapsedMs = 0;
    this.strikeKey = def.clip;
    this.strikeLayer = "full";
    // The RENDER countdown — how long the one-shot holds the body. Same length as the action; the DAMAGE
    // window is NOT read off it any more (that is actionElapsedMs against the table, in ms).
    this.strikeT = def.durationMs / 1000;
    this.activeMoveId = null;
    this.moveHitConfirmed = false;
    this.cancelT = 0;
    this.airChainLive = false;
    this.airComboStep = 0;
    this.comboStep = (step + 1) % SWORD_COMBO_CHAIN.length; // next press → next hit (wraps past the finisher)
    this.comboWindowT = this.strikeT + (def.cancelGraceMs ?? 0) / 1000; // open through this swing + grace
    this.snapToArmedStrike(); // show the full-body swing THIS frame (no fade-in lag)
    return step;
  }

  /** PARRY (RMB tap in the sword stance — Elden Ring model). A one-shot defensive table
   *  action: its ACTIVE window is the parry window (main.ts opens parryFrom/Until + streams
   *  `py` from it); the long tail is the whiff punish. Same commit gate as every action.
   *  Returns false if not fired (mid-commit / clip missing). */
  swordParry(): boolean {
    const prev = this.actionId ? COMBAT_ACTIONS[this.actionId] : null;
    if (prev && prev.cancelIntoMs !== undefined && this.chainClockMs() < prev.cancelIntoMs) return false;
    const def = COMBAT_ACTIONS.sword_parry;
    const a = this.acts[def.clip];
    if (!a) return false;
    a.reset();
    a.setLoop(THREE.LoopOnce, 1);
    a.clampWhenFinished = true;
    this.actionId = "sword_parry";
    this.actionElapsedMs = 0;
    this.strikeKey = def.clip;
    // TOP-ONLY deflection (user call 2026-07-22): the clip is upperOnly (legs stripped at
    // load) and rides the masked overlay at COMBAT_BLEND — the legs keep their locomotion
    // and movement continues. NO snapToArmedStrike (that zeroes the locomotion weights).
    this.strikeLayer = "upper";
    this.strikeT = def.durationMs / 1000;
    this.comboWindowT = 0; // a parry resets the chain — defense breaks the string
    this.comboStep = 0;
    return true;
  }

  /** A FULL-BODY melee strike owns the body — the movement root. Upper-layer deflections
   *  (the top-only parry) do NOT root: legs keep their locomotion. */
  meleeStrikeRoots(): boolean {
    return this.isMeleeStriking() && this.strikeLayer === "full";
  }

  /** RIPOSTE (the parry's punish — LMB at a STAGGERED enemy, fired by the action system in
   *  place of the combo step). An ordinary arced one-shot; the sweep claims its 3.5× damage
   *  with the action id (DroneHitMsg.a) so the server's per-action cap clears it. */
  swordRiposte(): boolean {
    const prev = this.actionId ? COMBAT_ACTIONS[this.actionId] : null;
    if (prev && prev.cancelIntoMs !== undefined && this.chainClockMs() < prev.cancelIntoMs) return false;
    const def = COMBAT_ACTIONS.sword_riposte;
    const a = this.acts[def.clip];
    if (!a) return false;
    a.reset();
    a.setLoop(THREE.LoopOnce, 1);
    a.clampWhenFinished = true;
    this.actionId = "sword_riposte";
    this.actionElapsedMs = 0;
    this.strikeKey = def.clip;
    this.strikeLayer = "full";
    this.strikeT = def.durationMs / 1000;
    this.comboStep = 0; // the execution ends the string — next press restarts at hit 1
    this.comboWindowT = this.strikeT + (def.cancelGraceMs ?? 0) / 1000;
    this.snapToArmedStrike();
    return true;
  }

  /** LAUNCHER (High Time — RMB focus + BACK + LMB, main.ts gates the input). A one-shot arced
   *  table action that POPS the target airborne: the sweep claims `launch` (DroneHitMsg.up)
   *  and the server's stepLaunch owns the body from there. Breaks the ground chain — the
   *  launcher IS the string's exit; the follow-up lives in the air. Same commit gate as the
   *  chain (a mid-active press buffers via main.ts). Returns false if not fired. */
  swordLauncher(): boolean {
    const prev = this.actionId ? COMBAT_ACTIONS[this.actionId] : null;
    if (prev && prev.cancelIntoMs !== undefined && this.chainClockMs() < prev.cancelIntoMs) return false;
    const def = COMBAT_ACTIONS.sword_launcher;
    const a = this.acts[def.clip];
    if (!a) return false;
    a.reset();
    a.setLoop(THREE.LoopOnce, 1);
    a.clampWhenFinished = true;
    this.actionId = "sword_launcher";
    this.actionElapsedMs = 0;
    this.strikeKey = def.clip;
    this.strikeLayer = "full";
    this.strikeT = def.durationMs / 1000;
    this.activeMoveId = null;
    this.moveHitConfirmed = false;
    this.cancelT = 0;
    this.comboStep = 0; // the launcher ends the ground string
    this.airComboStep = 0; // ...and the AIR string starts fresh on the way up
    this.airChainLive = false;
    this.comboWindowT = this.strikeT + (def.cancelGraceMs ?? 0) / 1000;
    this.snapToArmedStrike();
    return true;
  }

  // ── THE 2H GREATSWORD (Valheim-style mode — docs/2h-combat-spec.md; the gs decision
  //    layer is combat/greatsword.ts, lab-only for now). Same action machinery as the sd
  //    starters: gs_ one-shots ride the strike channel + the COMBAT_ACTIONS clock; the held
  //    BLOCK is a full-body override slot (gsBlockKey — the staggerKey pattern), sequenced
  //    raise → hold-loop → lower through the strike channel's one-shots. ──

  /** 2H chain swing — advances GS_COMBO_CHAIN (sweep → return → overhead finisher, wraps).
   *  Same commit gate + buffer contract as swordSwing. Returns the step fired, or -1. */
  gsSwing(): number {
    const prev = this.actionId ? COMBAT_ACTIONS[this.actionId] : null;
    if (prev && prev.cancelIntoMs !== undefined && this.chainClockMs() < prev.cancelIntoMs) return -1;
    if (this.comboWindowT <= 0) this.comboStep = 0;
    const step = this.comboStep % GS_COMBO_CHAIN.length;
    const id = GS_COMBO_CHAIN[step] ?? GS_COMBO_CHAIN[0];
    const def = COMBAT_ACTIONS[id];
    const a = this.acts[def.clip];
    if (!a) return -1;
    a.reset();
    a.setLoop(THREE.LoopOnce, 1);
    a.clampWhenFinished = true;
    this.actionId = id;
    this.actionElapsedMs = 0;
    this.strikeKey = def.clip;
    this.strikeLayer = "full";
    this.strikeT = def.durationMs / 1000;
    this.activeMoveId = null;
    this.moveHitConfirmed = false;
    this.cancelT = 0;
    this.comboStep = (step + 1) % GS_COMBO_CHAIN.length;
    this.comboWindowT = this.strikeT + (def.cancelGraceMs ?? 0) / 1000;
    this.snapToArmedStrike();
    return step;
  }

  /** 2H HEAVY (the secondary — Attack_11's stepping overhead). One committed one-shot;
   *  ends the chain. Returns false if not fired (mid-commit / clip missing). */
  gsHeavy(): boolean {
    const prev = this.actionId ? COMBAT_ACTIONS[this.actionId] : null;
    if (prev && prev.cancelIntoMs !== undefined && this.chainClockMs() < prev.cancelIntoMs) return false;
    const def = COMBAT_ACTIONS.gs_heavy;
    const a = this.acts[def.clip];
    if (!a) return false;
    a.reset();
    a.setLoop(THREE.LoopOnce, 1);
    a.clampWhenFinished = true;
    this.actionId = "gs_heavy";
    this.actionElapsedMs = 0;
    this.strikeKey = def.clip;
    this.strikeLayer = "full";
    this.strikeT = def.durationMs / 1000;
    this.comboStep = 0; // the heavy ends the string
    this.comboWindowT = this.strikeT + (def.cancelGraceMs ?? 0) / 1000;
    this.snapToArmedStrike();
    return true;
  }

  /** Held 2H BLOCK (RMB hold, Valheim-style). ON: the raise one-shot (gs_block_in) plays
   *  through the strike channel while gsBlockKey arms the hold loop beneath it; OFF: the
   *  loop clears and the lower one-shot plays. The TIMED-BLOCK parry window is the decision
   *  layer's clock (greatsword.ts) — this is only the pose. */
  /** `moving`: the raise/lower ONE-SHOTS are SKIPPED — they're full-body standing clips
   *  that froze the legs mid-walk (user call 2026-07-22: movement continues into the
   *  guard); the walk → guard-walk locomotion crossfade IS the transition when moving. */
  gsBlockHold(on: boolean, moving = false): void {
    if (on === (this.gsBlockKey !== "")) return;
    if (on) {
      this.gsBlockKey = this.acts["gs_block_loop"] ? "gs_block_loop" : "";
      const loop = this.acts["gs_block_loop"];
      if (loop) { loop.reset(); loop.setLoop(THREE.LoopRepeat, Infinity); }
      if (!moving) this.playGsOneShot("gs_block_in");
    } else {
      this.gsBlockKey = "";
      if (!moving) this.playGsOneShot("gs_block_out");
    }
  }

  /** A BLOCKED hit landed on the raised guard: the impact flinch, then back to the hold.
   *  While MOVING only the glow flash fires — the full-body flinch would freeze the legs. */
  gsBlockHit(moving = false): void {
    this.blockHitT = 0.18; // the shield-glow flash (updateCombatGlow)
    if (!moving) this.playGsOneShot("gs_block_hit");
  }

  /** A PARRY connected (timed block): brighter flash + the same crisp impact snap. */
  gsParryHit(moving = false): void {
    this.blockHitT = 0.24;
    if (!moving) this.playGsOneShot("gs_block_hit");
  }

  /** GUARD BREAK (energy hit 0 while blocking): the hold dies and the long break/stagger
   *  one-shot owns the body — the decision layer roots the player for its stun window. */
  gsGuardBreak(): void {
    this.gsBlockKey = "";
    this.playGsOneShot("gs_break");
  }

  /** True while the 2H hold loop is armed (raise/hold; the lower clears it). */
  gsBlocking(): boolean {
    return this.gsBlockKey !== "";
  }

  /** gs_ one-shot through the full-body strike channel (blockHit/parry precedent: no
   *  actionId — reactions are not table actions; they play at rate 1.0, authored pace). */
  private playGsOneShot(key: string): void {
    const a = this.acts[key];
    if (!a) return;
    a.reset();
    a.setLoop(THREE.LoopOnce, 1);
    a.clampWhenFinished = true;
    this.strikeKey = key;
    this.strikeLayer = "full";
    this.strikeT = a.getClip().duration;
    this.activeMoveId = null;
    this.moveHitConfirmed = false;
    this.cancelT = 0;
    this.snapToArmedStrike();
  }

  /** The held-block full-body override key ("" = not blocking) — selectLocomotion shows it
   *  whenever no strike one-shot owns the body (the raise/impact/lower play OVER it). */
  private gsBlockKey = "";

  /** 2H mode: the melee construct is the HAND-HELD greatsword instead of the forearm
   *  light-blade. Swapping disposes the current construct; the holster state machine
   *  rebuilds it toward weaponTargetId next tick (materialize-in — a nice mode beat). */
  private gsSword = false;
  setGreatsword(on: boolean): void {
    if (this.gsSword === on) return;
    this.gsSword = on;
    this.gsStowed = false; // a mode swap always redraws to the hand
    this.gsStowSwapT = 0;
    if (this.weaponTargetId && ITEMS[this.weaponTargetId]?.weapon?.class === "melee") {
      this.disposeWeapon();
      this.bladeBaseMark = null;
      this.bladeTipMark = null;
    }
  }

  // ── Z-STOW (user call 2026-07-22): the greatsword mounts to the BACK / draws again,
  //    on the pack's Sword_Sheathe_Back / Sword_Unsheathe_Back pair. The construct
  //    RE-MOUNTS mid-clip (the hand crosses the shoulder ~0.8 s in); stowed drops the
  //    armed stance (constructReady above). The CALLER owns the gameplay state. ──
  private gsStowed = false;
  private gsStowTarget = false;
  private gsStowSwapT = 0;
  gsStow(stow: boolean): void {
    this.playGsOneShot(stow ? "gs_sheathe" : "gs_unsheathe");
    this.gsStowTarget = stow;
    this.gsStowSwapT = 0.8;
  }
  gsIsStowed(): boolean {
    return this.gsStowed;
  }
  /** Re-parent the construct hand ↔ back. The back basis lives in weaponMount because
   *  applyArmOverrides re-copies weaponMount onto the weapon EVERY frame (see ~:5138). */
  private remountGreatsword(): void {
    if (!this.weapon || !this.gsSword) return;
    if (this.gsStowed) {
      const back = this.boneByName.get("Spine2") ?? this.boneByName.get("Spine1");
      if (!back) return;
      back.add(this.weapon);
      this.weaponMount.copy(GS_BACK_MOUNT);
      this.weapon.quaternion.copy(GS_BACK_MOUNT);
      this.weapon.position.copy(GS_BACK_POS);
    } else {
      this.boneByName.get("RightHand")!.add(this.weapon);
      const wdef = this.weaponId ? ITEMS[this.weaponId]?.weapon : undefined;
      if (wdef) this.configureWeaponHold(wdef);
      else {
        this.weaponMount.copy(WEAPON_MOUNT_PISTOL);
        this.weaponBasePosition.set(0, 0.04, 0);
      }
      this.weapon.quaternion.copy(this.weaponMount);
      this.weapon.position.copy(this.weaponBasePosition);
    }
  }

  /** AERIAL string (airborne TAP LMB — main.ts splits tap from the charge-plunge HOLD).
   *  Alternates SWORD_AIR_CHAIN and wraps; each fired hit re-lifts the juggled target
   *  (`launch`) while main.ts floats the attacker (character.aerialHop) — the DMC air rave.
   *  Same commit/chain discipline as the ground string, sharing the combo window clock.
   *  Returns the step fired, or -1 (mid-commit / clip missing). */
  swordAerial(): number {
    const prev = this.actionId ? COMBAT_ACTIONS[this.actionId] : null;
    if (prev && prev.cancelIntoMs !== undefined && this.chainClockMs() < prev.cancelIntoMs) return -1;
    if (this.comboWindowT <= 0) this.airComboStep = 0;
    const step = this.airComboStep % SWORD_AIR_CHAIN.length; // A ↔ B, wrapping — the juggle sustains
    const id = SWORD_AIR_CHAIN[step];
    const def = COMBAT_ACTIONS[id];
    const a = this.acts[def.clip];
    if (!a) return -1;
    a.reset();
    a.setLoop(THREE.LoopOnce, 1);
    a.clampWhenFinished = true;
    this.actionId = id;
    this.actionElapsedMs = 0;
    this.strikeKey = def.clip;
    this.strikeLayer = "full";
    this.strikeT = def.durationMs / 1000;
    this.activeMoveId = null;
    this.moveHitConfirmed = false;
    this.cancelT = 0;
    this.airComboStep = step + 1;
    this.comboWindowT = this.strikeT + (def.cancelGraceMs ?? 0) / 1000;
    this.airChainLive = true;
    this.snapToArmedStrike();
    return step;
  }

  /** THE GROUND POUND — HOLD-only now (user call 2026-07-23: the air combo must not end in
   *  the slam by default; LMB held a beat fires it). Chains at the current air hit's cancel
   *  point like any action; ends the string (no float after — you ride the slam down). */
  swordGpound(): boolean {
    const prev = this.actionId ? COMBAT_ACTIONS[this.actionId] : null;
    if (prev && prev.cancelIntoMs !== undefined && this.chainClockMs() < prev.cancelIntoMs) return false;
    const def = COMBAT_ACTIONS.sword_gpound;
    const a = this.acts[def.clip];
    if (!a) return false;
    a.reset();
    a.setLoop(THREE.LoopOnce, 1);
    a.clampWhenFinished = true;
    this.actionId = "sword_gpound";
    this.actionElapsedMs = 0;
    this.strikeKey = def.clip;
    this.strikeLayer = "full";
    this.strikeT = def.durationMs / 1000;
    this.activeMoveId = null;
    this.moveHitConfirmed = false;
    this.cancelT = 0;
    this.airComboStep = 0;
    this.comboWindowT = 0;
    this.airChainLive = false; // the slam ends the string — no float after it
    this.snapToArmedStrike();
    return true;
  }
  /** Next hit in the AERIAL chain (parallel to comboStep; reset by the launcher + lapse). */
  private airComboStep = 0;
  /** True between AERIAL hits while the chain is open — selectLocomotion floats the
   *  sd_air_idle combat stance instead of the fall/leap clips (the air-rave hold). Cleared
   *  by landing, the slam, and any ground action. */
  private airChainLive = false;

  /** Open the AIR RAVE on the way up (the High-Time rise): the launcher's OWN clip is the
   *  launch visual — Sword_UpperCut is authored ROOT-MOTION RISING (0 → 2 m across the
   *  swing; the RM data is the spec), so nothing overrides it. This just arms the float
   *  (sd_air_idle) for the hang once the swing's one-shot ends. */
  /** STINGER dash visual (soft-lock gap closer): while on, sd_dash owns the body — the
   *  controller homes the travel and ends the dash on arrival (main/lab drive both). */
  /** HIT FLASH (user call 2026-07-23 — landed attacks need an obvious cue): a brief
   *  white emissive pulse across the body materials, eased out over ~0.16 s. Pairs with
   *  the impact spark + positional hit sfx + hitstop. */
  flashHit(): void {
    if (this.sharedMats) return; // shared citizen materials: a flash would light EVERY citizen
    this.hitFlashT = HIT_FLASH_S;
    if (this.flashBase === null) {
      this.flashBase = this.materials.map((m) => {
        const e = (m as THREE.MeshLambertMaterial).emissive;
        return e ? e.clone() : null;
      });
    }
  }

  private hitFlashT = 0;
  private flashBase: (THREE.Color | null)[] | null = null;

  private updateHitFlash(dt: number): void {
    if (this.hitFlashT <= 0 || this.flashBase === null) return;
    this.hitFlashT = Math.max(0, this.hitFlashT - dt);
    const k = this.hitFlashT / HIT_FLASH_S;
    for (let i = 0; i < this.materials.length; i++) {
      const base = this.flashBase[i];
      if (!base) continue;
      const e = (this.materials[i] as THREE.MeshLambertMaterial).emissive;
      if (k <= 0) e.copy(base); // restored exactly — the flash never drifts the tint
      else e.copy(base).lerp(_flashWhite, k * 0.8);
    }
  }

  /** AIR-EVADE float (1H, user call 2026-07-23): the airborne dodge shows the aerial
   *  IDLE (sd_air_idle) while the controller circles the lock — no dodge clip. */
  setAirEvade(on: boolean): void {
    this.airEvadeOn = on;
  }

  private airEvadeOn = false;

  setStinger(on: boolean, key: "sd_dash" | "sd_airdash" | "sd_shortdash" = "sd_dash"): void {
    if (on && !this.stingerOn) {
      this.stingerKey = key; // long = C4; short = Regular_C; airborne = PS CommonAttack
      this.acts[key]?.reset(); // each dash starts at frame 0
      this.stingerRate = SD_DASH_NATURAL[key] ?? SWORD_DASH_ANIM;
    }
    this.stingerOn = on;
  }

  /** RE-TIME the dash clip to the flight (user call: the animation spans EXACTLY the time
   *  it takes to reach the enemy): each homing tick the caller passes the remaining flight
   *  seconds, and the playback rate is chosen so the clip's IMPACT frame (SD_DASH_IMPACT_S)
   *  arrives precisely at arrival — converging live as the target moves. `null` (at
   *  arrival) restores the natural retime for the slash/recovery tail. */
  retimeStinger(remainingFlightS: number | null): void {
    const a = this.acts[this.stingerKey];
    if (!this.stingerOn || !a) return;
    if (remainingFlightS === null) {
      this.stingerRate = SD_DASH_NATURAL[this.stingerKey] ?? SWORD_DASH_ANIM;
      return;
    }
    const impact = SD_DASH_IMPACTS[this.stingerKey] ?? 1.0;
    this.stingerRate = Math.max(0.6, Math.min(3.5, (impact - a.time) / Math.max(0.05, remainingFlightS)));
  }

  /** Seconds of the dash clip left past the current frame at the natural tail rate — the
   *  caller's arrival LINGER window (the slash + recovery play out exactly once). */
  stingerTailS(): number {
    const a = this.acts[this.stingerKey];
    const natural = SD_DASH_NATURAL[this.stingerKey] ?? SWORD_DASH_ANIM;
    return a ? Math.max(0.25, (a.getClip().duration - a.time) / natural) : 0.4;
  }

  private stingerOn = false;
  private stingerKey: "sd_dash" | "sd_airdash" | "sd_shortdash" = "sd_dash";
  private stingerRate = SWORD_DASH_ANIM;

  /** The aerial string is LIVE (launch → hits → until landing/slam): main/lab feed this to
   *  the controller each tick (character.raveFloat) for the eased mid-rave fall gravity. */
  isAirRave(): boolean {
    return this.airChainLive;
  }

  openAirRave(): void {
    this.airChainLive = true;
  }

  /** OBSERVER playback of a RELAYED combat action (PlayerPunchMsg.a → net.ts). Plays THAT action's own clip
   *  on a REMOTE avatar instead of the generic `punch_f` throw the relay used to fall back to — the whole
   *  point of putting the id on the wire. Purely visual: no combo chain, no action clock (`actionId` stays
   *  null — remotes are never stepped and never sweep), no damage. The SERVER owns the hit; this is the
   *  swing you see the other player make. Returns false if the clip isn't loaded. */
  playRelayedAction(id: CombatActionId, elapsedMs = 0): boolean {
    const def = COMBAT_ACTIONS[id];
    const a = this.acts[def.clip];
    if (!a) return false;
    a.reset();
    a.setLoop(THREE.LoopOnce, 1);
    a.clampWhenFinished = true;
    this.strikeKey = def.clip;
    this.strikeLayer = "full";
    const phase = Math.max(0, Math.min(def.durationMs, elapsedMs));
    a.time = a.getClip().duration * phase / def.durationMs;
    this.strikeT = (def.durationMs - phase) / 1000; // observers join the live swing
    this.activeMoveId = null;
    this.moveHitConfirmed = false;
    this.cancelT = 0;
    this.snapToArmedStrike(); // show the swing THIS frame (no fade-in lag)
    return true;
  }

  /** SWORD dodge — Space + direction. ALWAYS the directional combat ROLL (sd_roll_<dir> — the
   *  SSH Roll family; user pick 2026-07-21, the DMC roll read): the flat sd_dodge_* side-hops
   *  are retired from play (clips stay deployed for rollback). The controller's evade()
   *  supplies the dash travel + i-frames — crouching still passes its longer/lower-friction
   *  coast, so a crouched roll travels ~2× on the same clip. Rides the strike path so the
   *  armed branch renders it, snapped to full weight THIS frame. */
  /** AIR DODGE — no rolling mid-air (user call): the UAL Dodge_Left/Right one-shot, side
   *  picked by the controller from the input direction. Full-body via the strike channel
   *  (like the roll); the physics — impulse, i-frames, cooldown — are the controller's. */
  airDodge(side: "l" | "r"): void {
    const key = `dodge_${side}`;
    const a = this.acts[key];
    if (!a) return;
    a.reset();
    a.setLoop(THREE.LoopOnce, 1);
    a.clampWhenFinished = true;
    this.strikeKey = key;
    this.strikeLayer = "full";
    this.strikeT = a.getClip().duration / AIR_DODGE_ANIM;
    this.snapToArmedStrike();
  }

  /** 2H ROLL (Space in circling mode — the pack's 2H/Roll family; pure back rides BL).
   *  Identical contract to swordDodge: full-body one-shot, evade() owns travel/i-frames,
   *  and the ACTION CLOCK reuses the sword_roll_* table rows (both packs' rolls are the
   *  same 1.10 s, so the server's i-frame window stays honest). */
  gsRoll(dir: "f" | "b" | "l" | "r" | "bl" | "br"): void {
    const key = `gs_roll_${dir}`;
    const a = this.acts[key];
    if (!a) { this.swordDodge(dir); return; } // clip missing → the 1H roll family
    a.reset();
    a.setLoop(THREE.LoopOnce, 1);
    a.clampWhenFinished = true;
    this.strikeKey = key;
    this.strikeLayer = "full";
    this.strikeT = a.getClip().duration / SWORD_ROLL_ANIM;
    this.actionId = `sword_roll_${dir}` as CombatActionId;
    this.actionElapsedMs = 0;
    this.comboStep = 0;
    this.comboWindowT = 0;
    this.activeMoveId = null;
    this.moveHitConfirmed = false;
    this.cancelT = 0;
    this.snapToArmedStrike();
  }

  /** The SSH Dodge side-step dash. CURRENTLY UNROUTED (revised user call 2026-07-22, same
   *  day it was wired): locked walk-tier Space JUMPS (sd_jump_*), Shift-tier Space ROLLS —
   *  kept because the clips/table rows stand and the verb may come back on a binding. */
  swordDashDodge(dir: "f" | "b" | "l" | "r" | "bl" | "br"): void {
    const d4 = dir === "bl" || dir === "br" ? "b" : dir;
    const key = `sd_dodge_${d4}`;
    const a = this.acts[key];
    if (!a) { this.swordDodge(dir); return; } // clip missing → the roll
    a.reset();
    a.setLoop(THREE.LoopOnce, 1);
    a.clampWhenFinished = true;
    this.strikeKey = key;
    this.strikeLayer = "full";
    this.strikeT = a.getClip().duration / SWORD_DODGE_SPEED;
    this.actionId = `sword_dodge_${d4}` as CombatActionId;
    this.actionElapsedMs = 0;
    this.comboStep = 0;
    this.comboWindowT = 0;
    this.activeMoveId = null;
    this.moveHitConfirmed = false;
    this.cancelT = 0;
    this.snapToArmedStrike();
  }

  swordDodge(dir: "f" | "b" | "l" | "r" | "bl" | "br"): void {
    const key = `sd_roll_${dir}`;
    const a = this.acts[key];
    if (!a) return;
    a.reset();
    a.setLoop(THREE.LoopOnce, 1);
    a.clampWhenFinished = true;
    this.strikeKey = key;
    this.strikeLayer = "full";
    this.strikeT = a.getClip().duration / SWORD_ROLL_ANIM;
    // The evade is a table action too: it carries no arc and no damage, but the SERVER reads its i-frame
    // window off the same def, so the client must run the same clock for it.
    this.actionId = `sword_roll_${dir}` as CombatActionId;
    this.actionElapsedMs = 0;
    this.comboStep = 0; // a dodge breaks the attack chain
    this.comboWindowT = 0;
    this.activeMoveId = null;
    this.moveHitConfirmed = false;
    this.cancelT = 0;
    this.snapToArmedStrike();
  }



  /** True while a SWORD strike (sd_*) OR a hit flinch is playing — main.ts roots the base
   *  (combatStance) for its window; the flinch rooting is the HITSTUN beat. Includes the
   *  air-attack LANDING (the slam recovery roots on the ground); begin/loop are airborne. */
  isMeleeStriking(): boolean {
    return this.strikeT > 0 && (this.strikeKey.startsWith("sd_") || this.strikeKey.startsWith("gs_") || this.strikeKey.startsWith("hit_"));
  }

  private doubleJumpWingT = 0;
  /** DOUBLE JUMP one-shot (UAL DoubleJump): rides the
   *  jump-start override channel, so it plays over the airborne pose like any launch clip. */
  flagDoubleJump(): void {
    this.doubleJumpWingT = .36;
    const a = this.acts["djump"];
    if (!a) return;
    a.reset();
    a.setLoop(THREE.LoopOnce, 1);
    a.clampWhenFinished = true;
    this.jumpStartKey = "djump";
    this.jumpStartT = a.getClip().duration;
    if (!this.uprightDoubleJump) this.snapAir("djump");
  }

  /** Aiming a gun: the double jump drives the legs only (selectLocomotion's `aimedGun`). */
  private get uprightDoubleJump(): boolean {
    return this.weaponStance === "underhand" && this.gunOut
      || this.aiming && this.stanceK > 0.5 && this.aimPrefix !== "sd";
  }
  /** Weight of the borrowed double-jump legs under an aimed upper body. */
  private djumpLegK = 0;

  /** Bind-relative double-jump tuck under an upright gun hold.
   *  The pelvis, spine, arms and weapon retain the hold pose.
   *  The weight rises and falls inside the clip and eases, so neither the
   *  launch nor an RMB change mid-jump steps the legs. */
  private applyDoubleJumpLegs(dt: number): void {
    const act = this.acts["djump"];
    const live = !!act && this.jumpStartKey === "djump" && this.jumpStartT > 0 && this.jet === "none"
      && !this.airCombatOwnsBody() && this.uprightDoubleJump;
    const duration = act?.getClip().duration ?? 0;
    const time = Math.max(0, duration - this.jumpStartT);
    const target = live ? THREE.MathUtils.smoothstep(time, 0, 0.08) * THREE.MathUtils.smoothstep(this.jumpStartT, 0, 0.2) : 0;
    this.djumpLegK += (target - this.djumpLegK) * (1 - Math.exp(-20 * Math.max(0, dt)));
    if (this.djumpLegK < 1e-4) { this.djumpLegK = 0; return; }
    // The source clip is a somersault: its leg rotations depend on a rotating
    // pelvis. Borrowing them under the upright gun hold twists the knees. Use
    // a bind-relative tuck in the same hinge axes as the ordinary jump.
    const pulse=Math.sin(Math.PI*Math.min(1,time/Math.max(.001,duration)));
    for(const [name,angle] of [
      ['thigh_l',JUMP_SQUAT_THIGH*(1+.22*pulse)],['thigh_r',JUMP_SQUAT_THIGH*(.85+.2*pulse)],
      ['calf_l',-JUMP_SQUAT_SHIN*(1+.25*pulse)],['calf_r',-JUMP_SQUAT_SHIN*(.9+.25*pulse)],
      ['foot_l',-JUMP_SQUAT_FOOT],['foot_r',-JUMP_SQUAT_FOOT],
    ] as const){
      const bone=this.boneByName.get(name),bind=this.twistBind.get(name);if(!bone||!bind)continue;
      _hvQ.setFromAxisAngle(_hvAxis.set(0,0,1),angle);_hvTarget.copy(bind).multiply(_hvQ);
      bone.quaternion.slerp(_hvTarget,this.djumpLegK);
    }
  }

  // Death simulation stays in the captured avatar frame. Callers carry that
  // frame with its planet, while the body and detached apparatus move within it.
  private ragdoll: DeathRagdoll | null = null;
  private ragSpace: DeathPhysicsSpace | null = null;
  private ragSurface: RagdollSurface | null = null;
  private ragHipsRest: THREE.Vector3 | null = null;
  private readonly ragBoneRotations = new Map<string,THREE.Quaternion>();
  private readonly ragHipsRotation = new THREE.Quaternion();
  private readonly ragTorsoRotation = new THREE.Quaternion();
  private readonly ragRootRotation = new THREE.Quaternion();

  /** Preserve the complete incoming velocity; the optional impact is a separate
   * small kick. A numeric second argument retains the old NPC ground-plane API. */
  startRagdoll(velocityWorld: THREE.Vector3, groundOrOptions: number | RagdollOptions = 0): void {
    if (this.ragdoll) return;
    const options: RagdollOptions = typeof groundOrOptions === 'number' ? {groundY:groundOrOptions} : groundOrOptions;
    const positions: THREE.Vector3[] = [];
    this.group.updateMatrixWorld(true);
    for (const name of RAGDOLL_BONES) {
      const bone = this.boneByName.get(name);
      if (!bone) return;
      positions.push(this.group.worldToLocal(bone.getWorldPosition(new THREE.Vector3())));
      this.ragBoneRotations.set(name,bone.quaternion.clone());
    }
    const seed = options.seed ?? ((Math.random()*0x7fffffff)|0);
    this.group.getWorldQuaternion(_ragQ).invert();
    _ragImp.copy(velocityWorld).applyQuaternion(_ragQ);
    _ragJit.copy(options.impactWorld ?? _ragV.set(0,0,0)).applyQuaternion(_ragQ);
    this.ragdoll = new DeathRagdoll(positions,_ragImp,_ragJit,seed);
    this.ragSpace = new DeathPhysicsSpace(this.group,options);
    this.ragSurface = new RagdollSurface(this.group,[...(this.glbBody?.meshes??[]),...this.styledSkinned],RAGDOLL_BONES.map(name=>this.boneByName.get(name)!));
    this.ragHipsRest = this.boneByName.get(RAGDOLL_BONES[0])!.position.clone();
    this.boneByName.get(RAGDOLL_BONES[0])!.getWorldQuaternion(this.ragHipsRotation).premultiply(_ragQ);
    this.crystalWings?.startDeath(_ragImp,_ragJit,this.ragSpace.resolve,seed);
  }

  /** Respawn restores the original pelvis translation and attached apparatus. */
  endRagdoll(): void {
    this.ragdoll = null; this.ragSpace = null; this.ragSurface = null;
    const hips = this.boneByName.get(RAGDOLL_BONES[0]);
    if (hips && this.ragHipsRest) hips.position.copy(this.ragHipsRest);
    for(const [name,rotation]of this.ragBoneRotations)this.boneByName.get(name)?.quaternion.copy(rotation);
    this.ragBoneRotations.clear();
    this.ragHipsRest = null;
    this.crystalWings?.endDeath();
  }

  ragdollActive(): boolean { return this.ragdoll !== null; }

  stepRagdoll(dt: number): void {
    if (!this.ragdoll || !this.ragSpace) return;
    this.group.updateMatrixWorld(true);
    this.ragSpace.prepare();
    this.ragdoll.step(dt,this.ragSpace.resolve);
    this.fitRagdoll();
    for(let i=0;i<2&&this.ragSurface?.resolve(this.ragdoll,this.ragSpace.resolve,dt);i++)this.fitRagdoll();
    this.ragSurface?.remember();
    this.crystalWings?.stepDeath(dt);
  }

  private fitRagdoll():void {
    if(!this.ragdoll)return;
    const points = this.ragdoll.points;
    const hips = this.boneByName.get(RAGDOLL_BONES[0]);
    if (hips?.parent) {
      _ragW.copy(points[0].position);this.group.localToWorld(_ragW);
      hips.position.copy(hips.parent.worldToLocal(_ragW));
      this.ragdoll.rotation(this.ragTorsoRotation);
      this.group.getWorldQuaternion(this.ragRootRotation).multiply(this.ragTorsoRotation).multiply(this.ragHipsRotation);
      hips.parent.getWorldQuaternion(_ragQ).invert();hips.quaternion.copy(_ragQ).multiply(this.ragRootRotation);
      this.group.updateMatrixWorld(true);
    }
    for (const [name, ai, bi] of RAGDOLL_FIT) {
      if (name==='Hips') continue;
      const bone = this.boneByName.get(name), child = this.boneByName.get(RAGDOLL_BONES[bi]);
      if (!bone || !child) continue;
      bone.getWorldPosition(_ragV);child.getWorldPosition(_ragV2).sub(_ragV);
      if (_ragV2.lengthSq()<1e-8) continue;
      _ragW.copy(points[bi].position);this.group.localToWorld(_ragW);
      _ragV3.copy(points[ai].position);this.group.localToWorld(_ragV3);_ragW.sub(_ragV3);
      if (_ragW.lengthSq()<1e-8) continue;
      _ragQ.setFromUnitVectors(_ragV2.normalize(),_ragW.normalize());
      this.rotateBoneWorld(name,_ragQ);this.group.updateMatrixWorld(true);
    }
  }

  /** DEATH FADE (dying drone corpses): 0 = solid, 1 = gone. Whole-body opacity fade on the
   *  avatar's PRIVATE material set — shared-material citizens must never be faded (it would
   *  fade every ambient NPC in town at once), so this no-ops for them. */
  setDeathFade(k: number): void {
    if (this.sharedMats) return;
    for (const m of this.materials) {
      if (k > 0 && !m.transparent) m.transparent = true;
      m.opacity = 1 - k;
      m.depthWrite = k < 0.5;
    }
  }

  /** Directional HIT FLINCH (UAL hit_f/b/l/r) — the VICTIM half of the feedback stack: an
   *  un-guarded hit snaps the body from the side it came from (hitSide, main.ts). Rides the
   *  full-body strike path like blockHit; the caller gates it off mid-attack (a flinch that
   *  eats your own swing is agency theft — DMC lights don't stagger you out of a string) and
   *  blocking keeps its own guard reaction (blockHit). Clips are reaction-length (0.3–0.5 s). */
  flinch(dir: "f" | "b" | "l" | "r", strength=1): void {
    if (this.blocking) return; // the guard's flinch is blockHit's job
    // PROCEDURAL (user call 2026-08-03: "when flinching, the model freaks out — perhaps a
    // procedurally generated effect for flinching is warranted"). This used to fire a FULL-BODY
    // `hit_*` one-shot: it took the whole rig, ROOTED movement for the clip's 0.3–0.5 s, and
    // snapped every other clip family to zero. Under sustained fire the next hit lands before the
    // last reaction has read at all, so the body restarts the clip a few times a second and never
    // plays any of them — a whole-body twitch. Worse, it fought whatever the legs were doing.
    //
    // An impulse is the honest model for a flinch: it is a small, fast, DIRECTIONAL recoil, and
    // it should ride ON TOP of the locomotion instead of replacing it. Retriggering just adds to
    // the impulse (see applyFlinch), so rapid fire reads as being pushed around rather than as a
    // seizure — and nothing is rooted, so the player keeps control while taking fire.
    this.flinchDirF = dir === "f" ? 1 : dir === "b" ? -1 : 0;
    this.flinchDirR = dir === "r" ? 1 : dir === "l" ? -1 : 0;
    // Accumulate, capped: a second hit deepens the recoil, it does not restart it from zero.
    this.flinchAmp = Math.min(FLINCH_AMP_MAX, this.flinchAmp + FLINCH_AMP*Math.max(0,Math.min(2.5,strength)));
    this.flinchT = FLINCH_DUR;
  }

  /** HEAVY-HIT STAGGER (user call 2026-07-22 — Elden poise-break): a slow/high-damage hit
   *  INTERRUPTS whatever the victim was doing and reels them on the SSH Hurt_Heavy —
   *  grounded OR airborne (the strike override is grounded-agnostic). The CALLER cancels
   *  the gameplay (table clock / dummy string); this is the body's half.
   *
   *  The reel ENDS ON THE FLOOR (it is a knockdown, not a stagger — see CONFORM_STRIKES), so
   *  a GROUNDED one chains into the KIP-UP the way the launch knockdown's down→rise does
   *  (user call 2026-07-24: "they should kick back up like the other knockdown"). Airborne
   *  victims don't: their lift phase owns the body and will floor them properly first. */
  hurtHeavy(grounded = true): void {
    if (!this.startFullOneShot("sd_hurt_heavy")) { this.flinch("f"); return; }
    this.knockRiseQueued = grounded;
  }
  /** Total body-time of a grounded poise-break knockdown (s): the reel plus the kip-up it
   *  chains into. Callers size their own suppression window with this (the lab's hitstun)
   *  so the victim's gameplay stays down as long as the body is. */
  knockdownTotalS(): number {
    return (this.acts["sd_hurt_heavy"]?.getClip().duration ?? 1) + (this.acts["kip_up"]?.getClip().duration ?? 1.17);
  }
  /** True once a grounded knockdown reel is playing and owes a kip-up (see updateKnockRise). */
  private knockRiseQueued = false;
  /** Start `key` as a FULL-BODY one-shot on the strike channel (the reel/kip-up shape).
   *  False when the clip never loaded — the caller picks a fallback. */
  private startFullOneShot(key: string): boolean {
    const a = this.acts[key];
    if (!a) return false;
    a.reset();
    a.setLoop(THREE.LoopOnce, 1);
    a.clampWhenFinished = true;
    this.strikeKey = key;
    this.strikeLayer = "full";
    this.strikeT = a.getClip().duration;
    this.activeMoveId = null;
    this.moveHitConfirmed = false;
    this.cancelT = 0;
    this.snapToStrike();
    this.snapToArmedStrike(key);
    return true;
  }
  /** KNOCKDOWN → KIP-UP hand-off: the frame the poise-break reel retires, stand the body back
   *  up on the same clip the launch knockdown rises with. The GROUND CONFORM covers kip_up too,
   *  so the seat unwinds by construction as the pose stands (exactly as the lift path does). */
  private updateKnockRise(prevKey: string): void {
    if (!this.knockRiseQueued || this.strikeT > 0 || prevKey !== "sd_hurt_heavy") return;
    this.knockRiseQueued = false;
    this.startFullOneShot("kip_up");
  }

  /** Advance the COMBAT ACTION clock (ms) by `dt` seconds and retire the action when it runs out.
   *
   *  CALL THIS FROM THE FIXED TICK, NOT FROM update(). It is deliberately NOT folded into update() with
   *  the other timers, because update() runs once per RENDER FRAME and this clock is the one main.ts
   *  brackets its swept blade test with: the sweep at tick k covers action time [elapsed-dt, elapsed], so
   *  the ticks tile the action window exactly iff `elapsed` advances by exactly one FIXED_DT per tick.
   *  Advance it on the render frame instead and, at any refresh rate that isn't 60 Hz, consecutive fixed
   *  ticks read the same (or a doubly-advanced) `elapsed` — which re-sweeps some of the swing and, worse,
   *  NEVER SWEEPS the rest. That hole is precisely the frame-rate-dependent hit detection this whole change
   *  exists to kill, so the clock is fixed-tick-driven by construction.
   *
   *  `dt` is REAL seconds: never pre-scale it by a clip playback rate (see the note at ~:365). */
  /** Where the current action's ANIMATION is, in action ms. Hitstop slows only
   *  the animation clock, so after a confirmed hit the picture trails the action
   *  clock by the freeze. Chains, cancels and the action's end read THIS: a
   *  chain on the raw clock cut the swing mid-slash the moment it resumed from
   *  the freeze. Hit sweeps stay on actionElapsedMs (the server's clock), and a
   *  later chain can only widen THE CANCEL INVARIANT's margin. Bounded, so an
   *  avatar whose mixer is not advancing (far LOD) still chains. */
  private chainClockMs(): number {
    const act = this.actionId ? this.acts[COMBAT_ACTIONS[this.actionId].clip] : undefined;
    if (!act) return this.actionElapsedMs;
    const anim = act.time / Math.max(1e-3, Math.abs(act.timeScale)) * 1000;
    return Math.max(this.actionElapsedMs - MAX_ANIM_LAG_MS, Math.min(this.actionElapsedMs, anim));
  }

  stepAction(dt: number): void {
    // The COMBO WINDOW is gameplay state like the action clock — it decays HERE (fixed tick),
    // not in updateStateMachine (render dt), so "is the chain still open" and "has the cancel
    // point arrived" can never drift apart across frame rates.
    this.comboWindowT = Math.max(0, this.comboWindowT - dt);
    if (!this.actionId) return;
    this.actionElapsedMs += dt * 1000;
    if (this.chainClockMs() >= COMBAT_ACTIONS[this.actionId].durationMs) {
      // COMBO-END RECOVERY (user call 2026-07-23): the Regular string's attack clips stand
      // alone — a light that EXPIRES UNCHAINED ends the attack on its Sword_Regular_X_Rec.
      // Chaining before expiry never reaches here (the new action replaces this one), so
      // the rec plays exactly when the combo STOPS. Pure visual: no action id, no arc —
      // it rides the strike channel and any chain/dodge/movement cancels it.
      const recKey = `${COMBAT_ACTIONS[this.actionId].clip}_rec`;
      const rec = (this.actionId === "sword_light_1" || this.actionId === "sword_light_2" || this.actionId === "sword_light_3") ? this.acts[recKey] : undefined;
      if (rec) {
        rec.reset();
        rec.setLoop(THREE.LoopOnce, 1);
        rec.clampWhenFinished = true;
        this.strikeKey = recKey;
        this.strikeLayer = "full";
        this.strikeT = rec.getClip().duration; // UAL game-speed (rate 1.0)
      }
      this.actionId = null;
    }
  }

  /** A combo-END recovery (sd_lightN_rec) is playing — the ActionSystem lets held movement
   *  cancel it (running out of a dropped combo must not moonwalk through the flourish). */
  inComboRec(): boolean {
    return this.strikeT > 0 && this.strikeKey.endsWith("_rec");
  }

  cancelComboRec(): void {
    if (!this.inComboRec()) return;
    this.poseInertia.request(MOVE_CANCEL_INERTIA_S);
    this.strikeT = 0;
    this.strikeKey = "";
  }

  /** A grounded blade swing whose damage is DONE (its cancel point has passed —
   *  by THE CANCEL INVARIANT that is after the hit window, so the punch is
   *  already on the wire): held movement may take the body back. */
  swordMoveCancelable(): boolean {
    if (!this.actionId || this.strikeT <= 0 || this.strikeLayer !== "full" || !this.strikeKey.startsWith("sd_")) return false;
    const def = COMBAT_ACTIONS[this.actionId];
    if (def.arc === undefined || def.damage <= 0) return false;
    return this.chainClockMs() >= (def.cancelIntoMs ?? def.durationMs);
  }
  /** Hand the body back to movement: the swing's follow-through is dropped and
   *  the pose blends into the locomotion with the swing's momentum. The combo
   *  window stays open, so an attack press right after still continues it. */
  cancelSwordStrike(): void {
    this.poseInertia.request(MOVE_CANCEL_INERTIA_S);
    this.strikeT = 0;
    this.strikeKey = "";
    this.actionId = null;
  }

  /** ABANDON the in-flight combat action and reset the chain. Call whenever the fixed tick that owns
   *  the action clock is about to STOP RUNNING (opening the menu — mode "menu" has no stepAction call).
   *  Without it the clock FREEZES mid-swing while `strikeT`/`comboWindowT` keep draining on render dt,
   *  and when play resumes the stale action picks its sweep back up against drones that have since
   *  moved — a swing landing on targets that were never in front of the blade. Same clear the
   *  weapon-class switch does (see setHeld), for the same reason. */
  cancelAction(): void {
    this.actionId = null;
    this.comboStep = 0;
    this.comboWindowT = 0;
  }

  /** A released attack hold cannot keep dealing damage or holding an air pose.
   * Mixer weights still ease out normally; do not reset their playback phases. */
  endBladeAttack():void {
    if(this.strikeKey.startsWith('sd_')&&!this.strikeKey.startsWith('sd_hurt_')&&!this.isRolling())this.strikeT=0;
    this.cancelAction();this.airChainLive=false;this.airComboStep=0;
    this.setStinger(false);this.setAirEvade(false);
  }

  /** The COMBAT_ACTIONS action executing right now and how far into it we are, in ACTION MILLISECONDS —
   *  the gameplay truth main.ts sweeps the baked blade arc against on the fixed tick.  Null when no combat
   *  action is live.
   *
   *  (This replaced a boolean "is the blade dangerous right now?" predicate derived from unitless strike
   *  countdown FRACTIONS — client-only numbers the server could not know, so the two sides could never
   *  agree on when a blade was dangerous. The window now comes from COMBAT_ACTIONS in ms, which BOTH sides
   *  read. And callers get the CLOCK rather than a yes/no, because the swept test needs the two window
   *  positions that bracket the tick, not just a flag for the instant it happens to sample.) */
  activeAction(): { id: CombatActionId; elapsedMs: number } | null {
    return this.actionId ? { id: this.actionId, elapsedMs: this.actionElapsedMs } : null;
  }

  /** True while a combat action is mid-flight OR the combo chain is still open — main.ts
   *  keeps the Space-dodge available through this window without the RMB focus hold. */
  comboOpen(): boolean {
    return this.actionId !== null || this.comboWindowT > 0;
  }

  /** True while a ROLL one-shot owns the body (the dodge — either pack's family). */
  isRolling(): boolean {
    return this.strikeT > 0 && (this.strikeKey.startsWith("sd_roll_") || this.strikeKey.startsWith("gs_roll_"));
  }

  /** SEAMLESS ROLL EXIT: movement input past the dodge's travel window cancels the clip's
   *  recovery tail — the fighter runs straight out of the tumble instead of standing back
   *  up first (rolls are move-cancelable recoveries, DMC-style). No-op unless rolling. */
  cancelRollRecovery(): void {
    if (!this.isRolling()) return;
    this.strikeT = 0;
    this.strikeKey = "";
    if (this.actionId && this.actionId.startsWith("sword_roll")) this.actionId = null;
  }


  /** Snap the ARMED base weights so a just-fired full-body sword swing is at full weight THIS frame
   *  (the armed-stance analogue of snapToStrike): zero the armed locomotion clips, set the swing to 1. */
  private snapToArmedStrike(show: string = this.strikeKey): void {
    // The new strike owns the body this frame, but the switch is inertialized:
    // the old motion's momentum decays into it over ~6 frames (no teleport,
    // no mushy crossfade — the swing's own timing is untouched).
    this.poseInertia.request(STRIKE_INERTIA_S,STRIKE_INERTIA_MAX_S);
    for (const key of Object.keys(this.acts)) {
      if (key.startsWith("sd_") || key.startsWith("gs_") || key.startsWith("p_") || key.startsWith("r_") || key.startsWith("hit_")) {
        this.weights.set(key, key === show ? 1 : 0);
      }
    }
  }

  /** Target ground speed (m/s) the controller should use in the unarmed combat stance THIS tick.
   *  BLOCK roots (can't move while blocking). A FULL-body strike (kick/roll/flinch/parry) roots the
   *  base too — its dash window carries the travel. A masked PUNCH does NOT root: the legs keep their
   *  locomotion (top/bottom decoupled), so the player walks at the normal stance speed while punching.
   *  main.ts feeds this into character.combatSpeed each frame. */
  combatGroundSpeed(): number {
    if (this.blocking) return 0; // held block — rooted
    const striking = this.strikeT > 0 && this.strikeKey !== "";
    if (striking && this.strikeLayer === "full") return 0; // kick/roll/flinch/parry root; dash travels
    return COMBAT_WALK_SPEED; // stance walk; masked punches keep moving
  }

  /** Steer the jab onto the reticle: rotate the whole LEFT arm (at the shoulder) so the
   *  shoulder→hand line points at the reticle, keeping the clip's elbow bend (forearm/hand ride
   *  along). The clip extends the arm ~75° to the side, so without this the punch flies left.
   *  Correction RAMPS IN then HOLDS across the punch (incl. the recoil) so the fist drives straight
   *  out-and-back, not up — the clip's own arc rises, which read as an uppercut when it bled in. */
  private applyCombatPunchAim(): void {
    // Full-body kickbox strikes are authored complete (their own arc + step) — only steer the legacy
    // masked u_jab (strikeKey empty).
    if (this.strikeKey) return;
    if (this.punchT <= 0 || this.punchT0 <= 0 || this.combatK < 0.01 || this.farLOD) return;
    const k = this.combatK * Math.min(1, (1 - this.punchT / this.punchT0) * 4) * JAB_AIM; // ramp in (first ~25%), then hold
    if (k < 0.01) return;
    const arm = this.boneByName.get("LeftArm"); // upperarm_l
    const hand = this.boneByName.get("LeftHand");
    if (!arm?.parent || !hand) return;
    this.group.updateMatrixWorld(true);
    arm.getWorldPosition(_jSh);
    hand.getWorldPosition(_jHand);
    if (_jHand.distanceToSquared(_jSh) < 2.5e-3) return; // arm not extended (chamber) — leave it
    _jDir.copy(_jHand).sub(_jSh).normalize(); // current punch direction (shoulder → hand)
    // Target: the body heading, pitched to the reticle (aimPitch > 0 = look down).
    _jFwd.set(0, 0, -1).applyQuaternion(this.group.quaternion);
    _jUp.set(0, 1, 0).applyQuaternion(this.group.quaternion);
    _jRight.crossVectors(_jFwd, _jUp).normalize();
    _jTarget.copy(_jFwd).applyAxisAngle(_jRight, -Math.max(-0.7, Math.min(0.7, this.aimPitch))).applyAxisAngle(_jUp, JAB_OUTWARD).normalize();
    // Rotate the arm so dir → target by k, in world (children ride along ⇒ bend preserved).
    _jCorr.setFromUnitVectors(_jDir, _jTarget);
    _jCorrK.identity().slerp(_jCorr, k);
    arm.getWorldQuaternion(_jArmW);
    arm.parent.getWorldQuaternion(_jParent).invert();
    arm.quaternion.copy(_jParent).multiply(_jCorrK).multiply(_jArmW);
  }

  /** Register a BLOCKED hit (held block, low damage): a blue shield-glow FLASH + a full-body
   *  block-impact flinch. Rides the full-body strike path so it recoils in place, then resumes the
   *  guard. `dir` is where the hit came FROM: the SHIELD stance covers directionally (the kickbox
   *  pack's head-cover flinches, one per side), the DHTH fighting stance has the single dh_blockhit. */
  blockHit(dir: "f" | "b" | "l" | "r" = "f"): void {
    this.blockHitT = 0.18; // shield-flash window (s); decayed in updateStateMachine, read by updateCombatGlow
    const shielding = this.walkActive && this.blocking;
    const key = shielding && this.acts[`kc_blockhit_${dir}`] ? `kc_blockhit_${dir}` : "dh_blockhit";
    const a = this.acts[key];
    if (!a) return;
    a.reset();
    a.setLoop(THREE.LoopOnce, 1);
    a.clampWhenFinished = true;
    this.strikeKey = key; this.strikeLayer = "full";
    this.strikeT = a.getClip().duration / BLOCK_SPEED;
    // Not a cancellable move — clear any combo/cancel state.
    this.activeMoveId = null;
    this.moveHitConfirmed = false;
    this.cancelT = 0;
    this.snapToStrike(); // the flinch reacts THIS frame, the instant the hit lands
  }

  /** Register a successful PARRY (perfectly-timed block, no damage): a brighter shield FLASH + a crisp
   *  full-body deflect snap. The consequences (stagger/deflect) ride the damage path (main.ts). */
  parry(): void {
    this.blockHitT = 0.24; // a longer/brighter flash than a plain block
    // Per-stance deflect: the BLADE blocks with the pack's own Sword_Block (user pick
    // 2026-07-22 — was the unarmed dh_parry stand-in); the SHIELD stance parries with the
    // kickbox snap double-cover; the DHTH fighting stance keeps dh_parry.
    const shielding = this.walkActive && this.blocking;
    const key = this.aimPrefix === "sd" && this.acts["sd_block"] ? "sd_block"
      : shielding && this.acts["kc_parry"] ? "kc_parry" : "dh_parry";
    const a = this.acts[key];
    if (!a) return;
    a.reset();
    a.setLoop(THREE.LoopOnce, 1);
    a.clampWhenFinished = true;
    this.strikeKey = key;
    // The BLADE deflect is TOP-ONLY (user call 2026-07-22, same contract as the parry
    // swipe): upperOnly clip on the masked overlay — a connected parry never roots the
    // legs either. The shield/unarmed stances keep their full-body reactions.
    this.strikeLayer = key === "sd_block" ? "upper" : "full";
    this.strikeT = a.getClip().duration / (key === "sd_block" ? SWORD_BLOCK_ANIM : BLOCK_SPEED);
    this.activeMoveId = null;
    this.moveHitConfirmed = false;
    this.cancelT = 0;
    if (key !== "sd_block") this.snapToStrike(); // masked overlay weights the sd deflect
  }

  private flightPower=0;
  private divePreparation=0;
  setDivePreparation(amount:number):void{
    this.divePreparation=Number.isFinite(amount)?THREE.MathUtils.clamp(amount,0,1):0;
  }
  private flightLaunchRemaining=0;
  setFlightLaunchRemaining(remaining:number):void{
    this.flightLaunchRemaining=Number.isFinite(remaining)?THREE.MathUtils.clamp(remaining,0,FLIGHT_LAUNCH_TOTAL):0;
  }
  private flightApproach=0;
  setFlightApproach(amount:number):void{this.flightApproach=Number.isFinite(amount)?THREE.MathUtils.clamp(amount,0,1):0;}
  setFlightPower(power:number):void{this.flightPower=Number.isFinite(power)?THREE.MathUtils.clamp(power,0,1):0;}
  setJet(mode: "none" | "hover" | "glide" | "dive"): void {
    this.jet = mode;
    if(mode !== "glide") this.straightUpTime = 0;
  }

  // --- LIFT: the LAUNCHED/JUGGLED body (the DMC vertical game's victim half). Driven for
  //     enemy/remote avatars via avatarDriver from the streamed `lf` posture; a full-body
  //     override in selectLocomotion (liftKey) — a ballistic body has no locomotion. ---
  private liftPhase: "" | "up" | "fall" | "down" | "rise" = "";
  private liftKey = "";
  private liftT = 0;
  setLift(phase: "" | "up" | "fall" | "down" | "rise"): void {
    if (phase === this.liftPhase) return;
    // An air-to-air flip (fall↔up) must NOT kill a playing LiftAir hit flourish — the juggle
    // hit that re-lifts the body IS the thing that flipped the phase, so cancelling here
    // erased the very reaction it triggered one frame earlier (user report: enemies weren't
    // showing the LiftAir hits). The flourish rides through; updateLift resumes the correct
    // loop for the CURRENT phase when it ends. Ground transitions still supersede.
    const airToAir = (phase === "up" || phase === "fall") && (this.liftPhase === "up" || this.liftPhase === "fall");
    this.liftPhase = phase;
    if (airToAir && this.liftHitT > 0) return;
    this.liftHitT = 0; // a GROUND phase flip supersedes any in-flight juggle flourish
    const start = (key: string, once: boolean): void => {
      const a = this.acts[key];
      if (!a) { this.liftKey = ""; return; }
      a.reset();
      if (once) { a.setLoop(THREE.LoopOnce, 1); a.clampWhenFinished = true; }
      else a.setLoop(THREE.LoopRepeat, Infinity);
      this.liftKey = key;
      this.liftT = once ? a.getClip().duration : 0;
    };
    if (phase === "up") start("lift_up", true); // the pop → settles to lift_loop (updateLift)
    else if (phase === "fall") start("lift_fall", false);
    else if (phase === "down") start("lift_impact", true); // clamp holds the lying pose
    else if (phase === "rise") start("kip_up", true);
    else this.liftKey = "";
  }

  // --- STAGGER (parried — the Elden Ring stun): the streamed `st` posture drives the
  //     dizzy loop / recover tail, a full-body override like the lift set (below lift in
  //     selectLocomotion — a launch converts the stun into a juggle). ---
  private staggerPhase: "" | "loop" | "end" = "";
  private staggerKey = "";
  setStagger(phase: "" | "loop" | "end"): void {
    if (phase === this.staggerPhase) return;
    this.staggerPhase = phase;
    const key = phase === "loop" ? "st_loop" : phase === "end" ? "st_end" : "";
    const a = key ? this.acts[key] : null;
    if (!a) { this.staggerKey = ""; return; }
    a.reset();
    if (phase === "end") { a.setLoop(THREE.LoopOnce, 1); a.clampWhenFinished = true; }
    else a.setLoop(THREE.LoopRepeat, Infinity);
    this.staggerKey = key;
  }
  /** The launch pop one-shot settles into the suspended loop at the apex; a juggle-hit
   *  flourish (liftHit) plays over either and hands back to the phase clip when done. */
  private updateLift(dt: number): void {
    if (this.liftHitT > 0) {
      this.liftHitT -= dt;
      if (this.liftHitT <= 0 && (this.liftPhase === "up" || this.liftPhase === "fall")) {
        const key = this.liftPhase === "up" ? "lift_loop" : "lift_fall";
        const a = this.acts[key];
        if (a) { a.reset(); a.setLoop(THREE.LoopRepeat, Infinity); this.liftKey = key; }
      }
      return;
    }
    if (this.liftPhase !== "up" || this.liftKey !== "lift_up" || this.liftT <= 0) return;
    this.liftT -= dt;
    if (this.liftT <= 0) {
      const a = this.acts["lift_loop"];
      if (a) {
        a.reset();
        a.setLoop(THREE.LoopRepeat, Infinity);
        this.liftKey = "lift_loop";
      }
    }
  }

  /** Eased roll ground-dip (see the body pass) — smooth in AND out, no stand-up snap. */
  private rollDipK = 0;
  /** GPound landed-crouch drop (see the GPOUND LANDING CROUCH pass). */
  private gpoundDipK = 0;
  /** Eased lying drop for the floored lift pose (see the body pass). */
  private liftLieK = 0;
  /** Was the ground conform engaged last frame? (The engage frame seats with no ease — the
   *  landing impact is a beat, not a fade. See the GROUND CONFORM body pass.) */
  private conformWasActive = false;

  /** The pose's lowest sampled bone height in GROUP-local space (+Y = up there) — the
   *  ground-conform anchor (see the GROUND CONFORM body pass). Null before the rig binds.
   *  getWorldPosition refreshes each chain, so this reads THIS frame's mixer pose. */
  private lowestPoseY(): number | null {
    let low = Infinity;
    for (const n of CONFORM_BONES) {
      const b = this.boneByName.get(n);
      if (!b) continue;
      b.getWorldPosition(_conformV);
      this.group.worldToLocal(_conformV);
      if (_conformV.y < low) low = _conformV.y;
    }
    return low === Infinity ? null : low;
  }
  private liftHitT = 0;
  private liftHitSide: "l" | "r" = "l";
  /** JUGGLE-HIT flourish: while this body is LIFTED, an incoming hit snaps a LiftAir hit
   *  reaction (alternating side) over the suspended/fall loop, then the phase clip resumes
   *  (updateLift). No-op when not airborne-lifted — grounded reactions are flinch()'s job. */
  liftHit(): void {
    if (this.liftPhase !== "up" && this.liftPhase !== "fall") return;
    this.liftHitSide = this.liftHitSide === "l" ? "r" : "l";
    const key = `lift_hit_${this.liftHitSide}`;
    const a = this.acts[key];
    if (!a) return;
    a.reset();
    a.setLoop(THREE.LoopOnce, 1);
    a.clampWhenFinished = true;
    this.liftKey = key;
    this.liftHitT = a.getClip().duration;
  }

  /** Eased boost-run weight (0..1) from the controller — slows the leg cadence toward the boost-glide
   *  stride (see updateStateMachine). */
  private runBoostK = 0;
  setBoost(k: number): void {
    this.runBoostK = k;
  }

  /** SUPER-JUMP charge relay (main.ts: charging a vertical super-jump while NOT moving). Starts the SuperHero
   *  takeoff windup on the rising edge (holds it), and fires the launch on the falling edge (release). */
  setSuperJumpCharge(on: boolean): void {
    const a = this.acts["f_superjump"];
    if (!a) return;
    if (on && this.superJump === "") {
      a.reset();
      a.setLoop(THREE.LoopOnce, 1);
      a.clampWhenFinished = true;
      a.paused = false;
      this.superJump = "charge";
      this.superJumpKey = "f_superjump";
    } else if (!on && this.superJump === "charge") {
      a.paused = false; // RELEASE → resume from the held windup into the launch (the clip's last bit)
      this.superJump = "launch";
    }
  }

  /** Advance the super-jump takeoff: while CHARGING, play the windup then PAUSE at SUPERJUMP_HOLD_FRAC (hold
   *  the cocked pose); while LAUNCHING, let it run out → then clear (the airborne flight pose takes over). */
  private updateSuperJump(): void {
    if (this.superJump === "") return;
    const a = this.acts["f_superjump"];
    if (!a) { this.superJump = ""; this.superJumpKey = ""; return; }
    const dur = a.getClip().duration;
    if (this.superJump === "charge") {
      const hold = dur * SUPERJUMP_HOLD_FRAC;
      if (a.time >= hold) { a.time = hold; a.paused = true; } // reached the windup end → freeze, cocked
    } else { // launch
      if (a.time >= dur - 1e-3) { this.superJump = ""; this.superJumpKey = ""; } // launch done → hand off
    }
  }

  /** Boost-stream intensity 0..1 (eased) — main.ts scales the booster trail brightness/length by this. */
  boostStreamK(): number {
    return this.boostVisK * (1 - this.boosterDis.value); // fades with both the boost AND the deploy
  }
  private readonly flightUp = new THREE.Vector3();
  private readonly flightRight = new THREE.Vector3();
  private readonly flightBack = new THREE.Vector3();
  private readonly flightBasis = new THREE.Matrix4();

  /** Torso frame for flight effects: follows upright hover and prone glide,
   *  without inheriting imported bone-axis conventions or arm animation. */
  flightAnchorInto(out: THREE.Vector3, rotation: THREE.Quaternion): void {
    this.torsoCenterInto(out); // Refreshes the posed skeleton's world matrices.
    this.group.getWorldQuaternion(rotation);
    this.flightRight.set(1, 0, 0).applyQuaternion(rotation);
    const hips = this.boneByName.get("Hips");
    const head = this.boneByName.get("Head");
    if (!hips || !head) return;
    this.flightUp.setFromMatrixPosition(head.matrixWorld);
    this.flightBack.setFromMatrixPosition(hips.matrixWorld);
    this.flightUp.sub(this.flightBack).normalize();
    this.flightBack.crossVectors(this.flightRight, this.flightUp);
    if (this.flightBack.lengthSq() < 1e-6) return;
    this.flightBack.normalize();
    this.flightUp.crossVectors(this.flightBack, this.flightRight).normalize();
    this.flightBasis.makeBasis(this.flightRight, this.flightUp, this.flightBack);
    rotation.setFromRotationMatrix(this.flightBasis);
  }

  /** Torso centre in render space, following both upright and prone poses. */
  torsoCenterInto(out: THREE.Vector3): void {
    // Fresh matrices: a caller between the pose update and the render otherwise
    // reads LAST frame's bones — one frame of planet carry = visible jitter.
    this.group.updateMatrixWorld(true);
    const hips = this.boneByName.get("Hips");
    const head = this.boneByName.get("Head");
    if (!hips || !head) { out.setFromMatrixPosition(this.group.matrixWorld); return; }
    out.setFromMatrixPosition(hips.matrixWorld)
      .add(_torsoV.setFromMatrixPosition(head.matrixWorld))
      .multiplyScalar(0.5);
  }
  /** Fill `out[0..n)` with the WORLD (render-space) boost-stream emit points; returns the count (0 when
   *  the boost is inactive). The streams now come out of the player's HANDS and FEET (the shoulder booster
   *  pods are disabled) — four jets riding the animated hand/foot bones. main.ts feeds these into the trail. */
  boosterStreamSources(out: THREE.Vector3[]): number {
    if (this.boosterDis.value > 0.97) return 0; // boost inactive (same gate the pods used)
    this.group.updateMatrixWorld(true);
    let n = 0;
    for (const name of BOOST_STREAM_BONES) {
      const bone = this.boneByName.get(name);
      if (!bone) continue;
      if (n >= out.length) out.push(new THREE.Vector3());
      bone.getWorldPosition(out[n]);
      n++;
    }
    return n;
  }

  /** Jump-chain count from the character (0 grounded, 1 after the first jump, 2+ after the
   *  double-jump). ===1 means a hover tap is still pending, so the pre-hover descent holds
   *  the leap pose instead of flashing the falling clip. */
  setJumpChain(n: number): void {
    this.jumpChain = n;
  }

  /** Feet height above the surface (system units) from the controller — drives the landing ANTICIPATION
   *  (start the land a beat before touchdown). Init HUGE: REMOTE avatars never call setGroundClearance,
   *  so this stays huge for them → their anticipation never fires (they land on the touchdown edge). */
  private groundClearance = 1e9;
  setGroundClearance(d: number): void {
    this.groundClearance = d;
  }
  private fallSpeed = 0; // peak descent speed (m/s) over the current airborne stint — the land's impact tier

  /** Discard traversal/contact history after assigning the group's teleport
   * destination and orientation. Reapply live movement state before the next
   * update/driveAvatar call. The style, rig and equipped items remain loaded. */
  resetForTeleport(grounded = true): void {
    this.shotRecoil.reset();
    this.flightAimTransition.cancel();this.poseInertia.reset();this.flightAimEngaged=this.flightAimPoseSwitch=false;
    this.endRagdoll();
    this.zeroActs([this.jumpStartKey, ...Object.keys(this.acts).filter(key =>
      key.startsWith('wall_') || key.startsWith('ledge_') || key.startsWith('f_')
      || ['jump', 'p_jump', 'fall_loop', 'bl_start', 'bl_loop', 'bl_land'].includes(key))]);
    this.wallRunState=0;this.wallCatch.cancel();
    this.ledgeClimbState=this.ledgeClimbTime=0;this.ledgeExitAge=1;
    this.ledgeAnchor.set(0,0,0);this.ledgeContact.reset();
    this.crystalTravelPose.reset();
    this.glideAttitude.reset();
    this.flightVerticalPose.reset();
    this.flightLaunchPoseClock.reset();
    this.flightDiveStartClock.reset();this.flightLaunchAlignment.reset();this.diveStartPitch=this.previousDivePreparation=0;
    this.flightHorizontalSpeed=0;
    this.jet=this.prevJet='none';this.prevGrounded=grounded;this.seenHoverAirborne=false;
    this.flightHoverAge=1;
    this.flightPower=this.flightLaunchRemaining=this.flightApproach=this.divePreparation=0;
    this.jumpChain=this.prevJumpChain=this.doubleJumpWingT=0;
    this.jumpT=this.landT=this.jumpStartT=this.glideLaunchT=this.toIdleT=this.jetRecentT=0;
    this.diveLandActive=this.diveLandGrounded=this.landIsJet=this.bigLeapActive=false;
    this.bigLeapT=0;this.jumpStartKey=this.landClip=this.superJumpKey='';this.superJump='';
    this.hoverK=this.glideK=this.speedK=this.moveK=this.moveFwd=this.moveRight=this.climbRate=0;
    this.wingLateralSpeed=this.wingForwardSpeed=0;
    this.airK=grounded?0:1;this.straightUpTime=this.fallSpeed=this.runBoostK=0;
    this.groundClearance=grounded?0:1e9;
    this.hasPrevFwd=false;this.turnRate=0;
    this.resetFootGrounding();this.footfallTracker.reset();this.footfallMask=0;
    this.group.updateMatrixWorld(true);
    this.resetCloth();
    this.crystalWings?.resetForTeleport();
  }

  /** SNAP one air clip to full weight + zero every other clip THIS frame, so a launch pose (the jump
   *  leap / the jetpack takeoff) owns the first airborne frame outright instead of crossfading in over
   *  ~0.1s while the ground pose we left lingers. The mixer ease + selectLocomotion take over next frame. */
  private snapAir(key: string): void {
    const a = this.acts[key];
    if (!a) return;
    a.reset();
    for (const k of Object.keys(this.acts)) this.weights.set(k, k === key ? 1 : 0);
    a.setEffectiveWeight(1);
  }

  /** Flag a boost BIG-LEAP launch (set when character.bigLeapPending rises): play the sprinting-Rfoot
   *  launch one-shot (bl_start), then hold the airborne loop until touchdown lands on the sprint-land
   *  clip. Mirrors flagSuperJump — a one-shot latch consumed here. */
  flagBigLeap(): void {
    this.bigLeapActive = true;
    const act = this.acts["bl_start"];
    if (act) {
      act.reset();
      this.bigLeapT = Math.min(act.getClip().duration - 0.05, 0.6);
      // SNAP bl_start dominant THIS frame and KILL the generic jump/fall (+ bl_loop) outright: the
      // weight ease is ~0.1s, long enough for a residual leap/fall weight (from the ground/air pose we
      // launched out of) to flash the WRONG clip over the start of the leap. Zeroing them (and seeding
      // bl_start at full) means the new jump anim owns the launch with no override. bl_loop eases back
      // in when bigLeapT hits 0 (the airborne phase).
      this.weights.set("bl_start", 1);
      act.setEffectiveWeight(1);
      this.zeroActs(["jump", "p_jump", "fall_loop", "bl_loop"]);
    } else {
      this.bigLeapT = 0;
    }
  }

  /** Start the landing clip for a fall (ANTICIPATED in the air a beat before touchdown, or as a
   *  fallback at the touchdown edge). The clip is picked by the along-ground SPEED → phase
   *  (stand/walk/run/sprint) and the vertical IMPACT → tier (light/heavy). NO zeroActs: the fall loop
   *  (bl_loop) — or the super-jump dive (f_down) — crossfades smoothly INTO the land via the weight
   *  ease, then the land hands off to locomotion (fast when moving, see updateStateMachine). */
  private selectLand(speed: number, sprinting: boolean, impact: number): void {
    // ⚠⚠ THE GENERIC TOUCHDOWN LAND CLIP IS REMOVED (user call 2026-08-03: "as the player lands,
    // their arms stick out — I think we must have SOME animation triggering on land that we
    // absolutely need to find and remove"). It was this: `land_<stand|walk|run|sprint>_<light|heavy>`,
    // a FULL-BODY absorb whose arms swing wide, fired on EVERY touchdown and holding the whole rig
    // for its window. Same family of bug as f_takeoff on the launch and f_down on the fall — a
    // full-body one-shot winning frames the stance and aim layers should own.
    //
    // Touchdown now goes straight into locomotion, which already crossfades. The DIVE landing
    // (flagDiveLand → f_dive_land) is untouched: it is an explicit hand-picked verb with gameplay
    // attached (DIVE_LAND_PAUSE roots the body for its beat), exactly like the dive itself.
    void speed; void sprinting; void impact;
  }

  /** Start the DIVE landing — the SuperHero flight-land flourish. Normally fired by the ANTICIPATION in
   *  updateStateMachine with `ttl` = the live time-to-ground (≈DIVE_LAND_LEAD), so the clip's authored
   *  impact (DIVE_LAND_IMPACT_T) lands exactly when the sim's feet do; a smaller ttl (a short dive whose
   *  descent was still building, the touchdown-edge fallbacks with ttl 0) FAST-FORWARDS the clip by the
   *  shortfall so the impact still aligns — never the aerial approach playing on the ground. The guard
   *  makes late calls no-ops instead of restarting the clip mid-transition. Mirrors selectLand — the dive
   *  pose crossfades INTO it, then it hands off to locomotion. Stand-land fallback if unloaded. */
  flagDiveLand(ttl = 0): void {
    ttl=THREE.MathUtils.clamp(ttl,0,DIVE_LANDING.lead);
    if(this.diveLandActive&&this.landT>0&&(ttl>0||this.diveLandGrounded))return;
    // Touchdown aligns an anticipated clip once; later calls cannot restart it.
    this.diveLandActive=true;this.diveLandGrounded=ttl===0;this.landT=ttl+DIVE_LANDING.lock;
    const key = this.acts["f_dive_land"] ? "f_dive_land" : ["land_stand_heavy", "land"].find((k) => this.acts[k]);
    if (!key) return;
    this.landClip = key;
    this.landIsJet = false;
    const act = this.acts[key]!;
    act.reset();
    // Align the authored impact with the sim touchdown: start the clip so that IMPACT_T arrives in ttl.
    const start = key === "f_dive_land" ? Math.max(0, DIVE_LAND_IMPACT_T - ttl) : 0;
    act.time = Math.min(start, act.getClip().duration - 0.1);
  }

  setReload(progress: number | null): void {
    this.reloadP = progress;
  }

  private reloadP: number | null = null;

  /** LOD: far = show the impostor, hide all skinned meshes (skip skinning cost). */
  setFarLOD(far: boolean): void {
    if (far === this.farLOD) return;
    this.farLOD = far;
    if (this.crystalWings) this.crystalWings.group.visible = !far && this.crystalWings.loaded;
    this.refreshBodyVisibility();
  }

  /** Muzzle position in RENDER space (callers add the floating origin). */
  muzzleWorld(out: THREE.Vector3): boolean {
    if(this.crystalWeaponEnabled)return this.crystalWings!.weaponMorph.muzzleWorld(out,this.crystalWeaponFrame);
    if (!this.muzzle) return false;
    this.muzzle.updateWorldMatrix(true, false);
    this.muzzle.getWorldPosition(out);
    return true;
  }

  update(
    dt: number,
    speed: number,
    grounded: boolean,
    sprinting: boolean,
    moveDir?: { fwd: number; right: number; climb?: number; lateralSpeed?: number; forwardSpeed?: number },
    crouching = false,
    swimming = false,
  ): void {
    this.swimming = swimming;
    this.footfallMask = 0;
    this.compositionGrounded = grounded;
    // Local controllers provide measured body-frame velocity. Older remote/AI
    // callers have direction only; bound that fallback by their reported speed.
    const fallbackFwd = Number.isFinite(moveDir?.fwd) ? moveDir!.fwd : 0;
    const fallbackRight = Number.isFinite(moveDir?.right) ? moveDir!.right : 0;
    const fallbackSpeed = (Number.isFinite(speed) ? Math.abs(speed) : 0) / Math.max(1, Math.hypot(fallbackFwd, fallbackRight));
    this.wingLateralSpeed = Number.isFinite(moveDir?.lateralSpeed) ? moveDir!.lateralSpeed! : fallbackRight * fallbackSpeed;
    this.wingForwardSpeed = Number.isFinite(moveDir?.forwardSpeed) ? moveDir!.forwardSpeed! : fallbackFwd * fallbackSpeed;
    this.resolvedPose = resolveCharacterPose({
      itemId: this.crystalWeaponEnabled&&!this.aiming?null:this.weaponTargetId, speed, crouching, grounded,
      aimHeld: this.aiming || this.walkActive || this.combatActive, focused: this.swordFocus,
      blocked: this.mounted || swimming || this.jet === "glide" || this.jet === "dive",
    });
    // Capture before the mixer restores its clean base. The visible source may
    // already be halfway through a handoff when RMB or Shift changes again.
    const flightAimAllowed=(!grounded||this.crystalWeaponEnabled)&&!swimming&&!this.mounted&&!this.farLOD&&!this.debugClip
      &&!this.wallRunState&&!this.ledgeClimbState&&!this.airCombatOwnsBody()&&!this.composition.playing&&!this.emoteKey;
    const flightAim=flightAimAllowed&&(this.jet==='hover'||grounded&&this.crystalWeaponEnabled)&&this.resolvedPose.aiming
      &&!!this.weapon&&this.gunOut&&this.stowGestureT<=0;
    this.flightAimPoseSwitch=flightAim!==this.flightAimEngaged&&flightAimAllowed
      &&(flightAim||this.jet!=='none'||grounded&&this.crystalWeaponEnabled);
    if(this.flightAimPoseSwitch){
      this.flightAimTransition.begin(this.skeleton.bones,this.body);
      this.armSmooth.clear();this.gunSeatS=0;this.gunSteadyLocal=null;
      this.aimK=flightAim?1:0;
      this.hoverK=this.jet==='hover'?1:0;this.glideK=this.jet==='glide'||this.jet==='dive'?1:0;
      if(this.crystalWeaponEnabled)this.stanceK=flightAim?1:0;
      if(flightAim){
        this.stanceK=1;
        this.glideAttitude.reset();this.crystalTravelPose.reset();
        this.flightLaunchAlignment.reset();this.flightLaunchPoseClock.reset();this.flightDiveStartClock.reset();this.flightVerticalPose.reset();
      }
    }else if(!flightAimAllowed||this.jet==='none'&&!grounded)this.flightAimTransition.cancel();
    this.flightAimEngaged=flightAim;
    if (this.composition.playing) this.composition.step(dt, this.compositionState());
    this.idleT += dt;
    this.updateWeaponVisual(dt); // holster/draw "pixelate" transition
    // DEBUG: play exactly one clip at full weight with NO state machine, aim,
    // lean, leveling or bob — the raw retargeted clip, to verify 1-to-1 fidelity.
    if (this.mixer && this.debugClip) {
      this.straightUpTime = 0;
      this.flightVerticalPose.reset();
      this.footfallTracker.reset();
      for (const key of Object.keys(this.acts)) this.acts[key]!.setEffectiveWeight(key === this.debugClip ? 1 : 0);
      this.basePose?.restore();
      this.underhandStretchAppliedL = this.underhandStretchAppliedR = 1;
      this.mixer.update(dt);
      this.basePose?.capture();
      // Solo previews still need the same volume-preserving twist fallback as gameplay.
      // Imported library clips often leave twist helpers at bind while rotating the wrist.
      this.applyArmTwist();
      applyBodyDeformers(this.skeleton);
      this.body.position.set(0, 0, 0);
      this.body.rotation.set(0, 0, 0);
      this.updateCrystalWings(dt, speed, grounded, moveDir?.right ?? 0, sprinting);
      return;
    }
    this.phase += speed * STRIDE_RATE * dt;
    const ease = 1 - Math.exp(-12 * dt);
    this.speedK += (Math.min(1.2, speed / 6) - this.speedK) * ease;
    this.airK += ((grounded || swimming ? 0 : 1) - this.airK) * ease;
    const ease2 = 1 - Math.exp(-10 * dt);
    this.hoverK += ((this.jet === "hover" ? 1 : 0) - this.hoverK) * ease2;
    // Ride hunch: ease toward the driver-supplied speed fraction (0 when not
    // mounted) so the rider leans down over the bars as the bike speeds up.
    this.mountedHunchK += ((this.mounted ? this.mountedSpeedK : 0) - this.mountedHunchK) * (1 - Math.exp(-5 * dt));
    // The shift BUILDS slowly and releases a little faster. Weight transfer is a
    // whole-body commitment, not a steering input: at a quick ease it snapped on
    // the instant the chassis yawed, which read as twitchy no matter how wide the
    // input range was. Slow enough that a flick through an S-bend barely registers
    // and only a held corner reaches the full pose.
    const leanGoal = this.mounted ? this.mountedLeanTarget : 0;
    const leanRate = Math.abs(leanGoal) > Math.abs(this.mountedLeanK) ? 1.8 : 3.2;
    this.mountedLeanK += (leanGoal - this.mountedLeanK) * (1 - Math.exp(-leanRate * dt));
    // The tuck drops in faster than it comes out — hitting boost is a decision,
    // easing off it is a relax.
    const boostGoal = this.mounted ? this.mountedBoostTarget : 0;
    const boostRate = boostGoal > this.mountedBoostK ? 9 : 4;
    this.mountedBoostK += (boostGoal - this.mountedBoostK) * (1 - Math.exp(-boostRate * dt));
    // The DIVE rides the glide's visual kit (body lean, glide arm pose, gun fade, boost streams) — it IS
    // the full-flight look aimed down, so it shares glideK.
    this.glideK += ((this.jet === "glide" || this.jet === "dive" ? 1 : 0) - this.glideK) * ease2;
    // Smooth the body-local move direction (default forward when not supplied).
    this.moveFwd += ((moveDir ? moveDir.fwd : 0) - this.moveFwd) * ease;
    this.moveRight += ((moveDir ? moveDir.right : 0) - this.moveRight) * ease;
    // Move ramp (0 idle, 1 walking) — eased on the SAME 12/s clock as the clip blend so the
    // combat blade compensation tracks the fist_stance->pistol leg handoff (no torso wobble).
    this.moveK += (Math.min(1, speed / MOVE_RAMP) - this.moveK) * ease;
    // Measure the current trajectory without filtering velocity itself. Clip
    // weights and the final attitude correction provide the visual easing.
    this.climbRate = moveDir?.climb ?? 0;
    this.flightHorizontalSpeed=Math.abs(speed);
    // Every ascent keeps the authored Turn_Up gesture. Sustained powered ascent
    // earns the vertical formation/alignment envelope without changing clips.
    // Require half a second of actual travel within 25° of radial up;
    // leaving that cone (or flight) starts a fresh hold next time.
    const straightUp = !grounded && !swimming && !this.mounted && this.jet === "glide"
      && this.flightPower>.5 && this.divePreparation<=0
      && this.climbRate > 9 && Math.abs(speed) <= this.climbRate * Math.tan(25 * Math.PI / 180);
    this.straightUpTime = straightUp
      ? Math.min(FLIGHT_VERTICAL_FULL, this.straightUpTime + THREE.MathUtils.clamp(Number.isFinite(dt) ? dt : 0, 0, .1)) : 0;
    if(grounded||swimming||this.mounted)this.flightVerticalPose.reset();
    else this.flightVerticalPose.update(dt,this.straightUpTime);
    this.crouching = crouching && grounded;
    this.crouchK += ((this.crouching ? 1 : 0) - this.crouchK) * ease2;
    // Body turn rate (rad/s), measured as the signed yaw of the forward vector around the avatar's LOCAL
    // (radial) up — NOT a world-Y yaw. On the sphere "up" is radial, so the old atan2(fwd.x,-fwd.z) yaw
    // was distorted (wrong sign + magnitude) anywhere off the +Y pole, which made the glide turn anims
    // fire backwards/erratically. (+turnRate = turning RIGHT.) main.ts set group.quaternion this frame.
    _fwd.set(0, 0, -1).applyQuaternion(this.group.quaternion);
    _tUp.set(0, 1, 0).applyQuaternion(this.group.quaternion); // local up (radial)
    if (this.hasPrevFwd && dt > 1e-5) {
      _tCross.crossVectors(this.prevFwd, _fwd);
      const sinA = -_tCross.dot(_tUp); // − so + = RIGHT (clockwise about up)
      const cosA = Math.max(-1, Math.min(1, this.prevFwd.dot(_fwd)));
      this.turnRate += (Math.atan2(sinA, cosA) / dt - this.turnRate) * ease2;
    }
    this.prevFwd.copy(_fwd);
    this.hasPrevFwd = true;
    // Weapon stance: the gun (pistol/rifle) locomotion set whenever a GUN is
    // DRAWN — not only while aiming — if its clips loaded. Aiming layers the
    // torso-pitch solve on top separately (aimK); holster/melee fall to unarmed.
    // A real carried GUN is always armed. The two body constructs are not: at rest they hang
    // on the arm and the body plays the UNARMED set (no widened blade guard in idle) — only the
    // RMB hold (aim for the cannon, focus for the blade) brings the armed stance up... OR an
    // IN-FLIGHT SWORD ACTION. Since attack-from-neutral (the DMC rebuild) a swing no longer
    // implies focus, and the full-body strike override lives INSIDE the armed branch
    // (selectLocomotion, gated on stanceK) — without this term a no-RMB swing dealt its damage
    // while the body visibly kept idling/running (locally AND on remotes playing the relayed
    // action). The one-shot's weight snap covers frame 1; this keeps the branch alive after it.
    const constructReady = (this.cannonOut && this.aiming)
      || (this.swordOut && (this.swordFocus || this.isMeleeStriking()))
      // The GREATSWORD IN HAND is always the armed stance (user call 2026-07-22): its own
      // idle/run/strafe sets show even unlocked — a two-hander is carried, not worn.
      // Z-stowed on the back drops back to the unarmed sets.
      || (this.swordOut && this.gsSword && !this.gsStowed);
    const wantArmed = ((this.gunOut && (!this.crystalWeaponEnabled || this.aiming)) || constructReady)
      && this.acts[`${this.aimPrefix}_idle`] !== undefined;
    this.stanceK += ((wantArmed ? 1 : 0) - this.stanceK) * ease2;
    // Melee/fisticuffs guard weight + jab countdown.
    // UNARMED combat: stance + block weights (drive the glows + crossfade) + the jab/block-hit one-shots.
    // Faster ease than ease2 so the guard snaps in/out in a few frames (not sluggish, not instant).
    const easeCombat = 1 - Math.exp(-COMBAT_EASE_RATE * dt);
    this.combatK += ((this.combatActive ? 1 : 0) - this.combatK) * easeCombat;
    this.walkK += ((this.walkActive ? 1 : 0) - this.walkK) * easeCombat; // walk-mode head-look weight
    this.blockK += ((this.combatActive && this.blocking ? 1 : 0) - this.blockK) * easeCombat;
    this.punchT = Math.max(0, this.punchT - dt);
    this.blockHitT = Math.max(0, this.blockHitT - dt);
    this.updateGuardWalk(speed);
    // KICKBOX strike timers. strikeKey clears when its anim runs out (back to guard/move); the
    // cancel window (cancelT, armed by onMoveHit) keeps ticking so a slightly-late heavy still cancels.
    this.updateLift(dt); // launched-body pop → suspended-loop settle
    if (grounded) {
      this.airChainLive = false; // touching down ends the air string's float...
      this.airComboStep = 0; // ...AND the string itself — land to reset, always (a stale
      //                        step otherwise leaks into the next airborne stint)
    }
    const strikeWas = this.strikeKey;
    this.strikeT = Math.max(0, this.strikeT - dt);
    this.updateKnockRise(strikeWas); // knockdown reel retires → the kip-up takes over
    if (this.strikeT <= 0) this.strikeKey = "";
    // Z-STOW mount swap: fires mid-sheathe/draw (the hand at the shoulder).
    if (this.gsStowSwapT > 0) {
      this.gsStowSwapT -= dt;
      if (this.gsStowSwapT <= 0 && this.gsStowed !== this.gsStowTarget) {
        this.gsStowed = this.gsStowTarget;
        this.remountGreatsword();
      }
    }
    // comboWindowT does NOT decay here: it is GAMEPLAY state paired with actionElapsedMs and
    // must run on the same fixed-tick clock (stepAction) — draining it on render dt while the
    // cancel point advances on sim time made chains expire early whenever the two clocks
    // diverged (low FPS; grossly, the headless probe). Same split as the action clock itself.
    this.cancelT = Math.max(0, this.cancelT - dt);
    if (this.strikeT <= 0) {
      this.activeMoveId = null;
      this.moveHitConfirmed = false;
    }
    // Idle breakers: while standing guard (in the stance, not striking/blocking/moving), accrue
    // stillness and fire an occasional full-body flourish; any movement or action cancels it.
    const stanceGuard =
      (this.combatActive || this.combatK > 0.3) && this.strikeT <= 0 && !this.blocking;
    if (stanceGuard && speed < 0.4 && !this.crouching) {
      this.idleStillT += dt;
      if (this.idleBreakT > 0) {
        this.idleBreakT = Math.max(0, this.idleBreakT - dt);
        if (this.idleBreakT <= 0) this.idleBreakKey = ""; // flourish finished → back to guard
      } else if (this.idleStillT >= this.idleBreakNextT && this.acts["kc_break1"]) {
        const key = Math.random() < 0.5 ? "kc_break1" : "kc_break2";
        const a = this.acts[key];
        if (a) {
          a.reset();
          a.setLoop(THREE.LoopOnce, 1);
          a.clampWhenFinished = true;
          this.idleBreakKey = key;
          this.idleBreakT = a.getClip().duration;
        }
        this.idleStillT = 0;
        this.idleBreakNextT = IDLE_BREAK_MIN + Math.random() * IDLE_BREAK_VARY;
      }
    } else {
      this.idleStillT = 0;
      this.idleBreakT = 0;
      this.idleBreakKey = "";
    }

    // Idle gestures: in conversation (tight cadence) or just AMBIENT
    // (relaxed cadence — settlement people shift their weight and look
    // around instead of standing frozen). Mirrors the kickbox idle-breaker
    // pattern — cosmetic, so plain Math.random is fine.
    if (!this.conversationPerformance && (this.talking || this.ambient) && speed < 0.4 && grounded && !this.crouching) {
      if (this.talkGestureT > 0) {
        this.talkGestureT = Math.max(0, this.talkGestureT - dt);
        if (this.talkGestureT <= 0) this.talkGestureKey = "";
      } else {
        this.talkNextT -= dt;
        if (this.talkNextT <= 0 && this.acts["npc_gesture1"]) {
          const key = (["npc_gesture1", "npc_gesture2", "npc_gesture3"] as const)[
            Math.min(2, Math.floor(Math.random() * 3))
          ];
          const a = this.acts[key];
          if (a) {
            a.reset();
            a.setLoop(THREE.LoopOnce, 1);
            a.clampWhenFinished = true;
            this.talkGestureKey = key;
            this.talkGestureT = a.getClip().duration;
          }
          this.talkNextT = this.talking ? 2.5 + Math.random() * 4 : 9 + Math.random() * 16;
        }
      }
    } else if (this.talkGestureKey) {
      this.talkGestureKey = "";
      this.talkGestureT = 0;
    }

    if (this.mixer) {
      this.updateStateMachine(speed, grounded, sprinting, dt);
    } else {
      this.proceduralPose();
    }

    // BOOST STATE — eased whenever a FUEL-USING boost is active (hover/glide flight or boost-run).
    // boosterDis eases 1→0 when boosting, 0→1 when it ends; boostVisK follows the boost intensity. These
    // still drive the boost STREAM (boostStreamK gates on both, boosterStreamSources returns 0 when
    // boosterDis is high) — even though the shoulder pods that used to consume them are now disabled.
    const boostActive = this.jet !== "none" || this.runBoostK > 0.05;
    const disRate = dt * 6; // ~0.17s, matches the weapon draw/holster
    this.boosterDis.value = boostActive
      ? Math.max(0, this.boosterDis.value - disRate)
      : Math.min(1, this.boosterDis.value + disRate);
    const intensity = Math.min(1, this.glideK * 1 + this.hoverK * 0.7 + this.runBoostK * 0.9);
    this.boostVisK += (intensity - this.boostVisK) * (1 - Math.exp(-10 * dt));
    // SHOULDER BOOSTER PODS — DISABLED (commented out per design; see the build in the constructor). The
    // pods no longer render; the boost streams now emit from the hands and feet (boosterStreamSources).
    // Un-comment this placement AND the constructor build to bring the pods back.
    /*
    const deploy = 1 - this.boosterDis.value; // 0 retracted → 1 deployed
    const anyBooster = deploy > 0.001;
    if (anyBooster) this.rigRoot.updateMatrixWorld(true);
    this.rigRoot.getWorldQuaternion(_bRigQ);
    for (const b of this.boosters) {
      b.grp.visible = anyBooster;
      if (!anyBooster) continue;
      const side = Math.sign(b.deployOff.x) || 1;
      const hk = this.hoverK;
      // RIGID EXTENSION of the skeleton: take the SHOULDER bone's FULL world orientation (which carries the
      // flight clip's pitch AND ROLL) and remap it to the body frame via the bind-pose corrective. The pods
      // then move/rotate exactly with the shoulders — including bank/roll while flying — no lag, no drift.
      b.bone.getWorldQuaternion(_bBoneQ);
      _bBodyQ.copy(_bBoneQ).multiply(b.corrective); // body frame as carried by the bone
      b.bone.getWorldPosition(_bRoot);
      // Position: shoulder bone + offset in that (rolled) body frame → rigRoot-local for the child group.
      _bDeployed.copy(b.deployOff).applyQuaternion(_bBodyQ);
      _bRetracted.copy(b.retractOff).applyQuaternion(_bBodyQ);
      _bWorld.lerpVectors(_bRetracted, _bDeployed, deploy).add(_bRoot); // emerge from the back
      this.rigRoot.worldToLocal(_bWorld);
      b.grp.position.copy(_bWorld);
      // ORIENTATION: the body frame × (point-up / flat-back) × a slight outward fan (straightens on hover),
      // expressed in rigRoot-local (the grp's parent). Inherits the shoulder's roll → the pods bank with it.
      _bQL.copy(_bBodyQ).multiply(_PICK_IN_BODY);
      _bFanQ.setFromEuler(_bEuler.set(0, 0, side * 0.12 * (1 - hk)));
      _bQL.multiply(_bFanQ);
      b.grp.quaternion.copy(_bRigQ).invert().multiply(_bQL); // → rigRoot-local
      b.grp.scale.set(1, 1, 1); // keep the turbine barrel at its full flight shape in every mode (no hover squash)
      const pulse = 0.7 + Math.sin(this.idleT * 30 + side) * 0.3; // exhaust brightness flicker (not movement)
      const nm = (b.nozzle as THREE.Mesh).material as THREE.MeshBasicMaterial;
      nm.opacity = 0.9 * (0.45 + this.boostVisK * 0.55) * pulse;
      b.nozzle.scale.set(1 + this.boostVisK * 0.3, 0.45, 0.7); // flat exhaust glow, widens with boost intensity
    }
    */

    // RELY ON THE ANIMATION: the Manny clips bake their own bob, forward lean and
    // crouch-lowering, so when the mixer is driving we add NO procedural body tilt
    // or bob (those double up and fight the clip). Only the no-clips procedural
    // fallback gets the synthetic bob + lean.
    if (this.mixer) {
      // BOOST-RUN leap: the body rises WITH THE FEET so each leap is locked to a foot leaving the
      // ground. Drive it off min(footL, footR) height above the rig root — that's the "both feet off
      // the ground" (flight) amount, which peaks between footplants. Auto-calibrate stance↔flight to a
      // 0..1 envelope (immune to clip/rig scale), then layer the forward lunge. 0 when not boosting.
      let leap = 0;
      const fl = this.boneByName.get("foot_l"), fr = this.boneByName.get("foot_r");
      if (this.runBoostK > 0.01 && fl && fr) {
        this.group.updateMatrixWorld(true);
        _bUp.set(0, 1, 0).applyQuaternion(this.group.quaternion);
        this.rigRoot.getWorldPosition(_bRoot);
        const liftL = fl.getWorldPosition(_bFL).sub(_bRoot).dot(_bUp);
        const liftR = fr.getWorldPosition(_bFR).sub(_bRoot).dot(_bUp);
        const flight = Math.min(liftL, liftR); // both feet up = airborne; planted foot pins it low
        this.boostLiftLo += (flight - this.boostLiftLo) * (flight < this.boostLiftLo ? 0.4 : 0.01); // track the stance low
        this.boostLiftHi += (flight - this.boostLiftHi) * (flight > this.boostLiftHi ? 0.4 : 0.01); // track the flight high
        const range = Math.max(0.05, this.boostLiftHi - this.boostLiftLo);
        leap = Math.max(0, Math.min(1, (flight - this.boostLiftLo) / range));
        // EXTENDED FEET: point the toes of each OFF-ground foot, scaled by how high it is (planted
        // foot ≈ 0 → unchanged), so the feet trail extended through the leap instead of tucked. The
        // mixer overwrites the foot bones next frame, so this never compounds.
        _aoFwd.set(0, 0, -1).applyQuaternion(this.group.quaternion);
        _aoRight.crossVectors(_aoFwd, _bUp).normalize(); // body-right (plantarflex axis)
        const pointFoot = (foot: string, lift: number): void => {
          const ext = Math.max(0, Math.min(1, (lift - this.boostLiftLo) / range)) * this.runBoostK;
          if (ext < 0.01) return;
          _aoPitch.setFromAxisAngle(_aoRight, BOOST_FOOT_POINT * ext);
          this.rotateBoneWorld(foot, _aoPitch);
        };
        pointFoot("foot_l", liftL);
        pointFoot("foot_r", liftR);
      }
      this.body.position.y =
        this.runBoostK * BOOST_LEAP * leap -
        this.crouchK * CROUCH_DROP * (1 - CROUCH_MOVE_LIFT * this.moveK); // moveK is eased → smooth, no per-stride bob
      this.body.rotation.x = -this.runBoostK * BOOST_LEAN;
    } else {
      const bob = Math.abs(Math.cos(this.phase)) * 0.05 * this.speedK * (1 - this.airK);
      this.body.position.y = bob - this.crouchK * 0.32;
      this.body.rotation.x = bodyLean(this.speedK, sprinting, this.airK, this.glideK, false, this.aimK);
    }
    // ROLL DIP — the dodge hugs the ground: the clip's authored pelvis drop lives in a track
    // our in-place policy strips (and its FBX frame defeats keepHipsPos — see the sd_roll_*
    // defs), so the body root dips procedurally through the tumble: a half-sine to ~0.42 m at
    // mid-roll. EASED through rollDipK (not applied raw) so a move-cancelled recovery SURFACES
    // over ~0.15 s instead of snapping upright in one frame. Additive AFTER the bob/crouch pass.
    // Every ground roll is an SSH tumble again (the UAL side-step/flip experiments were
    // reverted) — they all dip. The AIR dodge (dodge_l/r keys) rides the strike channel
    // too but never matches the sd_roll_ prefix.
    let dipTarget = 0;
    if (this.strikeT > 0 && this.strikeKey.startsWith("sd_roll_")) {
      const a = this.acts[this.strikeKey];
      const dur = a ? a.getClip().duration / SWORD_ROLL_ANIM : 1.1;
      const prog = Math.min(1, Math.max(0, 1 - this.strikeT / dur));
      dipTarget = 0.42 * Math.sin(Math.PI * Math.min(1, prog / 0.85));
    }
    this.updateHitFlash(dt);
    this.rollDipK += (dipTarget - this.rollDipK) * (1 - Math.exp(-14 * dt));
    if (Math.abs(this.rollDipK) > 1e-4) this.body.position.y -= this.rollDipK;
    // GROUND CONFORM — the SYSTEMATIC floored-pose fix (user call 2026-07-23, replacing the
    // hand-tuned LIE_DROP): our in-place policy strips every clip's authored hip journey, so
    // ANY floored pose (lift_impact, the kip-up's lying start, future knockdowns) hovers at
    // standing pelvis height. Instead of measuring each clip by hand, ANCHOR TO THE BODY
    // ITSELF: while floored/rising, FK-sample the pose's LOWEST key bone (feet/hands/head/
    // pelvis, group-local) and drop the body root so that point rests at ground + a body
    // radius. The kip-up needs no special casing — as its pose stands up, the measured low
    // point rises and the offset unwinds by construction. Eased both ways (no snap).
    // ...and the same seat applies to a GROUNDED knockdown one-shot (CONFORM_STRIKES): the
    // poise-break reel lays the body out with its root stripped, so without this the victim
    // falls and floats at the bottom of the fall. Grounded only — an airborne victim's own
    // lift phase owns it, and there is no ground under them to seat against.
    // Seat the dive crouch as a whole, retaining the authored leg bends.
    const conformActive = this.liftPhase === "down" || this.liftPhase === "rise"
      || (grounded && this.diveLandActive)
      || (grounded && this.strikeT > 0 && CONFORM_STRIKES.has(this.strikeKey))
      || (grounded && CONFORM_EMOTES.has(this.emoteKey));
    let conformTarget = 0;
    if (conformActive) {
      const low = this.lowestPoseY();
      if (low !== null) conformTarget = Math.max(0, low - CONFORM_REST_Y);
    }
    // THE IMPACT INSTANT SEATS IMMEDIATELY. A launched body is floored by the sim in one frame
    // and its lying pose is already laid out, so easing IN from zero left it hovering ~0.8 m up
    // for a few frames — the landing read mushy, "it doesn't hit the ground" (measured,
    // _plungefall.mjs). Snap on the frame the conform ENGAGES; ease only while tracking.
    // RELEASE must then keep up with the POSE: the kip-up stands the body up in ~0.7 s and at
    // the old release rate (5/s) the seat lagged ~0.3 m, sinking the legs through the ground
    // mid-rise. The easing exists only to keep FK sampling noise out of the root, so a fast
    // release is free; the engage rate still smooths the poise-break's slower lie-down.
    if (conformActive && !this.conformWasActive) this.liftLieK = conformTarget;
    else this.liftLieK += (conformTarget - this.liftLieK) * (1 - Math.exp(-(conformTarget > this.liftLieK ? 8 : 18) * dt));
    this.conformWasActive = conformActive;
    if (this.liftLieK > 1e-4) this.body.position.y -= this.liftLieK;
    // GPOUND LANDING CROUCH — the slam's grounded tail floats the feet: the dive is authored
    // legs-tucked (+0.43 m feet, measured — _gpoundfeet.mts) with the DESCENT in the root
    // track our in-place policy strips (the sim owns it via slamDown), so after touchdown
    // the tucked pose hangs at standing height — "feet above the ground" (user report).
    // Same procedural family as the roll dip: while the gpound strike is GROUNDED, drop the
    // body root by the authored tuck, easing out over the clip's own leg-extension tail
    // (feet re-extend 0.83 s → end, 0.43 → 0.03). groundClearance keeps the DIVE untouched
    // (remotes leave it huge — they render the dive pose only; the slam lands too fast for
    // a remote to read the landed crouch, and the ballistic fast-track covers the victim).
    let gpTarget = 0;
    if (this.strikeT > 0 && this.strikeKey === "sd_gpound" && this.groundClearance < 0.25) {
      const a = this.acts.sd_gpound;
      const dur = a ? a.getClip().duration : 1.17;
      const tClip = Math.max(0, dur - this.strikeT); // gpound runs at rate 1.0 — clip time = action time
      const extend = Math.min(1, Math.max(0, (tClip - 0.83) / (dur - 0.83))); // the authored leg extension
      gpTarget = 0.42 * (1 - extend) + 0.03 * extend;
    }
    this.gpoundDipK += (gpTarget - this.gpoundDipK) * (1 - Math.exp(-(gpTarget > this.gpoundDipK ? 16 : 8) * dt));
    if (this.gpoundDipK > 1e-4) this.body.position.y -= this.gpoundDipK;

    // NO procedural body roll in flight: the SUPERHERO flight clips (Anim_Fly_Boost + Boost_Left/Right)
    // already bank into turns themselves, so the old ship-style glideBank double-banked + rolled the
    // straight glide. Keep the body upright; the clip owns the orientation.
    this.body.rotation.z = 0;
    this.applyUpperPoseStage(dt);
    this.applyContactPoseStage(dt, speed, grounded);
    if(this.crystalWings)this.crystalTravelPose.finish(dt,this.boneByName,
      !this.mounted&&!this.swimming&&!this.airCombatOwnsBody()&&this.landT<=0&&!this.composition.playing&&!this.emoteKey);
    if(this.flightAimTransition.active){
      this.flightAimTransition.apply(dt);
      // Derive wrist twist from the blended arms, before body/garment deformation.
      this.applyArmTwist();
    }
    if(this.skeleton){
      if(this.poseInertia.apply(dt,this.skeleton.bones))this.applyArmTwist();
      this.poseInertia.record(dt,this.skeleton.bones);
    }
    this.updateFootfalls(dt, speed, grounded);
    // Simulated cloth (cloak) rides the final animated pose; skipped at far LOD (hidden
    // anyway). clothWaterK eases toward the swim state so the cape gains buoyancy + drag
    // in the water (and drains back to a dry hang on exit).
    this.clothWaterK += ((this.swimming ? 1 : 0) - this.clothWaterK) * (1 - Math.exp(-4 * dt));
    if (!this.farLOD && this.clothSimulationEnabled && this.clothSim) {
      const parent = this.group.parent;
      const frame = this.clothFrame ?? (parent && !(parent as THREE.Scene).isScene ? parent : null);
      // The co-rotating frame (footPlanet.group) is posed by system.update() this frame, but its
      // matrixWorld is only recomputed by the RENDERER at draw time — AFTER this. Refresh it now
      // so the cloth reads THIS frame's pose, not last render's: a one-render-stale frame makes
      // the planet's spin carry read as a per-frame jitter on the cape (the outfitter has no frame
      // → it never saw this, which is why it looked calmer than the overworld).
      frame?.updateWorldMatrix(true, false);
      this.clothSim.step(dt, this.group, this.clothWaterK, this.clothWind.lengthSq() > 0 ? this.clothWind : null, frame);
    }
    if (!this.farLOD && this.clothSimulationEnabled && this.pbdCapes.length) {
      // collider lifecycle: rebuild when the body/outfit/flag signature moves (covers the async
      // body + garment loads); re-skin + refit the BVH surfaces at the configured cadence
      const sig = `${this.glbBody?.meshes.length ?? 0}:${this.styledSkinned.length}:${this.pbdBvh}`;
      if (sig !== this.pbdColliderSig) this.refreshPbdColliders();
      // Collide against THIS frame's pose, not last render's: bone matrixWorlds + boneMatrices
      // normally update at draw time, one frame after the animation was sampled, so the whole
      // cloth stack historically ran one pose stale — measured as 11–30 mm of torso sweep-through
      // at run/sprint. The animation pipeline above has already posed the bone locals; refresh
      // the skeleton now so the colliders, the capsules AND the pin/rest skinning read pose N.
      // (~0.1 ms; the renderer's own update later in the frame then finds everything current.)
      this.rigRoot.updateWorldMatrix(true, true);
      this.skeleton.update();
      if (this.pbdFrameNo++ % Math.max(1, this.pbdRefitEvery) === 0) {
        for (const col of this.pbdMeshCols) col.refresh();
      }
      const parent = this.group.parent;
      const frame = this.clothFrame ?? (parent && !(parent as THREE.Scene).isScene ? parent : null);
      frame?.updateWorldMatrix(true, false); // see the note above — one render stale otherwise
      // gravity is along the avatar's own down, so it stays correct anywhere on a planet
      _pbdDown.set(0, -1, 0).applyQuaternion(this.group.getWorldQuaternion(_pbdQuat)).normalize();
      const wind = this.clothWind.lengthSq() > 0 ? this.clothWind : null;
      for (const c of this.pbdCapes) c.update(dt, _pbdDown, frame, wind, this.clothWaterK);
    }
    // God-mode calibration pose stomps EVERYTHING above (it is the reference stance).
    this.applyDebugPose();
    applyBodyDeformers(this.skeleton);
    this.applyAuthoredEyePivots();
    this.updateCrystalWings(dt, speed, grounded, moveDir?.right ?? 0, sprinting);
    if (this.hairMotion.length) {
      const parent = this.group.parent;
      const frame = this.clothFrame ?? (parent && !(parent as THREE.Scene).isScene ? parent : null);
      for (const motion of this.hairMotion) motion.step(dt, this.group, frame, this.clothWind, this.clothWaterK,
        this.hairPhysicsEnabled && !this.farLOD && this.group.visible && motion.mesh.visible);
    }
  }

  /** Hips over the saddle, hands on the bars, boots on the pegs. Targets
   * are avatar-root local, then resolved through the live vehicle-carried
   * group transform, so the pose follows airborne pitch and roll. */
  private applyMountedPose(weight: number): void {
    const pose = this.mountedPose;
    const lean = this.mountedLeanK;
    const boost = this.mountedBoostK;
    const bodyOffset = pose?.bodyOffset ?? [0, -0.56, 0];
    // Weight shift: the hips slide INTO the corner, and the whole body drops
    // under boost. Both are added to the authored seat offset rather than
    // replacing it, so a chassis keeps whatever stance was authored for it.
    const seatX = bodyOffset[0] + lean * MOUNT_LEAN_SHIFT;
    const seatY = bodyOffset[1] - boost * MOUNT_BOOST_CROUCH;
    this.body.position.x += (seatX - this.body.position.x) * weight;
    this.body.position.y += (seatY - this.body.position.y) * weight;
    this.body.position.z += (bodyOffset[2] - this.body.position.z) * weight;
    // Base authored pitch, deepened by up to MOUNT_HUNCH_MAX radians at top
    // speed, and again by the boost tuck. Hands are IK-pinned to the bars, so
    // pitching the torso forward pulls the head down into a racing tuck.
    const bodyPitch = (pose?.bodyPitch ?? -0.16)
      - this.mountedHunchK * this.mountHunchMax
      - boost * MOUNT_BOOST_PITCH;
    this.body.rotation.x += (bodyPitch - this.body.rotation.x) * weight;
    // Roll into the turn. This used to be forced to zero while mounted; the
    // rider now carries the lean, which is what makes a corner read as ridden
    // rather than as the bike tilting underneath a passenger.
    const bodyRoll = -lean * MOUNT_LEAN_ROLL;
    this.body.rotation.z += (bodyRoll - this.body.rotation.z) * weight;

    // Authored FK bone offsets — the torso chain + clavicles are applied HERE,
    // before the world matrices are refreshed for IK, so the arms/legs re-solve
    // onto the grips/pegs after the body is reshaped.
    const bones = pose?.bones;
    if (bones) {
      for (const name in bones) {
        if (MOUNT_PRE_IK_BONES.has(name)) this.applyBoneOffset(name, bones[name], weight);
      }
    }
    this.group.updateMatrixWorld(true);

    // The rider watches the road: pin the head to face along the bike's heading
    // (a touch upward), independent of how far the torso is hunched — otherwise
    // the forward torso pitch would bury the chin in the tank. An authored `head`
    // offset then composes on top of the level look.
    const head = this.boneByName.get("Head");
    if (head?.parent) {
      this.group.getWorldQuaternion(_hPitch); // group (heading) world quat
      _hDesired.copy(_hPitch).multiply(this.headRestRelGroup); // head facing forward, level
      _hFwd.set(0, 0, -1).applyQuaternion(_hPitch);
      _hUp.set(0, 1, 0).applyQuaternion(_hPitch);
      _hRight.crossVectors(_hFwd, _hUp).normalize();
      // Under boost the rider stops sightseeing and gets down behind the cowl,
      // so most of the "watch the road" lift is given back.
      _hPitch.setFromAxisAngle(_hRight, this.mountHeadLift * (1 - boost * MOUNT_BOOST_HEAD));
      _hDesired.premultiply(_hPitch);
      head.parent.getWorldQuaternion(_hPar).invert();
      _hLocal.copy(_hPar).multiply(_hDesired);
      head.quaternion.slerp(_hLocal, weight);
    }
    if (bones?.head) this.applyBoneOffset("head", bones.head, weight);

    const target = (x: number, y: number, z: number): THREE.Vector3 => {
      _mountTarget.set(x, y, z);
      return this.group.localToWorld(_mountTarget);
    };
    const pole = (v: readonly [number, number, number]): THREE.Vector3 =>
      _mountPole.set(v[0], v[1], v[2]).applyQuaternion(this.group.quaternion).normalize();

    const lh = pose?.leftHand ?? [-0.5, 0.52, -1.02];
    const rh = pose?.rightHand ?? [0.5, 0.52, -1.02];
    const lf = pose?.leftFoot ?? [-0.56, -0.34, 0.12];
    const rf = pose?.rightFoot ?? [0.56, -0.34, 0.12];
    // Counter-steer: the inside hand pulls back, the outside hand pushes. Tiny,
    // but it is what stops the arms reading as two rigid struts in a corner.
    const handPush = lean * MOUNT_LEAN_HAND;
    // Boost pulls the grips in and down (elbows tucked) and the knees together.
    const elbowIn = boost * MOUNT_BOOST_ELBOW;
    const kneeIn = boost * MOUNT_BOOST_KNEE;
    this.ikLeg("LeftArm", "LeftForeArm", "LeftHand",
      target(lh[0] + elbowIn, lh[1] - elbowIn * 0.5, lh[2] + handPush),
      pole(pose?.leftElbowPole ?? [-0.7, -0.7, -0.2]), weight, false);
    this.ikLeg("RightArm", "RightForeArm", "RightHand",
      target(rh[0] - elbowIn, rh[1] - elbowIn * 0.5, rh[2] - handPush),
      pole(pose?.rightElbowPole ?? [0.7, -0.7, -0.2]), weight, false);
    this.ikLeg("LeftUpLeg", "LeftLeg", "LeftFoot",
      target(lf[0] + kneeIn, lf[1], lf[2]),
      pole(pose?.leftKneePole ?? [-0.35, 0, -1]), weight, false);
    this.ikLeg("RightUpLeg", "RightLeg", "RightFoot",
      target(rf[0] - kneeIn, rf[1], rf[2]),
      pole(pose?.rightKneePole ?? [0.35, 0, -1]), weight, false);

    // Remaining authored bones (wrists, ankles, toes …) are offset AFTER the IK,
    // so they re-orient the extremities without pulling them off the grips/pegs.
    if (bones) {
      for (const name in bones) {
        if (name !== "head" && !MOUNT_PRE_IK_BONES.has(name)) this.applyBoneOffset(name, bones[name], weight);
      }
    }
  }

  /** Kick the outside leg high over the rear of the saddle. Root warping moves
   * the hips onto the bike; this independent leg arc supplies the readable
   * Halo/motorcycle silhouette instead of hopping the whole avatar. */
  private applyVehicleMountSwing(): void {
    const progress = this.vehicleMountSwing;
    const weight = Math.min(1, Math.sin(Math.PI * progress) * 1.3);
    if (weight <= 0.001) return;
    const fromLeft = this.vehicleMountSide < 0;
    const rightLeg = fromLeft;
    const foot = rightLeg
      ? this.mountedPose?.rightFoot ?? [0.24, -0.34, -0.06]
      : this.mountedPose?.leftFoot ?? [-0.24, -0.34, -0.06];
    // Ground endpoint beside the planted leg -> high/rear control point -> peg.
    _mountSwingStart.set(rightLeg ? 0.18 : -0.18, 0.02, 0.16);
    _mountSwingEnd.set(foot[0], foot[1], foot[2]);
    const p = this.vehicleMountSwingReverse ? 1 - progress : progress;
    const a = 1 - p;
    _mountSwingLocal.set(
      a * a * _mountSwingStart.x + 2 * a * p * 0 + p * p * _mountSwingEnd.x,
      a * a * _mountSwingStart.y + 2 * a * p * 1.08 + p * p * _mountSwingEnd.y,
      a * a * _mountSwingStart.z + 2 * a * p * 0.48 + p * p * _mountSwingEnd.z,
    );
    this.group.updateMatrixWorld(true);
    this.group.localToWorld(_mountSwingLocal);
    // Knee OUT and slightly UP — the leg-over-the-saddle read. ⚠ The z used to be +0.65, i.e. BEHIND
    // the rider: a knee cannot bend backward, so the anti-hyperextension clamp spent most of the arc
    // pinning this pole to its floor (measured 0.28–0.35 against the thigh's anterior axis, twice per
    // swing). Pointing it slightly forward lets the authored arc play as-is, clamp untouched.
    _mountPole.set(rightLeg ? 0.5 : -0.5, 0.3, -0.35)
      .applyQuaternion(this.group.quaternion).normalize();
    if (rightLeg) {
      this.ikLeg("RightUpLeg", "RightLeg", "RightFoot", _mountSwingLocal, _mountPole, weight, false);
    } else {
      this.ikLeg("LeftUpLeg", "LeftLeg", "LeftFoot", _mountSwingLocal, _mountPole, weight, false);
    }
  }

  /** Multiply a rig bone's local rotation by an authored euler offset (XYZ
   *  radians). No-op if the bone name is unknown. */
  private applyBoneOffset(name: string, e: readonly [number, number, number], weight = 1): void {
    const bone = this.boneByName.get(name);
    if (!bone) return;
    _boneEuler.set(e[0] * weight, e[1] * weight, e[2] * weight);
    bone.quaternion.multiply(_boneQuat.setFromEuler(_boneEuler));
  }

  /** Masked/additive upper-body corrections consume the clean base pose. */
  private applyUpperPoseStage(dt: number): void {
    if(this.wallOwnsPose()){this.shotRecoil.reset();this.applyEyeLook(dt);this.applyFace(dt,0);return;}
    this.applyArmOverrides(dt);
    const travel = !!this.crystalWings && !this.mounted && !this.swimming && !this.airCombatOwnsBody()
      && this.landT <= 0 && !this.composition.playing && !this.emoteKey;
    // Local and remote callers can still carry a decaying/stale approach value
    // after changing flight mode. Hover owns its dangling legs; ease the old
    // glide preparation out rather than sampling a new airborne landing pose.
    const approach = travel && this.jet === 'glide' ? this.flightApproach : 0;
    // Reorient the authored dive/pull-up shape along the actual arc. Rotating
    // around the pelvis keeps the root/camera fixed and preserves the bank.
    // Near-vertical travel aligns before its sustained envelope qualifies. Landing
    // retains its own silhouette.
    const diveLanding=this.diveLandActive&&this.landT>0&&!!this.crystalWings&&!this.mounted&&!this.swimming;
    if(diveLanding&&this.glideAttitude.angle!==0)
      this.glideAttitude.release(this.group,this.boneByName,Math.max(0,this.landT-DIVE_LANDING.lock));
    else if(!travel||this.compositionGrounded||this.jet==='none')this.glideAttitude.reset();
    else {
      const launch=this.flightLaunchAlignment.amount;
      const diveStart=this.flightDiveStartClock.orientation;
      const ordinary=this.jet==='glide'&&this.divePreparation<=0
        ?(1-this.flightPower+this.flightPower*Math.max(this.flightVerticalPose.amount,flightVerticalAlignment(this.flightHorizontalSpeed,this.climbRate)))
          *THREE.MathUtils.smoothstep(Math.hypot(this.flightHorizontalSpeed,this.climbRate),6,14):0;
      const path=Math.atan2(this.climbRate,Math.max(.01,this.flightHorizontalSpeed));
      const gathering=this.divePreparation>0;
      // The free launch aims during the pause, then follows its real momentum.
      // Ctrl uses that same gesture aimed down, including the outgoing source
      // fade; neither countdown edge enables a second, unrelated body tilt.
      const turnDuration=.3+.14*THREE.MathUtils.smoothstep(this.diveStartPitch,0,Math.PI/2);
      const diveElapsed=this.flightDiveStartClock.automaticEntry?this.flightDiveStartClock.entryAge
        :FLIGHT_LAUNCH_TOTAL-this.flightDiveStartClock.remaining;
      const diveTurn=THREE.MathUtils.smootherstep(diveElapsed,0,turnDuration);
      const startupTarget=gathering||this.jet==='dive'?THREE.MathUtils.lerp(this.diveStartPitch,-Math.PI/2,diveTurn)
        :this.flightLaunchRemaining>FLIGHT_LAUNCH_TIME?-this.aimPitch:path+THREE.MathUtils.degToRad(8);
      const startup=Math.max(launch,diveStart);
      this.glideAttitude.update(dt,this.group,this.boneByName,this.flightHorizontalSpeed,this.climbRate,
        Math.max(ordinary,startup)*(1-THREE.MathUtils.smoothstep(approach,0,.25)),
        startup>.0001||this.flightDiveStartClock.remaining>0||this.flightVerticalPose.amount>.0001,
        startup>.0001?{pitch:startupTarget,limit:Math.PI,continuous:diveStart>.0001}:undefined);
    }
    this.crystalTravelPose.update(dt, this.group, this.boneByName,
      travel && this.jet === 'glide' && this.flightLaunchRemaining<=0 ? (1-this.flightPower)*(1-this.divePreparation) : 0,
      this.turnRate, this.climbRate, approach, this.compositionGrounded, this.acts["f_land"]?.getClip());
    // Swimming owns the whole body, including the head. A held RMB must not
    // pin the head upright while the swim stroke rolls the shoulders beneath it.
    if (this.swimming) {
      this.applyEyeLook(dt);
      this.applyFace(dt, 0);
      return;
    }
    this.applyUnarmedReadyTorso();
    this.applyRelaxedStandingPosture();
    // applyGlideTuck() REMOVED: it tucked the OLD jetpack boost clip's spread arms/legs in, but the
    // superhero Anim_Fly_Boost is a complete pose — the tuck pushed the left arm into the body + dropped
    // the left leg. The clip drives the limbs now.
    this.applyCombatBlade();
    // Lock the head to the reticle in WHICHEVER aim stance is active — the unarmed fighting stance
    // (combatK), the unarmed WALK stance (walkK — body is camera-locked, so the head still tracks the
    // reticle), or the gun-aim stance (aimK, gun out, grounded/jumping/hovering). Runs after the
    // arm/aim overrides so it has the final spine/neck pose to override from.
    // The RIFLE runs the lock PARTIALLY (0.65) as GAZE STABILIZATION: applyHeadLook pins the head
    // in the GROUP frame, world-stable, which is what stops the head bobbing along the sightline
    // while the torso sways with the gait (measured 0→13° of head sway per stride at an ADS walk).
    // The remaining 0.35 keeps some of the clips' authored cheek-on-stock tilt (head sits ~21° off
    // the neck sighting down the barrel). The historical full-lock wrench ("head is ALL messed up",
    // 67–70° off the neck) was measured against the OLD procedural chest stack, which bent the neck
    // away from where the lock pinned the head; the AO-led aim bends the neck ALONG the aim, so the
    // pinned head now sits naturally on it. Diagnose with client/test/_headdiag.mjs.
    const rifleGaze = this.aimPrefix === "r" ? 0.65 : 1;
    const gunHeadLock = this.aiming && this.stanceK > 0.5 && this.jet !== "glide" ? this.aimK * rifleGaze : 0;
    const headLockK = Math.max(this.combatK, this.walkK, gunHeadLock);
    this.applyHeadLook(headLockK);
    // SOCIAL head look (ambient NPCs): glance at a world target with the
    // head only — after the reticle lock so a real aim always wins.
    this.applyCompanionPose();
    this.applyHeadLookAt(dt);
    this.applyEyeLook(dt);
    this.applyFace(dt, headLockK);
    this.applyCombatPunchAim();
    this.updateCombatGlow();
  }

  private alignRelaxedPosture(weight: number, moving: number): void {
    if (this.bodyModelId !== "male" && this.bodyModelId !== "female") return;
    const neck = this.boneByName.get("neck_01"), head = this.boneByName.get("head");
    if (neck && head) applyRelaxedPosture(this.group, neck, head, this.headRestRelGroup, weight, moving);
  }

  /** Ordinary idle/walk clips carry a persistent forward neck and downward
   * head tilt. Correct only that relaxed presentation, before gaze and speech.
   * Explicit authored actions and calibration poses retain their own posture.
   */
  private applyRelaxedStandingPosture(): void {
    if (this.debugPose || this.authoringMixer || this.layeredAuthoringTime !== null
      || this.mounted || this.swimming || this.jet !== "none" || !this.compositionGrounded
      || this.strikeT > 0 || this.landT > 0 || this.emoteKey
      || this.composition.playing || this.airCombatOwnsBody()) return;
    const speed = this.speedK * 6;
    const runFade = 1 - THREE.MathUtils.smoothstep(speed, 3.1, 5.5);
    const weight = runFade * (1 - this.airK) * (1 - this.crouchK)
      * (1 - Math.max(this.aimK, this.combatK, this.walkK)) * (1 - this.runBoostK);
    this.alignRelaxedPosture(weight, THREE.MathUtils.smoothstep(speed, .1, 2));
  }

  /** Camera-locked empty-handed movement keeps a forward torso while the full
   * directional gait supplies the hips and arm swing. Correct the chest in
   * group space: damping spine locals alone loses their counter-rotation
   * against the animated pelvis. Distribute the correction down the spine
   * instead of hinging the chest at one joint. No previous-frame pose is used. */
  private applyUnarmedReadyTorso(): void {
    if (this.gunOut || this.swordOut || this.blocking || this.combatActive || this.mounted
      || this.strikeT > 0 || this.airCombatOwnsBody()) return;
    const gaze = this.walkK * (1 - this.airK);
    const weight = 0.65 * gaze * this.moveK * (1 - this.crouchK);
    if (gaze < 0.001) return;
    const chest = this.boneByName.get("spine_05");
    if (!chest) return;
    this.group.updateMatrixWorld(true);
    chest.getWorldQuaternion(_hLocal).normalize();
    this.group.getWorldQuaternion(_hDesired).multiply(this.chestRestRelGroup).normalize();
    _hSway.copy(_hLocal).slerp(_hDesired, weight);
    _hPitch.copy(_hSway).multiply(_hLocal.invert());
    _hPitch.slerp(_rest, 2 / 3); // one third of the world-space correction per joint
    for (const name of ["spine_01", "spine_02", "spine_03"]) this.rotateBoneWorld(name, _hPitch);
    // Add camera elevation to the current gait, shared across the spine. Head
    // gaze follows afterward, preserving the walk's arm swing and planted legs.
    this.group.getWorldQuaternion(_hDesired);
    _aoRight.set(1, 0, 0).applyQuaternion(_hDesired);
    _aoPitch.setFromAxisAngle(_aoRight, -THREE.MathUtils.clamp(this.aimPitch, -1.2, 1.2) * .40 * gaze / 3);
    for (const name of ["spine_01", "spine_02", "spine_03"]) this.rotateBoneWorld(name, _aoPitch);
  }

  /** Exactly one context owns the legs; derived twists follow final contacts. */
  private applyContactPoseStage(dt: number, speed: number, grounded: boolean): void {
    if (this.layeredAuthoringTime !== null) this.sampleAuthoringPose(this.layeredAuthoringTime, false);
    const landing=this.diveLandActive&&this.landT>0;
    const owner = legPoseOwner(this.mounted, this.airCombatOwnsBody()||landing||this.wallRunState!==0||this.ledgeClimbState!==0, this.jet !== "none", this.airLegT > 0, grounded, this.swimming);
    // Before the owner: a touchdown mid-flip eases these legs out beneath the
    // ground solver (or the jet legs) instead of dropping them in one frame.
    this.applyDoubleJumpLegs(dt);
    if (owner === "mount") {
      this.applyMountedPose(this.mountedBlend);
      this.applyVehicleMountSwing();
    } else if (owner === "ground") {
      this.bladeStride.apply(this.group,this.body,this.boneByName,this.weights,speed,this.solveBladeLeg);
      this.applyFootGrounding(dt, speed, true);
      const danceWeight=this.weights.get('emo_dance')??0;
      if(danceWeight>.001&&this.groundProbe)this.danceContact.apply(this.group,this.body,this.skeleton,
        [...(this.glbBody?.meshes??[]),...this.styledSkinned],this.groundProbe,danceWeight);
    } else {
      // Contact ownership changed. Do not let a fading ground solver move an
      // airborne/authored leg pose; clear its held poles before the next landing.
      this.groundIkK = 0;
      this.ikPelvis = 0;
      this.ikPole[0].set(0, 0, 0); this.ikPole[1].set(0, 0, 0);
      if (owner === "flight") this.applyJetLegs();
      else if (owner === "jump") this.applyJumpLegs();
    }
    // The existing solvers supply a live carry reference. Blend the claimed
    // arm ONCE, after all contact writes: fading out reacquires the moving
    // socket without a second IK solver dragging a fully released hand back.
    this.composition.apply(this.boneByName);
    if(this.wallRunState&&Math.abs(this.wallRunState)<3&&!this.mounted&&!this.swimming)this.wallCatch.apply(dt);
    else this.wallCatch.cancel();
    if(!this.mounted&&!this.swimming)this.ledgeContact.apply(this.group,this.boneByName,this.ledgeClimbState,this.ledgeClimbTime,this.ledgeAnchor,dt,grounded);
    // Contacts and composed gestures can rotate wrists too. Twist is derived last.
    this.applyArmTwist();
  }

  /** Re-seed the simulated cloth to a clean warm-started drape on the next update. Call when
   *  the avatar (re)loads into the world (disembark / login-restore / LOD-in) so the cape
   *  starts settled at the actual spawn pose instead of carrying a stale, frozen drape that
   *  "explodes" until it settles (the load freak-out). No-op if the body has no cloth. */
  resetCloth(): void {
    this.clothSim?.reseed();
    for (const c of this.pbdCapes) c.reseed();
    for (const hair of this.hairMotion) hair.reset();
    this.clothWaterK = this.swimming ? 1 : 0;
  }

  /** Layered base-pose state machine. Air states (jump/fall/glide/land) are
   *  stance-agnostic and override everything. On the ground it crossfades the
   *  UNARMED set (idle/walk/run by speed) against the PISTOL set (idle + a
   *  fwd/back/strafe directional blend) by `stanceK`, then eases all action
   *  weights toward the target and advances the mixer. With stanceK=0 it reduces
   *  exactly to the unarmed behaviour. */
  private updateStateMachine(speed: number, grounded: boolean, sprinting: boolean, dt: number): void {
    // Restore before activating actions: PropertyMixer captures original values on activation.
    this.basePose?.restore();
    this.underhandStretchAppliedL = this.underhandStretchAppliedR = 1;
    // Track recent jetpack use: a touchdown within ~0.3s of jetting is a jetpack
    // landing (uses f_land), otherwise the ordinary land clip.
    this.jetRecentT = this.jet !== "none" ? 0.3 : Math.max(0, this.jetRecentT - dt);
    if(this.ledgeClimbState)this.fallSpeed=0;
    else if (!grounded && !this.swimming) this.fallSpeed = Math.max(this.fallSpeed, -this.climbRate); // peak descent → the land's impact tier
    if (grounded) this.seenHoverAirborne = false; // fresh airborne stint begins on next liftoff
    // Jetpack TAKEOFF: the liftoff one-shot fires ONLY on the FIRST hover engage of an airborne
    // stint (the launch into flight), NOT on a mid-fall hover RE-ENGAGE (which would replay the
    // liftoff every time you tapped hover after falling). Two signals OR'd:
    //   • doubleJumped (jumpChain reaching 2): the robust LOCAL trigger — catches the launch even
    //     when a quick-tap hover frame is never observed (hover needs Space HELD; it can engage
    //     and drop to "none" within a frame).
    //   • enteredHover && !seen: covers REMOTES, whose jumpChain isn't synced — they only see the
    //     netJet none->hover edge; "first of the stint" makes it the launch, not a re-engage.
    // Clips CROSSFADE in/out (no hard-zero) so the handoff stays fluid; the predecessor
    // (jump/idle) eases out as f_takeoff eases in, and the normalize step keeps the sum at 1.
    const enteredHover = this.jet === "hover" && this.prevJet !== "hover";
    // A stalled downward glide can be nearly opposite the upright hover.
    // Ease this one handoff from zero rate; ordinary action/ground fades retain
    // their existing response and a resumed glide immediately leaves the ramp.
    const downwardPose=(this.weights.get('f_dive')??0)+(this.weights.get('f_pitch_down')??0)+(this.weights.get('f_dive_start')??0);
    this.flightHoverAge=enteredHover&&(this.prevJet==='glide'||this.prevJet==='dive')&&downwardPose>.2
      ?0:Math.min(.4,this.flightHoverAge+Math.max(0,dt));
    const doubleJumped = this.jumpChain === 2 && this.prevJumpChain < 2;
    // ⚠⚠ THE JETPACK LIFTOFF ONE-SHOT (f_takeoff / Takeoff_01) IS REMOVED — user call 2026-08-03.
    // It fired on the double-jump / hover entry and SNAPPED ITSELF to full weight for that frame
    // (`weights.set(key, 1)` + `setEffectiveWeight(1)` + zeroing jump/fall), so a full-body flight
    // clip owned the rig outright at exactly the moment a player is likely to press ADS. The aim
    // layer then posed on top of a body the clip had already thrown into a launch pose, which read
    // as "the whole torso flips around and the head dips low" (user report; it is why that report
    // could never be reproduced from a SETTLED hover — the artifact lives on the launch frame).
    // The hover blendspace crossfades in from the jump/fall pose on its own, which is what plays
    // now. `seenHoverAirborne` is still tracked: `jetLaunched` feeds the free-fall dive pose.
    if (doubleJumped || enteredHover) this.seenHoverAirborne = true;
    // Only the free third-jump entry plays Normal_to_Boost. Read the actual
    // remaining clock, including a remote first seen partway into the burst.
    this.glideLaunchT=this.jet==='glide'?this.flightLaunchRemaining:0;
    const wasDiving=this.prevJet==='dive';
    this.prevJet = this.jet;
    this.prevJumpChain = this.jumpChain;
    // Edge triggers: jump on takeoff, land on touchdown (when not jetting).
    if (this.prevGrounded && !grounded && !this.swimming && this.jet === "none" && !this.wallRunState) {
      this.diveLandActive=false;
      this.jumpT = 0.45;
      this.landT = 0; // a new jump cancels any lingering land clip
      // Reset BOTH jump clips + the fall loop: stance can flip mid-air (aim on/off), and
      // selectLocomotion re-picks p_jump vs jump each frame — whichever it lands on
      // must start from frame 0, not a stale clamped end pose from a prior jump.
      this.acts["jump"]?.reset();
      this.acts["p_jump"]?.reset();
      this.acts["fall_loop"]?.reset();
      // REGULAR JUMP: launch with the phase-matched START clip (js_run for a run, js_sprint for a sprint,
      // …, by along-ground speed), SNAPPED dominant so it shows immediately — no run/idle lingering (the
      // "running jump has no initial jump animation" fix). It then crossfades into bl_loop. Skip when
      // aiming (aim idle is continuous) / in combat-walk / mid boost-leap (flagBigLeap owns that launch).
      // LOCKED STRAFE JUMP (user call 2026-07-22): focus/locked + moving picks the SSH
      // DIRECTIONAL takeoff by the dominant move axis — Space while strafe-WALKING is a
      // jump now (the roll lives on the Shift/jog tier; actionSystem gates evadeReady).
      // Checked BEFORE the !aiming gate: the 1H lock IS the RMB hold now, so a locked
      // strafe jump happens while `aiming` is true — the gate below is for gun ADS.
      const strafeJump = this.swordFocus && !this.combatActive && !this.walkActive && !this.bigLeapActive
        && (Math.abs(this.moveFwd) > 0.2 || Math.abs(this.moveRight) > 0.2)
        ? `sd_jump_${Math.abs(this.moveFwd) >= Math.abs(this.moveRight) ? (this.moveFwd >= 0 ? "f" : "b") : (this.moveRight >= 0 ? "r" : "l")}`
        : null;
      if (strafeJump && this.acts[strafeJump]) {
        this.jumpStartKey = strafeJump;
        // The directional takeoff owns the WHOLE AIRBORNE STINT (user call 2026-07-22,
        // twice): it is once+clamp, so past its 0.68 s it HOLDS its final in-air pose —
        // never handing off to the cycling UAL jump clip, whose loop wrapped back to its
        // crouch-spring OPENING mid-arc (the "beginning of the regular jump animation"
        // report; weight-sampled in _jumpdiag.mjs). Landing/ground branches override on
        // touchdown, and the next takeoff edge re-picks.
        this.jumpStartT = 6;
        this.snapAir(strafeJump); // snapAir resets the clip — a clamped prior takeoff restarts clean
        // ...and SKIP the clip's grounded ANTICIPATION (user report ×3 — "the directional
        // part happens halfway up"): Jump_Begin clips are authored to START ON THE GROUND
        // (thigh-flexion profile, _jumpclip2.mjs: crouch builds to t≈0.15 s, the spring is
        // 0.15–0.20 s). The physics has no pre-jump window, so playback opens AT the
        // launch — the directional leap pose is on screen from the first airborne frame.
        this.acts[strafeJump]!.time = SD_JUMP_SKIP_S;
      // ⚠⚠ THE AUTHORED LAUNCH CLIP NEVER GOES THROUGH THE MIXER ANY MORE — it is FULL BODY, and
      // its top half is wrong in every stance that has its own upper body (user call 2026-08-03:
      // "the general jump animation BOTTOM HALF is fine… that should be used for essentially all
      // weapon states (including unarmed)"). `applyJumpLegs` samples the SAME clip onto the leg
      // bones only, so every stance gets the authored push-off while its own clip keeps the torso.
      // What is recorded here is just WHICH phase clip to sample and for how long.
      } else if (!this.combatActive && !this.walkActive && !this.bigLeapActive) {
        const phase = speed >= LAND_SPRINT_MIN ? "sprint" : speed >= LAND_RUN_MIN ? "run" : speed >= LAND_WALK_MIN ? "walk" : "stand";
        const key = [`js_${phase}`, "js_run", "js_stand"].find((k) => this.acts[k]);
        if (key) { this.jumpStartKey = key; this.jumpStartT = JUMP_START_DUR; }
        else this.snapAir(this.stanceK > 0.5 ? `${this.aimPrefix}_jump` : "jump"); // fallback: the leap pose
      }
    }
    // UNIVERSAL LANDING ANTICIPATION: a beat (~LAND_LEAD s) before the feet reach the ground — while
    // still descending past LAND_MIN_DESCENT — start the land so the fall loop crossfades INTO it as the
    // player arrives (no snap at touchdown). Phase by ground SPEED, tier by IMPACT (the live descent
    // speed). Fires for EVERY real fall (plain jump arc, walk-off-a-ledge, boost-leap, super-jump dive).
    // groundClearance is the LOCAL player's feet-above-ground; REMOTES leave it huge so this never fires
    // for them (they land on the touchdown edge below). Clearing bigLeapActive hands off from the leap loop.
    // A FULL-BODY SWORD STRIKE suppresses the land entirely: the GroundPound's slam descent trips this
    // anticipation, and its clip carries its own authored impact — a land pop over (or after) it is
    // exactly the "conflicting animations" read. Same for an air swing finishing through touchdown.
    const swordStrikeOwns = this.aimPrefix === "sd" && this.strikeLayer === "full" && this.strikeT > 0;
    if (swordStrikeOwns) this.landT = 0; // incl. one anticipated BEFORE the strike started (fall → attack)
    if (!this.ledgeClimbState && !swordStrikeOwns && !grounded && !this.swimming && this.jet === "none" && this.climbRate < -LAND_MIN_DESCENT && this.landT <= 0) {
      const ttl = this.groundClearance / Math.max(0.5, -this.climbRate); // time-to-ground at the current descent
      if (ttl < LAND_LEAD) {
        this.selectLand(speed, sprinting, -this.climbRate);
        this.bigLeapActive = false;
      }
    }
    // DIVE landing ANTICIPATION: the flight-land clip transitions fly→touch, so it must START in the AIR
    // a short beat before impact, within eight metres. Touchdown aligns the contact once, including
    // when network updates arrive without the anticipation frames.
    if (!grounded && !this.swimming && this.jet === "dive" && this.landT <= 0) {
      const ttl=diveLandingLead(this.groundClearance,this.climbRate);
      if(ttl!==null)this.flagDiveLand(ttl);
    }
    if (!this.prevGrounded && grounded && this.jet === "none") {
      // FALLBACK land at the touchdown edge — for a real fall (peak descent over the floor) that the
      // anticipation didn't catch (remotes with no clearance read, or a near-instant drop). SKIP if a
      // land is already running (anticipated) so we don't restart it mid-blend. Impact tier = the peak
      // descent of the stint (climbRate is ~0 post-clamp at the exact touchdown frame).
      if (this.landT <= 0 && !swordStrikeOwns && this.fallSpeed > LAND_MIN_DESCENT) this.selectLand(speed, sprinting, this.fallSpeed);
    }
    // REMOTE dive touchdown: remotes never anticipate (no clearance read) and their netJet still reads
    // "dive" at the grounded edge — fire the flight-land here. (The LOCAL player lands with jet already
    // "none" plus the diveLandPending relay, so this edge is remote-only in practice.)
    if (!this.prevGrounded && grounded && !this.swimming && (this.jet === "dive"||wasDiving||this.diveLandActive)) this.flagDiveLand();
    if(this.diveLandActive&&!grounded&&(this.jet!=='dive'||this.swimming)){
      this.diveLandActive=false;this.landT=0;
    }
    if (grounded || this.swimming) this.fallSpeed = 0; // swimming ends the fall without a ground impact
    // QUICK HAND-OFF to locomotion: once grounded and MOVING, cut the land short so the walk/run takes
    // over fast (the land is for the impact beat, not a stop). A still landing plays out, then idle.
    if (!this.diveLandActive && grounded && speed > LAND_WALK_MIN && this.landT > LAND_MOVE_EXIT) this.landT = LAND_MOVE_EXIT;
    // The big-leap anim ends when it touches down OR when it converts to flight (a Space-tap engages a
    // jet) — so a subsequent flight touchdown uses the jetpack land, not the sprint land.
    if (grounded || this.jet !== "none") this.bigLeapActive = false;
    // SWORD AIR ATTACK phase machine — uses the live grounded edge (begin/loop → land on touchdown).
    this.updateSuperJump();
    this.prevGrounded = grounded;
    this.airLegT = grounded || this.swimming || this.jet !== "none" ? 0 : this.airLegT + dt;
    this.jumpT = Math.max(0, this.jumpT - dt);
    this.landT = Math.max(0, this.landT - dt);
    if(this.landT===0)this.diveLandActive=false;
    this.bigLeapT = Math.max(0, this.bigLeapT - dt);
    this.jumpStartT = Math.max(0, this.jumpStartT - dt);

    // Water ends a fall without a landing flourish or a lingering launch pose.
    // Keep the mixer crossfade, but retire those selectors before choosing swim.
    if (this.swimming) {
      this.jumpT = this.landT = this.jumpStartT = this.glideLaunchT = 0;
      this.bigLeapActive = false;
    }

    this.flightLaunchPoseClock.update(this.glideLaunchT,dt,this.divePreparation);
    if((this.divePreparation>0&&this.previousDivePreparation<=0)||(this.jet==='dive'&&!wasDiving&&this.previousDivePreparation<=0))
      this.diveStartPitch=Number.isFinite(this.glideAttitude.pitch)?this.glideAttitude.pitch:-this.aimPitch;
    this.previousDivePreparation=this.divePreparation;
    this.flightDiveStartClock.update(this.divePreparation,this.jet==='dive'&&!grounded&&!this.swimming,dt);
    this.flightLaunchAlignment.update(this.glideLaunchT,dt);
    const target = selectLocomotion({
      speed,
      grounded,
      jet: this.jet,
      stanceK: this.stanceK,
      underhand: this.weaponStance === 'underhand' && this.gunOut,
      sprinting,
      crouching: this.crouching,
      swim: this.swimming,
      moveFwd: this.moveFwd,
      moveRight: this.moveRight,
      aimSet: this.aimPrefix,
      aiming: this.aiming,
      stance: this.resolvedPose.stance,
      swordFocus: this.swordFocus,
      liftKey: this.liftKey || undefined,
      staggerKey: this.staggerKey || undefined,
      gsBlockKey: this.gsBlockKey || undefined,
      gsMode: this.gsSword,
      // The float holds for the WHOLE airborne rave stint (not just while the chain window is
      // open) — otherwise the fall loop flashes between hits and after the rise, which is
      // exactly the "jumping/falling getting in the way" read. Landing clears it.
      airCombo: this.airChainLive,
      superJumpKey: this.superJumpKey,
      toIdleT: this.toIdleT,
      climbRate: this.climbRate,
      leapHold: this.jumpChain >= 1,
      bigLeap: this.bigLeapActive,
      bigLeapT: this.bigLeapT,
      jumpStartKey: this.jumpStartKey,
      jumpStartT: this.jumpStartT,
      jetLaunched: this.seenHoverAirborne, // ordinary airborne poses remain authoritative after flight
      turnRate: this.turnRate,
      jumpT: this.jumpT,
      landT: this.landT,
      glideLaunchT: this.glideLaunchT,
      flightPower: this.flightPower,
      divePreparation:this.divePreparation,
      diveStartT:this.flightDiveStartClock.remaining,
      diveStartWeight:this.flightDiveStartClock.weight,
      flightPitch: this.divePreparation>0?this.aimPitch:Math.atan2(-this.climbRate,Math.max(.01,speed)),
      straightUpTime: this.straightUpTime,
      // A completed Ctrl preparation has already faded the upward action. Do
      // not restore that weight when the discrete dive state takes ownership.
      flightVerticalBlend:this.jet==='dive'?Math.min(this.flightVerticalPose.amount,this.weights.get('f_pitch_up')??0):this.flightVerticalPose.amount,
      wallRunState: this.wallRunState,
      ledgeClimbState:this.ledgeClimbState,
      landJet: this.landIsJet,
      jetRecent: this.jetRecentT > 0,
      landClip: this.landClip,
      combat: this.combatActive || this.combatK > 0.01, // keep selecting the stance while it fades out (symmetric crossfade)
      walkMode: this.walkActive,
      blocking: this.blocking,
      guardPhase: this.guardPhase,
      guardDir: this.guardDir,
      punchT: this.punchT,
      blockHitT: this.blockHitT,
      strikeKey: this.strikeKey,
      strikeFull: this.strikeLayer === "full",
      stinger: this.stingerOn,
      stingerKey: this.stingerKey,
      airEvade: this.airEvadeOn,
      idleBreakKey: this.idleBreakKey,
      emoteKey: this.conversationPerformance ? this.conversationIdle : this.emoteKey,
      talking: this.conversationPerformance || this.talking,
      talkKey: this.talkGestureKey,
      has: (k) => this.acts[k] !== undefined,
    });

    // SMOOTH the masked combat overlay: a fixed high weight (COMBAT_BLEND) flips the arms the
    // instant it passes the base layer (THREE normalizes per-bone), so the stance read as instant.
    // Shape the overlay weight so the per-bone blend fraction tracks combatK: blend = W/(W+1) =
    // combatK ⟹ W = combatK/(1-combatK), capped at COMBAT_BLEND. Now the guard crossfades over
    // combatK's ease (a few frames) and still fully dominates at steady state.
    if (this.combatK > 0.001 && this.combatK < 0.999) {
      const w = Math.min(COMBAT_BLEND, this.combatK / (1 - this.combatK));
      for (const key of COMBAT_OVERLAY) if (target[key] !== undefined) target[key] = w;
    }

    if (this.sparseMixerActions) {
      for (const key of Object.keys(target) as ClipKey[]) {
        if ((target[key] ?? 0) <= 0 || this.sparseMixerActionKeys.has(key)) continue;
        const action = this.acts[key];
        if (!action) continue;
        action.enabled = true;
        action.play();
        action.setEffectiveWeight(this.weights.get(key) ?? 0);
        this.sparseMixerActionKeys.add(key);
      }
    }
    const actionKeys: Iterable<ClipKey> = this.sparseMixerActions
      ? this.sparseMixerActionKeys
      : Object.keys(this.acts) as ClipKey[];
    // Start the outgoing crossfade at zero slope. An immediate exponential
    // fade moved the chest several centimetres on the first grounded frame.
    this.ledgeExitAge+=dt;
    const ledgeRelease=grounded?THREE.MathUtils.smootherstep(this.ledgeExitAge,0,.18):1;
    const hoverRelease=!grounded&&this.jet==='hover'&&!this.airCombatOwnsBody()&&!this.composition.playing&&!this.emoteKey
      ?THREE.MathUtils.smoothstep(this.flightHoverAge,0,.4):1;
    const launchRelease=!grounded&&!this.swimming&&!this.airCombatOwnsBody()&&!this.composition.playing&&!this.emoteKey
      ?this.flightLaunchPoseClock.blendRate:1;
    // The flight/ADS edge has a single POST-solve crossfade. Internally, pose
    // the destination cleanly so gun-track never measures a diving torso and
    // the stance threshold cannot select a procedural one-arm fallback.
    const k = this.flightAimPoseSwitch?1:1 - Math.exp(-12 * dt * ledgeRelease * hoverRelease * launchRelease);
    const bladeCycle=this.acts.sd_run_f?.getClip().duration??.683333;
    const bladeRate=bladeRunCadence(speed);
    for (const key of actionKeys) {
      const cur = this.weights.get(key) ?? 0;
      const next = cur + ((target[key] ?? 0) - cur) * k;
      this.weights.set(key, next);
      const act = this.acts[key]!;
      act.setEffectiveWeight(next);
      // STRIDE-SYNC: warp a translating clip's playback so its stride matches the
      // sim's ground speed (no foot-slide). Capped at 1.5× — past that the torso
      // counter-swing goes frantic/jittery (armed RUN reuses the WALK clip since
      // no straight pistol-run exists, so it would otherwise play ~2×). A little
      // foot-slide at top speed is the better trade. Non-translating clips keep 1.
      const authored = this.authoredSpeed.get(key) ?? 0;
      // Kickbox strikes / idle-breakers are one-shot ACTIONS, not locomotion — play them at a fixed
      // rate (strikes nudged up by STRIKE_SPEED) so the stride-warp never slows them when stationary.
      const kcStrike = key.startsWith("kc_") && key !== "kc_idle" && !key.startsWith("kc_walk") && key !== "kc_break1" && key !== "kc_break2";
      const kcBreak = key === "kc_break1" || key === "kc_break2";
      // DHTH attack one-shots (punches/kicks) play at STRIKE_SPEED; the reactions (block-flinch/parry)
      // snap at BLOCK_SPEED. The guard/stance/held-block (dh_guard/dh_stance/dh_block) are not strikes.
      const dhStrike = key.startsWith("dh_punch_") || key.startsWith("dh_kick_");
      const dhReact = key === "dh_blockhit" || key === "dh_parry";
      // Dodge-rolls / block-flinches run faster than strikes (their strikeT uses the same rate) so the
      // motion lands with its short dash window rather than trailing it.
      let ts = key === "u_jab" ? JAB_SPEED
        : key.startsWith("sd_attack") ? SWORD_ATTACK_ANIM // DMC retime — see the const; table + bake mirror it
          : key === "sd_parry" ? SWORD_PARRY_ANIM // the parry swipe — same SSH cinematic retime
            : this.stingerOn && key === this.stingerKey ? this.stingerRate // stinger close — flight-retimed (any dash clip)
              : key === "sd_dash" || key === "sd_airdash" || key === "sd_shortdash" ? (SD_DASH_NATURAL[key] ?? SWORD_DASH_ANIM) // dash clips outside a stinger
                : key.startsWith("dodge_") ? AIR_DODGE_ANIM // airborne UAL dodge — snappy flip
                  : key.startsWith("sd_roll_") ? SWORD_ROLL_ANIM // crouch roll — slower, to span the longer 2× travel
                    : key.startsWith("sd_dodge_") ? SWORD_DODGE_SPEED // fast stand dodge
                      : key.startsWith("kc_roll_") ? ROLL_SPEED
                        : (key.startsWith("kc_blockhit_") || dhReact) ? BLOCK_SPEED
                          : (kcStrike || dhStrike) ? STRIKE_SPEED
                            : kcBreak ? 1
                              : grounded && authored > 0.5 ? Math.max(0.6, Math.min(1.5, speed / authored)) : 1;
      // Phase-align the complete PS Run octagon, including clips just reactivated
      // by sparse mixing. Steering must not swap to the other foot mid-stride.
      if(key.startsWith('sd_run_')){
        act.time=this.bladeRunPhase*act.getClip().duration;
        ts=bladeRate*act.getClip().duration/bladeCycle;
      }
      // A 20% quicker wall stride, preserving the speed-responsive cadence.
      if(key==='wall_run_l'||key==='wall_run_r')ts=1.2*THREE.MathUtils.clamp(speed/11.5,.65,1.25)
        *THREE.MathUtils.lerp(.35,1,THREE.MathUtils.smootherstep(this.wallCatch.progress,0,1));
      // 2H octagonal strafes: fixed stride-match rates (authored ~0.9 m/s walks / ~2.95 m/s
      // jogs vs the greatsword.ts circling speeds; authoredSpeed is 0 post-strip, so the
      // generic stride-sync can't do it).
      if (key.startsWith("gs_walk_") || key.startsWith("gs_gwalk_") || key.startsWith("sd_walk_")) ts = GS_WALK_ANIM;
      else if (key.startsWith("gs_jog_") || key.startsWith("gs_gjog_")) ts = GS_JOG_ANIM;
      else if (key === "gs_run") ts = GS_RUN_ANIM; // authored ~3.7 m/s vs RUN_SPEED 5.5
      else if (key === "gs_sprint") ts = GS_SPRINT_ANIM; // authored ~8.9 vs SPRINT 7.0 — slowed
      // BOOST-RUN: slow the stride toward BOOST_STRIDE as boost engages — slow, big strides that read
      // as leaps (the jet does the work; the vertical bound + lunge in the body branch sell it). Not
      // gated on authored: the sprint clip is in-place (low drift), so it must still slow when boosting.
      if (this.runBoostK > 0.01 && grounded && key !== "u_jab" && !kcStrike && !kcBreak && !key.startsWith("dh_")) ts += (BOOST_STRIDE - ts) * this.runBoostK;
      if(key==='f_dive_land'&&this.diveLandActive)ts=this.diveLandGrounded?Math.max(.1,act.getClip().duration-DIVE_LAND_IMPACT_T)/DIVE_LANDING.lock:1;
      act.timeScale = ts;
      if(key==='f_launch'){
        if(this.glideLaunchT>0)act.enabled=true;
        act.paused=false;
        act.time=this.flightLaunchPoseClock.progress*act.getClip().duration;
        act.timeScale=0;
      }
      if(key==='f_dive_start'){
        if(this.flightDiveStartClock.remaining>0)act.enabled=true;
        act.paused=false;
        act.time=this.flightDiveStartClock.pose.progress*act.getClip().duration;
        act.timeScale=0;
      }
      if(key==='ledge_step'||key==='ledge_climb'){
        // Seek from simulation time so skipped frames and remote entry stay in sync.
        if(this.ledgeClimbState===(key==='ledge_step'?1:2)){
          act.paused=false;act.time=THREE.MathUtils.clamp(this.ledgeClimbTime/ledgeDuration(this.ledgeClimbState),0,.999)*act.getClip().duration;
        }
        act.timeScale=0;
      }
      if (this.sparseMixerActions && next <= 1e-4 && (target[key] ?? 0) <= 0) {
        this.weights.set(key, 0);
        act.setEffectiveWeight(0);
        act.stop();
        this.sparseMixerActionKeys.delete(key);
      }
    }
    // NORMALIZE the effective weights to sum to 1. During a one-shot handoff the
    // predecessor is zeroed while the successor is still easing up from 0, so the
    // total can dip well below 1 for several frames — and three's mixer fills the
    // shortfall by blending toward the SkinnedMesh BIND pose, whose legs are spread.
    // That bleed was the "legs kick out / flail" between takeoff and idle. Scaling the
    // active clips back to sum=1 keeps the rest pose from ever showing through. (Steady
    // state already sums to ~1, so this is a no-op then; debugClip solos skip this loop.)
    // EXCLUDE the masked UPPER-BODY combat overlays (dh_guard / dh_punch_* / u_*): driven at a deliberately
    // high weight (COMBAT_BLEND), so folding them into the sum would scale the BASE layer — incl. the legs
    // — down to ~1/COMBAT_BLEND, and three then fills the shortfall on the leg bones with the BIND pose
    // (legs spread, ~6% animation). Keeping them out lets the legs run at full weight while the overlay
    // still dominates the torso via three's per-bone cumulative weight. (Full-body dh kicks/block are NOT
    // overlays — they ARE the base, so they normalize normally.)
    let wsum = 0;
    for (const key of actionKeys) if (!isUpperOverlay(key)) wsum += this.weights.get(key) ?? 0;
    if (wsum > 1e-3 && Math.abs(wsum - 1) > 1e-3) {
      for (const key of actionKeys) {
        if (isUpperOverlay(key)) continue;
        const w = this.weights.get(key) ?? 0;
        if (w > 0) this.acts[key]!.setEffectiveWeight(w / wsum);
      }
    }
    this.mixer!.update(dt);
    this.bladeRunPhase=(this.bladeRunPhase+dt*bladeRate/bladeCycle)%1;
    this.basePose?.capture();
  }

  /** The torso absorbs each shot; the assembled crystals answer with a pulse. */
  shoot(): void { this.crystalWings?.shoot(); const w=weaponStats(ITEMS[this.weaponId??'']?.weapon??{});this.shotRecoil.fire(this.weaponStance==='underhand',w.recoil/.015,w.recoilRecovery); }

  /** Point a controlled bone's child-axis toward a NATURAL-space direction
   *  (forward = +Z), expressed in the bone's rest parent frame. */
  private setLimb(name: string, target: THREE.Vector3, blend = 1): void {
    // ⚠ ONLY the bones in the restParentInv table (the limb ROOTS — thighs, upper arms, forearms)
    // can be limb-targeted; everything else has no rest-parent inverse and used to throw straight
    // out of the render loop (`applyQuaternion` on undefined — crash reported 2026-08-03 after the
    // hover squat passed shin bones in here). Bail instead: a missing procedural layer is a pose
    // that falls back to the clip, which is survivable; a thrown frame is not.
    const inv = this.restParentInv.get(name);
    const b = this.boneByName.get(name);
    if (!inv || !b) return;
    const tp = target.clone().applyQuaternion(inv).normalize();
    _q.setFromUnitVectors(LIMB_AXIS, tp);
    if (blend >= 1) b.quaternion.copy(_q);
    else b.quaternion.slerp(_q, blend);
  }

  /**
   * PROCEDURAL JUMP LAUNCH — LEGS ONLY (user call 2026-08-03: "the with-rifle jump is applying the
   * upper body transformations… remove that part of the jump animation (the top half). Perhaps
   * jumping should, generally, be procedural — applying the upper / lower halves separately").
   *
   * The authored `js_*` launch clips are full-body. With a gun out they drive the arms and
   * shoulders over the stance's own airborne pose (wrong top half at the hip carry) and fight the
   * aim stack outright at ADS (a flailing upper body). So with a gun out the clip is skipped and
   * the launch reads from the legs alone — a tuck at takeoff that extends out over the window.
   *
   * ⚠ Runs AFTER applyFootGrounding for the same reason applyJetLegs does: the foot IK re-solves
   * the leg chain and would wipe it. Axes are the MEASURED ones (client/test/_thighaxis.mjs):
   * thigh local +Z swings the knee forward (same sign both sides — NO mirror), shin local −Z flexes.
   */
  /**
   * True while an AUTHORED MELEE BEAT owns the body — the launcher/juggle string (`airChainLive`),
   * an air evade, the STINGER gap-closer (`stingerOn`, whose sd_dash owns the body outright), or
   * any full-body strike in flight.
   *
   * ⚠⚠ EVERY AIRBORNE PROCEDURAL PASS MUST CHECK THIS. The jump squat, the torso hold, the hip and
   * head square-up and the jump hunch all exist to shape a plain ballistic arc. A melee beat is
   * AUTHORED — layering a leg tuck and a squared pelvis over it fights the clip exactly the way the
   * flight clips used to fight the aim stack (user calls 2026-08-03, twice: first the aerial combos,
   * then "we're modifying the gap closers in blade mode erroneously").
   */
  private airCombatOwnsBody(): boolean {
    return this.airChainLive || this.airEvadeOn || this.stingerOn
      || (this.strikeLayer === "full" && this.strikeT > 0);
  }

  private applyJumpLegs(): void {
    if (this.jet !== "none" || this.airLegT <= 0 || this.airCombatOwnsBody()) return;
    if (this.jumpStartKey === 'djump' && this.jumpStartT > 0 && !this.uprightDoubleJump) return; // authored double-jump owns its legs
    // PROCEDURAL AIR SQUAT — knees up and forward, shins folded under, feet relaxed. Absolute from
    // each bone's BIND local (same contract as applyJetLegs) so it is identical in every stance and
    // does not depend on whatever clip is underneath.
    //
    // ⚠⚠ CLIP SAMPLING WAS TRIED FOR THIS AND ABANDONED. Reading the authored js_*/jump leg tracks
    // gave legs that matched unarmed exactly on paper, but read badly in motion: the launch sampler
    // handed over to the airborne one mid-arc and the pose the `jump` clip holds is a near-straight
    // dangle. Two stages, a crossfade and a bone-name parse later it still looked wrong, and the
    // authored source was the problem, not the plumbing. A single held squat is what a jump wants.
    // The mixer fades the double-jump out after its timer ends. Hand the legs
    // back at that same weight: a timer-only guard switched this layer from
    // zero to full strength in one frame (roughly a 70-degree knee jump).
    const handoff = 1 - Math.max(this.weights.get('djump') ?? 0, this.djumpLegK,
      this.weights.get('wall_run_l')??0,this.weights.get('wall_run_r')??0,
      this.weights.get('wall_jump_l')??0,this.weights.get('wall_jump_r')??0,
      this.weights.get('ledge_step')??0,this.weights.get('ledge_climb')??0);
    const k = Math.min(1, this.airLegT / JUMP_SQUAT_IN) * handoff * handoff;
    // LIVE, not a frozen pose: the tuck settles a little deeper just after the push-off and then
    // breathes, and the two legs run on opposite phases so they never look welded together.
    const settle = Math.exp(-2.2 * this.airLegT);          // a touch more tuck right off the ground
    const cyc = Math.sin(this.airLegT * JUMP_SQUAT_HZ * Math.PI * 2) * JUMP_SQUAT_SWING;
    const thighL = JUMP_SQUAT_THIGH * (1 + 0.18 * settle) + cyc;
    const thighR = JUMP_SQUAT_THIGH * 0.82 * (1 + 0.18 * settle) - cyc;
    const shinL = JUMP_SQUAT_SHIN * (1 + 0.22 * settle) - cyc * 0.8;
    const shinR = JUMP_SQUAT_SHIN * 0.88 * (1 + 0.22 * settle) + cyc * 0.8;
    for (const [rigName, amount] of [
      ["thigh_l", thighL], ["thigh_r", thighR],
      ["calf_l", -shinL], ["calf_r", -shinR],
      ["foot_l", -JUMP_SQUAT_FOOT - cyc * 0.5], ["foot_r", -JUMP_SQUAT_FOOT * 0.9 + cyc * 0.5],
    ] as const) {
      const bone = this.boneByName.get(rigName);
      const bind = this.twistBind.get(rigName);
      if (!bone || !bind) continue;
      _hvQ.setFromAxisAngle(_hvAxis.set(0, 0, 1), amount);
      _hvTarget.copy(bind).multiply(_hvQ);
      bone.quaternion.slerp(_hvTarget, k);
    }
  }

  /**
   * HOVER AIR-SQUAT — knees forward, shins folded, feet dangling (user call 2026-08-03, "mimic
   * Halo third-person jumping").
   *
   * ⚠⚠ RUNS AFTER applyFootGrounding, NOT with the other limb overrides. The foot ground IK
   * re-solves the whole leg chain (thigh → shin → foot), so a hover pose written earlier in the
   * frame was silently wiped: commanding a 40 deg shin fold moved the measured knee by 1.3 deg,
   * and flipping its sign changed nothing — the tell that a later pass owned the bones, not that
   * the sign was wrong.
   *
   * ⚠ The old version of this pose was gated `acts["f_idle"] === undefined`, i.e. a no-clips
   * FALLBACK — and the flight clips exist, so it had been dead code for as long as they have
   * shipped. Layering over the clip is the point: the clip keeps the body's motion, this shapes
   * the legs.
   */
  /**
   * Per-bone quaternion samplers for a clip's LEG tracks, built once per clip.
   *
   * ⚠⚠ THREE.JS TRACK NAMES ARE `.bones[NAME].property`. Splitting on the last dot yields
   * `.bones[thigh_l]` and matches nothing — that made an earlier version of this a SILENT NO-OP for
   * days (no error, the layer simply did nothing). Parse with the bracket form.
   *
   * This is the only way to take HALF a clip: an AnimationMixer action is all-or-nothing across the
   * skeleton, which is exactly why an armed stance cannot just play the unarmed flight clip.
   */
  private legSamplersFor(act: THREE.AnimationAction): { bone: THREE.Bone; interp: { evaluate(t: number): unknown }; duration: number }[] | null {
    const clip = act.getClip();
    const cached = this.jumpLegSamplers.get(clip.name);
    if (cached) return cached;
    const list: { bone: THREE.Bone; interp: { evaluate(t: number): unknown }; duration: number }[] = [];
    for (const track of clip.tracks) {
      const m = /^\.bones\[(.+)\]\.(\w+)$/.exec(track.name);
      if (!m || m[2] !== "quaternion" || !JET_LEG_BONES.has(m[1])) continue;
      const bone = this.boneByName.get(m[1]);
      if (bone) list.push({ bone, interp: track.createInterpolant(), duration: clip.duration });
    }
    this.jumpLegSamplers.set(clip.name, list);
    return list;
  }

  private applyJetLegs(): void {
    if (this.airCombatOwnsBody()) return; // an aerial melee beat outranks the jet pose too
    // ⚠⚠ GUNS BORROW THE UNARMED SUPERHERO LEGS. The authored flight set is COMPLETE for
    // unarmed/blade — f_idle / f_fwd / f_back / f_left / f_right — and the user likes that dangle,
    // banking and all. The ARMED sets are three clips each (f_<p|r>_idle/left/right: no forward, no
    // back), so a gun's own clip cannot supply it. Rather than approximate it procedurally, SAMPLE
    // the matching unarmed clip's LEG tracks and write them under the armed upper body — the same
    // legs in every stance, from the same source (user call 2026-08-03: "I like the unarmed leg
    // dangle when hovering, apply that to the gun version as well").
    // Unarmed/blade need nothing: their own clip already plays these legs.
    if (!this.gunOut) return;
    const hov = this.hoverK, gli = this.glideK;
    const k = Math.max(hov, gli);
    if (k <= 0.02) return;
    if (hov >= gli) {
      // Pick the same direction clip the unarmed selector would, so a strafe banks like theirs.
      const mag = Math.min(1, Math.hypot(this.moveFwd, this.moveRight));
      const key = mag < 0.15 ? "f_idle"
        : Math.abs(this.moveFwd) >= Math.abs(this.moveRight)
          ? (this.moveFwd >= 0 ? "f_fwd" : "f_back")
          : (this.moveRight >= 0 ? "f_right" : "f_left");
      const act = this.acts[key] ?? this.acts["f_idle"];
      const samplers = act ? this.legSamplersFor(act) : null;
      if (samplers && samplers.length) {
        for (const sm of samplers) {
          const v = sm.interp.evaluate(act!.time % sm.duration) as unknown as ArrayLike<number>;
          _hvTarget.set(v[0], v[1], v[2], v[3]).normalize();
          sm.bone.quaternion.slerp(_hvTarget, hov);
        }
        return;
      }
    }
    // ⚠⚠ ABSOLUTE, NOT ADDITIVE. The jet pose is now the ONLY thing shaping the legs in flight
    // (the f_* clip set is stripped), so it must not depend on whatever the base clip happens to
    // be doing underneath. An earlier additive version was tuned against the old flight idle and
    // blew up the moment the base became the stance's jump pose — thighs at 173°, a knee folded to
    // 2.7°. Each bone is driven from its BIND local plus a fixed hinge, then eased in by the jet
    // weight, so the pose is identical whatever clip is playing and still blends cleanly in and out.
    const sway = Math.sin(this.idleT * 1.3) * 0.05;
    const hoverPose: [string, number][] = [
      ["thigh_l", HOVER_KNEE_LEAD + sway], ["thigh_r", HOVER_KNEE_TRAIL - sway],
      ["calf_l", -HOVER_SHIN_FOLD], ["calf_r", -HOVER_SHIN_FOLD * 0.8],
      ["foot_l", -HOVER_FOOT_DANGLE], ["foot_r", -HOVER_FOOT_DANGLE * 0.85],
    ];
    const glidePose: [string, number][] = [
      ["thigh_l", -GLIDE_LEG_SWEEP], ["thigh_r", -GLIDE_LEG_SWEEP],
      ["calf_l", -GLIDE_LEG_TRAIL], ["calf_r", -GLIDE_LEG_TRAIL],
      ["foot_l", -GLIDE_FOOT_POINT], ["foot_r", -GLIDE_FOOT_POINT],
    ];
    // Glide wins where both are live (leaving a glide, hoverK rises as glideK falls).
    const pose = gli > hov ? glidePose : hoverPose;
    for (const [rigName, amount] of pose) {
      const bone = this.boneByName.get(rigName);
      const bind = this.twistBind.get(rigName);
      if (!bone || !bind) continue;
      _hvQ.setFromAxisAngle(_hvAxis.set(0, 0, 1), amount);
      _hvTarget.copy(bind).multiply(_hvQ);
      bone.quaternion.slerp(_hvTarget, k);
    }
  }

  /** Procedural gait / air / jetpack pose (airborne path + no-clips fallback),
   *  via limb targeting so it is independent of the Mixamo rest orientations. */
  private proceduralPose(): void {
    const swing = Math.sin(this.phase);
    const legAmp = 0.7 * this.speedK * (1 - this.airK);
    const armAmp = 0.5 * this.speedK * (1 - this.airK);
    const jetK = Math.max(this.hoverK, this.glideK);
    // Forward = +Z (natural). Legs hang down (-Y) with fore/aft swing; airborne
    // tucks/sweeps them back (-Z); glide sweeps them up-and-back.
    const legBack = this.airK * 0.4 + this.glideK * 0.9;
    const legUp = -this.glideK * 0.7;
    this.setLimb("LeftUpLeg", new THREE.Vector3(0.06, -1 + legUp, swing * legAmp + legBack));
    this.setLimb("RightUpLeg", new THREE.Vector3(-0.06, -1 + legUp, -swing * legAmp + legBack));
    // Arms hang down with counter-swing; airborne lifts them out to the sides.
    const armOut = 0.25 + jetK * 0.5;
    this.setLimb("LeftArm", new THREE.Vector3(armOut, -1 + jetK * 0.3, -swing * armAmp - this.glideK * 0.6));
    this.setLimb("RightArm", new THREE.Vector3(-armOut, -1 + jetK * 0.3, swing * armAmp - this.glideK * 0.6));
  }

  /**
   * The AIM LAYER (upper body only). The avatar runs two layers:
   *   1. BASE (selectLocomotion + mixer): drives the whole skeleton from clips,
   *      crossfading the WEAPONLESS set (idle/walk/run) against the WEAPON set
   *      (pistol directional clips) by stanceK.
   *   2. AIM (here): while in the weapon stance, override ONLY the upper body so
   *      the gun points at the reticle — the hips/legs keep the base clip's gait
   *      untouched (verified by avatar-leg-fidelity.mjs). With no clips loaded it
   *      falls back to a procedural right-arm aim.
   */
  private applyArmOverrides(dt: number): void {
    // Dynamic arm length is an additive IK correction, not persistent rig
    // tuning. Remove last frame's multiplier before measuring this frame so it
    // cannot compound and so switching weapons restores the authored body.
    this.clearUnderhandArmStretch();
    if (this.weaponStance !== "underhand" || !this.gunOut) {
      this.underhandStretchL = 1;
      this.underhandStretchR = 1;
    }
    // The stow/draw gesture owns the arms for its run — the aim stack eases out
    // (aimK target 0) and the support-hand grip fades via stowGestureK, then both
    // re-engage when the window closes. See playStowGesture.
    this.stowGestureT = Math.max(0, this.stowGestureT - dt);
    const stowing = this.stowGestureT > 0;
    this.stowGestureK += ((stowing ? 1 : 0) - this.stowGestureK) * (1 - Math.exp(-14 * dt));
    if (!stowing && this.stowGestureK < 0.001) this.stowGestureK = 0;
    const readyRate = 3000 / Math.max(120, ITEM_POSE_PROFILES[this.resolvedPose.item].raiseMs);
    this.aimK += ((this.resolvedPose.aiming && this.weapon && !stowing ? 1 : 0) - this.aimK) * (1 - Math.exp(-readyRate * dt));
    // Snap the tail to a hard zero: the ease is asymptotic, so aimK would otherwise sit at a tiny
    // positive value forever and keep the pre-mixer aim-bone reset armed indefinitely. Terminating
    // it makes "not aiming" a definite state — and clears the aim-time filter state, so the next
    // aim seeds fresh from the live pose instead of pulling toward a minutes-old average.
    if (!this.aiming && this.aimK < 0.001) {
      this.aimK = 0;
      this.armSmooth.clear();
    }
    // MOUNT RESET **BEFORE** the aim stages, not after. applyGunAim measures the barrel through
    // the weapon's transform; with the reset below it, the measurement carried LAST frame's
    // barrel-lock rotation, so the chest solve corrected an error that included the other
    // solver's output — a cross-frame feedback loop between the two. Measured as a limit-cycle:
    // 64 mm of muzzle oscillation at ADS IDLE with the full stack, ≤20 mm with any one stage
    // disabled. Reset first, and every frame measures the clean mount: deterministic, no cycle.
    if (this.weapon) this.weapon.quaternion.copy(this.weaponMount);
    // Keep the transition clocks above running, but let the full-body swim
    // stroke own the hips, spine and arms instead of composing land/air holds.
    if (this.swimming) {
      this.armSmooth.clear();
      return;
    }
    // CROUCH SPINE→HIP: a property of the crouch POSE, so it runs whether or not you are aiming
    // (and before the aim solves, which measure the torso they are correcting).
    this.crouchHipFold();
    this.adsAirTorsoHold(); // hold the aimed upper body through a jump / hover (see the method)
    if (this.aimK > 0.01 && !this.debugNoAim && this.weaponStance !== "over-shoulder") {
      if (this.stanceK > 0.5 && this.jet !== "glide") {
        // GUN STANCE (grounded, jumping OR hovering): layer the AUTHORED aim offset over the
        // torso so the upper body bends + looks along the aim (pitch from the camera, a subtle
        // yaw lead into turns) — distributed across spine→neck→head, not a rigid single-bone
        // pitch. Upper-body only: the legs play whatever base clip is active, and the gun/arms
        // ride spine_05 via FK. Bullets fire from the CAMERA (main.ts), so the pose is cosmetic.
        // Glide can't aim (aiming is forced false while gliding), so it's the one excluded case.
        this.applyAimOffset(dt);
      } else if (this.stanceK <= 0.5) {
        // FALLBACK (no pistol clips loaded): point the right arm in WORLD at the
        // camera look via the LIVE clip-posed shoulder. forward = group -Z, up =
        // +Y, pitch positive-looks-DOWN.
        const p = this.aimPitch;
        _aimDir.set(0.01, -Math.sin(p), -Math.cos(p)).normalize().applyQuaternion(this.group.quaternion);
        this.group.updateMatrixWorld(true);
        this.boneByName.get("RightShoulder")!.getWorldQuaternion(_aimParent).invert();
        _aimWorld.setFromUnitVectors(LIMB_AXIS, _aimDir);
        _aimParent.multiply(_aimWorld);
        this.boneByName.get("RightArm")!.quaternion.slerp(_aimParent, this.aimK);
        this.boneByName.get("RightForeArm")!.quaternion.slerp(_rest, this.aimK);
        this.boneByName.get("RightHand")!.quaternion.slerp(_rest, this.aimK);
      }
    }
    // HOVER (FALLBACK only): if the retargeted flight clips didn't load, let the
    // legs hang/dangle procedurally so the jetpack pose isn't the braced freefall
    // legs. When the flight clips ARE present they pose the legs properly, so this
    // is skipped. Gated on jet==="hover" so the hoverK decay tail can't yank the
    // legs vertical against the land/fall clip on touchdown.

    // Gliding (FALLBACK only): spread the arms into wings — the OLD procedural
    // glide clip tucked them. The retargeted f_boost clip poses the arms in a
    // forward boost, so skip the wing-spread when it's driving the glide.
    const glideArm = this.glideK * (1 - this.aimK) * (1 - this.stanceK);
    if (glideArm > 0.02 && this.acts["f_boost"] === undefined) {
      this.setLimb("LeftArm", new THREE.Vector3(1, -0.15, 0.35), glideArm);
      this.setLimb("RightArm", new THREE.Vector3(-1, -0.15, 0.35), glideArm);
    }
    if (this.weaponStance === "over-shoulder" && this.gunOut && !this.construct && !this.farLOD) {
      this.applyLauncherStance();
    } else {
      this.applyStanceHandPlacement();
      // Underhand weapons are rigidly aimed by pinUnderhandWeaponToBody. The
      // generic two-hand barrel lock pivots around a rifle foregrip and walks the
      // firing hand to compensate; on a long heavy gun that translation was up
      // to ~30 cm and changed with every gait frame, causing the barrel jump.
      if (this.weaponStance !== "underhand") this.applyBarrelLock();
      // (applyAdsGunAnchor REMOVED 2026-08-17: the GUN STEADY in seatWeaponInSupportReach
      // covers the same ADS-move regime in the GROUP frame. Two low-pass position
      // controllers on one hand — chest-frame anchor + group-frame steady — fought each
      // other, and their summed capped walks extended the arm into the degenerate-pole
      // regime = the reported ELBOW INVERSIONS. One joint, one boss.)
      this.applyReloadPose(); // bring the gun muzzle-DOWN during a reload (replaces the old barrel spin)
      this.applyWeaponGrip(dt); // pin the support hand to the weapon now the gun pose is final
    }
    this.shotRecoil.apply(dt,this.group,this.boneByName);
    this.applyFlinch(dt); // hit recoil rides LAST, on top of the finished pose
  }

  /**
   * PROCEDURAL FLINCH — a decaying directional shove of the upper body, applied after every other
   * layer so it reads no matter what the body is doing (running, aiming, holding a gun). See
   * flinch() for why this replaced the full-body `hit_*` clip.
   *
   * Shape: an immediate snap that eases back with a slight overshoot, so it reads as absorbing an
   * impact rather than as a pose held and dropped. Split across the spine and the head, with the
   * head taking the larger share (it is what reads at third-person distance). Nothing is rooted
   * and no clip is disturbed, so the legs keep striding and the aim keeps tracking.
   */
  private applyFlinch(dt: number): void {
    if (this.flinchT <= 0) { this.flinchAmp = 0; return; }
    this.flinchT = Math.max(0, this.flinchT - dt);
    const p = 1 - this.flinchT / FLINCH_DUR; // 0 at impact → 1 when spent
    if (this.flinchT <= 0) { this.flinchAmp = 0; return; }
    // Snap in over the first ~12%, then a damped spring home (one small overshoot).
    const env = p < 0.12
      ? p / 0.12
      : Math.exp(-4.2 * (p - 0.12)) * Math.cos(7.5 * (p - 0.12));
    const amp = this.flinchAmp * env;
    if (Math.abs(amp) < 1e-4) return;
    this.group.updateMatrixWorld(true);
    _flFwd.set(0, 0, -1).applyQuaternion(this.group.quaternion);
    _flUp.set(0, 1, 0).applyQuaternion(this.group.quaternion);
    _flRight.crossVectors(_flFwd, _flUp).normalize();
    // A hit from the FRONT rocks the body back (pitch about body-right); from the SIDE it rolls
    // about body-forward. Signs are the same empirical convention the ADS stance uses.
    const head = amp * FLINCH_HEAD_SHARE;
    const spine = (amp - head) / 2;
    if (this.flinchDirF !== 0) {
      _flQ.setFromAxisAngle(_flRight, this.flinchDirF * spine);
      this.rotateBoneWorld("spine_02", _flQ);
      this.rotateBoneWorld("spine_03", _flQ);
      _flQ.setFromAxisAngle(_flRight, this.flinchDirF * head);
      this.rotateBoneWorld("Head", _flQ);
    }
    if (this.flinchDirR !== 0) {
      _flQ.setFromAxisAngle(_flFwd, this.flinchDirR * spine);
      this.rotateBoneWorld("spine_02", _flQ);
      this.rotateBoneWorld("spine_03", _flQ);
      _flQ.setFromAxisAngle(_flFwd, this.flinchDirR * head);
      this.rotateBoneWorld("Head", _flQ);
    }
  }

  /**
   * (applyAdsGunAnchor DELETED 2026-08-17 — do not resurrect. The 2026-08-02 chest-space
   * "virtual gun" anchor and the group-space GUN STEADY in seatWeaponInSupportReach were
   * two low-pass position controllers with DIFFERENT reference frames driving the same
   * hand: the chest bobs within the group, so their smoothed targets disagreed every
   * gait cycle, and the summed capped corrections extended the arm toward straight —
   * where the elbow-perp bend plane degenerates and the raw per-frame pole flips sign =
   * the reported ELBOW/SHOULDER IK INVERSIONS. The steady covers the anchor's regime
   * (aimK·moveK) at a steadier frame; the seat's reach-deficit term covers the support
   * envelope the anchor existed for.)
   */

  /**
   * The AIM RIG (upper body), layered over the locomotion clips while RMB-aiming a gun — the
   * industry-standard layering (Lyra/ALS): locomotion base → additive AIM OFFSET (authored poses,
   * blended over yaw/pitch) → small procedural residual correction → IK last. The barrel is solved
   * toward the camera-parallel aim direction (aimDirWorld), and the fired bolt travels along that
   * SAME direction (Avatar.aimForward, read by main.ts) from the muzzle, so gun and bolt line up.
   * All layers eased by aimK, run AFTER mixer.update so they survive:
   *   1. AUTHORED torso bend — the baked UE aim-offset grid over spine_01..04 + neck/head, LOCAL
   *      additive. Skips spine_05, whose world is owned by layer 4.
   *   2. ARM-STEADY — low-pass the gait swing out of the arm chain (on the CLIP pose, before the
   *      authored aim is added, so the aim itself stays instant).
   *   3. AUTHORED arm aim — the same grid's ARM chains, MESH-SPACE additive (applyAimArmOffset):
   *      the authored clavicle/shoulder counter-animation that points the held gun along the pitch.
   *   4. GUN-TRACK residual — measure the actual muzzle line and close the remaining error with a
   *      chest pitch + clamped clavicle yaw (applyGunAim), then the ADS raise. Small by
   *      construction now: the authored layer does the gross aiming.
   * (The rifle head rides the grid's own head delta; applyHeadLook is disabled for it.)
   */
  private applyAimOffset(dt: number): void {
    const k = this.aimK;
    if (k < 0.01) return;
    // aimPitch > 0 looks DOWN; the grid pitch axis is +up/−down, so negate.
    // The ARM CANNON keeps the old 1.7 over-drive: it takes torso-only deltas (no arm block, its
    // bolts fire from the camera), so its bend is pure body language and was tuned at that scale.
    const pitchScale = this.construct === "cannon" ? 1.7 : this.aimOffsetPitchScale;
    const pitchDeg = clampDeg(-this.aimPitch * RAD2DEG * pitchScale, 90);
    // Body already yaw-locks to the camera; add a subtle authored torso lead while turning.
    const yawDeg = clampDeg(this.turnRate * this.aimOffsetYawLead, 135);
    this.aimSampler.sample(yawDeg, pitchDeg, this.crouchK, this.aimDelta, 0, AIM_OFFSET_ARM_START);
    // ARMS SAMPLE AT YAW 0: the yaw axis here is a turn-rate flourish, not a real aim-vs-body
    // divergence, and letting it swing the arm chain swings the GUN with every turn.
    this.aimSampler.sample(0, pitchDeg, this.crouchK, this.aimDelta, AIM_OFFSET_ARM_START, this.aimBones.length);
    // 2-first: damp the BASE pose (arms + neck/head) BEFORE any additive layer, so the filter only
    // sees the clip's gait swing and every authored/solved layer on top stays instant. The cannon
    // is exempt — its pistol-clip arm pose IS the aim, and damping it would fight the author.
    if (this.construct !== "cannon") this.applyAimArmSteady(k, dt);
    // 1b) GAIT-LEAN DAMP (user call 2026-08-02): the locomotion clips pitch the PELVIS forward
    //     with speed ("when moving forward, both bodies tilt forward at the hip") — while
    //     AIMING that lean is unwanted on top of the stance's own hip hinge. Remove most of the
    //     clip's forward pelvis pitch (thighs counter-rotate, so the stride is untouched); the
    //     gun-track solves after this and re-aims whatever the torso change did to the barrel.
    this.dampPelvisLean(k, dt);
    this.applyPelvisAimPitch(k);
    // 1) AUTHORED torso bend, LOCAL additive down the chain. We SKIP spine_05 (= the chest the
    //    gun-arms hang off) — its world is owned by the gun-track residual so the weapon lands on
    //    the reticle — and stop before the ARM block, which needs mesh-space application.
    for (let i = 0; i < AIM_OFFSET_ARM_START; i++) {
      const bone = this.aimBones[i];
      if (!bone || bone === this.chestBone) continue;
      const w = k * this.aimMask[i];
      if (w < 0.005) continue;
      _aoTarget.copy(bone.quaternion).multiply(this.aimDelta[i]); // local-space additive: base · delta
      bone.quaternion.slerp(_aoTarget, w);
    }
    // The ARM CANNON stops here: AUTHORED TORSO POSE ONLY. The layers below exist to aim a HELD
    // gun (they steady the gait swing, add the authored two-handed arm aim, then close the
    // measured residual) — every one of them assumes a prop in the hand. The cannon uses the
    // pistol animation exactly as authored, plus this authored torso offset, and nothing else.
    if (this.construct === "cannon") return;
    // 3) AUTHORED arm aim (instant, deterministic) → 4) measured residual → 5) barrel-preserving
    //    posture (ADS raise + stance). The stance runs AFTER the gun-track: a first version ran
    //    before it, on the theory that the solvers would "re-aim for free" — they did, by
    //    rotating the chest and clavicles straight back, which CANCELLED the visible posture
    //    (the reported "hunch effect is not there"). Barrel-preserving construction instead:
    //    every stance rotation carries an explicit counter-rotation, so nothing needs undoing.
    this.applyAimArmOffset(k);
    this.applyGunAim(k);
    this.applyAdsRaise(k); // AFTER the gun-track: barrel-preserving, so the elevation solve stands
    this.applyAdsStance(k);
  }

  /** ADS STANCE — a NATURAL shooter's posture while aiming (user direction 2026-08-01): the torso
   *  BLADES (support side toward the target, firing shoulder drawn back) and takes a forward
   *  HUNCH that curls the frame down behind the gun and pulls it in closer to the chest.
   *
   *  BARREL-PRESERVING, same contract as applyAdsRaise: each torso rotation is paired with an
   *  explicit counter-rotation further down the chain, composing to identity AT THE HANDS —
   *    blade: spine_01..03 yaw +θ  ·  both clavicles yaw −θ     ⟹ shoulder girdle carries the
   *          twist visibly, the arms keep their heading;
   *    hunch: spine_02..03 pitch +φ ·  both upper arms pitch −φ ⟹ the frame curls forward and
   *          DOWN around the gun, whose orientation stands; its position tucks toward the chest.
   *  Runs AFTER the gun-track + ADS raise so the measured solves never see (and undo) it. The
   *  head gaze lock runs later still and keeps the eyes on the reticle. Tune by eye:
   *  adsBlade / adsHunch (rad), live-tunable in the harness. */
  private applyAdsStance(k: number): void {
    const blade = this.adsBlade * k;
    const hunch = this.adsHunch * k;
    // Crouched, the chest folds FURTHER over the gun — spine only, see adsHunchCrouch.
    const crouchHunch = this.adsHunchCrouch * this.crouchAdsTorso() * k;
    // gunOut gate: the SWORD reaches this code path too (armed stance + RMB focus + a held
    // weapon), and a shooter's shoulder-pocket stance is wrong for a blade.
    if ((Math.abs(blade) < 1e-3 && Math.abs(hunch) < 1e-3 && Math.abs(crouchHunch) < 1e-3)
      || !this.weapon || this.construct || !this.gunOut) return;
    this.group.updateMatrixWorld(true);
    _aoFwd.set(0, 0, -1).applyQuaternion(this.group.quaternion);
    _aoUp.set(0, 1, 0).applyQuaternion(this.group.quaternion);
    _aoRight.crossVectors(_aoFwd, _aoUp).normalize();
    // SIGNS ARE EMPIRICAL, verified in the lab (user report 2026-08-01: the first cut leaned
    // BACKWARD and bladed LEFT — the axis-derivation comments were wrong; never trust them over
    // a screenshot). Positive adsBlade turns the torso to the shooter's RIGHT (firing shoulder
    // drawn back); positive adsHunch curls FORWARD. Negative values invert (the guards test
    // magnitude, so live experimentation with either sign works).
    if (Math.abs(blade) > 1e-3) {
      _adsQ.setFromAxisAngle(_aoUp, -blade / 3);
      this.rotateBoneWorld("spine_01", _adsQ);
      this.rotateBoneWorld("spine_02", _adsQ);
      this.rotateBoneWorld("spine_03", _adsQ);
      _adsQ.setFromAxisAngle(_aoUp, blade); // counter: the arms keep their solved heading
      this.rotateBoneWorld("LeftShoulder", _adsQ);
      this.rotateBoneWorld("RightShoulder", _adsQ);
    }
    if (Math.abs(hunch) + Math.abs(crouchHunch) > 1e-3) {
      // HIP HINGE (user call 2026-08-02): the forward lean previously came ENTIRELY from spine
      // curvature (measured: 14.8° lean = 14.8° spine bend, hip 0°) — an arched back, most
      // visible on the female's slimmer frame. A real aimed lean hinges at the HIP: the pelvis
      // pitches forward (the whole torso rides it, spine straighter), the thighs counter so the
      // legs/feet stand, and the spine keeps only a reduced curl. The arm counter below already
      // balances the TOTAL forward pitch (hinge + curl = hunch), so the barrel stands.
      // ONE FOLD, ONE SPLIT. The base ADS hunch and the extra crouched fold are the same motion,
      // so they share a single hip/spine split instead of the crouch part being bolted on as pure
      // spine curl.
      //
      // ⚠⚠ CROUCHED, THE HIP TAKES MORE OF IT, NOT LESS (user call 2026-08-03: "I want the hip
      // area to take more of the tilt from ADS instead of the upper spine"). Two earlier passes got
      // this backwards — first by adding the crouch fold as spine-only, then by CUTTING the hip
      // hinge outright — and both forced the whole fold into spine_02/03, which is exactly the
      // unnatural upper-back bend being reported. A crouched shooter folds at the hip and keeps a
      // straight back; the spine share drops rather than rises.
      const spineShare = ADS_HUNCH_SPINE_SHARE
        + (ADS_HUNCH_SPINE_SHARE_CROUCH - ADS_HUNCH_SPINE_SHARE) * this.crouchK;
      const fold = hunch + crouchHunch;
      const hinge = fold * (1 - spineShare);
      const curl = fold * spineShare;
      _adsQ.setFromAxisAngle(_aoRight, -hinge);
      this.rotateBoneWorld("pelvis", _adsQ);
      _adsQ.setFromAxisAngle(_aoRight, hinge); // legs counter: feet stay planted
      this.rotateBoneWorld("LeftUpLeg", _adsQ);
      this.rotateBoneWorld("RightUpLeg", _adsQ);
      _adsQ.setFromAxisAngle(_aoRight, -curl / 2); // − about this axis = curl forward/down
      this.rotateBoneWorld("spine_02", _adsQ);
      this.rotateBoneWorld("spine_03", _adsQ);
      // Counter the TOTAL forward pitch actually applied so the barrel stands. hinge + curl == fold
      // by construction; written out so a future re-split cannot silently break the counter.
      _adsQ.setFromAxisAngle(_aoRight, hinge + curl);
      this.rotateBoneWorld("LeftArm", _adsQ);
      this.rotateBoneWorld("RightArm", _adsQ);
      // SHRUG: the shoulders rise and tighten around the neck with the hunch. This is the part
      // that reads from BEHIND — the camera the game actually uses — where the forward spine
      // curl is motion along the view axis and effectively invisible (measured: a 28° curl
      // changed the behind-view silhouette imperceptibly). Roll about body-forward raises each
      // clavicle; the upper arms counter-roll so the arms/gun keep their solved line.
      // ASYMMETRIC on purpose (user call, 2026-08-01: "the left shoulder is sticking up too
      // high"): a shouldered rifle raises the FIRING shoulder into the stock, while the SUPPORT
      // shoulder stays low and relaxed behind its extended arm.
      const shrugL = hunch * 0.2;
      const shrugR = hunch * 0.6;
      _adsQ.setFromAxisAngle(_aoFwd, shrugL);
      this.rotateBoneWorld("LeftShoulder", _adsQ);
      _adsQ.setFromAxisAngle(_aoFwd, -shrugL);
      this.rotateBoneWorld("LeftArm", _adsQ); // counter: the support arm keeps its line
      _adsQ.setFromAxisAngle(_aoFwd, -shrugR);
      this.rotateBoneWorld("RightShoulder", _adsQ);
      _adsQ.setFromAxisAngle(_aoFwd, shrugR);
      this.rotateBoneWorld("RightArm", _adsQ); // counter: the firing arm keeps its line
    }
  }

  /**
   * HOLD THE ADS TORSO THROUGH A JUMP / HOVER.
   *
   * The airborne + flight clips lean the whole torso — measured on the rifle set, the spine tips
   * 14.8 deg standing at ADS, 25.8 deg hovering, and 43.9 deg hover-STRAFING (probe
   * client/test/_adshover.mjs). Aimed, that reads as the upper body pitching over and the sight
   * picture rolling away, which is the "flips over / flails" report. The aim LAYER then has to
   * bend the neck and arms further and further to drag the barrel back onto the reticle.
   *
   * So: while aiming, straighten the spine chain back toward the pose it holds on the ground by a
   * share, BEFORE the aim offset runs (which then adds its own deliberate lean on a clean base).
   * The legs keep the flight clip untouched — nothing is re-seated at the hip here, because
   * airborne there is no ground contact to preserve.
   */
  private adsAirTorsoHold(): void {
    // ⚠⚠ APPLIES IN EVERY STANCE, NOT JUST WHILE AIMING. The armed jump reads well because
    // `r_jump` holds an upright carry; the UNARMED/blade air clip instead throws the whole body
    // into a leap — measured 50° of torso lean with the body nearly horizontal — so the identical
    // leg squat rode on top of a torso lying back and the jump looked like a different move
    // entirely (user 2026-08-03: "that exact jump is not being applied to the standard + blade
    // combat"). Aiming still holds harder, since a sight picture tolerates less than a free jump.
    if (this.airCombatOwnsBody()) return; // the aerial string owns the body — see the helper
    // ⚠⚠ THE SQUARE-UPS BELONG TO A PLAIN BALLISTIC JUMP, NOT TO FLIGHT. On the jets the authored
    // clips BANK — a hover strafe rolls the hips into the direction of travel, which is the whole
    // read of moving sideways — and the hip square-up was removing 95% of exactly that roll, so
    // strafing left/right looked flat (user 2026-08-03: "we don't correctly take into account the
    // left-right tilt we should be applying when moving left / right"). Everything here fades out
    // with the jet EXCEPT the aim-driven hold, which still has a sight picture to protect.
    const jetK = Math.max(this.hoverK, this.glideK);
    const hold = Math.max(this.aimK * ADS_AIR_TORSO_HOLD, AIR_TORSO_HOLD * (1 - jetK));
    const k = this.airK * hold;
    if (k < 0.02) return;
    const P = (n: string): THREE.Vector3 | null => {
      const b = this.boneByName.get(n);
      return b ? b.getWorldPosition(new THREE.Vector3()) : null;
    };
    this.group.updateMatrixWorld(true);
    this.group.getWorldQuaternion(_plGroupQ);
    _aoFwd.set(0, 0, -1).applyQuaternion(_plGroupQ);
    _aoUp.set(0, 1, 0).applyQuaternion(_plGroupQ);
    _aoRight.crossVectors(_aoFwd, _aoUp).normalize();
    // ⚠⚠ SQUARE THE HIPS FIRST — pitch is not the only axis the air clips move. The unarmed/blade
    // `jump` clip ROLLS and YAWS the pelvis hard (measured 136° roll / −38° yaw in body space
    // against the armed clip's 4°), and since the pelvis carries the whole leg chain that threw
    // both legs ~12 cm to the shooter's right — "the legs are getting kicked out to the right when
    // unarmed / blade out when jumping". The procedural squat could never fix it: the leg LOCALS
    // were already identical across stances, the frame under them was not.
    // Removing a share of the pelvis's roll and yaw brings the legs back under the body; the torso
    // above rides along, which squares the shoulders too.
    const pelvisBone = this.boneByName.get("pelvis");
    if (pelvisBone) {
      for (const axis of [_aoFwd, _aoUp]) {
        pelvisBone.getWorldQuaternion(_plCur);
        _plRest.copy(_plGroupQ).multiply(this.pelvisRestRelGroup).invert();
        _plDelta.copy(_plCur).multiply(_plRest);
        twistAbout(_plDelta, axis, _plPitch);
        if (Math.abs(_plPitch.w) > 0.99997) continue;
        // Roll and yaw get a HARDER share than the pitch hold: a pitched torso still reads as a
        // leap, but hips rolled or twisted in the air have no purpose and only throw the legs out.
        _plPart.set(0, 0, 0, 1).slerp(_plPitch, this.airK * AIR_HIP_SQUARE * (1 - jetK)).invert();
        this.rotateBoneWorld("pelvis", _plPart);
      }
    }
    const chain = ["pelvis", "spine_01", "spine_02", "spine_03", "neck_01"];
    const lean: number[] = [];
    for (let i = 0; i < chain.length - 1; i++) {
      const a = P(chain[i]), b = P(chain[i + 1]);
      if (!a || !b) return;
      const d = b.sub(a);
      lean.push(Math.atan2(d.dot(_aoFwd), d.dot(_aoUp)));
    }
    // The PELVIS's own tilt off the body up-axis counts too: on the strafe flight clips most of
    // the lean lives there, not in the spine (measured 32.8 deg residual with the spine alone
    // corrected). Airborne there is no ground contact to preserve, so the whole body may
    // straighten — at a reduced share, so the legs keep some flight character.
    if (Math.abs(lean[0]) > 1e-3) {
      _plPart.setFromAxisAngle(_aoRight, lean[0] * k * ADS_AIR_PELVIS_SHARE);
      this.rotateBoneWorld("pelvis", _plPart);
    }
    // Signed BOTH ways, unlike the crouch transfer: a flight clip can lean the torso back as
    // readily as forward, and either one takes the sight picture with it.
    for (let j = 1; j < lean.length; j++) {
      const bend = lean[j] - lean[j - 1];
      if (Math.abs(bend) < 1e-3) continue;
      _plPart.setFromAxisAngle(_aoRight, bend * k);
      this.rotateBoneWorld(chain[j], _plPart);
    }
    // SQUARE THE HEAD. The unarmed/blade air clips yaw it, and nothing pins it back unless an aim
    // or combat stance owns the gaze — so a free jump looked slightly off to one side (user
    // 2026-08-03). Same treatment as the hips: remove the airborne yaw about body-up.
    const headBone = this.boneByName.get("Head");
    if (headBone) {
      headBone.getWorldQuaternion(_plCur);
      _plRest.copy(_plGroupQ).multiply(this.headRestRelGroup).invert();
      _plDelta.copy(_plCur).multiply(_plRest);
      twistAbout(_plDelta, _aoUp, _plPitch);
      if (Math.abs(_plPitch.w) <= 0.99997) {
        _plPart.set(0, 0, 0, 1).slerp(_plPitch, this.airK * AIR_HEAD_SQUARE * (1 - jetK)).invert();
        this.rotateBoneWorld("Head", _plPart);
      }
    }
    // JUMP HUNCH — the chest comes forward OVER the tucked legs. Holding the torso upright (above)
    // is what stops the clip throwing it around, but upright alone reads wrong with the legs out in
    // front: the body looked like it was sitting back. This is the deliberate counter-curve, hinged
    // at the HIP (spine_01 and up, so the legs are untouched) and faded out by aimK, since a sight
    // picture cannot afford it. (User 2026-08-03: "the torso should be hunched over more from the
    // hips when jumping… right now the torso comes back a bit and is straight-up".)
    // ⚠ JUMP ONLY — a HOVER must not lean. The tuck belongs to a ballistic arc; on the jets the
    // body hangs upright under them, and carrying the hunch across read as flying face-down (user
    // 2026-08-03: "when hovering, we shouldn't lean forward like in the jump"). Fades out with
    // whichever jet is live (`jetK`, computed at the top of this method).
    // A GUN gets much less of it (user 2026-08-03: "the lean should be less intense when holding a
    // rifle, significantly"). A carried weapon wants the torso near its carry attitude — the deep
    // ballistic tuck is a free-hands move, and on top of `r_jump`'s own lean it over-committed.
    const gunK = this.gunOut ? AIR_HUNCH_GUN : 1;
    const hunch = AIR_JUMP_HUNCH * gunK * this.airK * (1 - this.aimK) * (1 - jetK);
    if (hunch > 1e-3) {
      _plPart.setFromAxisAngle(_aoRight, -hunch / 3);
      this.rotateBoneWorld("spine_01", _plPart);
      this.rotateBoneWorld("spine_02", _plPart);
      this.rotateBoneWorld("spine_03", _plPart);
    }
  }

  /**
   * CROUCH SPINE→HIP TRANSFER. Move a share of the crouch pose's forward SPINE curl down into the
   * pelvis, so a crouched character folds at the hip with a straighter back.
   *
   * ⚠⚠ MEASURED, not assumed: the female crouch clip puts essentially ALL of its fold in the
   * spine — 18.8° lower + 21.4° upper against a 2° hip hinge (probe client/test/_femalecrouch.mjs).
   * That is an arched back, and it is what "the spine bends unnaturally" was reporting; the ADS
   * fold on top of it was only making an existing problem visible. Redistributing is the fix —
   * the TOTAL forward lean is untouched, so the silhouette and the head height stay put, but the
   * curve moves from the upper back to the hip where it belongs.
   *
   * Mechanically the inverse of dampPelvisLean: take the swing-twist pitch component of each
   * spine joint about body-right, remove a share of it, and add the same amount at the pelvis with
   * the thighs counter-rotating so the legs keep playing the crouch clip exactly.
   */
  private crouchHipFold(): void {
    if (this.crouchK < 1e-3) return;
    const share = CROUCH_SPINE_TO_HIP * this.crouchK;
    if (share < 1e-3) return;
    const P = (n: string): THREE.Vector3 | null => {
      const b = this.boneByName.get(n);
      return b ? b.getWorldPosition(new THREE.Vector3()) : null;
    };
    this.group.updateMatrixWorld(true);
    this.group.getWorldQuaternion(_plGroupQ);
    _aoFwd.set(0, 0, -1).applyQuaternion(_plGroupQ);
    _aoUp.set(0, 1, 0).applyQuaternion(_plGroupQ);
    _aoRight.crossVectors(_aoFwd, _aoUp).normalize();
    // Forward lean of each spine SEGMENT, measured geometrically off the live pose — no bind data
    // needed (the rig stores bind locals only for the arms). The joint's own contribution is the
    // step between consecutive segments.
    const chain = ["pelvis", "spine_01", "spine_02", "spine_03", "neck_01"];
    const lean: number[] = [];
    for (let i = 0; i < chain.length - 1; i++) {
      const a = P(chain[i]), b = P(chain[i + 1]);
      if (!a || !b) return;
      const d = b.sub(a);
      lean.push(Math.atan2(d.dot(_aoFwd), d.dot(_aoUp)));
    }
    let moved = 0;
    for (let j = 1; j < lean.length; j++) {
      const bend = lean[j] - lean[j - 1]; // this joint's own forward contribution
      if (bend <= 0) continue;            // only straighten FORWARD curl, never add to a backbend
      const take = bend * share;
      moved += take;
      // Rotating a parent carries its children rigidly, so every joint's own local bend — and
      // therefore every `bend` measured above — survives the loop.
      _plPart.setFromAxisAngle(_aoRight, take);
      this.rotateBoneWorld(chain[j], _plPart);
    }
    if (moved < 1e-4) return;
    // Re-seat the removed curl AT THE HIP; thighs counter so the legs keep playing the crouch clip.
    _plPart.setFromAxisAngle(_aoRight, -moved);
    this.rotateBoneWorld("pelvis", _plPart);
    _plPart.setFromAxisAngle(_aoRight, moved);
    this.rotateBoneWorld("LeftUpLeg", _plPart);
    this.rotateBoneWorld("RightUpLeg", _plPart);
  }

  /** Remove PELVIS_LEAN_DAMP of the clip's forward pelvis pitch while aiming (see call site).
   *  Pitch = the swing-twist component about body-right of (current − upright rest); the thighs
   *  counter-rotate by the removed amount so the legs keep playing the clip exactly. */
  private dampPelvisLean(k: number, dt: number): void {
    void dt;
    const pelvis = this.boneByName.get("pelvis");
    if (!pelvis?.parent) return;
    this.group.updateMatrixWorld(true);
    this.group.getWorldQuaternion(_plGroupQ);
    _aoFwd.set(0, 0, -1).applyQuaternion(_plGroupQ);
    _aoUp.set(0, 1, 0).applyQuaternion(_plGroupQ);
    _aoRight.crossVectors(_aoFwd, _aoUp).normalize();
    pelvis.getWorldQuaternion(_plCur);
    _plRest.copy(_plGroupQ).multiply(this.pelvisRestRelGroup);
    _plDelta.copy(_plCur).multiply(_plRest.invert()); // world delta from upright rest
    twistAbout(_plDelta, _aoRight, _plPitch); // the forward-lean component only
    if (Math.abs(_plPitch.w) > 0.99997) return; // < ~0.9° of lean — nothing to damp
    _plPart.set(0, 0, 0, 1).slerp(_plPitch, PELVIS_LEAN_DAMP * k).invert(); // remove that share
    this.rotateBoneWorld("pelvis", _plPart);
    _plPart.invert(); // legs counter: the stride stays exactly as authored
    this.rotateBoneWorld("LeftUpLeg", _plPart);
    this.rotateBoneWorld("RightUpLeg", _plPart);
  }

  /** AIM-PITCH PELVIS SHARE (user call 2026-08-02): looking up/down should move the WHOLE torso
   *  — pelvis included — in tandem, not just bend the spine. The pelvis takes a fraction of the
   *  camera pitch (smooth by construction — aimPitch is the smoothed camera input, eased by
   *  aimK); the thighs counter so the legs/feet stand, and the closed-loop chest solve then
   *  needs proportionally LESS spine bend, straightening the whole aim silhouette. */
  private applyPelvisAimPitch(k: number): void {
    const ang = -this.aimPitch * PELVIS_AIM_PITCH_SHARE * k; // aimPitch>0 looks DOWN → hinge forward
    if (Math.abs(ang) < 1e-4) return;
    const pelvis = this.boneByName.get("pelvis");
    if (!pelvis?.parent) return;
    this.group.updateMatrixWorld(true);
    _aoFwd.set(0, 0, -1).applyQuaternion(this.group.quaternion);
    _aoUp.set(0, 1, 0).applyQuaternion(this.group.quaternion);
    _aoRight.crossVectors(_aoFwd, _aoUp).normalize();
    _plPart.setFromAxisAngle(_aoRight, ang);
    this.rotateBoneWorld("pelvis", _plPart);
    _plPart.invert();
    this.rotateBoneWorld("LeftUpLeg", _plPart);
    this.rotateBoneWorld("RightUpLeg", _plPart);
  }

  /**
   * MESH-SPACE additive aim for the ARM chains — UE "Mesh Space" AimOffset semantics, the part of
   * the authored grid the old system threw away (it applied torso-only and reconstructed the arm
   * pose procedurally: chest pitch + clavicle yaw + counter-rotated raise — the robotic budget
   * path). These are Epic's authored aim poses: the clavicle involvement, shoulder counter-shrug
   * and wrist carry that uniform procedural spread cannot produce.
   *
   * Local-space additive would ride whatever the gait swung the arm to, so the aim direction would
   * swim with the walk cycle; mesh-space premultiplies the delta in root-relative component space,
   * keeping the AUTHORED aim orientation world-stable over any base pose:
   *     world'(b) = (C · D(b) · C⁻¹) · world(b)        C = the root bone's world rotation
   * Applied chain-down from a SNAPSHOT of the pre-pass worlds — the deltas were measured against
   * the whole pose at once, so applying against live matrices would fold each parent's delta into
   * its children a second time (chain double-counting).
   */
  private applyAimArmOffset(k: number): void {
    if (k < 0.01) return;
    const rootB = this.boneByName.get("root");
    if (!rootB) return;
    this.group.updateMatrixWorld(true);
    rootB.getWorldQuaternion(_amC);
    _amCInv.copy(_amC).invert();
    for (let side = 0; side < 2; side++) {
      const base = AIM_OFFSET_ARM_START + side * 4; // [clavicle, upperarm, lowerarm, hand]
      let ok = true;
      for (let j = 0; j < 4; j++) {
        const b = this.aimBones[base + j];
        if (!b?.parent) { ok = false; break; }
        b.getWorldQuaternion(_amWorld[j]);
      }
      if (!ok) continue;
      // The clavicle's parent (spine_05) is final at this point — its world is the chain's frame.
      this.aimBones[base]!.parent!.getWorldQuaternion(_amParent);
      for (let j = 0; j < 4; j++) {
        const b = this.aimBones[base + j]!;
        const w = k * this.aimMask[base + j];
        _amD.copy(this.aimDelta[base + j]);
        if (w < 1) _amD.slerp(_amIdent, 1 - w); // ease the delta, not the result
        _amNew.copy(_amC).multiply(_amD).multiply(_amCInv).multiply(_amWorld[j]);
        b.quaternion.copy(_amParent).invert().multiply(_amNew); // local = parent'⁻¹ · world'
        _amParent.copy(_amNew); // this bone's FINAL world frames the next bone down
      }
    }
  }

  /** Damp the BASE pose's gait swing while aiming (arms + neck/head) so the weapon and head don't
   *  bob with the walk/run. Low-passes each bone's local pose and pulls the bone toward that
   *  running average by aimK·aimArmSteady. Runs BEFORE the additive aim layers, so the filter only
   *  ever sees the clip — the authored aim offset, gun-track residual and ADS raise are added on
   *  top and stay instant. The tracking rate DROPS as the aim settles in (5/s hip → ~1.8/s at full
   *  ADS): the ADS base pose is near-constant, so at full aim the average may follow only slow
   *  trends and the 1–2 Hz gait bounce is filtered out (measured: 233 mm → target <100 mm of
   *  muzzle travel at an ADS walk). Seeded lazily from the current clip pose; cleared on release. */
  private applyAimArmSteady(k: number, dt: number): void {
    // SPEED-AWARE strength. Standing at ADS the sights should be rock-steady, so the filter is
    // strong and slow (w 0.97, ~0.8/s — the mean pose IS the idle pose). While MOVING the mean of
    // a gait swing is a pose that never occurs, and holding the arms there while the pelvis/legs
    // play the clip reads as the upper body detaching from the run — measured/reported as torso
    // jitter with the hands off the weapon. So in motion the filter relaxes to a light damp and
    // the AUTHORED rifle locomotion carries the look (its sway is alive, not broken); the aim
    // itself is held by the gun-track + barrel lock + head gaze lock, which run after.
    const move = this.moveK;
    const w = k * this.aimArmSteady * (1 - 0.4 * move);
    if (w < 0.01) return;
    const rateHz = 5 - 4.2 * k * (1 - move);
    for (const name of AIM_STEADY_BONES) {
      const b = this.boneByName.get(name);
      if (!b) continue;
      let sm = this.armSmooth.get(name);
      if (!sm) { sm = b.quaternion.clone(); this.armSmooth.set(name, sm); }
      // ERROR-ADAPTIVE tracking: gait sway oscillates a bone a few degrees around its mean —
      // filter it hard. But when the average sits FAR off the pose, it is stale, not steady:
      // the aim was entered mid-draw-crossfade (the average seeded from the blended pose and,
      // at 0.8/s, held the gun visibly wrong for ~2 s of aim entry — measured 30° of barrel yaw
      // on the first cold sample), or the player switched direction. Track fast until re-locked.
      const err = 2 * Math.acos(Math.min(1, Math.abs(sm.dot(b.quaternion)))); // rad, sm vs clip
      const boost = Math.min(1, Math.max(0, (err - 0.15) / 0.2)) * 10; // >8.6° apart → up to +10/s
      sm.slerp(b.quaternion, 1 - Math.exp(-(rateHz + boost) * dt));
      b.quaternion.slerp(sm, w); // pull the bone toward the calmed pose
    }
    // SPINE at HALF weight, and NOT relaxed while moving — this is the "reduce the torso swing"
    // lever (user call 2026-08-01): the diagonal gait clips twist the torso hard enough to swing
    // the gun 33–45° off the reticle at a run. Half weight keeps the torso alive with the gait
    // while cutting the swing; the aim solves close what remains. The BACK-RIGHT diagonal gets
    // extra: its run clip carries the heaviest authored torso roll of the whole set (a long-known
    // clip quirk) and still measured 18.6° through the half-weight damp.
    const brDiag = 4 * Math.max(0, -this.moveFwd) * Math.max(0, this.moveRight); // 1 at perfect br
    const wS = k * (0.5 + 0.35 * Math.min(1, brDiag));
    if (wS < 0.01) return;
    for (const name of AIM_STEADY_SPINE) {
      const b = this.boneByName.get(name);
      if (!b) continue;
      let sm = this.armSmooth.get(name);
      if (!sm) { sm = b.quaternion.clone(); this.armSmooth.set(name, sm); }
      const err = 2 * Math.acos(Math.min(1, Math.abs(sm.dot(b.quaternion))));
      const boost = Math.min(1, Math.max(0, (err - 0.15) / 0.2)) * 10;
      sm.slerp(b.quaternion, 1 - Math.exp(-(2.2 + boost) * dt));
      b.quaternion.slerp(sm, wS);
    }
  }

  /** Premultiply a WORLD-space rotation onto a bone, then write it back as the bone's local. */
  private rotateBoneWorld(name: string, worldDelta: THREE.Quaternion): void {
    const b = this.boneByName.get(name);
    if (!b?.parent) return;
    b.getWorldQuaternion(_aoRaiseW).premultiply(worldDelta);
    b.parent.getWorldQuaternion(_aoRaisePar).invert();
    b.quaternion.copy(_aoRaisePar).multiply(_aoRaiseW);
  }

  /** Stand this avatar on an infinite flat floor through its own root, instead of a planet.
   *
   *  For the MANNEQUIN PREVIEWS (character creator, locker) there is no planet to sample, so with
   *  no `groundProbe` applyFootGrounding disengages entirely — and that IK pass is what flattens the
   *  idle clip's plantarflexed soles. The feet then render pointed, standing on nothing. The rig is
   *  dropped (RIG_DROP) so animated feet meet the ground AT the root, which makes the root's own
   *  plane exactly the floor the avatar should stand on. Read live: the previews move/reorient the
   *  group after this is installed. */
  standOnFlatGround(): void {
    const root = new THREE.Vector3();
    const up = new THREE.Vector3();
    this.groundProbe = (worldPos, outPoint, outNormal) => {
      this.group.getWorldPosition(root);
      up.set(0, 1, 0).applyQuaternion(this.group.quaternion).normalize();
      const height = _fgDrop.copy(worldPos).sub(root).dot(up); // signed height of worldPos over the floor
      outPoint.copy(worldPos).addScaledVector(up, -height); // drop it onto the floor
      outNormal.copy(up);
      return true;
    };
  }

  /** PER-FOOT GROUND IK — best-practice foot placement. The avatar stands radially upright, so on
   *  sloped/uneven terrain its feet would stay radial-flat and not match the ground. Each frame this
   *  (1) samples the planet surface under the body centre + each foot via `groundProbe`, (2) shifts
   *  each PLANTED foot vertically to its own local ground (an ADDITIVE slope delta, so it's a no-op
   *  on flat ground — no foot-slide), (3) drops the pelvis so the legs don't over-extend on big
   *  height differences, (4) runs a 2-bone IK (thigh→calf→foot) to reach the target, and (5) rolls
   *  each foot's sole onto the local ground normal. All eased + gated: full at idle/walk, faded out
   *  at run speed, in the air, and while boosting (the run/boost clips own the legs there). */
  private applyFootGrounding(dt: number, speed: number, grounded: boolean): void {
    const ease = 1 - Math.exp(-8 * dt);
    const speedFade = Math.max(0, Math.min(1, 1 - (speed - IK_WALK) / (IK_RUN - IK_WALK)));
    const want = this.groundProbe && grounded && this.footIkEnabled ? speedFade * (1 - this.runBoostK) * (1 - this.airK) : 0;
    this.groundIkK += (want - this.groundIkK) * ease;
    if (!this.groundProbe || this.groundIkK < 0.01) {
      this.ikPelvis += (0 - this.ikPelvis) * ease; // relax the hip drop as IK disengages
      // Clear the held knee poles while IK is OFF (airborne/flight). Otherwise a stale, possibly
      // back-bent pole is frozen for the whole flight and carried straight into the touchdown blend
      // (the "knees bend backwards on landing until you run" bug). Cleared → each landing re-seeds
      // fresh from body-forward.
      this.ikPole[0].set(0, 0, 0);
      this.ikPole[1].set(0, 0, 0);
      return;
    }
    const ikE = 1 - Math.exp(-12 * dt); // per-foot smoothing (terrain is noisy — ease everything)
    this.group.updateMatrixWorld(true);
    _gUp.set(0, 1, 0).applyQuaternion(this.group.quaternion).normalize();
    _gFwd.set(0, 0, -1).applyQuaternion(this.group.quaternion).normalize();
    this.group.getWorldPosition(_gRoot);
    if (!this.groundProbe(_gRoot, _gCenterPt, _gCenterN)) return;
    const centerH = _gCenterPt.dot(_gUp); // ground height under the centre, along up
    this.ikCenterNormal.lerp(_gCenterN.normalize(), ikE).normalize(); // one shared, smoothed ground normal for both feet
    // Calibrate each foot's "top of foot" axis from the BIND pose (sole-up = mesh +Y), once.
    if (!this.ikSoleLocal) {
      this.ikSoleLocal = [new THREE.Vector3(0, 1, 0), new THREE.Vector3(0, 1, 0)];
      for (let i = 0; i < 2; i++) {
        const fb = this.boneByName.get(IK_LEG_NAMES[i][2]);
        const idx = fb ? this.skeleton.bones.indexOf(fb) : -1;
        if (idx >= 0) {
          _gBindMat.copy(this.skeleton.boneInverses[idx]).invert();
          _gBindQ.setFromRotationMatrix(_gBindMat).invert();
          this.ikSoleLocal[i].set(0, 1, 0).applyQuaternion(_gBindQ).normalize();
        }
      }
    }
    // Desired foot "up" = radial up rotated TOWARD the ground normal by (slope − deadzone): flat/gentle
    // ground ⇒ radial up (a fully FLAT sole — removes the clip's plantarflex), real slopes ⇒ conform.
    const slopeAng = Math.acos(Math.max(-1, Math.min(1, _gUp.dot(this.ikCenterNormal))));
    if (slopeAng > 1e-3) {
      _gAxis.crossVectors(_gUp, this.ikCenterNormal).normalize();
      _gDelta.setFromAxisAngle(_gAxis, Math.max(0, slopeAng - IK_FOOT_DEADZONE));
      _gFootTarget.copy(_gUp).applyQuaternion(_gDelta).normalize();
    } else {
      _gFootTarget.copy(_gUp);
    }

    // PASS 1: sample + smooth each foot's slope delta / normal / planted weight; build ABSOLUTE world
    // ankle targets; find the pelvis drop. (Targets are captured BEFORE the hip drop so they stay on
    // the fixed ground when the body lowers.)
    const targets: [THREE.Vector3, THREE.Vector3] = [_gTargetL, _gTargetR];
    let pelvisTarget = 0;
    for (let i = 0; i < 2; i++) {
      const foot = this.boneByName.get(IK_LEG_NAMES[i][2]);
      if (!foot) {
        this.ikFootW[i] += (0 - this.ikFootW[i]) * ikE;
        continue;
      }
      foot.getWorldPosition(_gFootPos);
      let slope = this.ikSlope[i];
      let planted = 0;
      if (this.groundProbe(_gFootPos, _gGroundPt, _gGroundN)) {
        slope = _gGroundPt.dot(_gUp) - centerH; // higher/lower than the centre's ground (flat ⇒ 0)
        const lift = _gFootPos.dot(_gUp) - centerH; // foot height over the centre ground
        planted = 1 - Math.max(0, Math.min(1, (lift - 0.35) / 0.25)); // 1 planted (idle ankle ~0.15–0.35), 0 mid-swing (>~0.6)
      }
      this.ikSlope[i] += (slope - this.ikSlope[i]) * ikE;
      this.ikFootW[i] += (planted - this.ikFootW[i]) * ikE;
      const w = this.ikFootW[i] * this.groundIkK;
      targets[i].copy(_gFootPos).addScaledVector(_gUp, this.ikSlope[i] * w); // absolute ground target (world)
      pelvisTarget = Math.max(pelvisTarget, -this.ikSlope[i] * this.ikFootW[i]); // the lowest planted foot
    }
    this.ikPelvis += (0.5 * pelvisTarget * this.groundIkK - this.ikPelvis) * ease;
    this.body.position.y -= this.ikPelvis; // lower the hips (fresh each frame; the boost/idle pass set y first)
    this.group.updateMatrixWorld(true);

    // PASS 2: 2-bone IK each planted leg to its fixed ground target, then roll the sole to the normal.
    // The held pole is stored AVATAR-LOCAL (see ikPole) so body turns carry it — rotate it into world
    // for the solve, then store the solver's updated pole back in local space.
    this.group.getWorldQuaternion(_gGroupQ);
    _gGroupQInv.copy(_gGroupQ).invert();
    for (let i = 0; i < 2; i++) {
      const w = this.ikFootW[i] * this.groundIkK;
      if (w < 0.01) continue;
      _gPoleWorld.copy(this.ikPole[i]);
      if (_gPoleWorld.lengthSq() > 1e-6) _gPoleWorld.applyQuaternion(_gGroupQ);
      this.ikLeg(IK_LEG_NAMES[i][0], IK_LEG_NAMES[i][1], IK_LEG_NAMES[i][2], targets[i], _gFwd, w, true, _gPoleWorld, ikE);
      this.ikPole[i].copy(_gPoleWorld);
      if (this.ikPole[i].lengthSq() > 1e-6) this.ikPole[i].applyQuaternion(_gGroupQInv);
      this.alignFootToNormal(IK_LEG_NAMES[i][2], this.ikSoleLocal![i], _gFootTarget, w);
    }
  }


  /** Apply an authored humanoid contact after sampling a base clip. Used by
   * Animation Lab and interaction playback; target is render-world space while
   * the optional bend pole is avatar-local. */
  applyHumanoidIk(
    effector: string,
    target: THREE.Vector3,
    weight: number,
    pole?: readonly [number, number, number],
  ): boolean {
    const chains: Record<string, readonly [string, string, string, readonly [number, number, number]]> = {
      hand_l: ["LeftArm", "LeftForeArm", "LeftHand", [-0.7, -0.7, -0.2]],
      hand_r: ["RightArm", "RightForeArm", "RightHand", [0.7, -0.7, -0.2]],
      foot_l: ["LeftUpLeg", "LeftLeg", "LeftFoot", [-0.35, 0, -1]],
      foot_r: ["RightUpLeg", "RightLeg", "RightFoot", [0.35, 0, -1]],
    };
    const chain = chains[effector];
    if (!chain) return false;
    const bend = pole ?? chain[3];
    _mountPole.set(bend[0], bend[1], bend[2]).applyQuaternion(this.group.quaternion).normalize();
    this.group.updateMatrixWorld(true);
    this.ikLeg(chain[0], chain[1], chain[2], target, _mountPole, THREE.MathUtils.clamp(weight, 0, 1), false);
    this.group.updateMatrixWorld(true);
    return true;
  }

  /** The ANATOMICAL knee-bend direction for a thigh bone, in that bone's own local space — i.e. which
   *  way this leg's knee is allowed to point, whatever the hip is doing. Calibrated once from the BIND
   *  pose: the rig binds standing and facing −Z, so body-forward at bind IS the anterior of the leg;
   *  baking it into the bone's frame makes it ride the live thigh rotation (hip yaw, flexion, a seated
   *  leg folded to horizontal) instead of standing still in world space.
   *
   *  Returns null for any chain that is not one of the two LEGS (IK_LEG_NAMES) — ikLeg is shared with
   *  the arms, and an elbow's anterior is the opposite hemisphere. Rebuilt when the rig is (setBodyModel
   *  clears the cache; the bind inverses are retuned in place by applyBodyRig). */
  private legAnteriorFor(thigh: THREE.Bone): THREE.Vector3 | null {
    if (!this.legAnterior) {
      this.legAnterior = new Map();
      for (const [thighN] of IK_LEG_NAMES) {
        const bone = this.boneByName.get(thighN);
        const idx = bone ? this.skeleton.bones.indexOf(bone) : -1;
        if (!bone || idx < 0) continue;
        _gBindMat.copy(this.skeleton.boneInverses[idx]).invert(); // bind world matrix of the thigh
        _gBindQ.setFromRotationMatrix(_gBindMat).invert();        // world → bone, at bind
        this.legAnterior.set(bone, new THREE.Vector3(0, 0, -1).applyQuaternion(_gBindQ).normalize());
      }
    }
    return this.legAnterior.get(thigh) ?? null;
  }

  /** Analytic 2-bone IK: bend the thigh→calf→foot chain so the ANKLE reaches `target` (world), with
   *  the knee hinging toward `pole` (world forward). Axis-agnostic: it rotates each bone by the world
   *  delta mapping its current joint→child vector onto the solved one (via rotateBoneWorld), so it's
   *  independent of the bone's local-axis layout. Blended toward the goal by `w` (0 keeps the clip pose). */
  private ikLeg(thighN: string, calfN: string, footN: string, target: THREE.Vector3, pole: THREE.Vector3, w: number, clipPole = true, poleState?: THREE.Vector3, poleEase = 1): void {
    const thigh = this.boneByName.get(thighN), calf = this.boneByName.get(calfN), foot = this.boneByName.get(footN);
    if (!thigh || !calf || !foot) return;
    foot.getWorldQuaternion(_gFootQ0); // remember the foot's clip orientation — the leg bend must NOT pitch it
    thigh.getWorldPosition(_gHip);
    calf.getWorldPosition(_gKnee);
    foot.getWorldPosition(_gAnkle);
    const L1 = _gHip.distanceTo(_gKnee);
    const L2 = _gKnee.distanceTo(_gAnkle);
    if (L1 < 1e-4 || L2 < 1e-4) return;
    _gGoal.copy(_gAnkle).lerp(target, w); // ease from the clip ankle toward the ground target
    _gA.copy(_gGoal).sub(_gHip);
    let D = _gA.length();
    if (D < 1e-4) return;
    // Cap extension below full straight (0.985, not 0.999): a fully straight leg makes the knee offset h→0,
    // so the bend-plane pole becomes undefined and flickers. Keeping a slight bend leaves a stable pole.
    const maxR = (L1 + L2) * 0.985, minR = Math.abs(L1 - L2) + 1e-3;
    if (D > maxR) D = maxR;
    else if (D < minR) D = minR;
    _gA.normalize();
    _gGoal.copy(_gHip).addScaledVector(_gA, D); // clamped, reachable goal along hip→target
    // knee via law of cosines; offset into the bend plane defined by the forward pole.
    const d1 = (L1 * L1 - L2 * L2 + D * D) / (2 * D);
    const h = Math.sqrt(Math.max(0, L1 * L1 - d1 * d1));
    // POLE = which way the knee points. From the CLIP's own bend (knee offset ⊥ hip→goal) so aim/strafe
    // poses keep their natural bend (a fixed body-forward pole flipped the knee backwards there). But that
    // ⊥ component goes TINY + noisy near a straight leg / on a tilted (sloped) goal — and its sign can flip
    // frame-to-frame, which flicked the knee OUTWARD. So: only TRUST the clip-pole when the knee is clearly
    // bent (the ⊥ part is a real fraction of the offset); otherwise HOLD the smoothed pole (poleState) and
    // ease it only from trustworthy frames. `pole` (body forward) is the cold-start fallback.
    let trust = false;
    if (clipPole) {
      _gPole.copy(_gKnee).sub(_gHip);
      const offLen2 = _gPole.lengthSq();
      _gPole.addScaledVector(_gA, -_gPole.dot(_gA)); // ⊥ hip→goal = clip knee-bend direction
      trust = offLen2 > 1e-8 && _gPole.lengthSq() / offLen2 > 0.03 && _gPole.lengthSq() > 1e-6; // ⊥ ≳17% of offset (knee bent ≳10°)
    }
    if (trust) _gPole.normalize();
    if (poleState) {
      if (trust) {
        if (poleState.lengthSq() < 1e-6) poleState.copy(_gPole);                              // seed
        else if (poleState.dot(_gPole) > 0) poleState.lerp(_gPole, poleEase).normalize();     // ease (same side)
        // dot ≤ 0 (would cross through zero): hold — transient near-straight noise, not a real reversal
      }
      if (poleState.lengthSq() > 1e-6) {
        _gPole.copy(poleState); // smoothed, held through the degenerate
      } else {
        _gPole.copy(pole).addScaledVector(_gA, -pole.dot(_gA)); // never seeded yet → body-forward this frame
        if (_gPole.lengthSq() < 1e-6) return;
        _gPole.normalize();
      }
    } else {
      if (!trust) { _gPole.copy(pole).addScaledVector(_gA, -pole.dot(_gA)); if (_gPole.lengthSq() < 1e-6) return; }
      _gPole.normalize();
    }
    // ANTI-HYPEREXTENSION: a knee NEVER points backward. Clamp the final pole into the forward
    // hemisphere of a reference direction, projected ⊥ hip→goal to get an in-plane reference.
    //
    // ⚠ THE REFERENCE IS THE THIGH'S OWN ANTERIOR AXIS, NOT THE CALLER'S POLE (2026-08-10). Using
    // `pole` made the guard circular: it only ever enforced whatever hemisphere the caller asked for,
    // so a caller that hands in a BACKWARD pole gets a backward knee with the guard's blessing — which
    // is exactly what the mount-swing pole (+Z ⇒ behind the body) did, measurably (knee pole −0.71
    // against body-forward mid-swing, probe test/_kneedir.mjs). The bind-derived anterior axis rides
    // the live thigh rotation instead, so it tracks hip yaw AND flexion: a wide crouch stance or a
    // seated rider keeps its natural knee direction (both measure ≥0.78 on this axis) while a pole
    // that has drifted behind the leg is pulled back in front of it. Legs only — `legAnteriorFor`
    // returns null for the arm chains, which bend the other way and keep the caller-pole guard.
    let kneeFloor = KNEE_FWD_MIN;
    const anterior = this.legAnteriorFor(thigh);
    if (anterior) {
      thigh.getWorldQuaternion(_gThighQ); // the CLIP thigh pose — read before the solve rotates it
      _gAnterior.copy(anterior).applyQuaternion(_gThighQ);
      _gFwdPerp.copy(_gAnterior).addScaledVector(_gA, -_gAnterior.dot(_gA));
      kneeFloor = KNEE_ANAT_MIN;
    } else {
      _gFwdPerp.copy(pole).addScaledVector(_gA, -pole.dot(_gA));
    }
    if (_gFwdPerp.lengthSq() > 1e-6) {
      _gFwdPerp.normalize();
      const fwdComp = _gPole.dot(_gFwdPerp);
      if (fwdComp < kneeFloor) {
        _gPole.addScaledVector(_gFwdPerp, kneeFloor - fwdComp).normalize();
        // HEAL the held state: the smoothed pole had drifted backward (the post-flight landing case —
        // IK is frozen mid-air so a stale/back-bent pole carries into the touchdown blend). Clamping
        // only _gPole fixes THIS frame's render but leaves poleState behind, so the knee stays bent
        // backward until running re-trains it. Write the clamped, forward pole back so it self-corrects
        // in one frame instead.
        if (poleState && poleState.lengthSq() > 1e-6) poleState.copy(_gPole);
      }
    }
    _gKneeWant.copy(_gHip).addScaledVector(_gA, d1).addScaledVector(_gPole, h);
    // aim the thigh: hip→knee onto hip→kneeWant
    _gCurDir.copy(_gKnee).sub(_gHip);
    if (_gCurDir.lengthSq() < 1e-8) return;
    _gCurDir.normalize();
    _gWantDir.copy(_gKneeWant).sub(_gHip).normalize();
    _gDelta.setFromUnitVectors(_gCurDir, _gWantDir);
    this.rotateBoneWorld(thighN, _gDelta);
    // aim the calf: knee→ankle onto knee→goal (positions refreshed through the new thigh pose)
    calf.getWorldPosition(_gKnee);
    foot.getWorldPosition(_gAnkle);
    _gCurDir.copy(_gAnkle).sub(_gKnee);
    if (_gCurDir.lengthSq() < 1e-8) return;
    _gCurDir.normalize();
    _gWantDir.copy(_gGoal).sub(_gKnee);
    if (_gWantDir.lengthSq() < 1e-8) return;
    _gWantDir.normalize();
    _gDelta.setFromUnitVectors(_gCurDir, _gWantDir);
    this.rotateBoneWorld(calfN, _gDelta);
    // Restore the foot's CLIP world orientation: the thigh/calf bend just reoriented the foot (its
    // child), which pitched the toes (visible as "toes up" even on flat ground). The leg solve owns
    // only the ankle POSITION; the foot's angle comes from the clip + alignFootToNormal.
    foot.parent!.getWorldQuaternion(_gFootPar).invert();
    foot.quaternion.copy(_gFootPar).multiply(_gFootQ0);
  }

  /** FLATTEN the foot's sole onto the ground: rotate the foot's calibrated top-of-foot axis (`soleLocal`,
   *  world-transformed by the foot's current orientation) onto `target` (the desired up — radial on flat,
   *  tilted to the slope). This removes the clip's plantarflex so the sole sits flat, AND conforms to
   *  slopes (because `target` is pre-tilted). Clamped to IK_FOOT_TILT_MAX, blended by `w`. */
  private alignFootToNormal(footN: string, soleLocal: THREE.Vector3, target: THREE.Vector3, w: number): void {
    const foot = this.boneByName.get(footN);
    if (!foot) return;
    foot.getWorldQuaternion(_gFootQ0);
    _gFootUp.copy(soleLocal).applyQuaternion(_gFootQ0).normalize(); // current top-of-foot (world)
    _gB.copy(target).normalize();
    const dot = Math.max(-1, Math.min(1, _gFootUp.dot(_gB)));
    let ang = Math.acos(dot);
    if (ang < 1e-3) return;
    if (ang > IK_FOOT_TILT_MAX) ang = IK_FOOT_TILT_MAX;
    _gAxis.crossVectors(_gFootUp, _gB);
    if (_gAxis.lengthSq() < 1e-8) return;
    _gAxis.normalize();
    _gDelta.setFromAxisAngle(_gAxis, ang * w);
    this.rotateBoneWorld(footN, _gDelta);
  }

  /** Anchor the LEFT hand to the held weapon's FOREGRIP — it otherwise floats above the rifle (the
   *  clip's support hand reads wrong after retarget). Builds the foregrip world point on the gun, then
   *  2-bone IKs the left arm (upperarm_l→lowerarm_l→hand_l) to it with the elbow forced DOWN. Eased by
   *  gun-out (stanceK); skipped during reload (it owns the left hand), melee, and glide. Runs after the
   *  aim solve so the gun pose is final. */
  /**
   * Walk the FIRING hand (and with it the whole rifle) inboard until the support hand can reach
   * the foregrip with a bent elbow. See GUN_SEAT_MAX_CARRY for why this exists.
   *
   * The correction is exactly the reach DEFICIT and nothing more, so it is identically zero
   * whenever the gun is already in reach — which is most of ADS. The shift is a pure translation
   * of the hand, so the barrel keeps the direction the aim solve gave it; only its position moves,
   * and reticle convergence (main.ts tryFootFire) already absorbs a few centimetres of muzzle
   * offset. ikLeg restores the hand's world orientation, so the trigger grip is untouched.
   */
  private seatWeaponInSupportReach(dt: number, w: number): void {
    const handR = this.boneByName.get("RightHand");
    const A = this.boneByName.get("LeftArm"), B = this.boneByName.get("LeftForeArm"), C = this.boneByName.get("LeftHand");
    if (!handR || !A || !B || !C) return;
    this.weapon!.updateWorldMatrix(true, false);
    A.getWorldPosition(_wShoulder);
    const armLen = _wShoulder.distanceTo(B.getWorldPosition(_wAxis)) + _wAxis.distanceTo(C.getWorldPosition(_wPerp));
    if (armLen < 1e-4) return;
    _gsGrip.copy(this.gripLTarget);
    this.weapon!.localToWorld(_gsGrip);
    const reach = armLen * GRIP_REACH_MAX;
    const deficit = _gsGrip.distanceTo(_wShoulder) - reach;
    const underhandStance = this.weaponStance === "underhand";
    // Cap by stance: the carry may travel a long way (the gun genuinely is held out there), ADS
    // barely at all (a shouldered rifle that wandered would read far worse than a bob).
    const shoulderStance = this.weaponStance === "shoulder";
    const cap = underhandStance ? 0.36 : shoulderStance
      ? GUN_SEAT_MAX_CARRY + (GUN_SEAT_MAX_ADS - GUN_SEAT_MAX_CARRY) * this.aimK
      : GUN_SEAT_MAX_CARRY;
    // Move the hand along grip → support shoulder: the shortest path out of the deficit, and the
    // one that reads as bringing the rifle ACROSS the chest rather than dropping or raising it.
    _gsDir.copy(_wShoulder).sub(_gsGrip);
    let want = deficit;
    if (underhandStance) {
      // Preserve the pelvis-relative height. Solve the reach intersection along
      // the horizontal/inboard component only; the generic shortest path points
      // upward at the shoulder and visibly turns this back into a rifle hold.
      _aoUp.set(0, 1, 0).applyQuaternion(this.group.quaternion);
      _gsDir.addScaledVector(_aoUp, -_gsDir.dot(_aoUp));
      // The weapon belongs beside the firing hip, never in the sagittal centre
      // of the body. Remove cross-body translation from the reach solve; it may
      // pull the gun closer fore/aft, but cannot steal its right-side offset.
      _aoFwd.set(0, 0, -1).applyQuaternion(this.group.quaternion);
      _aoRight.crossVectors(_aoFwd, _aoUp).normalize();
      _gsDir.addScaledVector(_aoRight, -_gsDir.dot(_aoRight));
      if (_gsDir.lengthSq() > 1e-8) {
        _gsDir.normalize();
        _wAxis.copy(_gsGrip).sub(_wShoulder);
        const bq = 2 * _wAxis.dot(_gsDir);
        const cq = _wAxis.lengthSq() - reach * reach;
        const disc = bq * bq - 4 * cq;
        want = disc >= 0 ? (-bq - Math.sqrt(disc)) / 2 : -bq / 2;
      }
    }
    want = THREE.MathUtils.clamp(want, 0, cap);
    this.gunSeatS += (want - this.gunSeatS) * (1 - Math.exp(-GUN_SEAT_HZ * dt));
    const lift = shoulderStance ? ADS_GUN_LIFT * this.aimK * w : 0;
    // ⚠ Do NOT early-out on the seat distance alone: the ADS OFFSET below has to apply even when
    // the gun is already in reach, which is most of ADS.
    if (this.gunSeatS < 1e-4 && this.aimK * w < 1e-3) return;
    if (_gsDir.lengthSq() < 1e-8) _gsDir.set(0, 0, 0); else _gsDir.normalize();
    handR.getWorldPosition(_gsTarget);
    const shR = this.boneByName.get("RightArm"), elR = this.boneByName.get("RightForeArm");
    if (!shR || !elR) return;
    // Right-arm length, for the reach clamp below (LEFT-arm scratch is free here —
    // the deficit was already measured above).
    shR.getWorldPosition(_wShoulder);
    const armR = _wShoulder.distanceTo(elR.getWorldPosition(_wAxis)) + _wAxis.distanceTo(_gsTarget);
    // POLE = the firing arm's CURRENT bend plane, still — but measured by ikLeg ITSELF
    // (clipPole=true) under its trust gate, easing into a persistent state
    // (gunSeatPole) whose same-side rule cannot cross through zero. The raw per-frame
    // elbow-perp used here before had a 1 mm degeneracy floor; once the GUN STEADY
    // made this solve do real work every walking frame, near-straight poses flipped
    // the raw perp's sign frame-to-frame = the reported ELBOW INVERSIONS
    // (user 2026-08-17). Body-down is ONLY the cold-start fallback + hemisphere
    // reference — never the live pole, honoring the 2026-08-03 re-planing lesson.
    _gsPole.set(0, -1, 0).applyQuaternion(this.group.quaternion);
    // ADS-MOVE GUN STEADY (see GUN_STEADY_MAX): pull the firing hand toward its
    // low-passed group-local position while aiming on the move. Group-local is
    // the right frame — the group translates smoothly (charVisPos), so what
    // remains in it is exactly the gait bob. Runs BEFORE the seat/ADS offsets
    // (all plain translations through the one ikLeg below).
    const steadyK = shoulderStance ? this.aimK * Math.min(1, this.moveK * 1.6) * w : 0;
    if (steadyK > 0.02) {
      _gsLocal.copy(_gsTarget);
      this.group.worldToLocal(_gsLocal);
      if (!this.gunSteadyLocal) this.gunSteadyLocal = _gsLocal.clone();
      // Track fast when far off the average — that is a real aim/stance move
      // (pitch flick, direction change), not bob; fighting it would read as lag.
      const err = this.gunSteadyLocal.distanceTo(_gsLocal);
      const boost = Math.min(1, Math.max(0, (err - 0.05) / 0.08)) * 12;
      this.gunSteadyLocal.lerp(_gsLocal, 1 - Math.exp(-(GUN_STEADY_HZ + boost) * dt));
      _gsSteady.copy(this.gunSteadyLocal);
      this.group.localToWorld(_gsSteady).sub(_gsTarget); // calm − current, world
      const mag = _gsSteady.length();
      if (mag > GUN_STEADY_MAX) _gsSteady.multiplyScalar(GUN_STEADY_MAX / mag);
      _gsTarget.addScaledVector(_gsSteady, steadyK);
    } else {
      this.gunSteadyLocal = null; // re-seed on next engage: zero correction, ramps in
    }
    _gsTarget.addScaledVector(_gsDir, this.gunSeatS * w);
    // …and seat the whole gun OUT / FORWARD / up while ADS so the stock clears the back.
    // Body axes: fwd = group −Z, up = group +Y, right = fwd × up (verified +X = the shooter's
    // right for TRANSLATION — note the rotation-sign warning elsewhere in this file does not
    // apply to a plain offset).
    _aoFwd.set(0, 0, -1).applyQuaternion(this.group.quaternion);
    _aoUp.set(0, 1, 0).applyQuaternion(this.group.quaternion);
    _aoRight.crossVectors(_aoFwd, _aoUp).normalize();
    const adsK = shoulderStance ? this.aimK * w : 0;
    _gsTarget.addScaledVector(_aoUp, lift);
    _gsTarget.addScaledVector(_aoRight, ADS_GUN_OUT * adsK);
    _gsTarget.addScaledVector(_aoFwd, ADS_GUN_FWD * adsK);
    // REACH CLAMP: the composed walks (steady + seat + ADS offsets) must never
    // straighten the arm — a straight arm has no bend plane for ANY pole logic to
    // hold onto, which is the raw material of every inversion.
    _gsSteady.copy(_gsTarget).sub(_wShoulder);
    const dR = _gsSteady.length();
    const maxR = armR * (underhandStance ? 0.93 : 0.96);
    if (dR > maxR) _gsTarget.copy(_wShoulder).addScaledVector(_gsSteady, maxR / dR);
    if (underhandStance) {
      // This is a SECOND firing-arm solve, after applyStanceHandPlacement.
      // Reusing clipPole here silently replaced the underhand body's outward
      // pole and let the elbow fold through the ribs as the locomotion clip
      // approached full extension. Rebuild the same body-right pole against
      // this pass's final shoulder→hand target.
      _stanceAxis.copy(_gsTarget).sub(_wShoulder);
      if (_stanceAxis.lengthSq() > 1e-8) _stanceAxis.normalize();
      _gsPole.copy(_aoRight).multiplyScalar(0.45).addScaledVector(_aoFwd, -0.85);
      _gsPole.addScaledVector(_stanceAxis, -_gsPole.dot(_stanceAxis));
      if (_gsPole.lengthSq() < 1e-6) _gsPole.copy(_aoRight);
      _gsPole.normalize();
    }
    this.ikLeg(
      "RightArm", "RightForeArm", "RightHand", _gsTarget, _gsPole, w,
      !underhandStance, this.gunSeatPole, 1 - Math.exp(-10 * dt),
    );
    this.weapon!.updateWorldMatrix(true, false);
  }

  private applyWeaponGrip(dt: number): void {
    if (!this.weapon || this.farLOD) return;
    // A body CONSTRUCT has no foregrip to pin the support hand to: the cannon is the arm, and
    // the blade rides the other one. The pistol clips already pose both arms — leave them be.
    if (this.construct || !this.hasSupportGrip()) return;
    // Release the support hand while AIRBORNE — jumping / super-jump / takeoff / launch put the arms in
    // the leap or jetpack pose, and anchoring the left hand to the gun fights that. (1−airK) fades it
    // out as the feet leave the ground and back in on landing.
    // GUN-ONLY: the support hand pins to a GUN's foregrip. The sword shares stanceK (armed locomotion)
    // but must NOT grab the forearm blade — gate on gunOut so a drawn sword leaves the left arm free.
    //
    // THE RIFLE TARGET IS THE CLIP'S OWN WRIST POINT (_wGripRifle, measured), so at the neutral
    // pose this IK is a near-no-op — it ENFORCES the authored hand-on-gun relationship after the
    // aim layers move the chain, exactly the Lyra contract (IK corrects centimeters; it never
    // poses the arm from scratch). The earlier attempt targeted an invented foregrip point ~10 cm
    // off the clip pose, which posed the arm — swinging the upper-arm roll ~95° and ballooning
    // the deltoid. That, plus the missing twist restore below, is why it was disabled.
    // ⚠⚠ NO AIRBORNE RELEASE FOR A GUN. The support hand used to fade out with `airK` on an unaimed
    // jump (the jump clips threw the arms out and the grip fought them). Two problems: the FADE
    // itself is what made the left elbow snap back and forth every jump — as the weight crosses the
    // solver's engage threshold the pole history resets and the elbow plane flips — and the reason
    // for it is gone anyway, because an armed jump now holds the stance's own `r_jump` upper body
    // rather than the unarmed jump's flung arms. A rifle is carried in two hands in the air, so the
    // grip just stays engaged and the elbow keeps one continuous plane.
    const w = (this.gunOut ? this.stanceK : 0)
      * (1 - this.glideK * (1 - this.aimK))       // only a GLIDE frees the hands, and not while aiming
      * (1 - this.stowGestureK);                  // …and the stow/draw gesture carries the gun itself
    if (w < 0.02) return;
    // The rifle clips leave the trigger hand open because their index finger is
    // authored for a conventional trigger. On the Tempest the rear control is
    // a broad top handle, so every digit must visibly close around it.
    if (this.weaponStance === "underhand") this.curlGripHand("r", w, true, 1.45);
    this.weapon.updateWorldMatrix(true, false);
    const rifle = this.hasSupportGrip();
    // A cross-body top handle naturally uses more of the arm than a shouldered
    // foregrip. Let that authored underhand pose approach full extension while
    // retaining a small bend; the generic 93% rifle cap leaves the hand visibly
    // hovering above the handle even though the elbow plane is now safe.
    const supportReachMax = this.weaponStance === "underhand" ? 0.98 : GRIP_REACH_MAX;
    // WRIST RELIEF (firing hand) — the ROOT CAUSE of the "right forearm is VERY thin" report.
    // The r_* clips author hand_r with ~55° of roll about the forearm axis (their grip frame),
    // and this body's broad auto-skinned weights collapse the whole forearm under that much
    // twist — no twist-bone fraction tuning could bridge it (A/B'd: fractions and even
    // twist-off barely moved it; unrolling the wrist visibly restores the volume). Unroll the
    // wrist toward neutral and counter-rotate the WEAPON (its child) so the gun's world
    // ORIENTATION stands exactly (its position shifts ~2 cm — the local mount offset rotates
    // with the hand — which is why this runs FIRST: the support hand must solve against the
    // FINAL gun placement, not a pre-relief one).
    // FEMALE SEAT (user calls 2026-08-02): her narrower retuned frame carries the gun toward the
    // sternum. Walk the RIGHT ARM outward/down via IK so the TRIGGER HAND STAYS ON the grip —
    // a first cut slid the weapon in the palm instead, which read as "trigger hand too high";
    // the gun (a child of hand_r) and its bore/muzzle follow the hand, so every solve after
    // this measures the moved gun. ikLeg restores the hand's world orientation itself.
    if (rifle && this.weaponStance !== "underhand" && w > 0.02 && this.bodyModelId === "female") {
      const handR0 = this.boneByName.get("RightHand");
      if (handR0) {
        this.group.updateMatrixWorld(true);
        _aoFwd.set(0, 0, -1).applyQuaternion(this.group.quaternion);
        _aoUp.set(0, 1, 0).applyQuaternion(this.group.quaternion);
        _aoRight.crossVectors(_aoFwd, _aoUp).normalize();
        handR0.getWorldPosition(_fgPos)
          .addScaledVector(_aoRight, FEM_GUN_RIGHT)
          .addScaledVector(_aoUp, -FEM_GUN_DOWN);
        _wPole.set(0, -1, 0).applyQuaternion(this.group.quaternion);
        this.ikLeg("RightArm", "RightForeArm", "RightHand", _fgPos, _wPole, w, false);
        this.weapon.updateWorldMatrix(true, false);
      }
    }
    if (rifle && this.weaponStance !== "underhand" && w > 0.02) {
      const handR = this.boneByName.get("RightHand");
      const foreR = this.boneByName.get("RightForeArm");
      if (handR?.parent && foreR) {
        // ADAPTIVE: measure the wrist's ACTUAL bind-relative roll and unroll to the target. A
        // fixed 0.6 rad relief was tuned at ADS (raw roll ~−55°) — but the HIP carry authors
        // ~−122°, leaving −88° at the wrist and a 62° skinning jump across the last third of
        // the forearm: the "twisted ribbon" forearm (user screenshot, 2026-08-01). Measured
        // per-frame, every stance lands at the same healthy wrist angle.
        const bind = this.twistBind.get("hand_r");
        if (bind) {
          _wRelQ.copy(bind).invert().multiply(handR.quaternion);
          let roll = 2 * Math.atan2(_wRelQ.x, _wRelQ.w);
          if (roll > Math.PI) roll -= 2 * Math.PI;
          else if (roll < -Math.PI) roll += 2 * Math.PI;
          const relief = THREE.MathUtils.clamp(RIFLE_WRIST_ROLL_TARGET - roll, -1.7, 1.7) * w;
          if (Math.abs(relief) > 1e-3) {
            this.group.updateMatrixWorld(true);
            this.weapon.getWorldQuaternion(_wRelQ); // weapon world, to be restored
            // POST-multiply a local-X twist: this changes the measured bind-relative roll by
            // exactly `relief`. (Rotating about the world forearm LINE was tried first and
            // under-delivered at large angles — the hand's twist axis diverges from the forearm
            // line by the wrist's bend, so 87° of commanded relief moved the roll only 47°.)
            _wParQ.set(Math.sin(relief / 2), 0, 0, Math.cos(relief / 2));
            handR.quaternion.multiply(_wParQ);
            this.group.updateMatrixWorld(true);
            handR.getWorldQuaternion(_wParQ).invert();
            this.weapon.quaternion.copy(_wParQ).multiply(_wRelQ); // gun world orientation unchanged
            this.weapon.updateWorldMatrix(true, false);
          }
        }
      }
    }
    // Bring the gun within the support arm's reach BEFORE choosing a target, so the target below
    // is one the arm can actually hold with a bent elbow. See seatWeaponInSupportReach.
    // Close only the remaining reach deficit after the stance-specific firing
    // hand placement. Heavy weapons still need this small whole-weapon shift:
    // without it the support target is clamped off the authored handle. Their
    // characteristic vertical/fore-aft placement comes from the stance pass;
    // this solve only walks toward the support shoulder as far as necessary.
    // The raised, pelvis-height underhand target now keeps its authored top
    // handle inside the support arm's soft-reach envelope. Running the rifle
    // seat on it would perform a second right-arm solve from the live animated
    // shoulder and reintroduce several centimetres of vertical gait motion.
    if (rifle && this.weaponStance !== "underhand") {
      this.seatWeaponInSupportReach(dt, w);
    }
    // The support hand goes to the REAL foregrip in every stance now. It used to fall back to
    // `_wGripRifleCarry` — the r_ clips' own left wrist, which floats ~14 cm off the weapon —
    // because the foregrip was out of reach at the hip. The seat pass above removes that gap, so
    // there is no longer anything to fall back to.
    _wGrip.copy(rifle ? this.gripLTarget : _wGripPistol);
    // Both bodies use the authored contact. Shorter arms are handled by the
    // reach/rail solves, not by moving the hand above the actual grip.
    if (this.reloadP !== null) {
      // While reloading, slide the support hand from the foregrip down to the MAG WELL and back
      // (trapezoid: out by 18%, held at the mag, back over the last 18%) — the mag-change gesture.
      const down = Math.min(1, this.reloadP / 0.18, (1 - this.reloadP) / 0.18);
      _wGrip.lerp(rifle ? _wMagRifle : _wMagPistol, down);
    }
    this.weapon.localToWorld(_wGrip); // → world (rides the gun, which is pitched down by applyReloadPose)
    if (this.weaponStance === "underhand") {
      // Controlled BODY GIVE: rotate the left clavicle a few degrees toward the
      // cross-body handle before solving the arm. This moves the arm root with
      // the shoulder girdle instead of stretching the elbow to its singularity.
      // Heavy-gun seating deliberately puts this handle across and outside the
      // torso. Allow the shoulder girdle enough give to follow it; otherwise
      // the reach clamp pulls the forearm back through the abdomen at carry.
      this.reachClavicleToward("LeftShoulder", "LeftArm", _wGrip, 0.26, w);
      this.weapon.updateWorldMatrix(true, false);
      this.applyUnderhandArmStretch("Left", _wGrip, supportReachMax, dt, w);
    }
    // REACH SAFETY NET (2026-08-02): release the grip only when the target is WELL beyond the
    // arm (>115% reach — a broken target, e.g. a future long foregrip on a short rig), so the
    // solver never locks a straight-arm strut against an impossible point. Deliberately far
    // outside the normal ADS band (0.91–1.1 across the run bob): a fade that overlaps the
    // working range OSCILLATES with the bob and shakes the whole arm (measured 104 mm/frame).
    let wL = w;
    {
      const A = this.boneByName.get("LeftArm"), B = this.boneByName.get("LeftForeArm"), C = this.boneByName.get("LeftHand");
      if (A && B && C) {
        A.getWorldPosition(_wShoulder);
        const armLen = _wShoulder.distanceTo(B.getWorldPosition(_wAxis)) + _wAxis.distanceTo(C.getWorldPosition(_wPerp));
        if (armLen > 1e-6) {
          const need = _wShoulder.distanceTo(_wGrip) / armLen;
          const t = THREE.MathUtils.clamp((need - 1.15) / 0.15, 0, 1);
          wL = w * (1 - t * t * (3 - 2 * t));
        }
      }
    }
    // The left arm used to be released entirely at the hip carry (`wL *= aimK`) — the only way to
    // avoid the strut-and-flip "spaz" while the foregrip sat beyond the arm's reach. It is what
    // left the support hand floating off the barrel in every unaimed stance (user report
    // 2026-08-03). seatWeaponInSupportReach removes the reach deficit that forced it, so the
    // support hand is now held to the gun in EVERY stance and the elbow keeps its bend.
    // SLIDE ALONG THE GUARD (2026-08-02): while ADS-moving the gun bobs with the firing arm and
    // the foregrip point swings past the left arm's reach (measured need 0.84→1.19× across the
    // run cycle). The generic reach clamp below pulls the hand OFF the gun toward the shoulder
    // (up to 15 cm off the barrel axis — the "hand disconnects" report) and leaves a straight
    // strut whose elbow plane flips with the bob. Instead, slide the target along the weapon's
    // +Z (down the handguard toward the receiver) just enough to stay reachable — the hand
    // glides on the gun like a real support hand, and the elbow keeps a bend. Solved exactly:
    // |t + s·ẑ − sh|² = R² → smallest s ≥ 0; capped before the trigger hand.
    if (rifle && this.weaponStance !== "underhand") {
      const A = this.boneByName.get("LeftArm"), B = this.boneByName.get("LeftForeArm"), C = this.boneByName.get("LeftHand");
      if (A && B && C) {
        A.getWorldPosition(_wShoulder);
        const armLen = _wShoulder.distanceTo(B.getWorldPosition(_wAxis)) + _wAxis.distanceTo(C.getWorldPosition(_wPerp));
        const R = armLen * supportReachMax;
        _wAxis.copy(_wGrip).sub(_wShoulder);
        const c = _wAxis.lengthSq() - R * R;
        let s = 0;
        this.weapon.getWorldQuaternion(_wPoleQ);
        // Slide DIRECTION: along the grip → under-receiver path (weapon frame), in world.
        const slideTo = this.holdDef.support?.slideTo ?? GRIP_SLIDE_TO;
        _wPerp.fromArray(slideTo as [number, number, number]).sub(this.gripLTarget).normalize().applyQuaternion(_wPoleQ);
        if (c > 0) {
          const bq = 2 * _wPerp.dot(_wAxis);
          const disc = bq * bq - 4 * c;
          // No real root = even the full slide can't reach (extreme pose): go to the closest
          // approach along the guard and let the clamp below cover the rest.
          s = THREE.MathUtils.clamp(disc > 0 ? (-bq - Math.sqrt(disc)) / 2 : -bq / 2, 0, GRIP_SLIDE_MAX);
        }
        // LIGHT SMOOTHING both ways — just enough to kill per-frame jitter. A slow return was
        // tried and read WORSE: the hand lingered at the receiver (where there is no guard to
        // cup) for most of each stride instead of riding back onto the handguard.
        this.gripSlideS += (s - this.gripSlideS) * (1 - Math.exp(-GRIP_SLIDE_HZ * dt));
        if (this.gripSlideS > 1e-4) _wGrip.addScaledVector(_wPerp, this.gripSlideS);
      }
    } else if (this.weaponStance === "underhand") {
      // A rotary cannon has one discrete carry handle, not a rifle-length rail.
      // Its authored socket is reachable after the stance seat, so keep the
      // palm on that handle instead of sliding it through empty receiver space.
      this.gripSlideS = 0;
    }
    // CLAMP THE TARGET TO ARM REACH. ikLeg caps extension internally at 0.985, but a capped arm
    // reads as a locked straight strut and the SHOULDER gets hauled forward to cover the shortfall
    // — the "left shoulder is very angled and jarring" report. Clamping the TARGET instead leaves
    // a real elbow bend and lets the shoulder sit where the clip put it. Also what keeps long/
    // modular foregrips safe on both body rigs, whose arm lengths differ.
    this.clampToReach("LeftArm", "LeftForeArm", "LeftHand", _wGrip, supportReachMax);
    // ELBOW POLE in the WEAPON frame — "under the barrel" stays meaningful at any elevation,
    // where a fixed avatar-down pole becomes PARALLEL to the arm axis when aiming straight up:
    // the ⊥ projection collapses and the elbow inverts (folds inward) — the reported bug. When
    // the projection degenerates, cross-fade to a perpendicular-by-construction pole (arm axis ×
    // a weapon axis), sign-matched, exactly the conditioning the old carry solve proved out.
    this.weapon.getWorldQuaternion(_wPoleQ);
    _wPole.set(0, -1, 0).applyQuaternion(_wPoleQ);
    if (this.weaponStance === "underhand") {
      // Left elbow stays on the player's LEFT/outside while the forearm crosses
      // the chest to the top carry handle. A weapon-down pole folds it inward
      // through the abdomen on this low, lateral target.
      _aoFwd.set(0, 0, -1).applyQuaternion(this.group.quaternion);
      _aoUp.set(0, 1, 0).applyQuaternion(this.group.quaternion);
      _aoRight.crossVectors(_aoFwd, _aoUp).normalize();
      // Keep the crossed support elbow both outside and IN FRONT of the torso.
      // A purely lateral pole lets the upper arm choose a bend plane through
      // the abdomen once armor broadens the silhouette.
      _wPole.copy(_aoRight).negate()
        .addScaledVector(_aoUp, -0.25)
        .addScaledVector(_aoFwd, 0.45)
        .normalize();
    }
    const lsh = this.boneByName.get("LeftArm");
    if (lsh && this.weaponStance !== "underhand") {
      lsh.getWorldPosition(_wShoulder);
      _wAxis.copy(_wGrip).sub(_wShoulder);
      if (_wAxis.lengthSq() > 1e-8) {
        _wAxis.normalize();
        _wPerp.copy(_wPole).addScaledVector(_wAxis, -_wPole.dot(_wAxis));
        const perpLen = _wPerp.length();
        const t = Math.min(1, perpLen / 0.25);
        const blend = t * t * (3 - 2 * t); // smoothstep — no hard switch, no step
        _wRef.set(1, 0, 0).applyQuaternion(_wPoleQ);
        if (Math.abs(_wRef.dot(_wAxis)) > 0.85) _wRef.set(0, 0, 1).applyQuaternion(_wPoleQ);
        _wCon.crossVectors(_wRef, _wAxis).normalize();
        if (_wCon.dot(_wPole) < 0) _wCon.negate();
        if (perpLen > 1e-6) _wPerp.divideScalar(perpLen); else _wPerp.copy(_wCon);
        _wPole.copy(_wCon).lerp(_wPerp, blend).normalize();
      }
    }
    // RATE-LIMIT the pole (2026-08-02): near full extension the ⊥ projection is tiny and the
    // bobbing weapon can swing it to the other side of the arm axis in one frame — the elbow
    // "spaz". The pole is only a bend PREFERENCE, so capping its per-frame rotation keeps the
    // elbow plane continuous with no visible lag; it still converges to the true pole in a few
    // frames whenever the pose settles.
    if (this.weaponStance === "underhand") {
      // This stance owns a fixed body-left pole. Rifle pole history tracks clip
      // bend noise and can drag the elbow across the body or flip near reach.
      this.gripPolePrev = null;
    } else if (wL > 0.02 && this.gripPolePrev) {
      const ang = this.gripPolePrev.angleTo(_wPole);
      if (ang > GRIP_POLE_RATE) {
        this.gripPolePrev.lerp(_wPole, GRIP_POLE_RATE / ang).normalize();
        _wPole.copy(this.gripPolePrev);
      } else this.gripPolePrev.copy(_wPole);
    } else if (wL > 0.02) this.gripPolePrev = _wPole.clone();
    else this.gripPolePrev = null;
    this.boneByName.get("LeftArm")?.getWorldQuaternion(_twPreL); // clip twist reference
    this.ikLeg("LeftArm", "LeftForeArm", "LeftHand", _wGrip, _wPole, wL, false);
    // Swing–twist restore: a 2-bone solve leaves the upper arm's ROLL wherever it lands, and roll
    // error is exactly the deltoid/lat collapse that got this IK disabled for the rifle. Keep the
    // IK's swing, restore the clip's twist, then re-solve — the twist rotation moves the hand
    // (it sits off the twist axis), so one more position pass puts it back on the grip.
    if (this.weaponStance !== "underhand") {
      this.preserveTwist("LeftArm", "LeftForeArm", _twPreL);
      this.ikLeg("LeftArm", "LeftForeArm", "LeftHand", _wGrip, _wPole, wL, false);
    }
    // WRIST PIN (rifle): orient the support hand in the WEAPON's frame, so the palm lies along
    // the handguard at every pitch. ikLeg restored the clip's WORLD wrist orientation, which
    // belongs to a hand floating off the gun — riding the solved forearm it read as a heavily
    // bent wrist. Measured from the clip at the neutral aim pose (client/test/_gripmeasure.mjs).
    if (rifle) {
      const hand = this.boneByName.get("LeftHand");
      if (hand?.parent) {
        _wRelQ.copy(_wPoleQ).multiply(this.gripLWristQ); // weapon world × authored offset
        hand.parent.getWorldQuaternion(_wParQ).invert();
        // AIM-GATED (2026-08-02): the pin was measured at the ADS pose — at the hip carry the
        // clips author the hand DRAPED on the guard top, and forcing the ADS palm-along-guard
        // orientation onto that arm read as a BACKWARDS hand. Carry keeps the clip's wrist.
        hand.quaternion.slerp(_wParQ.multiply(_wRelQ), wL * this.aimK);
        // Re-orienting the wrist swings the hand origin ~2 cm off the grip (it sits off the
        // forearm axis) — one more position pass closes it; ikLeg snapshots and RESTORES the
        // end-effector's world orientation, so the pin above survives this solve.
        this.ikLeg("LeftArm", "LeftForeArm", "LeftHand", _wGrip, _wPole, wL, false);
        // GRIP CURL (user call 2026-08-02, both bodies): the clips' left fingers hover in a loose
        // curl — they never wrap OUR handguard. Flex each finger joint about its local Z (axis
        // established by screenshot A/B: X extends, Z curls the tips toward the palm), eased by
        // the same weight as the grip. Thumb keeps the clip's pose (it already sits under the
        // guard); metacarpals keep the palm's shape.
        // Heavy handles need a firmer wrap than a rifle handguard; in
        // particular, close the source clip's long trigger-index pose.
        if (this.weaponStance === "underhand") this.curlGripHand("l", wL, true, 1.35);
        else this.curlGripHand("l", wL);
        // SUPPORT-HAND WRIST RELIEF — same treatment as the firing hand below the pistol grip:
        // the palm-on-guard orientation demands ~−93° of forearm-axis roll and the broad
        // auto-skin weights collapse the LEFT forearm into the twisted ribbon under the gun
        // (the user's close-up finally showed WHICH arm it was). Unroll to the same healthy
        // target; the fingers rotate a little around the guard, which reads fine — the ribbon
        // does not. Local-X post-multiply = exact; rotation about the wrist leaves its position.
        const bindL = this.twistBind.get("hand_l");
        if (bindL) {
          _wRelQ.copy(bindL).invert().multiply(hand.quaternion);
          let rollL = 2 * Math.atan2(_wRelQ.x, _wRelQ.w);
          if (rollL > Math.PI) rollL -= 2 * Math.PI;
          else if (rollL < -Math.PI) rollL += 2 * Math.PI;
          const reliefL = THREE.MathUtils.clamp(RIFLE_WRIST_ROLL_TARGET - rollL, -1.7, 1.7) * wL;
          if (Math.abs(reliefL) > 1e-3) {
            _wParQ.set(Math.sin(reliefL / 2), 0, 0, Math.cos(reliefL / 2));
            hand.quaternion.multiply(_wParQ);
          }
        }
      }
      // ELBOW SWIVEL (user calls 2026-08-02): female elbows flare OUT (they hugged the body in
      // the rifle hold); male firing elbow tucks IN a touch. Swivel each upper arm about the
      // shoulder→hand line — the hand sits ON that axis, so its position holds exactly; its
      // orientation is captured and restored, so the gun (child of hand_r) and both wrist pins
      // stand. The elbow simply orbits about the axis.
      if (this.weaponStance !== "underhand") {
        const swivels = this.bodyModelId === "female" ? ELBOW_SWIVELS_FEMALE : ELBOW_SWIVELS_MALE;
        for (const [up, fo, hd, ang] of swivels) {
          const shoulder = this.boneByName.get(up);
          const handB = this.boneByName.get(hd);
          if (!shoulder || !handB?.parent) continue;
          this.group.updateMatrixWorld(true);
          handB.getWorldQuaternion(_wRelQ); // restore after the swivel
          shoulder.getWorldPosition(_wShoulder);
          handB.getWorldPosition(_fgPos);
          _wAxis.copy(_fgPos).sub(_wShoulder);
          if (_wAxis.lengthSq() < 1e-8) continue;
          _wParQ.setFromAxisAngle(_wAxis.normalize(), ang * wL); // ADS-tuned — rides the aim gate
          this.rotateBoneWorld(up, _wParQ);
          this.group.updateMatrixWorld(true);
          handB.parent.getWorldQuaternion(_wParQ).invert();
          handB.quaternion.copy(_wParQ).multiply(_wRelQ);
          void fo;
        }
      }
    }
  }

  /** Additive local finger flex shared by authored support and heavy-weapon
   * top handles. Mirrored finger chains use the same local-Z sign: their local
   * X axes point in opposite directions, producing the matching physical curl. */
  private curlGripHand(side: "l" | "r", weight: number, absolute = false, curlScale = 1): void {
    for (const fname of GRIP_CURL_FINGERS) {
      for (let j = 0; j < 3; j++) {
        const bone = this.boneByName.get(`${fname}_0${j + 1}_${side}`);
        if (!bone) continue;
        const half = (GRIP_CURL[j] * (absolute ? curlScale : weight)) / 2;
        _wParQ.set(0, 0, Math.sin(half), Math.cos(half));
        if (absolute) {
          const rest = this.authoringRest.get(bone.name);
          if (rest) {
            _wRelQ.copy(rest.quaternion).multiply(_wParQ);
            bone.quaternion.slerp(_wRelQ, THREE.MathUtils.clamp(weight, 0, 1));
          } else bone.quaternion.multiply(_wParQ);
        } else bone.quaternion.multiply(_wParQ);
      }
    }
    // Thumb closes in opposition to the fingers rather than folding with them.
    for (let j = 0; j < 3; j++) {
      const bone = this.boneByName.get(`thumb_0${j + 1}_${side}`);
      if (!bone) continue;
      const half = (GRIP_CURL_THUMB[j] * (absolute ? curlScale : weight)) / 2;
      _wParQ.set(0, 0, Math.sin(half), Math.cos(half));
      if (absolute) {
        const rest = this.authoringRest.get(bone.name);
        if (rest) {
          _wRelQ.copy(rest.quaternion).multiply(_wParQ);
          bone.quaternion.slerp(_wRelQ, THREE.MathUtils.clamp(weight, 0, 1));
        } else bone.quaternion.multiply(_wParQ);
      } else bone.quaternion.multiply(_wParQ);
    }
  }


  /** DEV/probe read-out for the weapon grip: support-arm EXTENSION (1.0 = locked straight —
   *  which is also ikLeg's own cap, so a value pinned there means the target is out of reach and
   *  being clamped) and how far the support hand actually sits from its measured grip point on the
   *  weapon. Null when nothing is held (construct, melee, empty hand). Combat-lab HUD + probes. */
  gripDiagnostics(): { extL: number; extR: number; gripErr: number; contactErr: number } | null {
    if (!this.weapon || !this.gunOut || !this.hasSupportGrip()) return null;
    const ext = (a: string, b: string, c: string): number => {
      const A = this.boneByName.get(a), B = this.boneByName.get(b), C = this.boneByName.get(c);
      if (!A || !B || !C) return 0;
      A.getWorldPosition(_cdA); B.getWorldPosition(_cdB); C.getWorldPosition(_cdC);
      const total = _cdA.distanceTo(_cdB) + _cdB.distanceTo(_cdC);
      return total > 1e-6 ? _cdA.distanceTo(_cdC) / total : 0;
    };
    const extL = ext("LeftArm", "LeftForeArm", "LeftHand");
    const extR = ext("RightArm", "RightForeArm", "RightHand");
    const hand = this.boneByName.get("LeftHand");
    _cdG.copy(this.hasSupportGrip() ? this.gripLTarget : _wGripPistol);
    this.weapon.localToWorld(_cdG);
    if (!hand) return { extL, extR, gripErr: 0, contactErr: 0 };
    hand.getWorldPosition(_cdA);
    const gripErr = _cdG.distanceTo(_cdA);
    // Sliding on an authored foregrip is valid contact, not a detached hand.
    // Report distance off the allowed rail separately from fixed-socket error.
    _cdB.copy(_cdA); this.weapon.worldToLocal(_cdB);
    _cdG.copy(this.gripLTarget);
    if (this.weaponStance !== "underhand") {
      _cdC.fromArray((this.holdDef.support?.slideTo ?? GRIP_SLIDE_TO) as [number, number, number]).sub(this.gripLTarget);
      const length = _cdC.length();
      if (length > 1e-6) {
        _cdC.multiplyScalar(1 / length);
        const slide = THREE.MathUtils.clamp(_cdB.sub(this.gripLTarget).dot(_cdC), 0, Math.min(length, GRIP_SLIDE_MAX));
        _cdG.addScaledVector(_cdC, slide);
      }
    }
    this.weapon.localToWorld(_cdG);
    return { extL, extR, gripErr, contactErr: _cdG.distanceTo(_cdA) };
  }

  /**
   * ADS RAISE — bring the weapon UP to the sightline while aiming, as an additive delta over the
   * clavicle / upper-arm / forearm chain (plus a little chest lift).
   *
   * Why not `aimRaise`: that lifts both upper arms, which TILTS the barrel up, and applyGunAim
   * immediately re-levels it by pitching the chest DOWN — which lowers the arms again. Swept 0 →
   * 0.34 rad it moves the weapon 2–3 cm, in the wrong direction. The raise cancels itself.
   *
   * The construction here keeps the barrel level ON ITS OWN, so there is nothing for the gun-track
   * to undo. Rotating about body-right by −θ lifts; the forearm takes +(clav+arm), so the three
   * world deltas compose to identity AT THE HAND:
   *     clavicle −θc  ·  upperarm −θa  ·  forearm +(θc+θa)   ⟹  hand orientation unchanged
   * The hand's POSITION still rises, because it swung about the clavicle and the shoulder. So the
   * weapon translates up toward the eye with its aim intact — which is what "sights to the eye"
   * physically is, and why a pure rotation could never produce it.
   *
   * Runs AFTER applyGunAim for the same reason: it preserves the barrel, so the gun-track's
   * elevation solve stands. Eased by aimK, so it fades in exactly with the aim.
   */
  private applyAdsRaise(k: number): void {
    const a = this.adsRaise * k;
    if (a < 1e-3 || !this.weapon || this.construct || this.weaponStance !== "shoulder") return;
    _aoFwd.set(0, 0, -1).applyQuaternion(this.group.quaternion);
    _aoUp.set(0, 1, 0).applyQuaternion(this.group.quaternion);
    _aoRight.crossVectors(_aoFwd, _aoUp).normalize(); // the ONLY axis this rotates about
    const clav = a * ADS_CLAVICLE;
    const arm = a * ADS_UPPERARM;
    const chest = a * ADS_CHEST;
    // CHEST FIRST, and counted in the forearm's counter-rotation below. Applying it last left it
    // UNCOMPENSATED: it rotates the whole arm chain (and so the barrel) with nothing cancelling it,
    // which cost 17° of barrel error at full raise — almost exactly the chest angle itself.
    if (chest > 1e-4 && this.chestBone) {
      _adsQ.setFromAxisAngle(_aoRight, -chest);
      this.rotateBoneWorld("Spine2", _adsQ);
    }
    _adsQ.setFromAxisAngle(_aoRight, -clav); // − about body-right = up
    this.rotateBoneWorld("LeftShoulder", _adsQ);
    this.rotateBoneWorld("RightShoulder", _adsQ);
    _adsQ.setFromAxisAngle(_aoRight, -arm);
    this.rotateBoneWorld("LeftArm", _adsQ);
    this.rotateBoneWorld("RightArm", _adsQ);
    // Counter-rotate the forearms by the full inherited lift so the hands — and therefore the
    // barrel — keep the orientation the gun-track just solved.
    _adsQ.setFromAxisAngle(_aoRight, chest + clav + arm);
    this.rotateBoneWorld("LeftForeArm", _adsQ);
    this.rotateBoneWorld("RightForeArm", _adsQ);
    // SHOULDER DROP (user call 2026-08-02): settle the girdle DOWN a touch while ADS — the raise
    // above reads slightly shrugged. Mirrored clavicle ROLLS about body-forward drop each lateral
    // tip; each upper arm takes the inverse so the arm chain (and barrel) keeps its orientation.
    // Signs EMPIRICAL (measured via shoulder-tip world height in the harness).
    const drop = ADS_SHOULDER_DROP * k;
    if (drop > 1e-4) {
      _adsQ.setFromAxisAngle(_aoFwd, drop); // +roll drops the RIGHT tip (measured; −31.7mm @0.2)
      this.rotateBoneWorld("RightShoulder", _adsQ);
      _adsQ.invert();
      this.rotateBoneWorld("RightArm", _adsQ);
      this.rotateBoneWorld("LeftShoulder", _adsQ);
      _adsQ.invert();
      this.rotateBoneWorld("LeftArm", _adsQ);
    }
    // FEMALE STOCK SEAT (user call 2026-08-02): her retuned rig carries the rifle LOW and
    // CENTERED — the butt floated at the sternum instead of the shoulder crook. Same
    // barrel-preserving telescoping as the raise above, on two axes: extra lift about
    // body-right, plus a clavicle yaw about body-up (forearms take both sums back) that
    // walks the whole gun toward the firing shoulder. Tuned by A/B screenshot vs the male.
    if (this.bodyModelId === "female") {
      const lift = FEM_ADS_LIFT * k;
      const right = FEM_ADS_RIGHT * k;
      _adsQ.setFromAxisAngle(_aoRight, -lift);
      this.rotateBoneWorld("LeftShoulder", _adsQ);
      this.rotateBoneWorld("RightShoulder", _adsQ);
      // LATERAL walk about body-FORWARD (the roll axis): with the arms extended at ADS, a roll
      // sweeps the hands sideways. (A body-UP yaw was tried first and moved the stock fore/aft
      // instead — the arms arc around the shoulders, not across them.)
      _adsQ.setFromAxisAngle(_aoFwd, right);
      this.rotateBoneWorld("LeftShoulder", _adsQ);
      this.rotateBoneWorld("RightShoulder", _adsQ);
      _adsQ.setFromAxisAngle(_aoRight, lift);
      this.rotateBoneWorld("LeftForeArm", _adsQ);
      this.rotateBoneWorld("RightForeArm", _adsQ);
      _adsQ.setFromAxisAngle(_aoFwd, -right);
      this.rotateBoneWorld("LeftForeArm", _adsQ);
      this.rotateBoneWorld("RightForeArm", _adsQ);
    }
  }

  /**
   * UPPER-ARM TWIST CONSTRAINT (swing–twist decomposition).
   *
   * A 2-bone IK pins the hand's POSITION and leaves the upper arm's ROLL free; pinning the wrist's
   * ORIENTATION afterwards dumps the whole difference into that roll. Measured: the carry solve
   * twisted the right upper arm 59° off the clip pose, which collapses the lat and deltoid.
   *
   * A first attempt projected the pre-IK "up" onto the post-IK bone axis and made it WORSE
   * (126.9° → 136.5°) — that conflates SWING with TWIST, because the axis itself moved. The correct
   * operation is a swing–twist split about the CURRENT axis: keep the IK's swing (it is what aims
   * the limb) and restore the CLIP's twist (it is what the skin was authored around). Rotating
   * about the bone's own axis leaves the elbow on that axis, so the solved position is untouched.
   */
  private preserveTwist(upperN: string, foreN: string, pre: THREE.Quaternion): void {
    const upper = this.boneByName.get(upperN), fore = this.boneByName.get(foreN);
    if (!upper || !fore) return;
    upper.getWorldPosition(_twA);
    fore.getWorldPosition(_twB);
    _twAxis.copy(_twB).sub(_twA);
    if (_twAxis.lengthSq() < 1e-8) return;
    _twAxis.normalize();
    upper.getWorldQuaternion(_twNow);
    twistAbout(_twNow, _twAxis, _twTNow);
    twistAbout(pre, _twAxis, _twTRef);
    _twQ.copy(_twTRef).multiply(_twTNow.invert()); // swap this bone's twist for the clip's
    if (Math.abs(_twQ.w) > 0.99995) return;        // already there
    this.rotateBoneWorld(upperN, _twQ);
  }


  /** Pull `target` (world) toward the shoulder until it is within `frac` of the arm's full length.
   *  ikLeg caps extension at 0.985 internally, but a capped arm reads as a locked, straight strut —
   *  and the shipped hard-coded rifle foregrip measures exactly that today (extL 0.985, i.e. it asks
   *  for ~30 cm when the arm reaches ~22 cm). Clamping the TARGET instead leaves a visible elbow bend
   *  and keeps the pole well-defined. Also what makes long modular handguards safe on both body rigs,
   *  whose arm lengths differ. */
  private clampToReach(upperN: string, foreN: string, handN: string, target: THREE.Vector3, frac: number): void {
    const upper = this.boneByName.get(upperN), fore = this.boneByName.get(foreN);
    // NOTE: the hand must be looked up BY NAME. The Manny forearm's first child is
    // lowerarm_twist_01_l, not hand_l — measuring the chain through it understates the forearm by
    // ~33% and the clamp then hauls the grip target several cm short of the weapon.
    const hand = this.boneByName.get(handN);
    if (!upper || !fore || !hand) return;
    upper.getWorldPosition(_cShoulder);
    fore.getWorldPosition(_cElbow);
    const len = _cShoulder.distanceTo(_cElbow) + _cElbow.distanceTo(hand.getWorldPosition(_cReach));
    const max = len * frac;
    _cReach.copy(target).sub(_cShoulder);
    const d = _cReach.length();
    if (d > max && d > 1e-6) target.copy(_cShoulder).addScaledVector(_cReach, max / d);
  }

  /** Shoulder-fired launcher: a small torso brace, a bent firing elbow beside
   * the ribs, and a cross-body support arm. All changes are above the pelvis;
   * the locomotion / jump pose keeps ownership of the hips and legs.
   *
   * Seat the tube first, then solve both grips. Rifle gun-track, barrel-lock,
   * and support-reach corrections must not subsequently pull it off the shoulder. */
  private applyLauncherStance(): void {
    const weapon = this.weapon;
    const shoulder = this.boneByName.get("RightArm");
    const hand = this.boneByName.get("RightHand");
    if (!weapon || !shoulder || !hand) return;
    const w = this.stanceK * (1 - this.glideK) * (1 - this.stowGestureK);
    if (w < 0.02) return;
    this.group.getWorldQuaternion(_launcherWorldQ);
    _aoFwd.set(0, 0, -1).applyQuaternion(_launcherWorldQ);
    _aoUp.set(0, 1, 0).applyQuaternion(_launcherWorldQ);
    _aoRight.crossVectors(_aoFwd, _aoUp).normalize();

    // Blade the shoulder girdle: the support side faces forward and the firing
    // shoulder sits back under the tube. Measure the live clip so a run/strafe
    // cannot swing the opposite shoulder away and pull its hand off the grip.
    // Only the spine turns; the pelvis keeps the locomotion heading.
    const pitch = this.aimPitch * this.aimK;
    this.group.updateMatrixWorld(true);
    const supportShoulder = this.boneByName.get("LeftArm");
    if (supportShoulder) {
      shoulder.getWorldPosition(_stanceShoulder);
      supportShoulder.getWorldPosition(_stanceAxis).sub(_stanceShoulder).negate();
      const yaw = Math.atan2(_stanceAxis.dot(_aoFwd), _stanceAxis.dot(_aoRight));
      _launcherDeltaQ.setFromAxisAngle(_aoUp, (-0.72 - yaw) * 0.5 * w);
      this.rotateBoneWorld("spine_02", _launcherDeltaQ);
      this.rotateBoneWorld("spine_03", _launcherDeltaQ);
    }
    // Aim elevation is shared by the spine, with the remaining angle at the arms.
    _launcherDeltaQ.setFromAxisAngle(_aoRight, -(0.035 + pitch * 0.15) * w);
    this.rotateBoneWorld("spine_02", _launcherDeltaQ);
    this.rotateBoneWorld("spine_03", _launcherDeltaQ);
    this.group.updateMatrixWorld(true);
    shoulder.getWorldPosition(_launcherSeat);
    // Keep the thick wing barrel beside the cheek on narrower body builds as
    // well. The tube's inner underside still overlaps the shoulder pad.
    const head = this.boneByName.get("Head");
    let shoulderOut = 0.09;
    if (head) {
      head.getWorldPosition(_stanceElbow).sub(_launcherSeat);
      shoulderOut = Math.max(shoulderOut, _stanceElbow.dot(_aoRight) + 0.27);
    }
    _launcherSeat.addScaledVector(_aoRight, shoulderOut);

    // Relaxed carry stays shouldered with the muzzle slightly lowered. Reload
    // also pivots at the shoulder rather than dropping the entire tube at the elbow.
    const reloadDip = this.reloadP === null ? 0 : Math.sin(Math.PI * this.reloadP) * 0.22;
    _launcherDeltaQ.setFromAxisAngle(_underhandLocalRight, -pitch - 0.10 * (1 - this.aimK) - reloadDip);
    _launcherWorldQ.multiply(_launcherDeltaQ);
    _launcherOrigin.copy(LAUNCHER_SHOULDER_SEAT).applyQuaternion(_launcherWorldQ);
    _launcherOrigin.subVectors(_launcherSeat, _launcherOrigin);

    // The authored primary socket is in the palm, 4 cm along hand-local Y.
    _launcherHandQ.copy(this.weaponMount).invert().premultiply(_launcherWorldQ);
    _launcherTarget.fromArray(this.holdDef.primary.position as [number, number, number])
      .applyQuaternion(_launcherWorldQ).add(_launcherOrigin);
    _primaryGripP.set(0, 0.04, 0).applyQuaternion(_launcherHandQ);
    _launcherTarget.sub(_primaryGripP);
    _launcherPole.copy(_aoRight).multiplyScalar(1.1).addScaledVector(_aoUp, -1);
    this.solveLauncherArm("Right", _launcherTarget, _launcherPole, _launcherHandQ, w);

    // Recompose from the actual palm so partial draw/stow blends remain attached.
    // The inverse hand matrix also cancels body-proportion scale on the prop.
    weapon.position.copy(this.weaponBasePosition);
    weapon.quaternion.copy(this.weaponMount);
    weapon.scale.set(1, 1, 1);
    weapon.updateWorldMatrix(true, false);
    weapon.getWorldQuaternion(_wRelQ).slerp(_launcherWorldQ, w);
    hand.getWorldQuaternion(_wParQ);
    hand.getWorldPosition(_underhandPalmP);
    _launcherOrigin.set(0, 0.04, 0).applyQuaternion(_wParQ).add(_underhandPalmP);
    _primaryGripP.fromArray(this.holdDef.primary.position as [number, number, number]).applyQuaternion(_wRelQ);
    _launcherOrigin.sub(_primaryGripP);
    _underhandWorldM.compose(_launcherOrigin, _wRelQ, _underhandUnitScale);
    _underhandLocalM.copy(hand.matrixWorld).invert().multiply(_underhandWorldM);
    _underhandLocalM.decompose(weapon.position, weapon.quaternion, weapon.scale);
    weapon.updateWorldMatrix(true, false);

    _launcherTarget.copy(this.gripLTarget);
    weapon.localToWorld(_launcherTarget);
    _launcherHandQ.copy(_wRelQ).multiply(this.gripLWristQ);
    _launcherPole.copy(_aoRight).multiplyScalar(-0.45).addScaledVector(_aoUp, -1).addScaledVector(_aoFwd, 0.2);
    this.solveLauncherArm("Left", _launcherTarget, _launcherPole, _launcherHandQ, w);
    this.curlGripHand("r", w, false, 1.2);
    this.curlGripHand("l", w, true, 1.15);
  }

  private solveLauncherArm(
    side: "Left" | "Right", target: THREE.Vector3, pole: THREE.Vector3,
    wrist: THREE.Quaternion, weight: number,
  ): void {
    const hand = this.boneByName.get(`${side}Hand`);
    if (!hand?.parent) return;
    this.reachClavicleToward(`${side}Shoulder`, `${side}Arm`, target, 0.22, weight);
    this.group.updateMatrixWorld(true);
    // At steep elevations the support hand can slide back along the authored
    // grip rail. Keep contact with the weapon instead of clamping the hand into
    // empty space, and preserve a bend in the elbow on shorter arms.
    const slideTo = this.holdDef.support?.slideTo;
    const upper = this.boneByName.get(`${side}Arm`);
    const fore = this.boneByName.get(`${side}ForeArm`);
    if (side === "Left" && slideTo && this.weapon && upper && fore) {
      upper.getWorldPosition(_wShoulder);
      fore.getWorldPosition(_wAxis);
      hand.getWorldPosition(_wPerp);
      const reach = (_wShoulder.distanceTo(_wAxis) + _wAxis.distanceTo(_wPerp)) * 0.95;
      _wAxis.copy(target).sub(_wShoulder);
      _wPerp.fromArray(slideTo as [number, number, number]);
      this.weapon.localToWorld(_wPerp).sub(target);
      const railLength = _wPerp.length();
      const deficit = _wAxis.lengthSq() - reach * reach;
      if (deficit > 0 && railLength > 1e-5) {
        _wPerp.divideScalar(railLength);
        const along = _wAxis.dot(_wPerp);
        const discriminant = along * along - deficit;
        const slide = discriminant >= 0 ? -along - Math.sqrt(discriminant) : -along;
        target.addScaledVector(_wPerp, THREE.MathUtils.clamp(slide, 0, Math.min(railLength, GRIP_SLIDE_MAX)));
      }
    }
    this.ikLeg(`${side}Arm`, `${side}ForeArm`, `${side}Hand`, target, pole, weight, false);
    hand.parent.getWorldQuaternion(_wParQ).invert().multiply(wrist);
    hand.quaternion.slerp(_wParQ, weight);
    this.group.updateMatrixWorld(true);
    // A retuned wrist has a small off-axis translation. Close that gap after
    // orientation without changing the bend plane or stretching either arm.
    this.ikLeg(`${side}Arm`, `${side}ForeArm`, `${side}Hand`, target, pole, weight, false);
  }

  /** Low heavy-gun firing hand; the support socket is solved by applyWeaponGrip. */
  private applyStanceHandPlacement(): void {
    const hipCarry = this.weaponStance === "underhand";
    if (!hipCarry || !this.weapon || !this.gunOut || this.construct || this.farLOD) return;
    const hand = this.boneByName.get("RightHand");
    const shoulder = this.boneByName.get("RightArm");
    const elbow = this.boneByName.get("RightForeArm");
    const hips = hipCarry ? this.boneByName.get("Hips") : null;
    if (!hand || !shoulder || !elbow || (hipCarry && !hips)) return;
    const w = this.stanceK * (1 - this.glideK) * (1 - this.stowGestureK);
    if (w < 0.02) return;
    this.group.updateMatrixWorld(true);
    _aoFwd.set(0, 0, -1).applyQuaternion(this.group.quaternion);
    _aoUp.set(0, 1, 0).applyQuaternion(this.group.quaternion);
    _aoRight.crossVectors(_aoFwd, _aoUp).normalize();
    if (hipCarry) {
      // Use the retuned BIND pelvis, not the live animated pelvis. The latter
      // contains the walk cycle's vertical bounce (and its loop seam), which
      // was fed through the primary-hand IK into the entire heavy weapon. That
      // made its muzzle bob by ~10 cm and occasionally pitch several degrees in
      // one frame. rigTuneBase is body-proportion aware and is stored in the
      // avatar group's fixed mount frame, so it gives every body the same stable
      // hip-relative carry without freezing the avatar in world space.
      const hipsIndex = this.skeleton.bones.indexOf(hips!);
      if (hipsIndex >= 0 && this.rigTuneBase[hipsIndex]) {
        _stanceHand.setFromMatrixPosition(this.rigTuneBase[hipsIndex].world);
        this.group.localToWorld(_stanceHand);
      } else {
        hips!.getWorldPosition(_stanceHand);
      }
      _stanceHand
        .addScaledVector(_aoRight, UNDERHAND_HAND_SEAT[0])
        .addScaledVector(_aoUp, UNDERHAND_HAND_SEAT[1])
        .addScaledVector(_aoFwd, UNDERHAND_HAND_SEAT[2]);
    }
    if (hipCarry) {
      // The rear-hand anchor is low enough that a rifle-frozen clavicle makes
      // the arm fully straight. Let the firing shoulder follow it slightly;
      // the torso deforms naturally and the elbow retains a stable bend.
      this.reachClavicleToward("RightShoulder", "RightArm", _stanceHand, 0.20, w);
      this.group.updateMatrixWorld(true);
      // Do not length-scale the firing arm: the weapon is a descendant of its
      // hand, so a non-uniform upper-arm scale becomes gait-varying shear at
      // the muzzle. The bind-pelvis target is deliberately reachable; modest
      // clavicle give above supplies the remaining body compliance.
    }
    shoulder.getWorldPosition(_stanceShoulder);
    elbow.getWorldPosition(_stanceElbow);
    _stanceAxis.copy(_stanceHand).sub(_stanceShoulder).normalize();
    if (hipCarry) {
      // Heavy-cannon firing elbow trails behind the grip, with only enough
      // lateral component to keep it clear of the ribs. This matches the
      // braced rear-control silhouette instead of flaring like a rifle elbow.
      _stancePole.copy(_aoRight).multiplyScalar(0.45).addScaledVector(_aoFwd, -0.85);
      _stancePole.addScaledVector(_stanceAxis, -_stancePole.dot(_stanceAxis));
      if (_stancePole.lengthSq() < 1e-6) _stancePole.copy(_aoRight);
      _stancePole.normalize();
    } else {
      _stancePole.copy(_stanceElbow).sub(_stanceShoulder);
      _stancePole.addScaledVector(_stanceAxis, -_stancePole.dot(_stanceAxis));
      if (_stancePole.lengthSq() < 1e-6) _stancePole.copy(_aoUp).negate();
      else _stancePole.normalize();
    }
    this.clampToReach("RightArm", "RightForeArm", "RightHand", _stanceHand, hipCarry ? 0.97 : 0.96);
    this.ikLeg("RightArm", "RightForeArm", "RightHand", _stanceHand, _stancePole, w, false);
    if (hipCarry) this.pinUnderhandWeaponToBody();
    this.weapon.updateWorldMatrix(true, false);
  }

  /** Keep a hip-carried heavy weapon level and body-relative while locomotion
   * clips animate underneath it. The right-hand IK owns the primary socket's
   * position, but ikLeg deliberately preserves the clip wrist orientation;
   * without this seam the run clip rolls/pitches the whole child weapon by
   * ~36 degrees and carries the support handle tens of centimetres away.
   *
   * Recompose the weapon around its authored primary socket so that socket
   * remains in the firing hand. This is body-relative, so turning carries the
   * gun and its synchronous muzzle rigidly—no world-space lag or snap. */
  private pinUnderhandWeaponToBody(): void {
    if (!this.weapon) return;
    const hand = this.boneByName.get("RightHand");
    if (!hand) return;
    this.group.updateMatrixWorld(true);
    this.group.getWorldQuaternion(_wRelQ);       // desired weapon world: body frame
    // Aim elevation is part of the rigid underhand mount. Applying it here
    // keeps the primary handle fixed at the hip; the rifle barrel-lock path
    // would instead swing the whole rear hand around the distant top handle.
    _underhandPitchQ.setFromAxisAngle(_underhandLocalRight, -this.aimPitch * this.aimK);
    _underhandCarryQ.setFromAxisAngle(_underhandLocalUp, UNDERHAND_CARRY_YAW * (1 - this.aimK));
    _wRelQ.multiply(_underhandPitchQ).multiply(_underhandCarryQ);
    // Build the desired WORLD transform around the actual primary socket. The
    // old quaternion/local-position shortcut only works beneath a unit-scale
    // parent; body proportions and soft IK make this hand hierarchy non-uniform,
    // which displaced the socket by 20+ cm and scaled the gun with the arm.
    hand.getWorldPosition(_underhandPalmP);
    hand.getWorldQuaternion(_wParQ);
    _underhandWorldP.set(0, 0.04, 0).applyQuaternion(_wParQ).add(_underhandPalmP);
    _primaryGripP.fromArray(this.holdDef.primary.position as [number, number, number]);
    _primaryGripP.applyQuaternion(_wRelQ);
    _underhandWorldP.sub(_primaryGripP); // weapon origin that puts socket in palm
    _underhandWorldM.compose(_underhandWorldP, _wRelQ, _underhandUnitScale);
    _underhandLocalM.copy(hand.matrixWorld).invert().multiply(_underhandWorldM);
    _underhandLocalM.decompose(this.weapon.position, this.weapon.quaternion, this.weapon.scale);
    this.weapon.updateMatrix();
  }

  /** Rotate a clavicle toward a hand target by at most `maxAngle`. This is a
   * small, stance-only shoulder-girdle reach—not limb scaling—so skin and armor
   * get natural give while the two-bone arm remains comfortably solvable. */
  private reachClavicleToward(
    clavicleName: "LeftShoulder" | "RightShoulder",
    armName: "LeftArm" | "RightArm",
    target: THREE.Vector3,
    maxAngle: number,
    weight: number,
  ): void {
    const clavicle = this.boneByName.get(clavicleName);
    const arm = this.boneByName.get(armName);
    if (!clavicle || !arm || weight < 0.01) return;
    clavicle.getWorldPosition(_stanceReachPivot);
    arm.getWorldPosition(_stanceReachArm).sub(_stanceReachPivot);
    _stanceReachTarget.copy(target).sub(_stanceReachPivot);
    if (_stanceReachArm.lengthSq() < 1e-8 || _stanceReachTarget.lengthSq() < 1e-8) return;
    _stanceReachArm.normalize();
    _stanceReachTarget.normalize();
    _stanceReachQ.setFromUnitVectors(_stanceReachArm, _stanceReachTarget);
    const angle = 2 * Math.acos(THREE.MathUtils.clamp(_stanceReachQ.w, -1, 1));
    if (angle < 1e-4) return;
    const k = Math.min(1, maxAngle / angle) * weight;
    _stanceReachQ.slerp(_stanceReachIdentity, 1 - k);
    this.rotateBoneWorld(clavicleName, _stanceReachQ);
  }

  /** Remove the length multiplier installed during the previous IK pass. The
   * body rig/proportion scale underneath it is preserved exactly. */
  private clearUnderhandArmStretch(): void {
    const clear = (name: "LeftArm" | "RightArm", applied: number): void => {
      if (Math.abs(applied - 1) < 1e-6) return;
      const upper = this.boneByName.get(name);
      if (upper) upper.scale.x /= applied;
    };
    clear("LeftArm", this.underhandStretchAppliedL);
    clear("RightArm", this.underhandStretchAppliedR);
    this.underhandStretchAppliedL = 1;
    this.underhandStretchAppliedR = 1;
    this.group.updateMatrixWorld(true);
  }

  /** Soft reach for underhand weapon grips. Long bones use local X as their
   * authored length axis, so scaling only X lengthens the chain without making
   * the arm thicker. Stretch begins only beyond the requested bent-reach limit
   * and is capped at four percent; unreachable remainder still goes through
   * clampToReach, keeping the elbow pole well-conditioned. */
  private applyUnderhandArmStretch(
    side: "Left" | "Right",
    target: THREE.Vector3,
    bentReach: number,
    dt: number,
    weight: number,
  ): void {
    const upper = this.boneByName.get(`${side}Arm`);
    const fore = this.boneByName.get(`${side}ForeArm`);
    const hand = this.boneByName.get(`${side}Hand`);
    if (!upper || !fore || !hand) return;
    upper.getWorldPosition(_cShoulder);
    fore.getWorldPosition(_cElbow);
    hand.getWorldPosition(_cReach);
    const baseLength = _cShoulder.distanceTo(_cElbow) + _cElbow.distanceTo(_cReach);
    if (baseLength < 1e-5) return;
    const required = _cShoulder.distanceTo(target) / Math.max(1e-3, baseLength * bentReach);
    const wanted = 1 + (THREE.MathUtils.clamp(required, 1, UNDERHAND_ARM_STRETCH_MAX) - 1)
      * THREE.MathUtils.clamp(weight, 0, 1);
    const k = 1 - Math.exp(-UNDERHAND_ARM_STRETCH_HZ * dt);
    let stretch = side === "Left" ? this.underhandStretchL : this.underhandStretchR;
    stretch += (wanted - stretch) * k;
    if (side === "Left") {
      this.underhandStretchL = stretch;
      this.underhandStretchAppliedL = stretch;
    } else {
      this.underhandStretchR = stretch;
      this.underhandStretchAppliedR = stretch;
    }
    upper.scale.x *= stretch;
    this.group.updateMatrixWorld(true);
  }

  /**
   * BARREL LOCK — point the muzzle at the aim by rotating the WEAPON, not the body.
   *
   * The gun-track corrects a mis-pointed barrel by pitching the chest and yawing both clavicles.
   * That works, but yawing the shoulder girdle is exactly what reads as "the body spins to aim the
   * gun", so the yaw is now capped at ~10°. The consequence was the opposite complaint: the muzzle
   * drifts off to the side with the gait (measured 29 cm of lateral muzzle swing at a walk, 70 cm
   * and 46° of barrel wobble at a run) with nothing left to pull it back.
   *
   * The residual belongs on the WEAPON. It is a child of hand_r, so a small corrective rotation in
   * its own local frame re-points the barrel while the arms, shoulders and torso keep the pose the
   * clip authored. Clamped, because a large correction would read as the gun pivoting loose in the
   * hands — beyond the clamp the gun-track's body correction is still the right tool.
   */
  private applyBarrelLock(): void {
    const k = this.aimK;
    if (k < 0.01 || !this.weapon || !this.muzzle || this.construct || this.farLOD) return;
    const hand = this.boneByName.get("RightHand");
    if (!hand) return;
    this.group.updateMatrixWorld(true);
    this.muzzle.getWorldPosition(_blMuzzle);
    if (this.bore) this.bore.getWorldPosition(_blGrip); else hand.getWorldPosition(_blGrip);
    _blBarrel.copy(_blMuzzle).sub(_blGrip);
    if (_blBarrel.lengthSq() < 1e-8) return;
    _blBarrel.normalize();
    this.aimDirWorld(_blAim);
    const dot = Math.max(-1, Math.min(1, _blBarrel.dot(_blAim)));
    const ang = Math.acos(dot);
    if (ang < 1e-4) return;
    _blDelta.setFromUnitVectors(_blBarrel, _blAim);           // minimal barrel -> aim, in WORLD
    // Like the clavicle yaw, the lock's clamp opens while moving: a visibly pivoting gun reads
    // wrong standing still, but mid-gait the extra authority is what keeps the muzzle downrange.
    const lockMax = BARREL_LOCK_MAX + 0.28 * this.moveK;
    if (ang > lockMax) {                                      // clamp: keep it a nudge, not a pivot
      _blDelta.slerp(_blIdentity, 1 - lockMax / ang);
    }
    if (k < 1) _blDelta.slerp(_blIdentity, 1 - k);            // ease with the aim
    // PIVOT AT THE FOREGRIP (2026-08-02): rotating about the weapon origin (the hand_r mount)
    // swept the FOREGRIP through ±10 cm of chest space while the gait swayed the body under the
    // aim-locked barrel — reach the near-straight SUPPORT arm does not have, so its hand skated
    // down the guard / came off it ("hand disconnects" report, twice). The FIRING arm, by
    // contrast, is deeply bent with slack to spare. So: apply the same rotation, then WALK THE
    // RIGHT HAND (2-bone IK, orientation preserved) so the foregrip point stays put — the
    // correction's translation lands in the elbow that can absorb it, the support hand's target
    // barely moves, and the barrel still lands exactly on the aim.
    const rifle = this.hasSupportGrip();
    if (rifle) {
      this.weapon.updateWorldMatrix(true, false);
      _bpBefore.copy(this.gripLTarget).applyMatrix4(this.weapon.matrixWorld);
    }
    // world delta -> the weapon's own local frame (its parent is hand_r)
    this.weapon.parent?.getWorldQuaternion(_blPar);
    _blLocal.copy(_blPar).invert().multiply(_blDelta).multiply(_blPar);
    this.weapon.quaternion.premultiply(_blLocal);
    if (rifle) {
      this.weapon.updateWorldMatrix(true, false);
      _bpAfter.copy(this.gripLTarget).applyMatrix4(this.weapon.matrixWorld);
      _bpDelta.copy(_bpBefore).sub(_bpAfter);
      const len = _bpDelta.length();
      if (len > 1e-4) {
        if (len > BL_PIVOT_MAX) _bpDelta.multiplyScalar(BL_PIVOT_MAX / len);
        const handR = this.boneByName.get("RightHand");
        const shR = this.boneByName.get("RightArm");
        const elR = this.boneByName.get("RightForeArm");
        if (handR && shR && elR) {
          handR.getWorldPosition(_bpHand);
          // POLE = the arm's CURRENT bend plane (elbow perpendicular). This pass translates the
          // hand centimeters — it must not RE-PLANE the elbow: a body-down pole here dragged the
          // right elbow ~8–12 cm INWARD off the authored stance (user report, 2026-08-02).
          shR.getWorldPosition(_bpPole);
          _bpAxis.copy(_bpHand).sub(_bpPole).normalize();
          elR.getWorldPosition(_bpElb).sub(_bpPole);
          _bpPole.copy(_bpElb).addScaledVector(_bpAxis, -_bpElb.dot(_bpAxis));
          if (_bpPole.lengthSq() < 1e-6) _bpPole.set(0, -1, 0).applyQuaternion(this.group.quaternion);
          else _bpPole.normalize();
          _bpHand.add(_bpDelta);
          this.ikLeg("RightArm", "RightForeArm", "RightHand", _bpHand, _bpPole, k, false);
          this.weapon.updateWorldMatrix(true, false);
        }
      }
    }
  }

  /** RELOAD pose: instead of spinning the gun, bring it muzzle-DOWN (rotate the right forearm) so the
   *  player visibly drops the weapon to reload; the support hand goes to the mag well (applyWeaponGrip).
   *  Trapezoid envelope off reloadP so it dips down, holds, and comes back up. */
  private applyReloadPose(): void {
    const rp = this.reloadP;
    if (rp === null || !this.weapon || this.farLOD) return;
    const down = Math.min(1, rp / 0.18, (1 - rp) / 0.18);
    if (down < 0.01) return;
    _aoFwd.set(0, 0, -1).applyQuaternion(this.group.quaternion);
    _aoUp.set(0, 1, 0).applyQuaternion(this.group.quaternion);
    _rRight.crossVectors(_aoFwd, _aoUp).normalize(); // body-right
    _rPitch.setFromAxisAngle(_rRight, -down * RELOAD_GUN_DOWN); // negative about body-right = muzzle DOWN
    this.rotateBoneWorld("RightForeArm", _rPitch); // forearm + hand + gun swing down at the elbow
  }

  /** GUN-TRACK: match the held barrel's ELEVATION to the reticle by rotating spine_05 PURELY about
   *  body-right (a clean pitch — never a yaw or roll). The arms + gun ride the chest via FK, so this
   *  raises/lowers the gun to the aim's up/down angle; the body already yaw-locks to the camera, so
   *  the barrel is forward horizontally. Pure pitch is the whole point: a full 2-DOF "aim the barrel
   *  AT the reticle" solve yaws/rolls the chest to align the rifle's angled barrel, which swings the
   *  neck + head out sideways (the rifle "head rolled way out right" bug). Bullets converge on the
   *  real hit point via the bolt (main.ts); the pose just needs the gun at the right elevation, level
   *  and centred. Eased by aimK. Falls back to the upright-pitch pin when there's no weapon to measure. */
  private applyGunAim(k: number): void {
    const chest = this.chestBone;
    const hand = this.boneByName.get("RightHand");
    if (!chest?.parent || !hand || !this.weapon || !this.muzzle) {
      this.applyGunPitchPin(k);
      return;
    }
    this.group.updateMatrixWorld(true);
    this.muzzle.getWorldPosition(_aoMuzzle);
    // bore → muzzle = the gun's TRUE mechanical axis; hand_r is only the legacy proxy
    if (this.bore) this.bore.getWorldPosition(_aoGrip); else hand.getWorldPosition(_aoGrip);
    _aoBarrel.copy(_aoMuzzle).sub(_aoGrip);
    if (_aoBarrel.lengthSq() < 1e-8) { this.applyGunPitchPin(k); return; } // muzzle ≈ hand: can't measure
    _aoBarrel.normalize();
    _aoFwd.set(0, 0, -1).applyQuaternion(this.group.quaternion);
    _aoUp.set(0, 1, 0).applyQuaternion(this.group.quaternion);
    _aoRight.crossVectors(_aoFwd, _aoUp).normalize(); // body-right — the ONLY axis we rotate about
    this.aimDirWorld(_aoAimDir); // barrel elevation/heading (the bolt fires ALONG this from the muzzle — main.ts)
    // Project barrel + aim into the sagittal plane (⊥ body-right) and take the signed pitch angle
    // between them about body-right. Rotating spine_05 by exactly this leaves chest yaw/roll alone.
    _aoBarrelP.copy(_aoBarrel).addScaledVector(_aoRight, -_aoBarrel.dot(_aoRight));
    _aoAimP.copy(_aoAimDir).addScaledVector(_aoRight, -_aoAimDir.dot(_aoRight));
    if (_aoBarrelP.lengthSq() < 1e-6 || _aoAimP.lengthSq() < 1e-6) { this.applyGunPitchPin(k); return; }
    _aoBarrelP.normalize();
    _aoAimP.normalize();
    const cross = _aoCross.crossVectors(_aoBarrelP, _aoAimP).dot(_aoRight);
    // RAW, same-frame correction ON PURPOSE. The measurement is absolute (the chest resets
    // pre-mixer), so applying it unfiltered cancels this frame's base-pose bob in this frame —
    // the closed loop IS the stabilizer. Low-passing this angle was tried and measured WORSE
    // (head ±5.6° → ±11.3° at ADS idle): the lag phase-shifts the correction against the bob it
    // is cancelling. Steadiness comes from calming the INPUT pose (applyAimArmSteady), not from
    // filtering the correction.
    const angle = Math.atan2(cross, Math.max(-1, Math.min(1, _aoBarrelP.dot(_aoAimP)))); // barrel→aim elevation
    _aoPitch.setFromAxisAngle(_aoRight, angle); // pure world pitch about body-right
    chest.getWorldQuaternion(_aoChestW);
    _aoWorld.copy(_aoPitch).multiply(_aoChestW); // pitched chest world
    if (this.aimRoll) { _aoRollQ.setFromAxisAngle(_aoFwd, this.aimRoll); _aoWorld.premultiply(_aoRollQ); } // a touch of aim-lean
    chest.parent.getWorldQuaternion(_aoParInv).invert();
    _aoTarget.copy(_aoParInv).multiply(_aoWorld); // → chest local
    chest.quaternion.slerp(_aoTarget, k); // ease onto the pitched pose
    // YAW correction: the gun is held in the right hand, so its barrel points off-axis horizontally.
    // Correct that by yawing BOTH gun-arms about body-up (NOT the chest — that would swing the head).
    // Re-measure the barrel after the chest pitch, take the horizontal angle to the aim, rotate the
    // clavicles by it (eased). The arms + gun yaw together so the grip holds; head/torso stay put.
    this.group.updateMatrixWorld(true);
    this.muzzle.getWorldPosition(_aoMuzzle);
    if (this.bore) this.bore.getWorldPosition(_aoGrip); else hand.getWorldPosition(_aoGrip);
    _aoBarrel.copy(_aoMuzzle).sub(_aoGrip);
    if (_aoBarrel.lengthSq() < 1e-8) return;
    _aoBarrel.normalize();
    _aoBarrelP.copy(_aoBarrel).addScaledVector(_aoUp, -_aoBarrel.dot(_aoUp)); // barrel in the horizontal plane
    _aoAimP.copy(_aoAimDir).addScaledVector(_aoUp, -_aoAimDir.dot(_aoUp)); // aim in the horizontal plane
    if (_aoBarrelP.lengthSq() < 1e-6 || _aoAimP.lengthSq() < 1e-6) return;
    // DEGENERACY GUARD. This yaw is measured in the HORIZONTAL PLANE, so as the barrel pitches the
    // projection shrinks and the measured angle blows up — then gets applied to BOTH clavicles (the
    // shoulder blades), which swings the shoulders out and drags the lats with them. Measured: a 30°
    // look-down swung the clavicles ±40°. Fade the correction out by how much barrel actually
    // REMAINS in the horizontal plane, and cap what is left: a two-handed rifle is already centred,
    // so a genuinely needed correction here is small — large values are projection artifacts.
    const horiz = _aoBarrelP.length(); // 0 = barrel vertical (no usable yaw), 1 = fully horizontal
    _aoBarrelP.normalize();
    _aoAimP.normalize();
    const yawCross = _aoCross.crossVectors(_aoBarrelP, _aoAimP).dot(_aoUp);
    let yaw = Math.atan2(yawCross, Math.max(-1, Math.min(1, _aoBarrelP.dot(_aoAimP))));
    // The clamp OPENS while moving: standing, a big clavicle yaw reads as "the body spins to
    // aim" (the original reason for the 10° cap), but mid-gait the same correction disappears
    // into the stride — and the diagonal clips need it (33–45° of barrel swing uncorrected).
    const yawMax = CLAVICLE_YAW_MAX + 0.17 * this.moveK;
    yaw = Math.max(-yawMax, Math.min(yawMax, yaw)) * k * Math.min(1, horiz / 0.5);
    if (Math.abs(yaw) < 1e-4) return;
    _aoPitch.setFromAxisAngle(_aoUp, yaw); // reuse _aoPitch as the world yaw delta
    this.rotateBoneWorld("LeftShoulder", _aoPitch);
    this.rotateBoneWorld("RightShoulder", _aoPitch);
  }

  /** Fallback gun-track when there's no weapon mesh to measure (the procedural primitives haven't
   *  built yet): pitch spine_05's upright world by the reticle pitch about body-right — the old
   *  behaviour, approximate but keeps the torso looking along the aim until the mesh exists. */
  private applyGunPitchPin(k: number): void {
    const chest = this.chestBone;
    if (!chest?.parent) return;
    this.group.updateMatrixWorld(true);
    _aoFwd.set(0, 0, -1).applyQuaternion(this.group.quaternion);
    _aoUp.set(0, 1, 0).applyQuaternion(this.group.quaternion);
    _aoRight.crossVectors(_aoFwd, _aoUp).normalize();
    _aoPitch.setFromAxisAngle(_aoRight, -this.aimPitch);
    this.group.getWorldQuaternion(_aoWorld).multiply(this.chestRestRelGroup);
    _aoWorld.premultiply(_aoPitch);
    chest.parent.getWorldQuaternion(_aoParInv).invert();
    _aoTarget.copy(_aoParInv).multiply(_aoWorld);
    chest.quaternion.slerp(_aoTarget, k);
  }

  dispose(scene: THREE.Scene): void {
    this.crystalWings?.dispose();
    this.crystalWings = null;
    for (const motion of this.hairMotion) motion.dispose();
    this.hairMotion.length = 0;
    LIVE_AVATARS.delete(this);
    this.mixer?.stopAllAction();
    this.mixer = null;
    scene.remove(this.group);
    this.group.traverse((o) => {
      // GLB body/cosmetic geometry is template-shared across avatars — disposeAvatarGeometry
      // frees only what this avatar owns (its covers index) and leaves the shared buffers.
      if (o instanceof THREE.Mesh) disposeAvatarGeometry(o.geometry);
      if (o instanceof THREE.Sprite) o.material.dispose();
    });
    this.skeleton.dispose();
    this.baseShirtMat.dispose();
    this.basePantsMat.dispose();
    // garmentMat is a per-avatar CLONE and so lives outside `materials` — for a
    // shared-material citizen it IS materials[5] and must not be touched.
    if (!this.sharedMats) {
      for (const m of this.materials) m.dispose();
      this.garmentMat.dispose();
    }
  }
}

/** Build one full avatar material set (indices: 0 suit, 1 detail, 2 visor/accent, 3 dark,
 *  4 glbSkin, 5 glbColored, 6 holo, 7 glbFaceSkin, 8 glbFaceDetail). Extracted from the
 *  constructor so ambient citizens can share a single set (sharedAmbientMaterials). */
/**
 * The "colored" (non-skin body parts + worn garments) material.
 *
 * A FACTORY rather than a `.clone()` of the shared one, because the per-avatar
 * garment instance needs the identical shader: `Material.clone()` copies the
 * declared fields but NOT `onBeforeCompile`, so a cloned garment silently lost
 * its lighting floor and shaded visibly cooler/darker than the body it sat on.
 */
function buildColoredMaterial(): THREE.MeshLambertMaterial {
  const material = new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true, fog: true });
  addBodyLightingFloor(material, 0.15, 0.15);
  return material;
}

function buildAvatarMaterials(): THREE.Material[] {
  const suit = new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true, fog: true });
  const detail = new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true, fog: true });
  const visor = new THREE.MeshLambertMaterial({ color: 0x000000, emissive: PALETTE.visorGlow, fog: true });
  const dark = new THREE.MeshLambertMaterial({ color: PALETTE_HEX.shadow, flatShading: true, fog: true });
  // GLB body-model materials: baked vertex colours with AUTHORED normals (flatShading would
  // discard the smooth-shaded face/curves baked by build-male.mts). glbSkin stays WHITE:
  // the skin-tone axis is painted into each avatar's COLOR_0 clone (paintAppearance), not
  // multiplied here — that is what lets ambient citizens share one material set.
  // The reference body is deliberately plane-shaded: each sculpted polygon carries a
  // stable studio-light value. Keep the morphing face on authored normals below, but use
  // geometric face normals across the torso and limbs.
  const glbSkin = new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true, fog: true });
  const glbColored = buildColoredMaterial();
  const glbFaceSkin = new THREE.MeshLambertMaterial({ vertexColors: true, fog: true });
  // Brows + lashes are a thin shell just above deforming face skin. A small depth bias
  // preserves that ordering when brow/lid morphs bring the two surfaces nearly coplanar.
  const glbFaceDetail = new THREE.MeshLambertMaterial({
    vertexColors: true, fog: true,
    polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2,
  });
  addBodyLightingFloor(glbSkin, 0.15, 0.15);
  // (glbColored's floor is applied inside buildColoredMaterial, so the per-avatar
  //  garment instance is built identically rather than cloned — see below.)
  // The face remains somewhat softer than the plane-shaded torso, but now carries the
  // reference's stronger cheek, nose, and jaw lighting gradients.
  addBodyLightingFloor(glbFaceSkin, 0.15);
  addBodyLightingFloor(glbFaceDetail, 0.15);
  // HOLOGRAPHIC body: the real body mesh, drawn as a projection. Additive over a near-black base
  // so it glows against the dark rather than tinting it, and depthWrite off so the far side reads
  // through the near one (a hologram is volume, not a shell). The heavy rim IS the fresnel edge.
  // The LIT colour carries the form (lambert shading over the sculpted body) and the emissive is
  // only a floor glow — at full emissive the whole mesh saturates to a flat cutout and the fresnel
  // has nothing to sit against.
  const holo = new THREE.MeshLambertMaterial({
    color: new THREE.Color(PALETTE.visorGlow),
    emissive: new THREE.Color(PALETTE.visorGlow),
    emissiveIntensity: 0.3,
    transparent: true,
    opacity: 0.78,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
    fog: true,
  });
  const materials = [suit, detail, visor, dark, glbSkin, glbColored, holo, glbFaceSkin, glbFaceDetail];
  // Stylized shading: rim light only — NO sun sheen (the specular read as incorrectly shiny).
  // paintLocal on every body material: the painted band edges are jittered by noise, and
  // keying that noise on WORLD space makes it swim across the skin as the character walks.
  // Object space pins it to the body, which is where a painter would have put it.
  addStylizedShading(suit, { rim: 0.5, rimPow: 2.5, spec: 0, paintLocal: true });
  addStylizedShading(detail, { rim: 0.5, rimPow: 2.5, spec: 0, paintLocal: true });
  addStylizedShading(visor, { rim: 0.6, rimPow: 2.5, spec: 0, paintLocal: true });
  addStylizedShading(dark, { rim: 0.4, rimPow: 3.0, spec: 0, paintLocal: true });
  addStylizedShading(glbSkin, { rim: 0.5, rimPow: 2.5, spec: 0, paintLocal: true });
  addStylizedShading(glbColored, { rim: 0.5, rimPow: 2.5, spec: 0, paintLocal: true });
  addStylizedShading(glbFaceSkin, { rim: 0.5, rimPow: 2.5, spec: 0, paintLocal: true });
  addStylizedShading(glbFaceDetail, { rim: 0.5, rimPow: 2.5, spec: 0, paintLocal: true });
  // The hologram opts OUT of the painted light: it is not a lit surface, and banding
  // a projection reads as a rendering fault rather than as a brush stroke.
  addStylizedShading(holo, { rim: 2.4, rimPow: 1.5, spec: 0, painted: false }); // wide, hot fresnel
  return materials;
}

/** The ONE material set every ambient citizen shares (never tinted, never disposed).
 *  Suit/accent/holo keep their defaults — citizens wear no accent-group pieces (the
 *  drifter set is all "colored"; only the unworn head slot carries accent trim). */
let _sharedAmbient: THREE.Material[] | null = null;
function sharedAmbientMaterials(): THREE.Material[] {
  return (_sharedAmbient ??= buildAvatarMaterials());
}

/** Soft round white→transparent radial gradient for the combat glow sprites (tinted per
 *  material). Lazily built + cached so the node test importing this module never touches DOM. */
let _glowTex: THREE.CanvasTexture | undefined;
function glowTexture(): THREE.CanvasTexture {
  if (_glowTex) return _glowTex;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = 64;
  const ctx = canvas.getContext("2d")!;
  const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  g.addColorStop(0, "rgba(255,255,255,1)");
  g.addColorStop(0.45, "rgba(255,255,255,0.45)");
  g.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 64, 64);
  _glowTex = new THREE.CanvasTexture(canvas);
  return _glowTex;
}
